import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from "@angular/core";
import { Location } from "@angular/common";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { ActivatedRoute, Router } from "@angular/router";
import { ReportService } from "../report-system/core/services/report.service";
import { DataShareService } from "../report-system/core/services/data-share.service";
import { UserManagementService } from "../user-management/user-management.service";
import {
  DashboardGroupWithReportsResponse,
  DashboardReportData,
  DashboardReportParameter,
  DashboardWithReportsItem,
  SavedDocumentListItem,
  SavedDocumentDetail,
  SavedIntervalWindow,
} from "../report-system/core/utils/types/report.types";
import { firstValueFrom } from "rxjs";

// One report whose chosen frequency range differs from its original configured
// period — shown in the "Apply" change-review dialog.
interface PdfReportChange {
  id: number;
  name: string;
  originalFrom: string;
  originalTo: string;
  newFrom: string;
  newTo: string;
}

// Changed reports grouped by frequency, with an overall count.
interface PdfApplyChanges {
  daily: PdfReportChange[];
  weekly: PdfReportChange[];
  monthly: PdfReportChange[];
  total: number;
}

@Component({
  selector: "app-downloadpdf",
  templateUrl: "./downloadpdf.component.html",
  styleUrls: ["./downloadpdf.component.css"],
  standalone: false,
})
export class DownloadpdfComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly debugPdf = false;
  private readonly debugSelection = false;
  // Dedicated table-render tracing, independent of debugPdf so it can be left on
  // (or flipped off) without the full pagination firehose.
  private readonly debugTables = true;
  private reportContentCache = new Map<number, string>();
  private selectedResizableWrapper: HTMLElement | null = null;
  @ViewChildren("pageEditor") pageEditors!: QueryList<ElementRef<HTMLDivElement>>;
  @ViewChild("pagesWorkspaceRef") pagesWorkspaceRef!: ElementRef<HTMLDivElement>;
  @ViewChild("blockPickerRef") blockPickerRef!: ElementRef<HTMLDivElement>;
  @ViewChild("fontPickerRef") fontPickerRef!: ElementRef<HTMLDivElement>;
  @ViewChild("sizePickerRef") sizePickerRef!: ElementRef<HTMLDivElement>;
  @ViewChild("lineSpacingPickerRef") lineSpacingPickerRef!: ElementRef<HTMLDivElement>;

  pages: Array<{ id: number; html: string }> = [
    {
      id: 1,
      html: "<h2>Document Title</h2><p>Start writing your document here.</p>",
    },
  ];
  activePageIndex = 0;
  // Page currently in the scroll viewport — drives the "Page X of Y" footer.
  // Kept separate from activePageIndex, which reflow pushes to the last page.
  currentPageIndex = 0;
  private nextPageId = 2;
  // Number of fixed leading pages that sit OUTSIDE the reflow flow before the
  // flowing content begins. MOR reports reserve 1 (the full-bleed cover on page
  // 1); reflow never backfills it. The Table of Contents is NOT counted here — it
  // leads the flowing content as a page-exclusive block so it can span multiple
  // pages. 0 means content starts on page 1 (all non-MOR reports).
  private reservedLeadingPages = 0;

  activeBlock = "p";
  selectedFont = "Arial";
  selectedFontSize = 16;
  selectedLineSpacing = 1.5;
  // Active inline-format states for the toolbar (reflect the current selection).
  isBold = false;
  isItalic = false;
  isUnderline = false;
  isStrike = false;
  private toolbarSyncPending = false;
  // Selection captured before a focus-stealing control (e.g. the native color
  // picker) clears it, so the chosen format can be applied to the right text.
  private savedSelectionRange: Range | null = null;
  // Caret preservation across a reflow. Reflow moves DOM nodes between page
  // editors, which drops the caret (it jumps to the top of the page and vanishes
  // mid-typing). A zero-width marker is dropped at the caret before reflow and
  // travels with its block; once reflow settles the caret is re-placed at the
  // marker. See insertCaretMarker() / restorePendingCaret().
  private caretMarkerSeq = 0;
  private pendingCaretMarkerId: string | null = null;
  // Set after inserting a table so we scroll to it once reflow fully settles
  // (pagination is async and may move the table to a different page first).
  private pendingTableReveal = false;
  // The chart/table/image unit currently being dragged, so we can MOVE the real
  // node on drop instead of letting the browser copy it across page editors.
  private draggedUnit: HTMLElement | null = null;
  textColor = "#111827";
  highlightColor = "#ffff00";
  wordCount = 0;
  characterCount = 0;
  editorLoading = true;
  // Shown before anything is loaded/rendered. The user must choose how to start;
  // "Continue with Existing" runs the current dashboard-group render flow.
  entryDialogOpen = true;

  // Set when the group opened is a MOR (Monthly Operation Report) type, flagged
  // via the ?isMor=1 query param from the report list. MOR reports require the
  // user to name the MOR before proceeding (both "Apply" and "Continue"); the
  // entry dialog then surfaces a required "MOR Name" field above the frequency
  // ranges.
  isMorReport = false;
  // The MOR cover title, entered line by line (Line 1, Line 2, …). Line 1 is
  // required; further lines are optional and filled top-down (a line can't be
  // filled while an earlier one is blank). Empty lines are never rendered. The
  // lines are stacked on the cover with a size/weight hierarchy. For a MOR the
  // three lines are prefilled on open — see buildDefaultMorNameLines().
  morNameLines: string[] = ["Monthly Operation Report"];
  morNameError = "";
  private readonly maxMorNameLines = 6;
  // A single MOR name input by default, with a button beside it that reveals the
  // line-by-line builder. MOR groups open with the builder already revealed so
  // the prefilled lines 2 and 3 are visible and editable, not hidden behind the
  // button.
  morMultiLineOpen = false;

  // Entry dialog → left "data configuration" section. On open we collect every
  // parameter code in the group and ask the backend for the date range it holds
  // data for, then prefill "Data from" = oldest, "Data to" = newest.
  dataConfigLoading = false;
  dataConfigError = "";
  // The left section offers three frequency groups (Daily / Weekly / Monthly),
  // each with its own "Data from / Data to" range. On Apply / Continue, each
  // report is scoped by the range of ITS OWN configured frequency — daily-
  // configured reports use the Daily range, monthly reports the Monthly range,
  // and a frequency with no reports is simply never applied.
  readonly pdfIntervalOptions: Array<{
    value: "daily" | "weekly" | "monthly";
    label: string;
  }> = [
    { value: "daily", label: "Daily" },
    { value: "weekly", label: "Weekly" },
    { value: "monthly", label: "Monthly" },
  ];
  intervalRanges: Record<
    "daily" | "weekly" | "monthly",
    { from: string; to: string }
  > = {
    daily: { from: "", to: "" },
    weekly: { from: "", to: "" },
    monthly: { from: "", to: "" },
  };
  // Each frequency group has a collapsible "Reports" list — all closed by
  // default. Opening one reveals the reports configured at that frequency.
  expandedIntervals: Record<"daily" | "weekly" | "monthly", boolean> = {
    daily: false,
    weekly: false,
    monthly: false,
  };
  // The group's reports bucketed by their configured frequency, listed inside the
  // matching group. Populated once the group payload loads.
  intervalReports: Record<
    "daily" | "weekly" | "monthly",
    Array<{ id: number; name: string }>
  > = { daily: [], weekly: [], monthly: [] };
  // Text reports (headings / narrative blocks) have no data range, so they're
  // kept out of the frequency accordions and surfaced as a single count instead.
  textReportCount = 0;
  dataBoundsOldest = ""; // min selectable date (oldest available)
  dataBoundsNewest = ""; // max selectable date (newest available)
  // The window actually applied to the render, PER frequency. A report is clipped
  // to the window of its own configured frequency. Empty = no window for that
  // frequency (render every embedded value, as before).
  private exportWindows: Record<
    "daily" | "weekly" | "monthly",
    { from: string; to: string }
  > = {
    daily: { from: "", to: "" },
    weekly: { from: "", to: "" },
    monthly: { from: "", to: "" },
  };
  // Overall span across the per-frequency windows — used only for the saved-
  // document metadata (dataFrom / dataTo) and hydrate, not for clipping.
  private exportWindowFrom = "";
  private exportWindowTo = "";

  // "Apply" refetches each report's data for the chosen period (sequentially,
  // in render order) and shows a 1/N progress counter. Text reports are skipped.
  applyInProgress = false;
  applyCurrent = 0;
  applyTotal = 0;
  applyError = "";

  // "Apply" change review: a report is "changed" when the range chosen for its
  // frequency differs from the period it was originally configured with. When any
  // report changed, Apply first asks the user to review the changes or apply
  // directly; "Review" shows the per-frequency breakdown below.
  applyConfirmOpen = false; // the review-or-apply prompt
  applyReviewOpen = false; // the detailed per-frequency change list
  applyReviewError = ""; // validation error for an edited per-report range
  applyChanges: PdfApplyChanges = {
    daily: [],
    weekly: [],
    monthly: [],
    total: 0,
  };
  // Per-report range overrides set by editing a row in the review dialog. Keyed
  // by reportId; when present, a report is fetched/clipped to this range instead
  // of its frequency's range.
  private reportWindows = new Map<number, { from: string; to: string }>();

  // Entry dialog → right "Saved Documents" section. Documents the user previously
  // saved (for this site) that can be reopened and edited.
  savedDocsLoading = false;
  savedDocsError = ""; // load failure — gates the list
  savedDocActionError = ""; // open/delete failure — shown without hiding the list
  savedDocuments: SavedDocumentListItem[] = [];
  savedDocSearch = ""; // filters the saved-documents list by name/owner
  savedDocOpeningId: string | number | null = null;
  // Identity of the document currently in the editor. Set when a saved document
  // is opened (or after the first save) so re-saving updates it in place instead
  // of creating a new one. Null = a brand-new, never-saved document.
  private currentDocumentId: string | number | null = null;
  private currentDocumentName: string | null = null;
  // Per-row kebab (•••) menu: which row's menu is open, which is awaiting a
  // delete confirmation, and which is mid-delete.
  savedDocMenuId: string | number | null = null;
  savedDocConfirmDeleteId: string | number | null = null;
  savedDocDeletingId: string | number | null = null;
  // Viewport coords for the open menu so it can be position:fixed and escape the
  // scrollable list (otherwise the list clips it and scrolls to reveal it).
  savedDocMenuPos: { top: number; right: number } | null = null;
  // Group payload fetched for the data config, reused on "Continue" so the
  // render flow doesn't refetch the (potentially multi-MB) with-reports payload.
  private cachedGroupForRender: {
    id: number;
    data: DashboardGroupWithReportsResponse;
  } | null = null;

  exportInProgress = false;
  exportProgress = 0;
  exportProgressText = "Preparing export...";
  exportDialogOpen = false;
  exportFileName = "";
  private exportCancelled = false;
  imageToolbarVisible = false;
  imageToolbarTop = 0;
  imageToolbarLeft = 0;
  // The floating chart/image toolbar opens collapsed to a single handle so
  // selecting a visual never hides the document text sitting behind it. The
  // user expands it when they need the controls, and the choice sticks for the
  // rest of the session (repeated nudge/zoom edits shouldn't cost a click each).
  imageToolbarExpanded = false;
  rawDebugLines: string[] = [];
  chartEditorOpen = false;
  chartEditorMessage = "";
  chartEditorTitle = "";
  chartEditorTitleSize = 24;
  chartEditorTitleColor = "#f8fafc";
  chartEditorBgStart = "#1f2937";
  chartEditorBgCenter = "#2b3444";
  chartEditorBgEnd = "#3f3f46";
  chartEditorBgOuter = "#111827";
  chartEditorDateLabelMode: "all" | "auto" = "auto";
  chartEditorDateLabelRotation = -40;
  chartEditorValueLabelSize = 11;
  chartEditorValueLabelRotation = 0;
  chartEditorLegendSize = 15;
  chartEditorXAxisLabelSize = 12;
  chartEditorYAxisLabelSize = 12;
  chartEditorTitlePadTop = 0;
  chartEditorTitlePadRight = 0;
  chartEditorTitlePadBottom = 0;
  chartEditorTitlePadLeft = 0;
  chartEditorMultiAxis = false;
  chartEditorSeriesAxis: boolean[] = [];
  chartEditorSeriesNames: string[] = [];
  chartEditorSeriesColors: string[] = [];
  chartEditorPreviewDataUrl = "";
  chartEditorChartType:
    | "bar" | "line" | "dot" | "bar_label" | "line_label"
    | "pie" | "donut" | "gauge" | "numeric" | "table" | "text" | "box" = "bar";
  readonly chartTypeOptions: Array<{
    value: "bar" | "line" | "dot" | "bar_label" | "line_label" | "pie" | "donut" | "gauge" | "table";
    label: string;
  }> = [
    { value: "bar", label: "Bar" },
    { value: "line", label: "Line" },
    { value: "dot", label: "Dot" },
    { value: "bar_label", label: "Bar Label" },
    { value: "line_label", label: "Line Label" },
    { value: "table", label: "Table" },
    { value: "gauge", label: "Gauge" },
    { value: "pie", label: "Pie" },
    { value: "donut", label: "Donut" },
  ];
  tableEditorOpen = false;
  tableEditorMessage = "";
  tableEditorTitleBg = "#0b2345";
  tableEditorHeaderBg = "#021b39";
  tableEditorOddRowBg = "#15335a";
  tableEditorEvenRowBg = "#001a3a";
  tableEditorTextColor = "#f8fafc";
  tableEditorBorderColor = "#2b3f59";
  tableEditorTitleAlign: "left" | "center" | "right" = "left";
  tableEditorTitleSize = 13;
  tableEditorPreviewHtml: SafeHtml = "";
  private chartEditorBasePayload: {
    title: string;
    chartType?: "bar" | "line" | "dot" | "bar_label" | "line_label" | "pie" | "donut" | "gauge" | "numeric" | "table" | "text" | "box";
    chartData: {
      categories: string[];
      series: Array<{ name: string; data: Array<number | null>; type: "bar" | "line" | "dot" | "bar_label" | "line_label" | "pie" | "donut" | "gauge" | "numeric" | "table" | "text" | "box" }>;
    };
    options?: {
      bgStart?: string;
      bgCenter?: string;
      bgEnd?: string;
      bgOuter?: string;
      seriesColors?: string[];
      // The text painted on the snapshot. Distinct from `title` (the report name,
      // which the interval detection keys off): "" paints no title, and undefined
      // falls back to `title`. See drawChartSnapshotDataUrl.
      titleText?: string;
      titleColor?: string;
      titleFontSize?: number;
      legendTextColor?: string;
      gridColor?: string;
      axisTextColor?: string;
      dateLabelMode?: "all" | "auto";
      dateLabelRotation?: number;
      valueLabelSize?: number;
      valueLabelRotation?: number;
      legendFontSize?: number;
      xAxisLabelSize?: number;
      yAxisLabelSize?: number;
      titlePadTop?: number;
      titlePadRight?: number;
      titlePadBottom?: number;
      titlePadLeft?: number;
      multiAxis?: boolean;
      multiAxisIndices?: number[];
    };
  } | null = null;
  private chartEditorTargetWrapper: HTMLElement | null = null;
  private tableEditorTargetWrapper: HTMLElement | null = null;
  dialogType: "none" | "link" | "table" = "none";
  linkUrl = "";
  tableRows = 2;
  tableCols = 2;
  fontDropdownOpen = false;
  blockDropdownOpen = false;
  sizeDropdownOpen = false;
  lineSpacingDropdownOpen = false;
  private lastLoadedDashboardGroupId: number | null = null;
  private readonly maxAutoPages = 80;

  // --- Local draft persistence (autosave to localStorage) ---
  // No backend "save document" endpoint exists, so edits are persisted locally,
  // keyed per dashboard group. This never touches stored reports/dashboards.
  private readonly draftVersion = 1;
  // Drafts are stored in IndexedDB (handles large chart-snapshot documents that
  // exceed the ~5 MB localStorage quota). localStorage is kept only as a
  // read-migration source and a tiny-document fallback.
  private readonly draftDbName = "downloadpdf-drafts";
  private readonly draftStoreName = "drafts";
  private draftDb: IDBDatabase | null = null;
  private documentReady = false;
  private draftAutosaveTimer: ReturnType<typeof setTimeout> | null = null;
  // Set when an edit asks to autosave while a reflow is mid-flight (the page
  // model is intentionally stale during pagination). reflowFrom re-arms the
  // autosave once it settles so we never persist a half-paginated document.
  private draftAutosavePending = false;
  // True once the user explicitly restored a draft, so a still-pending API load
  // must not overwrite their restored document.
  private userRestoredDraft = false;
  private pendingDraft: {
    pages: Array<{ id: number; html: string }>;
    savedAt: number;
    activePageIndex: number;
  } | null = null;
  draftRestoreAvailable = false;
  draftSaving = false;
  draftSaveError = false;
  draftSavedAt: number | null = null;
  // Drives the auto-save status indicator in the toolbar.
  draftState: "idle" | "unsaved" | "saving" | "saved" | "error" = "idle";

  // --- Structural table editing (floating toolbar) ---
  tableToolbarVisible = false;
  tableToolbarTop = 0;
  tableToolbarLeft = 0;
  private selectedTableCell: HTMLTableCellElement | null = null;
  // Cell fill color + the cells captured (from the current selection) to fill.
  cellFillColor = "#fde68a";
  private cellColorTargets: HTMLTableCellElement[] = [];

  // --- Hover "+" controls to add a column (right) / row (bottom) on any table ---
  tableControlsVisible = false;
  tableColAddTop = 0;
  tableColAddLeft = 0;
  tableRowAddTop = 0;
  tableRowAddLeft = 0;
  private hoveredTable: HTMLElement | null = null;
  private tableControlsHideTimer: ReturnType<typeof setTimeout> | null = null;
  private tableHoverRafPending = false;
  // --- Hover "−" controls to delete the column (top) / row (left) under the
  // cursor on any table, including report tables (applied across page fragments) ---
  tableDelControlsVisible = false;
  tableDelColTop = 0;
  tableDelColLeft = 0;
  tableDelRowTop = 0;
  tableDelRowLeft = 0;
  private hoveredCell: HTMLTableCellElement | null = null;
  // True while reflowFrom() is paginating. During reflow the live DOM is the
  // source of truth, so the pageEditors.changes subscription must not re-hydrate
  // from the (stale) page model — that would re-inject content into editors
  // reflow just emptied, duplicating reports.
  private reflowInProgress = false;
  // The full render order (reports as they lay out, group-wide deduped), captured
  // at render time. The Table of Contents is driven by MOR Heading reports: each
  // becomes a bold top-level entry and the normal reports that follow it become
  // numbered children. Only what comes AFTER the TOC block is listed. Page numbers
  // are resolved later from the paginated DOM by refreshTableOfContents().
  private tocOrder: Array<{
    reportId: number;
    name: string;
    isToc: boolean;
    isMorHead: boolean;
  }> = [];
  // For MOR reports: the dashboard→reports grouping used to build the page-2
  // Table of Contents. Each dashboard becomes a numbered heading (1., 2., …) and
  // its reports the numbered children (1.1, 1.2, …). Page numbers are resolved
  // from the paginated DOM by refreshTableOfContents() (reused via the shared
  // data-toc-page-for / data-report-id markers).
  private morTocGroups: Array<{
    name: string;
    reports: Array<{ id: number; name: string }>;
  }> = [];
  private readonly defaultChartPalette = [
    "#4f7de8",
    "#7cc85e",
    "#2dd4bf",
    "#f59e0b",
    "#ef4444",
    "#a78bfa",
  ];

  readonly blockOptions: Array<{ value: "p" | "h1" | "h2" | "h3"; label: string }> = [
    { value: "p", label: "Paragraph" },
    { value: "h1", label: "Heading 1" },
    { value: "h2", label: "Heading 2" },
    { value: "h3", label: "Heading 3" },
  ];

  readonly fontFamilies = [
    "Arial",
    "Arial Black",
    "Bahnschrift",
    "Book Antiqua",
    "Calibri",
    "Cambria",
    "Candara",
    "Century Gothic",
    "Comic Sans MS",
    "Consolas",
    "Constantia",
    "Corbel",
    "Courier New",
    "Franklin Gothic Medium",
    "Garamond",
    "Georgia",
    "Impact",
    "Lucida Console",
    "Lucida Sans Unicode",
    "Microsoft Sans Serif",
    "Palatino Linotype",
    "Segoe Print",
    "Segoe Script",
    "Segoe UI",
    "Tahoma",
    "Times New Roman",
    "Trebuchet MS",
    "Verdana",
  ];

  readonly fontSizes = [12, 14, 16, 18, 20, 24, 28, 32, 36];
  readonly lineSpacingOptions = [1, 1.15, 1.5, 2, 2.5, 3];

  constructor(
    private route: ActivatedRoute,
    private reportService: ReportService,
    private location: Location,
    private router: Router,
    private sanitizer: DomSanitizer,
    private dataShareService: DataShareService,
    private userService: UserManagementService
  ) {}

  ngOnInit(): void {
    // Defer all loading/rendering until the user picks an option in the entry
    // dialog. Drop the editor loader so the dialog (z-index 4001) isn't covered
    // by the loader overlay (z-index 5000) while it's open.
    this.entryDialogOpen = true;
    this.editorLoading = false;

    // MOR groups arrive with ?isMor=1 and must be named before proceeding. The
    // name is prefilled with the usual three-line cover title, with the builder
    // revealed so all three lines are editable up front.
    this.isMorReport =
      this.route.snapshot.queryParamMap.get("isMor") === "1";
    if (this.isMorReport) {
      this.morNameLines = this.buildDefaultMorNameLines();
      this.morMultiLineOpen = true;
    }

    // While the dialog is open, fetch the group's reports to learn its data date
    // range and prefill the left "data configuration" section.
    const dashboardGroupId = Number(
      this.route.snapshot.queryParamMap.get("dashboardGroupId")
    );
    if (dashboardGroupId && !Number.isNaN(dashboardGroupId)) {
      this.loadDataConfiguration(dashboardGroupId);
    }

    // Right section: list documents the user previously saved for this site.
    this.loadSavedDocuments();
  }

  /** Saved documents matching the search box (by name or owner). */
  get filteredSavedDocuments(): SavedDocumentListItem[] {
    const query = this.savedDocSearch.trim().toLowerCase();
    if (!query) {
      return this.savedDocuments;
    }
    return this.savedDocuments.filter((doc) => {
      const name = (doc.documentName || "").toLowerCase();
      const owner = String(doc.createdByName || doc.createdBy || "").toLowerCase();
      return name.includes(query) || owner.includes(query);
    });
  }

  /** Entry dialog → right section: load the site's saved documents. */
  private loadSavedDocuments(): void {
    const siteId = this.dataShareService.getSiteId();
    if (!siteId) {
      this.savedDocuments = [];
      return;
    }
    this.savedDocsLoading = true;
    this.savedDocsError = "";
    this.reportService.listDocuments(siteId).subscribe({
      next: (response) => {
        this.savedDocsLoading = false;
        this.savedDocuments =
          response?.success && Array.isArray(response.data) ? response.data : [];
      },
      error: () => {
        this.savedDocsLoading = false;
        this.savedDocsError = "Could not load saved documents.";
      },
    });
  }

  /** Open a previously saved document and hydrate it into the editor. */
  openSavedDocument(doc: SavedDocumentListItem): void {
    if (this.savedDocOpeningId !== null) {
      return;
    }
    this.savedDocActionError = "";
    this.savedDocOpeningId = doc.id;
    this.reportService.getDocument(doc.id).subscribe({
      next: (response) => {
        const detail = response?.data;
        if (!response?.success || !detail) {
          this.failOpenSavedDocument();
          return;
        }
        // Large documents come back as a presigned S3 URL instead of inline
        // pages (a multi-MB response would exceed the API limit) — fetch it.
        if (!Array.isArray(detail.pages) && detail.downloadUrl) {
          void this.hydrateFromDownloadUrl(detail);
          return;
        }
        if (!Array.isArray(detail.pages)) {
          this.failOpenSavedDocument();
          return;
        }
        this.hydrateFromSavedDocument(detail);
      },
      error: () => {
        this.failOpenSavedDocument();
      },
    });
  }

  /** Fetch a large document's JSON (incl. pages) from its presigned S3 URL. */
  private async hydrateFromDownloadUrl(
    detail: SavedDocumentDetail
  ): Promise<void> {
    try {
      const res = await fetch(detail.downloadUrl as string);
      if (!res.ok) {
        throw new Error(`document download failed (${res.status})`);
      }
      const json = await res.json();
      const merged: SavedDocumentDetail = { ...detail, ...json };
      if (!Array.isArray(merged.pages)) {
        this.failOpenSavedDocument();
        return;
      }
      this.hydrateFromSavedDocument(merged);
    } catch (error) {
      console.error("[PDF] open saved document failed", error);
      this.failOpenSavedDocument();
    }
  }

  /** Shared failure path when a saved document can't be opened. */
  private failOpenSavedDocument(): void {
    this.savedDocOpeningId = null;
    this.savedDocActionError = "Could not open that document.";
    this.loadSavedDocuments();
  }

  /** Toggle a row's kebab (•••) menu; closes any other open menu. */
  toggleSavedDocMenu(doc: SavedDocumentListItem, event: Event): void {
    event.stopPropagation();
    this.savedDocConfirmDeleteId = null;
    if (this.savedDocMenuId === doc.id) {
      this.closeSavedDocMenu();
      return;
    }
    // Anchor the fixed-position menu just under the kebab button. The dialog has
    // a CSS transform, so position:fixed is relative to the dialog box (not the
    // viewport) — compute the offset relative to the dialog accordingly.
    const btn = event.currentTarget as HTMLElement | null;
    if (btn) {
      const rect = btn.getBoundingClientRect();
      const dialog = btn.closest(".entry-choice-dialog") as HTMLElement | null;
      const base = dialog ? dialog.getBoundingClientRect() : null;
      const baseTop = base ? base.top : 0;
      const baseRight = base ? base.right : window.innerWidth;
      this.savedDocMenuPos = {
        top: rect.bottom - baseTop + 4,
        right: baseRight - rect.right,
      };
    }
    this.savedDocMenuId = doc.id;
  }

  /** Close the kebab menu (and any pending delete confirmation). */
  closeSavedDocMenu(): void {
    this.savedDocMenuId = null;
    this.savedDocConfirmDeleteId = null;
    this.savedDocMenuPos = null;
  }

  /** Menu → Open. */
  onSavedDocMenuOpen(doc: SavedDocumentListItem, event: Event): void {
    event.stopPropagation();
    this.closeSavedDocMenu();
    this.openSavedDocument(doc);
  }

  /** True when the current user saved this document (owner-only delete). */
  private isOwnDocument(doc: SavedDocumentListItem): boolean {
    const userId = this.userService.getAuthenticatedUserId();
    return (
      !!userId &&
      doc.createdBy !== null &&
      doc.createdBy !== undefined &&
      String(doc.createdBy) === String(userId)
    );
  }

  /** Menu → Delete: block non-owners up front, else ask for confirmation. */
  onSavedDocMenuDelete(doc: SavedDocumentListItem, event: Event): void {
    event.stopPropagation();
    if (!this.isOwnDocument(doc)) {
      this.savedDocActionError = "You can only delete documents you saved.";
      this.closeSavedDocMenu();
      return;
    }
    this.savedDocConfirmDeleteId = doc.id;
  }

  /** Inline confirm → cancel. */
  cancelSavedDocDelete(event: Event): void {
    event.stopPropagation();
    this.savedDocConfirmDeleteId = null;
  }

  /** Inline confirm → delete the document. */
  confirmSavedDocDelete(doc: SavedDocumentListItem, event: Event): void {
    event.stopPropagation();
    if (this.savedDocDeletingId !== null) {
      return;
    }
    this.savedDocActionError = "";
    this.savedDocDeletingId = doc.id;
    this.reportService.deleteDocument(doc.id).subscribe({
      next: (response) => {
        this.savedDocDeletingId = null;
        if (response?.success === false) {
          this.savedDocActionError = "Could not delete that document.";
          this.closeSavedDocMenu();
          this.loadSavedDocuments();
          return;
        }
        // Drop it from the list without a full refetch.
        this.savedDocuments = this.savedDocuments.filter(
          (item) => item.id !== doc.id
        );
        this.closeSavedDocMenu();
      },
      error: (err) => {
        this.savedDocDeletingId = null;
        // Backend enforces owner-only delete → 403 for another user's document.
        this.savedDocActionError =
          err?.status === 403
            ? "You can only delete documents you saved."
            : "Could not delete that document.";
        this.closeSavedDocMenu();
        // Re-render the list so it stays in sync after the failed attempt.
        this.loadSavedDocuments();
      },
    });
  }

  /**
   * Replace the editor content with a saved document's pages (mirrors
   * restoreDraft). Skips the dashboard-group render path entirely.
   */
  private hydrateFromSavedDocument(detail: SavedDocumentDetail): void {
    // A fresh dashboard load must not overwrite the document the user just opened.
    this.userRestoredDraft = true;
    this.entryDialogOpen = false;
    this.documentReady = false;
    this.editorLoading = true;

    // Remember which document this is so re-saving updates it in place.
    this.currentDocumentId = detail.id;
    this.currentDocumentName = detail.documentName;
    // Restore the saved data window so it persists across reopen → re-save.
    this.exportWindowFrom = detail.dataFrom || "";
    this.exportWindowTo = detail.dataTo || "";
    // Prefer the per-frequency ranges when the document carries them; otherwise
    // fall back to applying the single overall range to every frequency.
    const savedWindows = new Map(
      (detail.intervalWindows || []).map((win) => [win.interval, win])
    );
    for (const opt of this.pdfIntervalOptions) {
      const win = savedWindows.get(opt.value);
      const from = win?.from || detail.dataFrom || "";
      const to = win?.to || detail.dataTo || "";
      if (from) {
        this.intervalRanges[opt.value].from = from;
      }
      if (to) {
        this.intervalRanges[opt.value].to = to;
      }
      this.exportWindows[opt.value] = {
        from: this.intervalRanges[opt.value].from,
        to: this.intervalRanges[opt.value].to,
      };
    }

    this.pages = (detail.pages || []).map((page, index) => ({
      id: Number(page?.id) || index + 1,
      html: String(page?.html || ""),
    }));
    if (!this.pages.length) {
      this.pages = [{ id: 1, html: "" }];
    }
    this.nextPageId =
      this.pages.reduce(
        (max, page) => Math.max(max, Number(page.id) || 0),
        0
      ) + 1;
    this.activePageIndex = Math.min(
      Math.max(0, detail.activePageIndex || 0),
      this.pages.length - 1
    );
    this.savedDocOpeningId = null;

    setTimeout(() => {
      this.hydratePagesToDom(true);
      this.reflowFrom(0);
      this.focusActivePage();
      this.updateDocumentStats();
      this.editorLoading = false;
      this.documentReady = true;
      this.draftState = "saved";
    }, 0);
  }

  /** Toggle an interval accordion open/closed (independent of the others). */
  toggleIntervalAccordion(value: "daily" | "weekly" | "monthly"): void {
    this.expandedIntervals[value] = !this.expandedIntervals[value];
  }

  /**
   * Bucket the group's DATA reports by their own configured frequency so each
   * accordion lists only the reports that render at that interval. Text reports
   * (headings / narrative blocks) carry no data range, so they're excluded from
   * the buckets and counted separately (textReportCount). Reordered/deduped the
   * same way the renderer lays them out.
   */
  private groupReportsByInterval(): void {
    const buckets: Record<
      "daily" | "weekly" | "monthly",
      Array<{ id: number; name: string }>
    > = { daily: [], weekly: [], monthly: [] };
    let textCount = 0;
    const group = this.cachedGroupForRender?.data;
    if (group) {
      for (const report of this.collectRenderReports(group)) {
        if (this.isTextReport(report)) {
          textCount += 1;
          continue;
        }
        const bucket = this.normalizeIntervalBucket(report.interval);
        if (bucket) {
          buckets[bucket].push({
            id: report.reportId,
            name: report.reportName || `Report ${report.reportId}`,
          });
        }
      }
    }
    this.textReportCount = textCount;
    this.intervalReports = buckets;
  }

  /**
   * Map a report's configured frequency onto one of the three accordion buckets.
   * Sub-daily intervals (hourly/minute) collapse into Daily; anything
   * unrecognised is dropped rather than mis-bucketed.
   */
  private normalizeIntervalBucket(
    interval: string | null | undefined
  ): "daily" | "weekly" | "monthly" | null {
    switch (interval) {
      case "monthly":
        return "monthly";
      case "weekly":
        return "weekly";
      case "daily":
      case "15minutes":
      case "30minutes":
      case "1hour":
        return "daily";
      default:
        return null;
    }
  }

  /** Entry dialog close (×): leave the editor without rendering anything. */
  closeEntryDialog(): void {
    this.entryDialogOpen = false;
    void this.closeEditor();
  }

  /**
   * The MOR name the entry dialog opens with: the report title, the site it's
   * for, and the month it covers (the previous one) — e.g. opened for BPCL
   * Mumbai in July 2026:
   *
   *   Monthly Operation Report
   *   for BPCL Mumbai
   *   - June 2026
   *
   * These are only defaults; the user can edit or remove any line, and a cleared
   * line simply never renders on the cover. The site line is dropped when no site
   * name is known rather than rendering "for " with nothing after it.
   */
  private buildDefaultMorNameLines(): string[] {
    const siteName = (
      this.userService.getCurrentSitename() ||
      this.userService.getSiteDetail()?.[0]?.site_name ||
      ""
    ).trim();
    const { month, year } = this.getPreviousMonth();
    const lines = ["Monthly Operation Report"];
    if (siteName) {
      lines.push(`for ${siteName}`);
    }
    lines.push(`- ${month} ${year}`);
    return lines;
  }

  /**
   * The previous calendar month, with its own year so January rolls back to the
   * prior year (opened Jan 2026 → December 2025).
   */
  private getPreviousMonth(): { month: string; year: number } {
    const now = new Date();
    // Last day of the previous month (first of this month, minus one day).
    const prevMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    prevMonth.setDate(prevMonth.getDate() - 1);
    return {
      month: prevMonth.toLocaleString("en-US", { month: "long" }),
      year: prevMonth.getFullYear(),
    };
  }

  /**
   * For a MOR group the document must be named before it can be generated. Line 1
   * is required and the lines must be filled in order (no content after a blank
   * line). Sets `morNameError` and returns false on failure; a no-op (returns
   * true) for non-MOR groups.
   */
  private validateMorName(): boolean {
    if (!this.isMorReport) {
      return true;
    }
    const lines = this.morNameLines.map((line) => (line || "").trim());
    if (!lines[0]) {
      this.morNameError = "Enter the first MOR name line to continue.";
      return false;
    }
    // No gaps: once a blank line appears, no later line may carry content.
    let blankSeen = false;
    for (const line of lines) {
      if (!line) {
        blankSeen = true;
        continue;
      }
      if (blankSeen) {
        this.morNameError =
          "Fill the lines in order — a later line can't be filled while an earlier one is empty.";
        return false;
      }
    }
    this.morNameError = "";
    return true;
  }

  /** trackBy for the MOR name line inputs — index-stable so editing a line
   *  doesn't recreate its input (which would drop focus mid-type). */
  trackByIndex(index: number): number {
    return index;
  }

  /** Whether a new (blank) MOR name line may be added: only once the last line
   *  has content, and below the max line count. */
  canAddMorNameLine(): boolean {
    if (this.morNameLines.length >= this.maxMorNameLines) {
      return false;
    }
    const last = this.morNameLines[this.morNameLines.length - 1] || "";
    return last.trim().length > 0;
  }

  /** Add another blank line for the user to fill (Line N+1). */
  addMorNameLine(): void {
    if (!this.canAddMorNameLine()) {
      return;
    }
    this.morNameLines.push("");
    this.morNameError = "";
  }

  /** Remove an added line (never Line 1). */
  removeMorNameLine(index: number): void {
    if (index <= 0 || index >= this.morNameLines.length) {
      return;
    }
    this.morNameLines.splice(index, 1);
    this.morNameError = "";
  }

  /** A line input is locked until the line above it has content — this enforces
   *  the top-down fill order (can't fill Line 3 while Line 2 is blank). */
  isMorNameLineDisabled(index: number): boolean {
    if (index <= 0) {
      return false;
    }
    return !(this.morNameLines[index - 1] || "").trim();
  }

  onMorNameLineChange(): void {
    this.morNameError = "";
  }

  /** Reveal the line-by-line title builder (multi-line MOR name). */
  openMorMultiLine(): void {
    this.morMultiLineOpen = true;
    if (!this.morNameLines.length) {
      this.morNameLines = [""];
    }
  }

  /** Collapse back to the single MOR name input. Added lines are kept (they still
   *  render on the cover); the user can remove them with the per-line ×. */
  closeMorMultiLine(): void {
    this.morMultiLineOpen = false;
    this.morNameError = "";
  }

  /** The MOR name lines to render on the cover: the leading run of non-empty
   *  lines (validation guarantees no interior gaps), trimmed. */
  private morNameLinesForRender(): string[] {
    const out: string[] = [];
    for (const line of this.morNameLines) {
      const trimmed = (line || "").trim();
      if (!trimmed) {
        break;
      }
      out.push(trimmed);
    }
    return out;
  }

  /** Entry dialog → proceed with the current (existing) render behaviour. */
  continueWithExisting(): void {
    // MOR reports must be named before the editor opens.
    if (!this.validateMorName()) {
      return;
    }
    // Apply each frequency's range to the render (reports are clipped to the
    // window of their own configured frequency). Empty inputs → no clipping.
    this.commitExportWindows();
    this.entryDialogOpen = false;
    this.loadDashboardGroupFromQuery();
  }

  /** The display label for a frequency bucket (for validation messages). */
  private intervalLabel(bucket: "daily" | "weekly" | "monthly"): string {
    return (
      this.pdfIntervalOptions.find((opt) => opt.value === bucket)?.label ??
      bucket
    );
  }

  /**
   * The per-frequency applied ranges to store on a saved document, so its card
   * can show "Daily 04-01→04-30 · Monthly 06-01→06-30" instead of a single
   * min→max span. Only frequencies that actually have reports and a set range are
   * included (an untouched, report-less Weekly is left out).
   */
  private buildSavedIntervalWindows(): SavedIntervalWindow[] {
    const windows: SavedIntervalWindow[] = [];
    for (const opt of this.pdfIntervalOptions) {
      const hasReports = this.intervalReports[opt.value].length > 0;
      const applied = this.exportWindows[opt.value];
      if (hasReports && applied && applied.from && applied.to) {
        windows.push({
          interval: opt.value,
          label: opt.label,
          from: applied.from,
          to: applied.to,
        });
      }
    }
    return windows;
  }

  /**
   * Freeze the per-frequency ranges the user picked as the windows applied to the
   * render, and derive the overall span used for saved-document metadata.
   */
  private commitExportWindows(): void {
    const froms: string[] = [];
    const tos: string[] = [];
    for (const opt of this.pdfIntervalOptions) {
      const range = this.intervalRanges[opt.value];
      this.exportWindows[opt.value] = { from: range.from, to: range.to };
      if (range.from) {
        froms.push(range.from);
      }
      if (range.to) {
        tos.push(range.to);
      }
    }
    this.exportWindowFrom = froms.length
      ? froms.reduce((a, b) => (a < b ? a : b))
      : "";
    this.exportWindowTo = tos.length
      ? tos.reduce((a, b) => (a > b ? a : b))
      : "";
  }

  /**
   * "Apply": validate the per-frequency ranges, then detect which reports have a
   * range different from their original configured period. If any changed, ask
   * the user to review the changes or apply directly; otherwise run immediately.
   */
  async applyDataConfiguration(): Promise<void> {
    if (this.applyInProgress) {
      return;
    }
    // MOR reports must be named before the data configuration can be applied.
    if (!this.validateMorName()) {
      return;
    }
    const group = this.cachedGroupForRender?.data;
    if (!group) {
      this.applyError = "Report data isn't loaded yet.";
      return;
    }

    // Same dedup/order the renderer uses, minus text reports (no data to fetch).
    const reports = this.collectRenderReports(group).filter(
      (report) => !this.isTextReport(report)
    );

    // Validate the range of every frequency that actually has reports; ignore
    // frequencies with no reports (e.g. Weekly here) — their range is unused.
    const usedBuckets = new Set<"daily" | "weekly" | "monthly">();
    for (const report of reports) {
      const bucket = this.normalizeIntervalBucket(report.interval);
      if (bucket) {
        usedBuckets.add(bucket);
      }
    }
    for (const bucket of usedBuckets) {
      const range = this.intervalRanges[bucket];
      if (!range.from || !range.to) {
        this.applyError = `Choose both dates for the ${this.intervalLabel(
          bucket
        )} range.`;
        return;
      }
      if (range.from > range.to) {
        this.applyError = `${this.intervalLabel(
          bucket
        )}: 'Data from' must be on or before 'Data to'.`;
        return;
      }
    }

    this.applyError = "";
    // Fresh Apply → drop any per-report overrides from a previous review session.
    this.reportWindows.clear();

    // Change detection: which reports' chosen range differs from the period they
    // were originally configured with. If any, gate Apply behind a review prompt.
    this.applyChanges = this.computeReportChanges(reports);
    if (this.applyChanges.total > 0) {
      this.applyReviewOpen = false;
      this.applyReviewError = "";
      this.applyConfirmOpen = true;
      return;
    }

    await this.runApply();
  }

  /**
   * Build the per-frequency list of reports whose chosen range differs from the
   * period they were originally configured with (report.period). A frequency with
   * no reports or no range set contributes nothing.
   */
  private computeReportChanges(reports: DashboardReportData[]): PdfApplyChanges {
    const changes: PdfApplyChanges = {
      daily: [],
      weekly: [],
      monthly: [],
      total: 0,
    };
    for (const report of reports) {
      const bucket = this.normalizeIntervalBucket(report.interval);
      if (!bucket) {
        continue;
      }
      const range = this.intervalRanges[bucket];
      if (!range.from || !range.to) {
        continue;
      }
      const originalFrom = this.toDateInputValue(report.period?.startDate);
      const originalTo = this.toDateInputValue(report.period?.endDate);
      if (originalFrom !== range.from || originalTo !== range.to) {
        changes[bucket].push({
          id: report.reportId,
          name: report.reportName || `Report ${report.reportId}`,
          originalFrom,
          originalTo,
          newFrom: range.from,
          newTo: range.to,
        });
        changes.total += 1;
      }
    }
    return changes;
  }

  /** Review dialog → show the detailed per-frequency change breakdown. */
  onApplyReview(): void {
    this.applyReviewOpen = true;
  }

  /** Review dialog → dismiss without applying (stay in the entry dialog). */
  onApplyCancel(): void {
    this.applyConfirmOpen = false;
    this.applyReviewOpen = false;
    this.applyReviewError = "";
  }

  /**
   * Review dialog → apply. Validate any per-report ranges the user edited in the
   * review, capture them as overrides, then run. A report the user edited is
   * fetched/clipped to its own range instead of its frequency's range.
   */
  onApplyProceed(): void {
    for (const opt of this.pdfIntervalOptions) {
      for (const change of this.applyChanges[opt.value]) {
        if (!change.newFrom || !change.newTo) {
          this.applyReviewError = `Choose both dates for "${change.name}".`;
          this.applyReviewOpen = true;
          return;
        }
        if (change.newFrom > change.newTo) {
          this.applyReviewError = `"${change.name}": 'from' must be on or before 'to'.`;
          this.applyReviewOpen = true;
          return;
        }
      }
    }
    this.applyReviewError = "";

    // Capture each changed report's (possibly edited) range as a per-report
    // override so runApply / clipping use it instead of the frequency range.
    this.reportWindows.clear();
    for (const opt of this.pdfIntervalOptions) {
      for (const change of this.applyChanges[opt.value]) {
        this.reportWindows.set(change.id, {
          from: change.newFrom,
          to: change.newTo,
        });
      }
    }

    this.applyConfirmOpen = false;
    this.applyReviewOpen = false;
    void this.runApply();
  }

  /**
   * Refetch each report's data for the range of ITS OWN configured frequency
   * (daily reports use the Daily range, monthly reports the Monthly range) and
   * render with it. A frequency with no reports is never applied. Reports are
   * processed sequentially in render order with a 1/N progress counter. Text
   * reports carry no time-series data, so they're skipped. Each report keeps its
   * existing configuration — only the period and fetched values change.
   */
  private async runApply(): Promise<void> {
    const group = this.cachedGroupForRender?.data;
    if (!group) {
      return;
    }
    const reports = this.collectRenderReports(group).filter(
      (report) => !this.isTextReport(report)
    );

    this.applyTotal = reports.length;
    this.applyCurrent = 0;
    this.applyInProgress = true;

    try {
      for (let i = 0; i < reports.length; i += 1) {
        const report = reports[i];
        this.applyCurrent = i + 1;
        // Prefer a per-report override (edited in the review) over the frequency
        // range. No matching range (unrecognised interval) → leave as-is.
        const bucket = this.normalizeIntervalBucket(report.interval);
        const range =
          this.reportWindows.get(report.reportId) ||
          (bucket ? this.intervalRanges[bucket] : null);
        if (!range || !range.from || !range.to) {
          continue;
        }
        try {
          const res = await firstValueFrom(
            this.reportService.getReportDataByDateRange(
              report.reportId,
              range.from,
              range.to
            )
          );
          if (res?.success && res.data) {
            this.mergeRefetchedReportData(report, res.data);
          }
        } catch {
          // A single report's refetch failed — keep its embedded data and move
          // on rather than aborting the whole batch.
        }
      }

      // Freeze the per-frequency windows so any report that failed to refetch is
      // at least clipped to its own frequency's requested range.
      this.commitExportWindows();
      this.entryDialogOpen = false;
      this.loadDashboardGroupFromQuery();
    } finally {
      this.applyInProgress = false;
    }
  }

  /**
   * The reports the renderer will lay out, in order and deduplicated group-wide
   * (mirrors renderDashboardGroupIntoDocument). Returns live references into the
   * group so mutating their values/period reaches the render.
   */
  private collectRenderReports(
    group: DashboardGroupWithReportsResponse
  ): DashboardReportData[] {
    const list: DashboardReportData[] = [];
    const seen = new Set<string>();
    for (const dashboard of group.dashboards || []) {
      const reports = this.getUniqueReports(
        this.orderReportsByLayout(dashboard)
      );
      for (const report of reports) {
        const key = this.getReportDedupKey(report);
        if (seen.has(key)) {
          continue;
        }
        seen.add(key);
        list.push(report);
      }
    }
    return list;
  }

  /**
   * Merge a date-range refetch response back into a report: replace each
   * parameter's values (matched by paramCode) and the report period, keeping all
   * other configuration. Flag the report so the render redraws it fresh from the
   * new data instead of reusing a now-stale chart snapshot.
   */
  private mergeRefetchedReportData(
    report: DashboardReportData,
    data: {
      period?: { startDate?: string; endDate?: string };
      parameters?: Array<{
        paramCode?: string;
        values?: Array<{ start: string; end: string; value: number | null }>;
      }>;
    }
  ): void {
    if (data.period?.startDate && data.period?.endDate) {
      report.period = {
        startDate: data.period.startDate,
        endDate: data.period.endDate,
      };
    }
    const valuesByCode = new Map<
      string,
      Array<{ start: string; end: string; value: number | null }>
    >();
    for (const param of data.parameters || []) {
      if (param?.paramCode) {
        valuesByCode.set(param.paramCode, param.values || []);
      }
    }
    for (const param of report.parameters || []) {
      const refetched = valuesByCode.get(param.paramCode);
      if (refetched) {
        param.values = refetched;
      }
    }
    (report as unknown as { __windowClipped?: boolean }).__windowClipped = true;
  }

  /**
   * Clip each report's parameter values to the window of ITS OWN configured
   * frequency (daily reports to the Daily window, monthly to the Monthly window)
   * so the rendered PDF only contains data in that range. Returns the group
   * unchanged when no window is set for any frequency. Reports whose data is
   * actually clipped are flagged so the render bypasses any stale chart snapshot.
   */
  private clipReportsToWindow(
    group: DashboardGroupWithReportsResponse
  ): DashboardGroupWithReportsResponse {
    const windows = this.exportWindows;
    const hasAnyWindow = this.pdfIntervalOptions.some((opt) => {
      const w = windows[opt.value];
      return w.from || w.to;
    });
    if (!hasAnyWindow) {
      return group;
    }

    // Deep clone so the cached payload (reused on re-render) is never mutated.
    const clone: DashboardGroupWithReportsResponse = JSON.parse(
      JSON.stringify(group)
    );
    for (const dashboard of clone.dashboards || []) {
      for (const report of dashboard.reports || []) {
        // Prefer a per-report override (edited in the review); otherwise the
        // window for this report's own frequency. No matching window
        // (unrecognised interval) → leave the report's data untouched.
        const bucket = this.normalizeIntervalBucket(report.interval);
        const window =
          this.reportWindows.get(report.reportId) ||
          (bucket ? windows[bucket] : null);
        if (!window || (!window.from && !window.to)) {
          continue;
        }
        const from = window.from;
        const to = window.to;
        let clipped = false;
        for (const param of report.parameters || []) {
          if (!Array.isArray(param.values)) {
            continue;
          }
          const before = param.values.length;
          param.values = param.values.filter((v) => {
            // Compare the YYYY-MM-DD date portions directly. Lexicographic order
            // matches chronological order for that format and sidesteps any
            // local-vs-UTC timezone drift at the window edges.
            const match = String(v?.start ?? "").match(/\d{4}-\d{2}-\d{2}/);
            // Keep values we can't date-parse rather than silently dropping them.
            if (!match) {
              return true;
            }
            const day = match[0];
            if (from && day < from) {
              return false;
            }
            if (to && day > to) {
              return false;
            }
            return true;
          });
          if (param.values.length !== before) {
            clipped = true;
          }
        }
        if (clipped) {
          (report as unknown as { __windowClipped?: boolean }).__windowClipped =
            true;
        }
      }
    }
    return clone;
  }

  /**
   * Entry dialog, left section: fetch the group's reports, collect every
   * parameter code, then ask the backend for the oldest/newest dates that have
   * data so we can prefill the "Data from / Data to" range.
   */
  private loadDataConfiguration(dashboardGroupId: number): void {
    this.dataConfigLoading = true;
    this.dataConfigError = "";
    this.reportService.getDashboardGroupWithReports(dashboardGroupId).subscribe({
      next: (response) => {
        const group = response?.data;
        if (!response?.success || !group) {
          this.dataConfigLoading = false;
          this.dataConfigError = "Could not load report data.";
          return;
        }
        // Cache so "Continue" can render without refetching this payload.
        this.cachedGroupForRender = { id: dashboardGroupId, data: group };
        // Bucket the reports by interval for the accordions.
        this.groupReportsByInterval();
        const codes = this.collectReportCodes(group);
        if (!codes.length) {
          this.dataConfigLoading = false;
          this.dataConfigError = "No data codes found for this report.";
          return;
        }
        this.fetchDataDateBounds(group.siteId, codes);
      },
      error: () => {
        this.dataConfigLoading = false;
        this.dataConfigError = "Could not load report data.";
      },
    });
  }

  /** Unique parameter codes across every report/parameter in the group. */
  private collectReportCodes(
    group: DashboardGroupWithReportsResponse
  ): string[] {
    const codes = new Set<string>();
    for (const dashboard of group.dashboards || []) {
      for (const report of dashboard.reports || []) {
        for (const param of report.parameters || []) {
          if (param?.paramCode) {
            codes.add(param.paramCode);
          }
        }
      }
    }
    return Array.from(codes);
  }

  /** Ask the backend for the data date range and prefill the inputs. */
  private fetchDataDateBounds(siteId: number, codes: string[]): void {
    this.reportService.getReportDataDateBounds({ siteId, codes }).subscribe({
      next: (response) => {
        this.dataConfigLoading = false;
        const bounds = response?.data;
        if (!response?.success || !bounds) {
          this.dataConfigError = "Could not determine the available data range.";
          return;
        }
        this.dataBoundsOldest = this.toDateInputValue(bounds.oldestDate);
        this.dataBoundsNewest = this.toDateInputValue(bounds.newestDate);
        // Prefill every interval's range: from = oldest, to = newest available.
        for (const opt of this.pdfIntervalOptions) {
          this.intervalRanges[opt.value].from = this.dataBoundsOldest;
          this.intervalRanges[opt.value].to = this.dataBoundsNewest;
        }
      },
      error: () => {
        this.dataConfigLoading = false;
        this.dataConfigError = "Could not determine the available data range.";
      },
    });
  }

  /** Normalise an ISO timestamp or date string to a YYYY-MM-DD input value. */
  private toDateInputValue(value: string | null | undefined): string {
    if (!value) {
      return "";
    }
    if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
      return value.slice(0, 10);
    }
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) {
      return "";
    }
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }

  ngAfterViewInit(): void {
    document.execCommand("defaultParagraphSeparator", false, "p");

    this.pageEditors.changes.subscribe(() => {
      // While reflow is paginating, the live DOM is authoritative and the page
      // model is intentionally stale. Re-hydrating here would re-inject stale
      // content into editors reflow has just emptied (duplicating reports), so
      // skip until reflow settles (reflowFrom() syncs the model on completion).
      if (this.reflowInProgress) {
        return;
      }
      this.hydratePagesToDom();
      this.prepareResizableImages();
      this.syncPagesFromDom();
      setTimeout(() => this.updateDocumentStats(), 0);
    });

    this.hydratePagesToDom();
    this.prepareResizableImages();
    setTimeout(() => {
      this.reflowFrom(0);
      this.updateDocumentStats();
      this.focusActivePage();
    }, 0);
  }

  ngOnDestroy(): void {
    // Flush any pending edits to the local draft before the view goes away.
    if (this.documentReady) {
      // Only re-read the model from the DOM when pagination is settled. Mid-reflow
      // the DOM is half-distributed; the last settled sync is the safe snapshot.
      if (!this.reflowInProgress) {
        this.syncPagesFromDom();
      }
      void this.saveDraft(false);
    }
    if (this.draftAutosaveTimer) {
      clearTimeout(this.draftAutosaveTimer);
      this.draftAutosaveTimer = null;
    }
    if (this.statsTimer) {
      clearTimeout(this.statsTimer);
      this.statsTimer = null;
    }
    if (this.tableControlsHideTimer) {
      clearTimeout(this.tableControlsHideTimer);
      this.tableControlsHideTimer = null;
    }
  }

  // Commands that can change content height and therefore require repagination.
  // Everything else (bold/italic/underline/color/align/link…) only restyles the
  // current selection, so we skip the expensive reflow + full-document sync.
  private static readonly LAYOUT_COMMANDS = new Set<string>([
    "fontName",
    "fontSize",
    "formatBlock",
    "insertImage",
    "insertHTML",
    "insertUnorderedList",
    "insertOrderedList",
    "indent",
    "outdent",
    "insertHorizontalRule",
    "insertParagraph",
    "removeFormat",
    "undo",
    "redo",
  ]);

  format(command: string, value?: string): void {
    const editor = this.getActiveEditor();
    if (!editor) {
      return;
    }

    // Focus the selection's own editable host — it may be an editable table
    // island nested in the page editor. Refocusing the wrong host collapses the
    // selection, so the command would no-op on table content.
    this.focusSelectionEditableHost(editor);
    document.execCommand(command, false, value);

    if (DownloadpdfComponent.LAYOUT_COMMANDS.has(command)) {
      this.prepareResizableImages();
      this.syncPagesFromDom();
      this.reflowFrom(this.activePageIndex);
    } else if (this.selectionSpansMultiplePages()) {
      // A selection spanning pages was just restyled across all of them in the
      // DOM; syncing only the active page would drop the off-page formatting on
      // the next rehydrate. Persist every page the selection touched.
      this.syncPagesFromDom();
      this.scheduleDraftAutosave();
    } else {
      // Non-layout command: only the active page's markup changed.
      this.syncActivePageFromDom();
    }
    this.updateDocumentStats();
  }

  /** True when the current selection spans more than one page editor. */
  private selectionSpansMultiplePages(): boolean {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed) {
      return false;
    }
    const range = sel.getRangeAt(0);
    const pageOf = (node: Node | null): Element | null => {
      const el =
        node && node.nodeType === Node.ELEMENT_NODE
          ? (node as Element)
          : node?.parentElement || null;
      return el?.closest(".page-editor") || null;
    };
    const startPage = pageOf(range.startContainer);
    const endPage = pageOf(range.endContainer);
    return !!startPage && !!endPage && startPage !== endPage;
  }

  setBlock(blockTag: string): void {
    this.activeBlock = blockTag;
    if (blockTag === "p") {
      this.format("formatBlock", "<p>");
      return;
    }

    this.format("formatBlock", `<${blockTag}>`);
  }

  get activeBlockLabel(): string {
    return (
      this.blockOptions.find((item) => item.value === this.activeBlock)?.label ||
      "Paragraph"
    );
  }

  openLinkDialog(): void {
    // The selection is captured on the button's mousedown (before focus moves to
    // the button and collapses it) — see the template. Re-saving here on click
    // would overwrite it with the now-collapsed selection, so we don't.
    this.dialogType = "link";
    this.linkUrl = "";
  }

  /**
   * Remove the hyperlink covering the current selection. Restores the selection
   * captured on mousedown (the button click would otherwise have collapsed it),
   * and when the caret merely sits inside a link with nothing selected, expands
   * the selection to the whole anchor so execCommand("unlink") has a target.
   */
  unlinkSelection(): void {
    this.restoreEditorSelectionForInsert();
    this.expandSelectionToEnclosingAnchor();
    this.format("unlink");
    this.savedSelectionRange = null;
  }

  /** If the selection is collapsed inside an <a>, select the whole anchor. */
  private expandSelectionToEnclosingAnchor(): void {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      return;
    }
    const range = selection.getRangeAt(0);
    if (!range.collapsed) {
      return;
    }
    const node = range.commonAncestorContainer;
    const element =
      node.nodeType === Node.ELEMENT_NODE
        ? (node as Element)
        : node.parentElement;
    const anchor = element?.closest("a");
    if (!anchor) {
      return;
    }
    const anchorRange = document.createRange();
    anchorRange.selectNodeContents(anchor);
    selection.removeAllRanges();
    selection.addRange(anchorRange);
  }

  setFontFamily(fontFamily: string): void {
    this.selectedFont = fontFamily;
    this.format("fontName", fontFamily);
    this.fontDropdownOpen = false;
  }

  toggleFontDropdown(event: MouseEvent): void {
    event.stopPropagation();
    this.fontDropdownOpen = !this.fontDropdownOpen;
    this.blockDropdownOpen = false;
    this.sizeDropdownOpen = false;
    this.lineSpacingDropdownOpen = false;
  }

  setFontSize(size: number): void {
    this.selectedFontSize = size;

    const editor = this.getActiveEditor();
    if (!editor) {
      return;
    }

    // Focus the selection's own editable host (may be an editable table island)
    // so the size applies to the selected table text, not a collapsed caret.
    this.focusSelectionEditableHost(editor);
    // Use the legacy <font size="7"> form deterministically. With styleWithCSS
    // ON, execCommand("fontSize") instead emits `font-size: xxx-large` (the
    // size-7 keyword, ~48px). If that marker is missed it renders enormous,
    // blowing the page height up into many blank/overflowing pages. Forcing CSS
    // off here guarantees the <font size="7"> we convert below.
    document.execCommand("styleWithCSS", false, "false");
    document.execCommand("fontSize", false, "7");

    // A selection can span page boundaries, so the temporary size-7 markers may
    // be created in MORE than the active page. Convert them in EVERY editor and
    // also catch the CSS keyword form as a fallback, so none survive at the
    // browser's ~48px size-7 default.
    const editors = this.pageEditors?.toArray() || [];
    for (const ref of editors) {
      const pageEl = ref.nativeElement;
      pageEl.querySelectorAll('font[size="7"]').forEach((tag) => {
        tag.removeAttribute("size");
        (tag as HTMLElement).style.fontSize = `${size}px`;
      });
      pageEl
        .querySelectorAll<HTMLElement>('[style*="xxx-large"]')
        .forEach((tag) => {
          if (tag.style.fontSize.trim() === "xxx-large") {
            tag.style.fontSize = `${size}px`;
          }
        });
    }

    this.syncPagesFromDom();
    // A multi-page selection may grow content on a page earlier than the caret,
    // so start the forward repagination at the earliest page that now overflows
    // (the forward pass stops at the first page that fits, so a stale start index
    // would miss the overflow). Falls back to the active page when nothing
    // overflows (e.g. the size was reduced); pull-up still runs from the top.
    const firstOverflow = editors.findIndex(
      (ref) =>
        ref.nativeElement.scrollHeight > ref.nativeElement.clientHeight + 1
    );
    this.reflowFrom(
      firstOverflow === -1 ? this.activePageIndex : firstOverflow
    );
    this.updateDocumentStats();
    this.sizeDropdownOpen = false;
  }

  setTextColor(color: string): void {
    this.textColor = color;
    this.applyTextColor();
  }

  setHighlightColor(color: string): void {
    this.highlightColor = color;
    this.applyHighlight();
  }

  /**
   * Apply the currently selected text color to the selection without reopening
   * the colour picker. Lets the user re-apply the same colour on a single click
   * (the picker is only needed to change the colour).
   */
  applyTextColor(): void {
    this.restoreSavedSelection();
    this.format("foreColor", this.textColor);
    this.savedSelectionRange = null;
  }

  /** Apply the currently selected highlight colour on a single click. */
  applyHighlight(): void {
    this.restoreSavedSelection();
    this.format("hiliteColor", this.highlightColor);
    this.savedSelectionRange = null;
  }

  /**
   * Capture the current editor selection before a focus-stealing control (the
   * native color input opens an OS dialog) collapses it. Called on the control's
   * mousedown, which fires while the editor selection is still intact.
   */
  saveSelection(): void {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      return;
    }
    const range = selection.getRangeAt(0);
    const container = range.commonAncestorContainer;
    const element =
      container.nodeType === Node.ELEMENT_NODE
        ? (container as Element)
        : container.parentElement;
    const editor = element?.closest(".page-editor") as HTMLElement | null;
    if (!editor) {
      // No editor selection right now — drop any stale saved range so we don't
      // later restore a selection that no longer exists.
      this.savedSelectionRange = null;
      return;
    }
    this.savedSelectionRange = range.cloneRange();
  }

  /** Re-apply the saved selection so the next command targets that text. */
  private restoreSavedSelection(): HTMLDivElement | null {
    const range = this.savedSelectionRange;
    if (!range) {
      return null;
    }
    // Bail out if the saved range points at nodes that were removed/replaced
    // (e.g. by a reflow) — restoring a detached range throws and would abort the
    // whole insert.
    const container = range.startContainer as Node & { isConnected?: boolean };
    if (!container || container.isConnected === false) {
      this.savedSelectionRange = null;
      return null;
    }
    const el =
      container.nodeType === Node.ELEMENT_NODE
        ? (container as Element)
        : container.parentElement;
    const editor = el?.closest(".page-editor") as HTMLDivElement | null;
    if (!editor) {
      this.savedSelectionRange = null;
      return null;
    }
    const editors = this.pageEditors?.toArray() || [];
    const idx = editors.findIndex((ref) => ref.nativeElement === editor);
    if (idx >= 0) {
      this.activePageIndex = idx;
    }
    editor.focus();
    const selection = window.getSelection();
    if (selection) {
      try {
        selection.removeAllRanges();
        selection.addRange(range);
      } catch {
        this.savedSelectionRange = null;
        return null;
      }
    }
    return editor;
  }

  insertHorizontalRule(): void {
    this.format("insertHorizontalRule");
  }

  insertImage(): void {
    // Intentionally left empty. Use local file picker via insertImageFromFile.
  }

  insertImageFromFile(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) {
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const imageDataUrl = String(reader.result || "");
      if (imageDataUrl) {
        this.format("insertImage", imageDataUrl);
      }
    };
    reader.readAsDataURL(file);
    input.value = "";
  }

  openTableDialog(): void {
    // Capture the caret before the dialog steals focus, so the table is inserted
    // at the cursor position instead of failing for lack of a selection.
    this.saveSelection();
    this.dialogType = "table";
    this.tableRows = 2;
    this.tableCols = 2;
  }

  confirmDialog(): void {
    if (this.dialogType === "link") {
      if (this.linkUrl.trim()) {
        this.restoreEditorSelectionForInsert();
        this.format("createLink", this.linkUrl.trim());
      }
      this.savedSelectionRange = null;
      this.closeDialog();
      return;
    }

    if (this.dialogType === "table") {
      const rows = Number(this.tableRows);
      const cols = Number(this.tableCols);

      if (!rows || !cols || rows < 1 || cols < 1) {
        return;
      }

      const tableStyle =
        "width:100%;border-collapse:collapse;table-layout:fixed;margin:12px 0;";
      const cellStyle =
        "border:1px solid #cfd6dd;padding:8px;min-width:48px;";
      let tableHtml = `<table class="doc-table" style="${tableStyle}"><tbody>`;
      for (let r = 0; r < rows; r += 1) {
        tableHtml += "<tr>";
        for (let c = 0; c < cols; c += 1) {
          tableHtml += `<td style="${cellStyle}">&nbsp;</td>`;
        }
        tableHtml += "</tr>";
      }
      tableHtml += "</tbody></table><p><br></p>";

      // Insert the table directly into the DOM at block level. execCommand
      // ("insertHTML") is unreliable for block elements after focus has moved to
      // a dialog, so we do it deterministically instead.
      this.restoreEditorSelectionForInsert();
      this.insertBlockHtmlAtCaret(tableHtml);
      this.prepareResizableImages();
      this.syncPagesFromDom();
      // Pagination is async and may move the table to another page; defer the
      // scroll-to-table until reflow fully settles (handled in reflowFrom).
      this.pendingTableReveal = true;
      this.reflowFrom(this.activePageIndex);
      this.updateDocumentStats();
      this.savedSelectionRange = null;
      this.closeDialog();
    }
  }

  /** Scroll the just-inserted table into view and drop the caret in it. */
  private revealInsertedTable(): void {
    const table = document.querySelector(
      'table[data-just-inserted="1"]'
    ) as HTMLElement | null;
    if (!table) {
      return;
    }
    table.removeAttribute("data-just-inserted");

    const editor = table.closest(".page-editor") as HTMLElement | null;
    const editors = this.pageEditors?.toArray() || [];
    const idx = editors.findIndex((ref) => ref.nativeElement === editor);
    if (idx >= 0) {
      this.activePageIndex = idx;
      this.currentPageIndex = idx;
    }

    // Put the caret in the first cell first, then scroll the table into view so
    // the focus change doesn't override our scroll position.
    const firstCell = table.querySelector("td,th") as HTMLElement | null;
    if (firstCell) {
      this.placeCaretAtStart(firstCell);
    }
    // Scroll the workspace so the whole .doc-page holding the table is visible.
    const docPage = table.closest(".doc-page") as HTMLElement | null;
    const target = docPage || table;
    try {
      target.scrollIntoView({ block: "center", behavior: "auto" });
    } catch {
      target.scrollIntoView();
    }
    this.syncPagesFromDom();
  }

  /**
   * Insert block-level HTML at the editor's top level, just after the block that
   * currently holds the caret (or at the end if there is no caret). Avoids the
   * invalid nesting and silent no-ops that execCommand("insertHTML") can cause.
   */
  private insertBlockHtmlAtCaret(html: string): void {
    const editor = this.getActiveEditor();
    if (!editor) {
      return;
    }

    const temp = document.createElement("div");
    temp.innerHTML = html;
    // Mark the new table so we can scroll to it after pagination settles.
    const newTable = temp.querySelector("table");
    if (newTable) {
      newTable.setAttribute("data-just-inserted", "1");
    }
    const newNodes = Array.from(temp.childNodes);
    if (!newNodes.length) {
      return;
    }

    // Determine where to insert: `anchor` is the node to insert BEFORE
    // (null = append to the end of the editor).
    let anchor: ChildNode | null = null;
    const selection = window.getSelection();
    if (
      selection &&
      selection.rangeCount > 0 &&
      selection.anchorNode &&
      editor.contains(selection.anchorNode)
    ) {
      const anchorNode = selection.anchorNode;
      if (anchorNode === editor) {
        // Caret sits directly on the editor; use the offset to pick the child.
        anchor = editor.childNodes[selection.anchorOffset] || null;
      } else {
        // Climb to the editor's direct child holding the caret, insert after it.
        let node: Node | null = anchorNode;
        while (node && node.parentNode && node.parentNode !== editor) {
          node = node.parentNode;
        }
        anchor = node && node.parentNode === editor ? node.nextSibling : null;
      }
    }

    for (let i = 0; i < newNodes.length; i += 1) {
      const node = newNodes[i];
      if (anchor) {
        editor.insertBefore(node, anchor);
      } else {
        editor.appendChild(node);
      }
      anchor = node.nextSibling;
    }
  }

  /**
   * Restore the selection captured before the dialog opened so the insert lands
   * at the right spot. Falls back to focusing the active editor (caret at end)
   * when there is no saved selection, so insertHTML always has a target.
   */
  private restoreEditorSelectionForInsert(): HTMLElement | null {
    const restored = this.restoreSavedSelection();
    if (restored) {
      return restored;
    }
    const editor = this.getActiveEditor();
    if (!editor) {
      return null;
    }
    editor.focus();
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      this.placeCaretAtEnd(editor);
    }
    return editor;
  }

  closeDialog(): void {
    this.dialogType = "none";
  }

  toggleBlockDropdown(event: MouseEvent): void {
    event.stopPropagation();
    this.blockDropdownOpen = !this.blockDropdownOpen;
    this.fontDropdownOpen = false;
    this.sizeDropdownOpen = false;
    this.lineSpacingDropdownOpen = false;
  }

  toggleSizeDropdown(event: MouseEvent): void {
    event.stopPropagation();
    this.sizeDropdownOpen = !this.sizeDropdownOpen;
    this.fontDropdownOpen = false;
    this.blockDropdownOpen = false;
    this.lineSpacingDropdownOpen = false;
  }

  toggleLineSpacingDropdown(event: MouseEvent): void {
    event.stopPropagation();
    this.lineSpacingDropdownOpen = !this.lineSpacingDropdownOpen;
    this.fontDropdownOpen = false;
    this.blockDropdownOpen = false;
    this.sizeDropdownOpen = false;
  }

  selectLineSpacing(value: number): void {
    this.selectedLineSpacing = value;
    this.applyLineSpacingToSelection(value);
    this.lineSpacingDropdownOpen = false;
  }

  private applyLineSpacingToSelection(value: number): void {
    const editor = this.getActiveEditor();
    if (!editor) {
      return;
    }

    editor.focus();
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      return;
    }
    const range = selection.getRangeAt(0);
    const targetBlocks = new Set<HTMLElement>();

    const addBlock = (node: Node | null): void => {
      if (!node) {
        return;
      }
      const element = (node.nodeType === Node.ELEMENT_NODE
        ? (node as Element)
        : node.parentElement) as Element | null;
      const block = element?.closest(
        "p,div,li,h1,h2,h3,h4,h5,h6,blockquote,td,th"
      ) as HTMLElement | null;
      if (block && editor.contains(block)) {
        targetBlocks.add(block);
      }
    };

    if (range.collapsed) {
      addBlock(selection.anchorNode);
    } else {
      const walker = document.createTreeWalker(editor, NodeFilter.SHOW_ELEMENT);
      let current = walker.currentNode as Element | null;
      while (current) {
        if (range.intersectsNode(current)) {
          const tag = current.tagName.toLowerCase();
          if (
            tag === "p" || tag === "div" || tag === "li" ||
            tag === "h1" || tag === "h2" || tag === "h3" ||
            tag === "h4" || tag === "h5" || tag === "h6" ||
            tag === "blockquote" || tag === "td" || tag === "th"
          ) {
            targetBlocks.add(current as HTMLElement);
          }
        }
        current = walker.nextNode() as Element | null;
      }
      addBlock(range.startContainer);
      addBlock(range.endContainer);
    }

    if (!targetBlocks.size) {
      const fallback = editor.querySelector("p,div,li,h1,h2,h3,h4,h5,h6,blockquote") as HTMLElement | null;
      if (fallback) {
        targetBlocks.add(fallback);
      }
    }

    const normalized = Number(value) > 0 ? Number(value) : 1.5;
    targetBlocks.forEach((block) => {
      block.style.lineHeight = `${normalized}`;
    });

    this.syncPagesFromDom();
    this.reflowFrom(this.activePageIndex);
    this.updateDocumentStats();
  }

  selectBlock(option: "p" | "h1" | "h2" | "h3"): void {
    this.setBlock(option);
    this.blockDropdownOpen = false;
  }

  selectFontSize(size: number): void {
    this.setFontSize(size);
    this.sizeDropdownOpen = false;
  }

  setActivePage(index: number): void {
    this.activePageIndex = index;
    // Reflect the page the user just interacted with in the footer immediately.
    this.currentPageIndex = index;
  }

  private scrollRafPending = false;

  /** Track the page in the scroll viewport so the footer shows the real page. */
  onWorkspaceScroll(): void {
    // Throttle to one update per animation frame — scroll fires very often.
    if (this.scrollRafPending) {
      return;
    }
    this.scrollRafPending = true;
    requestAnimationFrame(() => {
      this.scrollRafPending = false;
      this.updateVisiblePageIndex();
      // Keep the per-table "+" controls glued to their table while scrolling.
      if (this.tableControlsVisible && this.hoveredTable?.isConnected) {
        this.showTableControls(this.hoveredTable);
      } else if (this.tableControlsVisible) {
        this.tableControlsVisible = false;
      }
      // Keep the per-cell "−" delete controls glued to their cell too.
      if (this.tableDelControlsVisible && this.hoveredCell?.isConnected) {
        this.showTableDeleteControls(this.hoveredCell);
      } else if (this.tableDelControlsVisible) {
        this.tableDelControlsVisible = false;
      }
    });
  }

  /** Pick the doc-page whose center is closest to the viewport center. */
  private updateVisiblePageIndex(): void {
    const workspace = this.pagesWorkspaceRef?.nativeElement;
    if (!workspace) {
      return;
    }
    const pages = Array.from(
      workspace.querySelectorAll(".doc-page")
    ) as HTMLElement[];
    if (!pages.length) {
      return;
    }

    const workspaceRect = workspace.getBoundingClientRect();
    const viewportCenterY = workspaceRect.top + workspace.clientHeight / 2;

    let bestIndex = 0;
    let bestDistance = Number.POSITIVE_INFINITY;
    for (let i = 0; i < pages.length; i += 1) {
      const rect = pages[i].getBoundingClientRect();
      const pageCenter = rect.top + rect.height / 2;
      const distance = Math.abs(pageCenter - viewportCenterY);
      if (distance < bestDistance) {
        bestDistance = distance;
        bestIndex = i;
      }
    }

    if (bestIndex !== this.currentPageIndex) {
      this.currentPageIndex = bestIndex;
    }
  }

  insertPageBreak(): void {
    const insertAt = this.activePageIndex + 1;
    this.pages.splice(insertAt, 0, { id: this.nextPageId++, html: "<p><br></p>" });
    setTimeout(() => {
      this.activePageIndex = insertAt;
      this.focusActivePage();
    }, 0);
  }

  @HostListener("document:click", ["$event"])
  onDocumentClick(event: MouseEvent): void {
    const rawTarget = event.target as EventTarget | null;
    const targetNode = rawTarget as Node | null;
    const targetEl =
      rawTarget instanceof Element
        ? rawTarget
        : (targetNode?.parentElement as Element | null);
    const insideBlock = !!(
      targetNode && this.blockPickerRef?.nativeElement?.contains(targetNode)
    );
    const insideFont = !!(
      targetNode && this.fontPickerRef?.nativeElement?.contains(targetNode)
    );
    const insideSize = !!(
      targetNode && this.sizePickerRef?.nativeElement?.contains(targetNode)
    );
    const insideLineSpacing = !!(
      targetNode && this.lineSpacingPickerRef?.nativeElement?.contains(targetNode)
    );
    const pathElements = this.getEventPathElements(event);
    const insideResizable =
      pathElements.some((el) => el.classList?.contains("resizable-image")) ||
      (targetEl ? !!targetEl.closest(".resizable-image") : false);
    const insideImageToolbar =
      pathElements.some((el) => el.classList?.contains("image-quick-toolbar")) ||
      (targetEl ? !!targetEl.closest(".image-quick-toolbar") : false);
    this.debugSelectionLog(
      "[SELECT] document:click",
      this.describeElement(targetEl),
      "insideResizable:",
      insideResizable,
      "insideImageToolbar:",
      insideImageToolbar
    );

    if (!insideBlock) {
      this.blockDropdownOpen = false;
    }
    if (!insideFont) {
      this.fontDropdownOpen = false;
    }
    if (!insideSize) {
      this.sizeDropdownOpen = false;
    }
    if (!insideLineSpacing) {
      this.lineSpacingDropdownOpen = false;
    }
    // Close the saved-document kebab menu on any outside click.
    if (this.savedDocMenuId !== null) {
      const insideSavedMenu = targetEl
        ? !!targetEl.closest(".entry-saved-menu, .entry-saved-kebab")
        : false;
      if (!insideSavedMenu) {
        this.closeSavedDocMenu();
      }
    }
    if (!insideResizable && !insideImageToolbar) {
      this.debugSelectionLog("[SELECT] deselect from document click");
      this.selectResizableWrapper(null);
    }

    const insideTableToolbar =
      pathElements.some((el) => el.classList?.contains("table-quick-toolbar")) ||
      (targetEl ? !!targetEl.closest(".table-quick-toolbar") : false);
    const insideTableCell = targetEl ? !!targetEl.closest("td,th") : false;
    if (!insideTableToolbar && !insideTableCell) {
      this.tableToolbarVisible = false;
      this.selectedTableCell = null;
    }
  }

  @HostListener("window:resize")
  @HostListener("window:scroll")
  onViewportChange(): void {
    this.updateFloatingImageToolbar();
    if (this.tableToolbarVisible) {
      this.updateTableToolbarFromSelection();
    }
    this.updateVisiblePageIndex();
  }

  // Reflect the current selection's formatting back into the toolbar (block type,
  // font, size, line spacing, and bold/italic/underline/strike states) so the
  // controls show what the selected text actually is. Throttled to one update
  // per frame because selectionchange fires very frequently.
  @HostListener("document:selectionchange")
  onSelectionChange(): void {
    if (this.toolbarSyncPending) {
      return;
    }
    this.toolbarSyncPending = true;
    requestAnimationFrame(() => {
      this.toolbarSyncPending = false;
      this.syncToolbarFromSelection();
    });
  }

  private syncToolbarFromSelection(): void {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || !selection.anchorNode) {
      return;
    }

    const anchor = selection.anchorNode;
    const element =
      anchor.nodeType === Node.ELEMENT_NODE
        ? (anchor as Element)
        : anchor.parentElement;
    if (!element) {
      return;
    }

    // Only sync when the selection is inside an editor; otherwise leave the
    // toolbar untouched (e.g. when focus is on a toolbar control or dialog).
    const editor = element.closest(".page-editor") as HTMLElement | null;
    if (!editor) {
      return;
    }

    // Block type (Paragraph / Heading 1-3).
    const block = element.closest(
      "h1,h2,h3,h4,h5,h6,p,div,li,blockquote"
    ) as HTMLElement | null;
    if (block) {
      const tag = block.tagName.toLowerCase();
      this.activeBlock =
        tag === "h1" || tag === "h2" || tag === "h3" ? tag : "p";
    }

    // Computed font / size / line spacing from the nearest element.
    const styleTarget = (element instanceof HTMLElement
      ? element
      : block) as HTMLElement | null;
    if (styleTarget) {
      const computed = window.getComputedStyle(styleTarget);

      const family = this.matchFontFamily(computed.fontFamily);
      if (family) {
        this.selectedFont = family;
      }

      const sizePx = Math.round(parseFloat(computed.fontSize) || 0);
      if (sizePx > 0) {
        this.selectedFontSize = sizePx;
      }

      const spacing = this.computeLineSpacingRatio(computed, sizePx);
      if (spacing) {
        this.selectedLineSpacing = spacing;
      }
    }

    // Inline format states for the B / I / U / S buttons.
    try {
      this.isBold = document.queryCommandState("bold");
      this.isItalic = document.queryCommandState("italic");
      this.isUnderline = document.queryCommandState("underline");
      this.isStrike = document.queryCommandState("strikeThrough");
    } catch {
      /* queryCommandState can throw if no active editable context */
    }
  }

  /** Match a computed font-family stack against our known font list. */
  private matchFontFamily(computedFamily: string): string | null {
    if (!computedFamily) {
      return null;
    }
    const first = computedFamily
      .split(",")[0]
      .trim()
      .replace(/^["']|["']$/g, "");
    const match = this.fontFamilies.find(
      (font) => font.toLowerCase() === first.toLowerCase()
    );
    return match || null;
  }

  /** Derive a line-spacing ratio, snapping to a known option when close. */
  private computeLineSpacingRatio(
    computed: CSSStyleDeclaration,
    fontSizePx: number
  ): number | null {
    const lineHeight = computed.lineHeight;
    if (!lineHeight || lineHeight === "normal") {
      return null;
    }
    const lineHeightPx = parseFloat(lineHeight);
    if (!lineHeightPx || !fontSizePx) {
      return null;
    }
    const ratio = lineHeightPx / fontSizePx;
    let nearest = this.lineSpacingOptions[0];
    let bestDistance = Number.POSITIVE_INFINITY;
    for (let i = 0; i < this.lineSpacingOptions.length; i += 1) {
      const distance = Math.abs(this.lineSpacingOptions[i] - ratio);
      if (distance < bestDistance) {
        bestDistance = distance;
        nearest = this.lineSpacingOptions[i];
      }
    }
    return bestDistance <= 0.15 ? nearest : Math.round(ratio * 100) / 100;
  }

  removeFormatting(): void {
    this.format("removeFormat");
    this.format("unlink");
  }

  onPageInput(index: number): void {
    this.activePageIndex = index;
    this.prepareResizableImages();
    this.syncPagesFromDom();
    // Mark the caret before reflow so it survives the DOM node moves reflow makes
    // between page editors (otherwise it jumps to the top of the page and vanishes
    // mid-typing). restorePendingCaret() re-places it once reflow settles.
    this.pendingCaretMarkerId = this.insertCaretMarker();
    this.reflowFrom(index);
    this.updateDocumentStats();
    this.updateTableToolbarFromSelection();
  }

  /**
   * Drop a zero-width marker span at the current caret and return its id. The
   * marker is moved along with its containing block when reflow shuffles content
   * between page editors, so the caret can be re-placed exactly where it was. Any
   * marker left over from an in-flight reflow is cleared first so at most one
   * exists. Returns null when the caret isn't inside a page editor.
   */
  private insertCaretMarker(): string | null {
    // Clear any stale marker from a still-settling reflow before adding a new one.
    document
      .querySelectorAll("[data-caret-marker]")
      .forEach((node) => node.remove());

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      return null;
    }
    const range = selection.getRangeAt(0);
    const node = range.startContainer;
    const el =
      node.nodeType === Node.ELEMENT_NODE
        ? (node as Element)
        : node.parentElement;
    if (!el || !el.closest(".page-editor")) {
      return null;
    }

    const marker = document.createElement("span");
    const id = `pdf-caret-${this.caretMarkerSeq++}`;
    marker.id = id;
    marker.setAttribute("data-caret-marker", "1");
    // Zero footprint so it never affects the height measurements reflow relies on.
    marker.style.cssText =
      "display:inline;padding:0;margin:0;border:0;width:0;height:0;font-size:0;line-height:0;";

    const at = range.cloneRange();
    at.collapse(true);
    try {
      at.insertNode(marker);
    } catch {
      return null;
    }
    return id;
  }

  /**
   * Re-place the caret at the pending marker (set before a reflow) and remove the
   * marker. Follows the marker wherever reflow moved it — including onto another
   * page editor — and focuses that editable host so typing continues seamlessly.
   */
  private restorePendingCaret(): void {
    const id = this.pendingCaretMarkerId;
    if (!id) {
      return;
    }
    this.pendingCaretMarkerId = null;
    const marker = document.getElementById(id);
    if (!marker || !marker.parentNode) {
      return;
    }
    const parent = marker.parentNode;
    const host = (marker.closest('[contenteditable="true"]') ||
      marker.closest(".page-editor")) as HTMLElement | null;
    const index = Array.prototype.indexOf.call(parent.childNodes, marker);
    marker.remove();

    const selection = window.getSelection();
    if (!selection) {
      return;
    }
    try {
      if (host && document.activeElement !== host) {
        host.focus();
      }
      const range = document.createRange();
      const safeIndex = Math.min(Math.max(index, 0), parent.childNodes.length);
      range.setStart(parent, safeIndex);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
    } catch {
      // Position became invalid (node removed by reflow) — leave focus on host.
    }
  }

  /** Strip any caret-preservation markers from serialized page HTML so they never
   *  leak into the saved/exported document. Cheap no-op when none are present. */
  private stripCaretMarkers(html: string): string {
    if (!html || html.indexOf("data-caret-marker") === -1) {
      return html;
    }
    return html.replace(/<span[^>]*data-caret-marker[^>]*>\s*<\/span>/gi, "");
  }

  /**
   * Intercept pastes so we never inject raw clipboard HTML (Word/Excel/web pages
   * carry scripts, event handlers, and heavy inline styles that corrupt the
   * pagination engine). We sanitize to a safe subset and insert that instead.
   */
  onEditorPaste(event: ClipboardEvent, index: number): void {
    const clipboard = event.clipboardData;
    if (!clipboard) {
      return;
    }

    event.preventDefault();
    this.activePageIndex = index;

    const editor = this.getActiveEditor();
    if (!editor) {
      return;
    }
    editor.focus();

    const html = clipboard.getData("text/html");
    const text = clipboard.getData("text/plain");

    // Guard against pasting enormous HTML (e.g. a page full of base64 images).
    // Injecting multiple MB into the 6-7 MB editor DOM can hang the browser and
    // overwhelm pagination. Above the cap, fall back to plain text.
    const MAX_PASTE_HTML = 1_500_000;
    const MAX_PASTE_TEXT = 500_000;

    let inserted = false;
    if (html && html.trim()) {
      const clean = this.sanitizePastedHtml(html);
      if (clean && clean.trim() && clean.length <= MAX_PASTE_HTML) {
        document.execCommand("insertHTML", false, clean);
        inserted = true;
      }
    }
    if (!inserted && text) {
      // insertText preserves line breaks and is always safe.
      document.execCommand(
        "insertText",
        false,
        text.length > MAX_PASTE_TEXT ? text.slice(0, MAX_PASTE_TEXT) : text
      );
      inserted = true;
    }
    if (!inserted) {
      return;
    }

    // Pasted content can change height and add images/tables → full layout pass.
    this.prepareResizableImages();
    this.syncPagesFromDom();
    // Keep the caret at the end of the pasted content through the reflow.
    this.pendingCaretMarkerId = this.insertCaretMarker();
    this.reflowFrom(this.activePageIndex);
    this.updateDocumentStats();
  }

  /**
   * Make Backspace able to remove the blank lines above (or below) rendered
   * blocks like charts/tables. Those gaps are empty <p><br></p> spacers; the
   * browser's default Backspace can't delete them because the element before is
   * non-editable (the chart/table), so the merge is a no-op. Here we detect that
   * case and remove the empty spacer ourselves, then reflow (which backfills).
   */
  onEditorKeydown(event: KeyboardEvent, index: number): void {
    if (event.key !== "Backspace" && event.key !== "Delete") {
      return;
    }
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || !selection.isCollapsed) {
      return; // let the default handle real selections / mid-text edits
    }
    const editor = this.pageEditors?.toArray()?.[index]?.nativeElement;
    if (!editor) {
      return;
    }
    const range = selection.getRangeAt(0);
    const block = this.getCaretBlock(range.startContainer, editor);
    if (!block) {
      return;
    }

    if (event.key === "Backspace") {
      if (!this.isCaretAtBlockStart(range, block)) {
        return; // normal backspace inside text
      }
      const prevBlock = this.findAdjacentBlock(block, index, "previous");

      // The current line is blank → delete it and pull the caret to the previous block.
      if (this.isBlockEmpty(block) && prevBlock) {
        event.preventDefault();
        this.deleteSpacerBlock(block, index);
        setTimeout(() => this.placeCaretAtEndOfBlock(prevBlock), 0);
        return;
      }

      // The current line has content but an empty spacer sits directly above it
      // (e.g. between a chart and this text) → remove that spacer.
      if (prevBlock && this.isBlockEmpty(prevBlock) && !this.isNonEditableBlock(prevBlock)) {
        event.preventDefault();
        this.deleteSpacerBlock(prevBlock, index);
        setTimeout(() => this.placeCaretAtStart(block), 0);
        return;
      }

      // Caret at the very start of the FIRST block on a later page, with content
      // above on the PREVIOUS page: browsers can't Backspace-join across separate
      // page editors, so do it ourselves — merge this block into the last block
      // of the previous page, then re-paginate.
      if (
        prevBlock &&
        !editor.contains(prevBlock) &&
        !this.isNonEditableBlock(prevBlock) &&
        !this.isNonEditableBlock(block)
      ) {
        event.preventDefault();
        this.mergeBlockIntoPrevious(block, prevBlock, index);
        return;
      }
      return;
    }

    // Delete (forward): remove an empty spacer line immediately below the caret.
    if (event.key === "Delete") {
      if (!this.isCaretAtBlockEnd(range, block)) {
        return;
      }
      const nextBlock = this.findAdjacentBlock(block, index, "next");
      if (nextBlock && this.isBlockEmpty(nextBlock) && !this.isNonEditableBlock(nextBlock)) {
        event.preventDefault();
        this.deleteSpacerBlock(nextBlock, index);
        setTimeout(() => this.placeCaretAtEndOfBlock(block), 0);
      }
    }
  }

  /** Nearest editable block element that contains the caret node. */
  private getCaretBlock(node: Node | null, editor: HTMLElement): HTMLElement | null {
    const el =
      node && node.nodeType === Node.ELEMENT_NODE
        ? (node as Element)
        : node?.parentElement || null;
    const block = el?.closest(
      "p,li,h1,h2,h3,h4,h5,h6,blockquote,div"
    ) as HTMLElement | null;
    if (!block || !editor.contains(block) || block === editor) {
      return null;
    }
    // Ignore structural containers (sections, imported wrappers, chart holders).
    if (
      block.querySelector(
        "p,li,table,.report-chart,.report-table-block,.resizable-image"
      )
    ) {
      return null;
    }
    return block;
  }

  /** A block with no visible text and no media counts as an empty spacer. */
  private isBlockEmpty(el: HTMLElement): boolean {
    if (
      el.querySelector(
        "img,table,hr,canvas,svg,.report-chart,.report-table-block,.resizable-image"
      )
    ) {
      return false;
    }
    const text = String(el.textContent || "")
      .replace(/ /g, " ")
      .replace(/[​-‍﻿]/g, "")
      .trim();
    return text.length === 0;
  }

  /** True when nothing editable can merge into this block (chart/table/etc.). */
  private isNonEditableBlock(el: HTMLElement): boolean {
    return (
      el.getAttribute("contenteditable") === "false" ||
      !!el.querySelector(
        ".report-chart, .report-table-block, .resizable-image, img, table"
      )
    );
  }

  private isCaretAtBlockStart(range: Range, block: HTMLElement): boolean {
    const pre = range.cloneRange();
    pre.selectNodeContents(block);
    pre.setEnd(range.startContainer, range.startOffset);
    return pre.toString().replace(/ /g, " ").length === 0;
  }

  private isCaretAtBlockEnd(range: Range, block: HTMLElement): boolean {
    const post = range.cloneRange();
    post.selectNodeContents(block);
    post.setStart(range.endContainer, range.endOffset);
    return post.toString().replace(/ /g, " ").length === 0;
  }

  /** All leaf editable blocks of an editor, in document order. */
  private collectEditableBlocks(editor: HTMLElement): HTMLElement[] {
    return (
      Array.from(
        editor.querySelectorAll("p,li,h1,h2,h3,h4,h5,h6,blockquote,div")
      ) as HTMLElement[]
    ).filter(
      (el) =>
        !el.querySelector(
          "p,li,div,table,.report-chart,.report-table-block,.resizable-image"
        ) && !this.isNonEditableBlock(el)
    );
  }

  /**
   * Join a block at the top of a page into the last block of the previous page
   * (cross-page Backspace merge that the browser can't do across separate
   * contenteditables), then re-paginate and restore the caret at the join.
   */
  private mergeBlockIntoPrevious(
    block: HTMLElement,
    prevBlock: HTMLElement,
    index: number
  ): void {
    // The first node we move marks the join point for the caret.
    const joinAnchor = block.firstChild as ChildNode | null;
    while (block.firstChild) {
      prevBlock.appendChild(block.firstChild);
    }
    block.remove();
    this.syncPagesFromDom();
    this.reflowFrom(Math.max(0, index - 1));
    setTimeout(() => {
      const sel = window.getSelection();
      if (!sel) {
        return;
      }
      const r = document.createRange();
      if (joinAnchor && joinAnchor.isConnected) {
        r.setStartBefore(joinAnchor);
      } else {
        r.selectNodeContents(prevBlock);
        r.collapse(false);
      }
      r.collapse(true);
      sel.removeAllRanges();
      sel.addRange(r);
    }, 0);
  }

  /** Previous/next editable block in document order, crossing page boundaries. */
  private findAdjacentBlock(
    block: HTMLElement,
    index: number,
    direction: "previous" | "next"
  ): HTMLElement | null {
    const editor = this.pageEditors?.toArray()?.[index]?.nativeElement;
    if (!editor) {
      return null;
    }
    const blocks = this.collectEditableBlocks(editor);
    const pos = blocks.indexOf(block);
    if (pos === -1) {
      return null;
    }
    if (direction === "previous") {
      if (pos > 0) {
        return blocks[pos - 1];
      }
      const prevEditor =
        this.pageEditors?.toArray()?.[index - 1]?.nativeElement;
      if (prevEditor) {
        const prevBlocks = this.collectEditableBlocks(prevEditor);
        return prevBlocks.length ? prevBlocks[prevBlocks.length - 1] : null;
      }
      return null;
    }
    if (pos < blocks.length - 1) {
      return blocks[pos + 1];
    }
    const nextEditor = this.pageEditors?.toArray()?.[index + 1]?.nativeElement;
    if (nextEditor) {
      const nextBlocks = this.collectEditableBlocks(nextEditor);
      return nextBlocks.length ? nextBlocks[0] : null;
    }
    return null;
  }

  /** Remove an empty spacer block (and its now-empty report section), reflow. */
  private deleteSpacerBlock(block: HTMLElement, index: number): void {
    const parentSection = block.closest(
      "section.report-block"
    ) as HTMLElement | null;
    block.remove();
    if (
      parentSection &&
      !parentSection.querySelector(
        "img,table,.report-chart,.report-table-block,.resizable-image"
      ) &&
      !String(parentSection.textContent || "").trim()
    ) {
      parentSection.remove();
    }
    this.activePageIndex = index;
    this.syncPagesFromDom();
    this.reflowFrom(0);
    this.updateDocumentStats();
  }

  // ----------------------------------------------------------------------------
  // Drag-to-move for charts / tables / images.
  // Each page is its own contenteditable, so the browser's native drag-and-drop
  // COPIES the element across pages (leaving a duplicate). We intercept the drop,
  // prevent the native copy, and move the actual node ourselves.
  // ----------------------------------------------------------------------------

  onEditorDragStart(event: DragEvent, index: number): void {
    const target = event.target as Element | null;
    if (!target) {
      return;
    }
    const unit = this.getDraggableUnit(target);
    if (!unit) {
      this.draggedUnit = null;
      return; // not a chart/table/image → let default (e.g. text drag) proceed
    }
    this.draggedUnit = unit;
    this.activePageIndex = index;
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = "move";
      try {
        // Some browsers need data set for the drag to start.
        event.dataTransfer.setData("text/plain", "");
      } catch {
        /* ignore */
      }
    }
  }

  onEditorDragOver(event: DragEvent): void {
    if (!this.draggedUnit) {
      return; // not our drag → leave native behaviour untouched
    }
    event.preventDefault(); // mark as a valid drop target
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = "move";
    }
  }

  onEditorDrop(event: DragEvent, index: number): void {
    if (!this.draggedUnit) {
      return;
    }
    // Prevent the browser's default drop (which inserts a COPY) — we move the
    // real node instead, so nothing is duplicated.
    event.preventDefault();
    event.stopPropagation();

    const unit = this.draggedUnit;
    this.draggedUnit = null;

    const editor = this.pageEditors?.toArray()?.[index]?.nativeElement;
    if (!editor) {
      return;
    }
    // Ignore a drop onto the dragged unit itself.
    const dropTarget = event.target as Node | null;
    if (dropTarget && unit.contains(dropTarget)) {
      return;
    }

    this.dropUnitAtPoint(unit, editor, event.clientY);

    this.activePageIndex = index;
    this.prepareResizableImages();
    this.syncPagesFromDom();
    this.reflowFrom(0);
    this.updateDocumentStats();
  }

  onEditorDragEnd(): void {
    this.draggedUnit = null;
  }

  /**
   * The element that should move as one unit when dragged: a whole chart/table
   * report section, otherwise the outermost resizable image wrapper. (Inline
   * images inside a text report move on their own, not the whole text block.)
   */
  private getDraggableUnit(el: Element): HTMLElement | null {
    const chartOrTable = el.closest(
      ".report-chart, .report-chart-image, .report-table-block"
    ) as HTMLElement | null;
    if (chartOrTable) {
      const section = chartOrTable.closest(
        "section.report-block"
      ) as HTMLElement | null;
      if (section) {
        return section;
      }
    }
    let wrapper = el.closest(".resizable-image") as HTMLElement | null;
    while (wrapper) {
      const parent = wrapper.parentElement?.closest(
        ".resizable-image"
      ) as HTMLElement | null;
      if (!parent) {
        break;
      }
      wrapper = parent;
    }
    return wrapper;
  }

  /**
   * Move an existing node to the editor's top level at the vertical drop point.
   * Because it is the same DOM node, this relocates it (no copy/duplicate).
   */
  private dropUnitAtPoint(
    unit: HTMLElement,
    editor: HTMLElement,
    dropY: number
  ): void {
    const children = (Array.from(editor.children) as HTMLElement[]).filter(
      (child) => child !== unit && !unit.contains(child) && !child.contains(unit)
    );
    let reference: HTMLElement | null = null;
    for (let i = 0; i < children.length; i += 1) {
      const rect = children[i].getBoundingClientRect();
      if (dropY < rect.top + rect.height / 2) {
        reference = children[i];
        break;
      }
    }
    if (reference) {
      editor.insertBefore(unit, reference);
    } else {
      editor.appendChild(unit);
    }
  }

  /** Place the caret at the end of a block and focus its editor. */
  private placeCaretAtEndOfBlock(block: HTMLElement): void {
    if (!block || !block.isConnected) {
      return;
    }
    const editor = block.closest(".page-editor") as HTMLElement | null;
    const editors = this.pageEditors?.toArray() || [];
    const editorIndex = editors.findIndex(
      (ref) => ref.nativeElement === editor
    );
    if (editorIndex >= 0) {
      this.activePageIndex = editorIndex;
    }
    if (editor) {
      editor.focus();
    }
    this.placeCaretAtEnd(block);
  }

  /**
   * Reduce arbitrary clipboard HTML to a safe, lightweight subset:
   *  - strip scripts/handlers/javascript: (via sanitizeImportedHtml),
   *  - drop Office/markup cruft (style, meta, o:p, comments, …),
   *  - unwrap disallowed tags but keep their text,
   *  - keep only a small whitelist of attributes and inline style properties.
   */
  private sanitizePastedHtml(html: string): string {
    const base = this.sanitizeImportedHtml(html);
    if (!base) {
      return "";
    }

    const container = document.createElement("div");
    container.innerHTML = base;

    // Remove elements that should never survive a paste.
    container
      .querySelectorAll("style, meta, link, title, head, script, xml, o\\:p")
      .forEach((el) => el.remove());

    // Remove comments (Word emits conditional-comment blocks).
    const commentWalker = document.createTreeWalker(
      container,
      NodeFilter.SHOW_COMMENT
    );
    const comments: Comment[] = [];
    let comment = commentWalker.nextNode();
    while (comment) {
      comments.push(comment as Comment);
      comment = commentWalker.nextNode();
    }
    comments.forEach((node) => node.parentNode?.removeChild(node));

    const allowedTags = new Set<string>([
      "P", "BR", "B", "STRONG", "I", "EM", "U", "S", "STRIKE", "DEL",
      "SUP", "SUB", "H1", "H2", "H3", "H4", "H5", "H6", "BLOCKQUOTE",
      "UL", "OL", "LI", "A", "SPAN", "DIV", "TABLE", "THEAD", "TBODY",
      "TFOOT", "TR", "TD", "TH", "HR", "IMG", "FONT", "PRE", "CODE",
    ]);

    // Document order → parents are processed before children, so unwrapping a
    // disallowed parent still lets its (now lifted) children be cleaned later.
    const elements = Array.from(container.querySelectorAll("*"));
    for (let i = 0; i < elements.length; i += 1) {
      const el = elements[i] as HTMLElement;
      const tag = el.tagName.toUpperCase();
      if (!allowedTags.has(tag)) {
        const parent = el.parentNode;
        if (parent) {
          while (el.firstChild) {
            parent.insertBefore(el.firstChild, el);
          }
          parent.removeChild(el);
        }
        continue;
      }
      this.cleanPastedElementAttributes(el);
    }

    this.relaxNonBreakingSpaces(container);
    return container.innerHTML;
  }

  /** Keep only safe attributes + a whitelist of inline style properties. */
  private cleanPastedElementAttributes(el: HTMLElement): void {
    const tag = el.tagName.toUpperCase();
    const keep = new Set<string>();
    if (tag === "A") {
      keep.add("href");
      keep.add("target");
      keep.add("rel");
    } else if (tag === "IMG") {
      keep.add("src");
      keep.add("alt");
    } else if (tag === "TD" || tag === "TH") {
      keep.add("colspan");
      keep.add("rowspan");
    }

    const attributes = Array.from(el.attributes);
    for (let i = 0; i < attributes.length; i += 1) {
      const name = attributes[i].name.toLowerCase();
      if (name === "style") {
        continue;
      }
      if (!keep.has(name)) {
        el.removeAttribute(attributes[i].name);
      }
    }

    const style = el.getAttribute("style");
    if (style) {
      const safe = this.filterInlineStyle(style);
      if (safe) {
        el.setAttribute("style", safe);
      } else {
        el.removeAttribute("style");
      }
    }

    if (tag === "A") {
      const href = el.getAttribute("href") || "";
      if (/^\s*javascript:/i.test(href)) {
        el.removeAttribute("href");
      }
    }
    if (tag === "IMG") {
      const src = el.getAttribute("src") || "";
      if (/^\s*javascript:/i.test(src)) {
        el.remove();
      }
    }
  }

  /** Allow only a small set of visual style properties; reject any url()/expression. */
  private filterInlineStyle(style: string): string {
    const allowedProps = new Set<string>([
      "color", "background-color", "font-weight", "font-style", "font-size",
      "text-align", "text-decoration", "line-height", "font-family",
    ]);

    return style
      .split(";")
      .map((decl) => decl.trim())
      .filter(Boolean)
      .filter((decl) => {
        const idx = decl.indexOf(":");
        if (idx < 0) {
          return false;
        }
        const prop = decl.slice(0, idx).trim().toLowerCase();
        const value = decl.slice(idx + 1).toLowerCase();
        if (!allowedProps.has(prop)) {
          return false;
        }
        if (
          value.includes("url(") ||
          value.includes("expression") ||
          value.includes("javascript:")
        ) {
          return false;
        }
        return true;
      })
      .join("; ");
  }

  onEditorClick(event: MouseEvent, index: number): void {
    this.activePageIndex = index;

    const rawTarget = event.target as EventTarget | null;
    const target =
      rawTarget instanceof Element
        ? rawTarget
        : (rawTarget as Node | null)?.parentElement || null;
    if (!target) {
      this.debugSelectionLog("[SELECT] editor:click no target");
      return;
    }
    // Show the structural-table toolbar when a cell is clicked; hide otherwise.
    this.updateTableToolbarForNode(target);
    this.debugSelectionLog(
      "[SELECT] editor:click",
      "index:",
      index,
      "target:",
      this.describeElement(target),
      "path:",
      this.getEventPathSummary(event)
    );

    const existingWrapper =
      this.resolveWrapperFromEvent(event) || this.resolveWrapperFromTarget(target);
    const clickedImageFromEvent = this.resolveImageFromEvent(event);
    const clickedImageFromTarget = this.resolveImageFromTarget(target);
    this.debugSelectionLog(
      "[SELECT][CLICK] resolve",
      "index:",
      index,
      "target:",
      this.describeElement(target),
      "path:",
      this.getEventPathSummary(event),
      "img(event):",
      this.describeElement(clickedImageFromEvent),
      "img(target):",
      this.describeElement(clickedImageFromTarget),
      "wrapper:",
      this.describeElement(existingWrapper)
    );
    if (existingWrapper) {
      event.stopPropagation();
      this.debugSelectionLog(
        "[SELECT] editor:click wrapper found",
        this.describeElement(existingWrapper)
      );
      this.selectResizableWrapper(existingWrapper);
      return;
    }

    const clickedImage = clickedImageFromEvent || clickedImageFromTarget;

    if (!clickedImage) {
      const allImagesInEditor = this.pageEditors?.toArray()?.[index]?.nativeElement?.querySelectorAll("img")
        ?.length;
      this.debugSelectionLog(
        "[SELECT][CLICK] no image/wrapper found",
        "editorImageCount:",
        allImagesInEditor ?? 0
      );
      return;
    }

    const editor = this.pageEditors?.toArray()?.[index]?.nativeElement;
    if (!editor) {
      return;
    }

    this.enableResizableImagesInEditor(editor);
    const wrapped =
      this.resolveWrapperFromEvent(event) ||
      this.resolveWrapperFromTarget(target) ||
      (clickedImage.closest(".resizable-image") as HTMLElement | null);
    this.debugSelectionLog(
      "[SELECT][CLICK] post-wrap",
      "clickedImage:",
      this.describeElement(clickedImage),
      "resolvedWrapper:",
      this.describeElement(wrapped)
    );
    if (wrapped) {
      event.stopPropagation();
      this.debugSelectionLog(
        "[SELECT] editor:click wrapped after prepare",
        this.describeElement(wrapped)
      );
      this.selectResizableWrapper(wrapped);
      return;
    }
    this.debugSelectionLog(
      "[SELECT] editor:click image still not wrapped",
      this.describeElement(clickedImage)
    );
  }

  onEditorMouseDown(event: MouseEvent, index: number): void {
    this.activePageIndex = index;
    const target = event.target instanceof Element ? event.target : null;
    if (!target) {
      return;
    }

    // Grabbing a cell's right/bottom border resizes the column/row instead of
    // placing the caret or selecting the surrounding chart/table wrapper.
    if (this.maybeStartTableResize(event, target)) {
      return;
    }

    let wrapper =
      this.resolveWrapperFromEvent(event) || this.resolveWrapperFromTarget(target);
    const mousedownImageFromEvent = this.resolveImageFromEvent(event);
    const mousedownImageFromTarget = this.resolveImageFromTarget(target);
    this.debugSelectionLog(
      "[SELECT][MOUSEDOWN] resolve",
      "index:",
      index,
      "target:",
      this.describeElement(target),
      "path:",
      this.getEventPathSummary(event),
      "img(event):",
      this.describeElement(mousedownImageFromEvent),
      "img(target):",
      this.describeElement(mousedownImageFromTarget),
      "wrapper:",
      this.describeElement(wrapper)
    );
    if (!wrapper) {
      const clickedImage = mousedownImageFromEvent || mousedownImageFromTarget;

      if (!clickedImage) {
        const allImagesInEditor = this.pageEditors?.toArray()?.[index]?.nativeElement?.querySelectorAll("img")
          ?.length;
        this.debugSelectionLog(
          "[SELECT][MOUSEDOWN] no clicked image",
          "editorImageCount:",
          allImagesInEditor ?? 0
        );
        return;
      }

      const editor = this.pageEditors?.toArray()?.[index]?.nativeElement;
      if (!editor) {
        return;
      }

      // Ensure direct image clicks become selectable immediately.
      this.enableResizableImagesInEditor(editor);
      wrapper =
        this.resolveWrapperFromEvent(event) ||
        this.resolveWrapperFromTarget(target) ||
        (clickedImage.closest(".resizable-image") as HTMLElement | null);
      if (!wrapper) {
        this.debugSelectionLog(
          "[SELECT][MOUSEDOWN] clicked image exists but wrapper missing after prepare",
          this.describeElement(clickedImage)
        );
        return;
      }
    }

    this.selectResizableWrapper(wrapper);

    const handleEl = target.closest(".image-resize-handle") as HTMLElement | null;
    const clickedHandle = !!handleEl;
    const handleDir = String(handleEl?.getAttribute("data-resize-dir") || "");
    const rect = wrapper.getBoundingClientRect();
    const fromRight = rect.right - event.clientX;
    const fromBottom = rect.bottom - event.clientY;
    const dragDir = handleDir;

    this.debugSelectionLog(
      "[SELECT] editor:mousedown wrapper",
      this.describeElement(wrapper),
      "clickedHandle:",
      clickedHandle,
      "fromRight:",
      fromRight,
      "fromBottom:",
      fromBottom,
      "dragDir:",
      dragDir
    );

    if (dragDir) {
      event.preventDefault();
      event.stopPropagation();
      this.startResizeDrag(wrapper, event, dragDir);
    }
  }

  private statsTimer: ReturnType<typeof setTimeout> | null = null;

  // Word/character counts are footer-only display. Reading innerText of every
  // page (which forces layout) and scanning a huge string on every toolbar action
  // makes large documents hang, so defer it until edits settle.
  private updateDocumentStats(): void {
    if (this.statsTimer) {
      clearTimeout(this.statsTimer);
    }
    this.statsTimer = setTimeout(() => {
      this.statsTimer = null;
      this.computeDocumentStats();
    }, 300);
  }

  private computeDocumentStats(): void {
    const plainText = this.pageEditors
      ? this.pageEditors
          .toArray()
          .map((ref) => ref.nativeElement.innerText || "")
          .join(" ")
          .trim()
      : "";

    this.characterCount = plainText.length;
    this.wordCount = plainText ? plainText.split(/\s+/).length : 0;
  }

  private getActiveEditor(): HTMLDivElement | null {
    const editors = this.pageEditors?.toArray() || [];
    return editors[this.activePageIndex]?.nativeElement || null;
  }

  /**
   * Focus the editable host that owns the current selection so an execCommand
   * targets the right text. The selection may sit in an editable table island
   * (`.report-table-block[contenteditable=true]`) nested in the page editor;
   * focusing an ancestor instead would collapse the selection. Only focuses when
   * the host isn't already active (to avoid collapsing it), and falls back to the
   * page editor when the selection isn't inside any editable region.
   */
  private focusSelectionEditableHost(fallback: HTMLElement): void {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const node = selection.getRangeAt(0).startContainer;
      const el =
        node.nodeType === Node.ELEMENT_NODE
          ? (node as Element)
          : node.parentElement;
      const host = el?.closest(
        '[contenteditable="true"]'
      ) as HTMLElement | null;
      if (host) {
        if (document.activeElement !== host) {
          host.focus();
        }
        return;
      }
    }
    fallback.focus();
  }

  private focusActivePage(): void {
    const editor = this.getActiveEditor();
    if (editor) {
      editor.focus();
    }
  }

  private reflowFrom(startIndex: number): void {
    if (!this.pageEditors || !this.pageEditors.length) {
      return;
    }

    this.debugLog(
      "[PDF][REFLOW] ▶ start — from page",
      startIndex + 1,
      "of",
      this.pages.length
    );
    this.debugDumpPageDom("reflow:before");

    this.reflowInProgress = true;
    // Consolidate any same-page table fragments first so a table is never shown
    // split twice on one page; the forward pass below re-splits across pages.
    const allEditors = this.pageEditors.toArray();
    let earliestMerged = -1;
    for (let e = 0; e < allEditors.length; e += 1) {
      if (this.mergeAdjacentTableFragments(allEditors[e].nativeElement)) {
        if (earliestMerged === -1) {
          earliestMerged = e;
        }
      }
    }
    // If we merged fragments, start the forward pass at the earliest affected
    // page so the now-taller table is re-split from there.
    const forwardStart =
      earliestMerged === -1
        ? startIndex
        : Math.min(startIndex, earliestMerged);

    // Pre-create all the blank pages the overflow will need in ONE shot. Without
    // this, moveOverflowForward creates a single page, returns to wait for Angular
    // to render it, then the whole reflow re-runs — an O(pages) async cascade that
    // re-measures the entire (multi-MB) document each cycle. After this pre-pass,
    // the forward sweep below distributes everything in a single synchronous run.
    if (this.ensureBlankPageCapacity()) {
      this.debugLog(
        "[PDF][REFLOW] ⏸ pre-created blank pages — total now",
        this.pages.length,
        "; waiting for render, then one distribution pass"
      );
      setTimeout(() => this.reflowFrom(startIndex), 0);
      return;
    }

    const waitingForNewPage = this.moveOverflowForward(forwardStart);
    if (waitingForNewPage) {
      // Reflow continues asynchronously after the new page renders; stay marked
      // in-progress so the changes subscription keeps deferring to the live DOM.
      this.debugLog(
        "[PDF][REFLOW] ⏸ paused — added a page, waiting for it to render, then re-run"
      );
      return;
    }
    // Pull content back up to fill any gaps left when content shrank (e.g. a
    // smaller font, deletion). Scan from the first non-reserved page so the
    // leading reserved pages (MOR cover) are never backfilled with report
    // content. moveOverflowForward already ensured no page overflows, so this
    // only fills genuine free space.
    this.pullContentBackward(this.reservedLeadingPages);
    this.removeTrailingEmptyPages();
    this.syncPagesFromDom();
    // Pagination is final for this pass — refresh any Table of Contents page
    // numbers so they always reflect where reports actually landed (covers
    // initial render, edits, and reports dragged between pages).
    this.refreshTableOfContents();
    this.reflowInProgress = false;
    this.debugDumpPageDom("reflow:after");
    this.debugLog("[PDF][REFLOW] ✔ settled — pages now", this.pages.length);
    // Reflow is done moving nodes around; put the caret back where the user was
    // typing (the marker travelled with its block, even onto another page).
    this.restorePendingCaret();
    // An edit asked to autosave while we were paginating; now that the model is
    // consistent again, honour it.
    if (this.draftAutosavePending) {
      this.draftAutosavePending = false;
      this.scheduleDraftAutosave();
    }
    // Page count may have changed; refresh the footer's current-page tracker.
    setTimeout(() => this.updateVisiblePageIndex(), 0);
    // Reflow has fully settled — now scroll to a just-inserted table (if any),
    // since pagination may have moved it to its final page.
    if (this.pendingTableReveal) {
      this.pendingTableReveal = false;
      setTimeout(() => this.revealInsertedTable(), 0);
    }
  }

  /**
   * Pre-create the blank pages the current overflow will need, in one shot, so
   * moveOverflowForward can distribute content in a single synchronous sweep
   * instead of the create-one-page-then-wait-and-rerun cascade.
   *
   * Returns true when it added pages (caller should wait a tick for Angular to
   * render them, then re-run reflow). Returns false when enough capacity already
   * exists — which is what stops it re-adding on the re-run: the pages it created
   * are now counted as available trailing blanks, so the deficit is zero.
   */
  private ensureBlankPageCapacity(): boolean {
    const editors = this.pageEditors?.toArray() || [];
    if (!editors.length) {
      return false;
    }

    let overflowPx = 0;
    let usable = 0;
    for (let i = 0; i < editors.length; i += 1) {
      const el = editors[i].nativeElement;
      usable = Math.max(usable, el.clientHeight || 0);
      const o = el.scrollHeight - el.clientHeight;
      // Only count overflow that adding pages can actually relieve. A page whose
      // overflow comes from a single indivisible block (taller than the page,
      // not splittable) will stay overflowing no matter how many pages we add —
      // counting it would make us pre-create pages every reflow forever.
      if (o > 1 && this.pageOverflowIsRelievable(el)) {
        overflowPx += o;
      }
    }
    if (overflowPx <= 1 || usable <= 0) {
      return false;
    }

    // Trailing blank pages can already absorb part of the overflow.
    let trailingBlanks = 0;
    for (let i = editors.length - 1; i >= 0; i -= 1) {
      if (this.isBlankPageEl(editors[i].nativeElement)) {
        trailingBlanks += 1;
      } else {
        break;
      }
    }

    // Over-provision slightly. Content never packs to 100% of a page (block
    // boundaries leave slack), and splitting a too-tall block ADDS height (each
    // continuation re-adds a wrapper), so a raw overflow/usable estimate
    // under-shoots and forces several extra reflow cycles. Use 88% of the page
    // as the effective capacity and add one page of headroom; any surplus blank
    // pages are trimmed by removeTrailingEmptyPages at the end.
    const pagesNeeded = Math.ceil(overflowPx / (usable * 0.88)) + 1;
    const room = Math.max(0, this.maxAutoPages - this.pages.length);
    const toAdd = Math.min(Math.max(0, pagesNeeded - trailingBlanks), room);
    for (let i = 0; i < toAdd; i += 1) {
      this.pages.push({ id: this.nextPageId++, html: "<p><br></p>" });
    }
    return toAdd > 0;
  }

  /**
   * True when a page's overflow can be reduced by adding pages — i.e. it has
   * more than one block (some can move forward), or its single block is one we
   * can split across the boundary (a text report with >1 inner paragraph, or a
   * data table with >1 row). A lone indivisible block taller than the page is
   * NOT relievable: it will be left clipped, so its overflow must not drive
   * page creation.
   */
  private pageOverflowIsRelievable(el: HTMLElement): boolean {
    if (el.children.length !== 1) {
      return el.children.length > 1;
    }
    const only = el.firstElementChild as HTMLElement | null;
    if (!only) {
      return false;
    }
    if (only.classList.contains("report-block")) {
      const inner = only.querySelector(
        ":scope > .imported-report-content"
      ) as HTMLElement | null;
      if (inner && inner.children.length > 1) {
        return true;
      }
      const rows = only.querySelectorAll(
        "table.report-data-table tbody > tr"
      );
      if (rows.length > 1) {
        return true;
      }
    }
    return false;
  }

  private moveOverflowForward(startIndex: number): boolean {
    const editors = this.pageEditors.toArray();

    // When the document has a page-exclusive block (e.g. the Table of Contents),
    // a later page may need isolating even though earlier pages are settled, so
    // we can't take the "cascade settled → stop scanning" shortcut below.
    const docHasExclusive = editors.some((e) =>
      Array.from(e.nativeElement.children).some((c) => this.isExclusiveBlock(c))
    );

    for (let index = startIndex; index < editors.length; index += 1) {
      const currentEditor = editors[index].nativeElement;
      let guard = 0;
      let pushedForward = false;
      this.removeTrailingWhitespaceNodes(currentEditor);

      while (
        (currentEditor.scrollHeight > currentEditor.clientHeight + 1 ||
          this.pageHasUnisolatedExclusive(currentEditor)) &&
        currentEditor.lastElementChild &&
        guard < 300
      ) {
        guard += 1;
        // Whether this page genuinely overflows (vs. only needing a page-exclusive
        // block isolated). Splitting tables/text is only valid for real overflow;
        // for isolation we move whole blocks forward instead.
        const overflowing =
          currentEditor.scrollHeight > currentEditor.clientHeight + 1;
        const nextIndex = index + 1;
        const nextEditor = editors[nextIndex]?.nativeElement;

        if (!nextEditor) {
          if (this.pages.length >= this.maxAutoPages) {
            this.debugLog(
              "[PDF][REFLOW] ✋ hit maxAutoPages cap",
              this.maxAutoPages,
              "— page",
              index + 1,
              "still overflows by",
              currentEditor.scrollHeight - currentEditor.clientHeight,
              "px; remaining content will be clipped"
            );
            return false;
          }

          this.debugLog(
            "[PDF][REFLOW] ➕ page",
            index + 1,
            "overflows (scrollH",
            currentEditor.scrollHeight,
            "> clientH",
            currentEditor.clientHeight,
            ") → adding page",
            nextIndex + 1
          );
          this.pages.push({ id: this.nextPageId++, html: "<p><br></p>" });
          this.activePageIndex = nextIndex;
          setTimeout(() => {
            this.reflowFrom(index);
            setTimeout(() => {
              const activeEditor = this.getActiveEditor();
              if (activeEditor) {
                this.placeCaretAtEnd(activeEditor);
              }
            }, 0);
          }, 0);
          return true;
        }

        const movingNode = currentEditor.lastElementChild;
        if (!movingNode) {
          break;
        }

        // Decide whether to row-split a table or move it forward whole.
        // - If the table is the ONLY thing on this page yet the page still
        //   overflows, it cannot fit on any page whole → split it.
        // - If it is taller than the usable page height → split.
        // - If it is being pushed off the bottom but there is still MEANINGFUL
        //   room above it on this page, split it so those rows fill the space
        //   instead of leaving a large blank gap above the table.
        // - Only when the remaining room is too small to hold a sensible chunk
        //   of rows do we move the WHOLE table forward (avoids an ugly split
        //   that would orphan just a header / a row or two).
        let tableNeedsSplit = false;
        if (
          overflowing &&
          movingNode instanceof HTMLElement &&
          movingNode.classList.contains("table-report-block")
        ) {
          const editorStyle = window.getComputedStyle(currentEditor);
          const padY =
            (parseFloat(editorStyle.paddingTop) || 0) +
            (parseFloat(editorStyle.paddingBottom) || 0);
          const usableHeight = currentEditor.clientHeight - padY;
          const isAloneOnPage = movingNode === currentEditor.firstElementChild;
          const tallerThanPage = movingNode.offsetHeight > usableHeight;
          // Free vertical space that was available for the table on this page
          // (page height minus the content above it).
          const freeSpace =
            currentEditor.clientHeight -
            (currentEditor.scrollHeight - movingNode.offsetHeight);
          // ~ table header + a couple of data rows.
          const MIN_FILL_SPACE = 140;
          tableNeedsSplit =
            isAloneOnPage || tallerThanPage || freeSpace >= MIN_FILL_SPACE;
        }

        if (
          tableNeedsSplit &&
          this.splitTableBlockOverflow(movingNode as HTMLElement, nextEditor)
        ) {
          pushedForward = true;
          this.removeTrailingWhitespaceNodes(currentEditor);
          if (index === this.activePageIndex) {
            this.activePageIndex = nextIndex;
            setTimeout(() => this.placeCaretAtEnd(nextEditor), 0);
          }
          continue;
        }

        // Runaway guard: when the ONLY block left on this page STILL makes the
        // page overflow, it cannot fit on any page — whether from its own height
        // or its margins (offsetHeight excludes margins, so a sub-page-height
        // block with a bottom margin still overflows). Pushing it forward just
        // marches it through blank pages until maxAutoPages (the "15 real pages
        // but 80 total" symptom). Split a text report block by flowing its
        // trailing paragraphs onto the next page (no content lost); only when it
        // genuinely can't be split do we leave it here (clipped) so the rest of
        // the document still paginates.
        if (movingNode === currentEditor.firstElementChild) {
          if (
            movingNode instanceof HTMLElement &&
            movingNode.classList.contains("report-block") &&
            this.splitTextReportBlockOverflow(movingNode, nextEditor)
          ) {
            this.debugLog(
              "[PDF][REFLOW] ✂ split tall text report across page",
              index + 1,
              "→",
              nextIndex + 1,
              "report:",
              movingNode.getAttribute("data-report-id")
            );
            pushedForward = true;
            this.removeTrailingWhitespaceNodes(currentEditor);
            continue;
          }
          this.debugLog(
            "[PDF][REFLOW] ⚠ single block on page",
            index + 1,
            "overflows and can't be split — leaving in place instead of pushing forever"
          );
          break;
        }

        nextEditor.insertBefore(movingNode, nextEditor.firstChild);
        pushedForward = true;
        this.removeTrailingWhitespaceNodes(currentEditor);

        // Keep typing flow natural: when current page overflows, continue on next page.
        if (index === this.activePageIndex) {
          this.activePageIndex = nextIndex;
          setTimeout(() => this.placeCaretAtEnd(nextEditor), 0);
        }
      }

      // Cascade settled: this page fit without pushing anything to the next, so
      // every later page is unchanged. Stop instead of scanning all remaining
      // pages (a forced layout read each) — the key win for large documents.
      // Skip this shortcut when an exclusive block exists, since a later page may
      // still need isolating independently of earlier pages.
      if (!pushedForward && !docHasExclusive) {
        break;
      }
    }

    return false;
  }

  private splitTableBlockOverflow(
    sourceBlock: HTMLElement,
    nextEditor: HTMLElement
  ): boolean {
    const sourceBody = sourceBlock.querySelector(
      "table.report-data-table tbody"
    ) as HTMLElement | null;
    if (!sourceBody) {
      return false;
    }

    const sourceRows = sourceBody.querySelectorAll("tr");
    if (sourceRows.length <= 1) {
      return false;
    }

    // The table id lives on the inner .report-table-block, not on the section
    // wrapper — read it via getTableBlockId. Reading the (absent) attribute off
    // the section yielded "" for every table, so a split fragment latched onto
    // ANY table at the top of the next page and merged two unrelated tables.
    const tableId = this.getTableBlockId(sourceBlock);
    let targetBlock = null as HTMLElement | null;
    const firstNextChild = nextEditor.firstElementChild as HTMLElement | null;
    if (
      firstNextChild &&
      this.isTableSection(firstNextChild) &&
      tableId &&
      this.getTableBlockId(firstNextChild) === tableId
    ) {
      targetBlock = firstNextChild;
    }

    if (!targetBlock) {
      targetBlock = sourceBlock.cloneNode(true) as HTMLElement;
      // Mark the carried-over fragment "(cont.)" so a table spanning pages doesn't
      // read as a second, unrelated table. Tables no longer render their own title
      // bar (the report-name heading above them covers it), so the heading carries
      // the marker; documents saved back when tables had a title bar still have
      // one, and it takes precedence so those keep marking it where they always did.
      const targetTitle = (targetBlock.querySelector(".report-chart-title") ||
        targetBlock.querySelector(".report-name-heading")) as HTMLElement | null;
      if (targetTitle) {
        const baseTitle = String(targetTitle.textContent || "").trim();
        if (baseTitle && !/\(cont\.\)$/i.test(baseTitle)) {
          targetTitle.textContent = `${baseTitle} (cont.)`;
        }
      }
      const targetBody = targetBlock.querySelector(
        "table.report-data-table tbody"
      ) as HTMLElement | null;
      if (!targetBody) {
        return false;
      }
      targetBody.innerHTML = "";
      nextEditor.insertBefore(targetBlock, nextEditor.firstChild);
    }

    const destinationBody = targetBlock.querySelector(
      "table.report-data-table tbody"
    ) as HTMLElement | null;
    if (!destinationBody) {
      return false;
    }

    const rowToMove = sourceBody.lastElementChild as HTMLElement | null;
    if (!rowToMove) {
      return false;
    }

    const rowPreview = (rowToMove.textContent || "")
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, 90);
    this.debugLog(
      "[TABLE][SPLIT] move start",
      "tableId:",
      tableId,
      "row:",
      rowPreview,
      "sourceRowsBefore:",
      sourceBody.querySelectorAll("tr").length,
      "targetRowsBefore:",
      destinationBody.querySelectorAll("tr").length,
      "currentEditorScroll:",
      sourceBlock.closest(".page-editor")?.scrollHeight,
      "currentEditorClient:",
      sourceBlock.closest(".page-editor")?.clientHeight
    );

    destinationBody.insertBefore(rowToMove, destinationBody.firstChild);
    this.debugLog(
      "[TABLE][SPLIT] move done",
      "tableId:",
      tableId,
      "sourceRowsAfter:",
      sourceBody.querySelectorAll("tr").length,
      "targetRowsAfter:",
      destinationBody.querySelectorAll("tr").length
    );
    return true;
  }

  /**
   * Split a too-tall TEXT report block (a `.report-block` whose body is an
   * `.imported-report-content` div of paragraphs) across the page boundary by
   * moving its LAST inner block into a continuation copy at the top of the next
   * page. Moves one inner block per call (like splitTableBlockOverflow moves one
   * row) and returns true so the caller re-measures and calls again as needed.
   *
   * Returns false when there is nothing left to split (≤1 inner block, e.g. a
   * single oversized paragraph), so the caller can fall back to leaving the block
   * in place rather than looping forever.
   */
  private splitTextReportBlockOverflow(
    sourceBlock: HTMLElement,
    nextEditor: HTMLElement
  ): boolean {
    const sourceInner = sourceBlock.querySelector(
      ":scope > .imported-report-content"
    ) as HTMLElement | null;
    if (!sourceInner || sourceInner.children.length <= 1) {
      return false;
    }

    const reportId = String(sourceBlock.getAttribute("data-report-id") || "");
    let targetBlock: HTMLElement | null = null;
    const firstNextChild = nextEditor.firstElementChild as HTMLElement | null;
    if (
      firstNextChild &&
      firstNextChild.classList.contains("report-block") &&
      firstNextChild.getAttribute("data-report-cont") === "1" &&
      String(firstNextChild.getAttribute("data-report-id") || "") === reportId
    ) {
      targetBlock = firstNextChild;
    }

    if (!targetBlock) {
      // Shallow clone keeps the <section> tag/classes/attributes but no children;
      // give it a fresh empty content body and mark it as a continuation.
      targetBlock = sourceBlock.cloneNode(false) as HTMLElement;
      targetBlock.setAttribute("data-report-cont", "1");
      const targetInner = document.createElement("div");
      targetInner.className = sourceInner.className;
      targetBlock.appendChild(targetInner);
      nextEditor.insertBefore(targetBlock, nextEditor.firstChild);
    }

    const destInner = targetBlock.querySelector(
      ":scope > .imported-report-content"
    ) as HTMLElement | null;
    if (!destInner) {
      return false;
    }

    const nodeToMove = sourceInner.lastElementChild as HTMLElement | null;
    if (!nodeToMove) {
      return false;
    }
    destInner.insertBefore(nodeToMove, destInner.firstChild);
    return true;
  }

  /**
   * Inverse of moveOverflowForward: pull content up from later pages to fill
   * free space on earlier pages. Runs after the forward pass (so no page
   * overflows) and only moves an element/row when it still fits — so it can
   * never create an overflow, it only closes gaps left when content shrank.
   */
  private pullContentBackward(startIndex: number): void {
    if (!this.pageEditors || !this.pageEditors.length) {
      return;
    }
    const editors = this.pageEditors.toArray();

    for (let index = Math.max(0, startIndex); index < editors.length; index += 1) {
      const currentEditor = editors[index]?.nativeElement;
      const nextEditor = editors[index + 1]?.nativeElement;
      if (!currentEditor || !nextEditor) {
        continue;
      }

      let guard = 0;
      while (nextEditor.firstElementChild && guard < 600) {
        guard += 1;
        const candidate = nextEditor.firstElementChild as HTMLElement;

        // Never merge across a page-exclusive boundary: a page-exclusive block
        // (e.g. the Table of Contents) must stay alone on its page, so don't pull
        // it up onto a page with other content, and don't pull other content up
        // onto a page whose last block is page-exclusive.
        if (
          this.isExclusiveBlock(candidate) ||
          this.isExclusiveBlock(currentEditor.lastElementChild)
        ) {
          break;
        }

        // Split table continued onto the next page: merge its rows back into the
        // matching table that ends the current page, one row at a time. The
        // table id lives on the inner .report-table-block, not the section.
        const lastCurrent = currentEditor.lastElementChild as HTMLElement | null;
        const candidateTableId = this.isTableSection(candidate)
          ? this.getTableBlockId(candidate)
          : "";
        if (
          candidateTableId &&
          lastCurrent &&
          this.isTableSection(lastCurrent) &&
          candidateTableId === this.getTableBlockId(lastCurrent)
        ) {
          const rowMoved = this.mergeTableRowBackward(
            candidate,
            lastCurrent,
            currentEditor
          );
          if (!rowMoved) {
            break; // not even one more row fits → stop on this page
          }
          if (this.isTableBlockEmpty(candidate)) {
            candidate.remove(); // fully merged back; drop the empty continuation
          }
          continue;
        }

        // Split TEXT report continued onto the next page: merge its paragraphs
        // back into the matching report block ending the current page, one at a
        // time, so a report re-joins when earlier content shrinks (the table
        // path above does this for rows; this is the text-report equivalent).
        const candReportContId =
          candidate.classList.contains("report-block") &&
          candidate.getAttribute("data-report-cont") === "1"
            ? String(candidate.getAttribute("data-report-id") || "")
            : "";
        if (
          candReportContId &&
          lastCurrent &&
          lastCurrent.classList.contains("report-block") &&
          String(lastCurrent.getAttribute("data-report-id") || "") ===
            candReportContId
        ) {
          const paraMoved = this.mergeTextParagraphBackward(
            candidate,
            lastCurrent,
            currentEditor
          );
          if (!paraMoved) {
            break; // not even one more paragraph fits → stop on this page
          }
          if (this.isReportBlockContentEmpty(candidate)) {
            candidate.remove(); // fully merged back; drop the empty continuation
          }
          continue;
        }

        // Generic block: tentatively move it up and keep it only if it fits.
        currentEditor.appendChild(candidate);
        this.removeTrailingWhitespaceNodes(nextEditor);
        if (currentEditor.scrollHeight > currentEditor.clientHeight + 1) {
          nextEditor.insertBefore(candidate, nextEditor.firstChild);
          break;
        }
      }
    }
  }

  /** Move the first body row of a split table continuation back to the page
   *  above, keeping it only if it still fits. Returns false when it doesn't. */
  private mergeTableRowBackward(
    sourceBlock: HTMLElement,
    destBlock: HTMLElement,
    currentEditor: HTMLElement
  ): boolean {
    const sourceBody = sourceBlock.querySelector(
      "table.report-data-table tbody"
    ) as HTMLElement | null;
    const destBody = destBlock.querySelector(
      "table.report-data-table tbody"
    ) as HTMLElement | null;
    if (!sourceBody || !destBody) {
      return false;
    }
    const row = sourceBody.firstElementChild as HTMLElement | null;
    if (!row) {
      return false;
    }

    destBody.appendChild(row);
    if (currentEditor.scrollHeight > currentEditor.clientHeight + 1) {
      // Doesn't fit → put the row back at the front of the continuation.
      sourceBody.insertBefore(row, sourceBody.firstChild);
      return false;
    }
    return true;
  }

  private isTableBlockEmpty(block: HTMLElement): boolean {
    const body = block.querySelector("table.report-data-table tbody");
    return !body || body.children.length === 0;
  }

  /** Move the first inner paragraph of a split TEXT continuation back to the
   *  matching report block above, keeping it only if it still fits. Returns
   *  false when it doesn't (caller stops merging on this page). */
  private mergeTextParagraphBackward(
    sourceCont: HTMLElement,
    destBlock: HTMLElement,
    currentEditor: HTMLElement
  ): boolean {
    const sourceInner = sourceCont.querySelector(
      ":scope > .imported-report-content"
    ) as HTMLElement | null;
    const destInner = destBlock.querySelector(
      ":scope > .imported-report-content"
    ) as HTMLElement | null;
    if (!sourceInner || !destInner) {
      return false;
    }
    const para = sourceInner.firstElementChild as HTMLElement | null;
    if (!para) {
      return false;
    }
    destInner.appendChild(para);
    if (currentEditor.scrollHeight > currentEditor.clientHeight + 1) {
      // Doesn't fit → put it back at the front of the continuation.
      sourceInner.insertBefore(para, sourceInner.firstChild);
      return false;
    }
    return true;
  }

  /** True when a report block's content body has no remaining inner blocks. */
  private isReportBlockContentEmpty(block: HTMLElement): boolean {
    const inner = block.querySelector(
      ":scope > .imported-report-content"
    ) as HTMLElement | null;
    return !inner || inner.children.length === 0;
  }

  /** True when this top-level node is a report data-table block (or wraps one). */
  private isTableSection(el: HTMLElement): boolean {
    return (
      el.classList.contains("table-report-block") ||
      !!el.querySelector(".report-table-block")
    );
  }

  /** The table id (lives on the inner .report-table-block, not the section). */
  private getTableBlockId(section: HTMLElement): string {
    const inner = section.querySelector(
      ".report-table-block"
    ) as HTMLElement | null;
    return String((inner || section).getAttribute("data-table-id") || "");
  }

  /**
   * A split table must never have two fragments on the SAME page. Merge any
   * consecutive table sections that share a table id back into the first one
   * (dropping the "(cont.)" duplicate). If the merged table then overflows, the
   * forward pass re-splits it cleanly across a page boundary.
   */
  private mergeAdjacentTableFragments(editor: HTMLElement): boolean {
    let merged = false;
    let child = editor.firstElementChild as HTMLElement | null;
    while (child) {
      const next = child.nextElementSibling as HTMLElement | null;
      if (
        next &&
        this.isTableSection(child) &&
        this.isTableSection(next) &&
        this.getTableBlockId(child) &&
        this.getTableBlockId(child) === this.getTableBlockId(next)
      ) {
        this.mergeAllTableRows(next, child);
        next.remove();
        merged = true;
        // Stay on `child` so a third fragment also merges in.
        continue;
      }
      child = next;
    }
    return merged;
  }

  /** Move every body row from one table section into another, in order. */
  private mergeAllTableRows(source: HTMLElement, dest: HTMLElement): void {
    const sourceBody = source.querySelector(
      "table.report-data-table tbody"
    ) as HTMLElement | null;
    const destBody = dest.querySelector(
      "table.report-data-table tbody"
    ) as HTMLElement | null;
    if (!sourceBody || !destBody) {
      return;
    }
    while (sourceBody.firstElementChild) {
      destBody.appendChild(sourceBody.firstElementChild);
    }
  }

  private removeTrailingWhitespaceNodes(container: HTMLElement): void {
    while (
      container.lastChild &&
      container.lastChild.nodeType === Node.TEXT_NODE &&
      !(container.lastChild.textContent || "").trim()
    ) {
      container.removeChild(container.lastChild);
    }
  }

  private removeTrailingEmptyPages(): void {
    this.syncPagesFromDom();

    // Keep the reserved leading pages plus at least one content page, so an empty
    // document can't strip the reserved (blank) MOR cover pages from the end.
    const minPages = this.reservedLeadingPages + 1;
    while (this.pages.length > minPages) {
      const lastPage = this.pages[this.pages.length - 1];

      // A page is only removable when it has neither text NOR media. Checking
      // plain text alone would treat a page holding just a chart/image/table
      // (which legitimately carry no text) as empty and delete real content.
      if (!this.isBlankPageHtml(lastPage.html)) {
        break;
      }

      this.debugLog(
        "[PDF][REFLOW] 🗑 removing trailing blank page",
        this.pages.length
      );
      this.pages.pop();
      if (this.activePageIndex >= this.pages.length) {
        this.activePageIndex = this.pages.length - 1;
      }
    }
  }

  private syncPagesFromDom(): void {
    const editors = this.pageEditors?.toArray() || [];
    if (!editors.length) {
      return;
    }

    for (let i = 0; i < editors.length; i += 1) {
      if (!this.pages[i]) {
        this.pages[i] = { id: this.nextPageId++, html: "" };
      }
      this.pages[i].html = this.stripCaretMarkers(
        editors[i].nativeElement.innerHTML
      );
    }

    this.scheduleDraftAutosave();
  }

  // Sync only the active page's markup. Used by non-layout edits so we avoid
  // reading innerHTML of every page on each keystroke/format in large documents.
  private syncActivePageFromDom(): void {
    const editors = this.pageEditors?.toArray() || [];
    const editor = editors[this.activePageIndex]?.nativeElement;
    if (!editor) {
      return;
    }
    if (!this.pages[this.activePageIndex]) {
      this.pages[this.activePageIndex] = { id: this.nextPageId++, html: "" };
    }
    this.pages[this.activePageIndex].html = this.stripCaretMarkers(
      editor.innerHTML
    );

    this.scheduleDraftAutosave();
  }

  private hydratePagesToDom(force = false): void {
    const editors = this.pageEditors?.toArray() || [];
    if (!editors.length) {
      return;
    }

    for (let i = 0; i < editors.length; i += 1) {
      const modelHtml = this.pages[i]?.html || "";
      const el = editors[i].nativeElement;
      if (force || (!el.innerHTML.trim() && modelHtml)) {
        el.innerHTML = modelHtml;
      }
      this.enableResizableImagesInEditor(el);
    }
  }

  private prepareResizableImages(): void {
    const editors = this.pageEditors?.toArray() || [];
    for (let i = 0; i < editors.length; i += 1) {
      this.enableResizableImagesInEditor(editors[i].nativeElement);
    }
  }

  private enableResizableImagesInEditor(editor: HTMLElement): void {
    const wrappers = Array.from(
      editor.querySelectorAll(".resizable-image")
    ) as HTMLElement[];
    for (let i = 0; i < wrappers.length; i += 1) {
      this.bindResizableWrapper(wrappers[i]);
    }

    const images = Array.from(editor.querySelectorAll("img"));

    for (let i = 0; i < images.length; i += 1) {
      const img = images[i];
      const parent = img.parentElement;
      if (!parent || parent.classList.contains("resizable-image")) {
        continue;
      }
      // The MOR cover image is a fixed, full-bleed page background — never wrap
      // it for resize/selection (that would give it a fixed width and break the
      // edge-to-edge fit).
      if (img.classList.contains("mor-cover-image")) {
        continue;
      }

      const wrapper = document.createElement("span");
      wrapper.className = "resizable-image";
      wrapper.contentEditable = "false";

      const attrWidth = img.getAttribute("width");
      const cssWidth = img.style.width;
      if (attrWidth && /^\d+$/.test(attrWidth)) {
        wrapper.style.width = `${attrWidth}px`;
      } else if (attrWidth) {
        wrapper.style.width = attrWidth;
      } else if (cssWidth) {
        wrapper.style.width = cssWidth;
      } else if (img.clientWidth > 0) {
        wrapper.style.width = `${img.clientWidth}px`;
      }

      img.removeAttribute("width");
      img.removeAttribute("height");

      parent.insertBefore(wrapper, img);
      wrapper.appendChild(img);
      this.bindResizableWrapper(wrapper);
    }

    // Some report charts are not plain <img> (svg/canvas/custom DOM).
    // Wrap whole chart blocks so toolbar resize works consistently.
    const chartBlocks = Array.from(
      editor.querySelectorAll(".report-chart, .report-table-block")
    ) as HTMLElement[];

    for (let i = 0; i < chartBlocks.length; i += 1) {
      const block = chartBlocks[i];
      const parent = block.parentElement;
      if (!parent || parent.classList.contains("resizable-image")) {
        continue;
      }

      const wrapper = document.createElement("span");
      wrapper.className = "resizable-image resizable-block";
      wrapper.contentEditable = "false";

      const widthFromBlock = block.getBoundingClientRect().width;
      const widthFromParent = parent.getBoundingClientRect().width;
      const baseWidth = Math.max(120, Math.round(widthFromBlock || widthFromParent || 0));
      if (baseWidth > 0) {
        wrapper.style.width = `${baseWidth}px`;
        wrapper.dataset.widthPx = String(baseWidth);
      }

      parent.insertBefore(wrapper, block);
      wrapper.appendChild(block);

      // Report TABLES keep an editable content island inside the (non-editable)
      // resize wrapper, so the document toolbar (font size, bold, align, color…)
      // can format the title and cell text. Chart blocks stay non-editable —
      // they're rendered images, not text.
      if (block.classList.contains("report-table-block")) {
        block.contentEditable = "true";
      }

      this.bindResizableWrapper(wrapper);
    }

    this.normalizeTemporalChartSnapshotsInEditor(editor);

    // Charts/images render fully on load instead of being clipped until clicked.
    this.fitResizableWrappersToPage(editor);
  }

  /**
   * Make charts/images fit the printable page the moment they are hydrated,
   * instead of overflowing the page (which `overflow:hidden` then clips) until
   * the user clicks them. A 900px chart snapshot only fits because of a fragile
   * `max-width:100%` cap that fails during initial hydration/pagination; here we
   * enforce it deterministically:
   *   1. force every chart box (wrapper / report-chart / inner span / image) to
   *      `box-sizing:border-box` + `max-width:100%` so padding/borders can never
   *      add width and push content off the page, and
   *   2. clamp each outermost wrapper to the page CONTENT width (clientWidth minus
   *      the editor's horizontal padding — the earlier bug used clientWidth, which
   *      still left the wrapper ~112px too wide).
   */
  private fitResizableWrappersToPage(editor: HTMLElement): void {
    const wrappers = Array.from(
      editor.querySelectorAll(".resizable-image")
    ) as HTMLElement[];
    for (let i = 0; i < wrappers.length; i += 1) {
      const wrapper = wrappers[i];
      // Only size the outermost wrapper; nested chart wrappers flow fluidly inside it.
      const parent = wrapper.parentElement;
      const nested = !!(parent && parent.closest(".resizable-image"));
      this.fitResizableWrapperWidth(wrapper, nested);
    }

    // Chart snapshots are data-URL images that may report their final layout only
    // after decoding. Re-fit once each finishes so a late decode cannot leave the
    // wrapper oversized and clipped.
    const images = Array.from(
      editor.querySelectorAll(".resizable-image img")
    ) as HTMLImageElement[];
    for (let i = 0; i < images.length; i += 1) {
      const img = images[i];
      if (img.dataset.fitBound === "1" || img.complete) {
        continue;
      }
      img.dataset.fitBound = "1";
      img.addEventListener(
        "load",
        () => {
          const editorEl = img.closest(".page-editor") as HTMLElement | null;
          if (editorEl) {
            this.fitResizableWrappersToPage(editorEl);
          }
        },
        { once: true }
      );
    }
  }

  private pageContentWidth(pageEditor: HTMLElement): number {
    const style = window.getComputedStyle(pageEditor);
    const padX =
      (parseFloat(style.paddingLeft) || 0) + (parseFloat(style.paddingRight) || 0);
    return Math.max(0, pageEditor.clientWidth - padX);
  }

  private fitResizableWrapperWidth(wrapper: HTMLElement, nested: boolean): void {
    const pageEditor = wrapper.closest(".page-editor") as HTMLElement | null;
    if (!pageEditor) {
      return;
    }
    const contentWidth = this.pageContentWidth(pageEditor);
    if (contentWidth <= 0) {
      return;
    }

    const isChartWrapper = !!wrapper.querySelector(
      ".report-chart, .chart-snapshot-image"
    );

    // Border-box + max-width:100% on every level guarantees no descendant (and no
    // padding/border) can push past its container, so the single fixed outer width
    // below is enough to keep the whole chart on the page.
    if (isChartWrapper) {
      const boxes = [
        wrapper,
        ...(Array.from(
          wrapper.querySelectorAll(
            ".report-chart, .chart-image-resizable, .chart-snapshot-image"
          )
        ) as HTMLElement[]),
      ];
      for (let i = 0; i < boxes.length; i += 1) {
        boxes[i].style.boxSizing = "border-box";
        boxes[i].style.maxWidth = "100%";
      }
      const img = wrapper.querySelector("img") as HTMLImageElement | null;
      if (img) {
        img.style.setProperty("width", "100%", "important");
        img.style.setProperty("max-width", "100%", "important");
        img.style.setProperty("height", "auto", "important");
      }
    } else {
      wrapper.style.boxSizing = "border-box";
      wrapper.style.maxWidth = "100%";
    }

    // Nested wrappers stay fluid (width:100% of the fitted outer wrapper); only the
    // outermost wrapper gets a concrete width clamped to the page content box.
    if (nested) {
      wrapper.style.width = "100%";
      return;
    }

    // Charts are full-width snapshots by design and the document is regenerated
    // fresh on every open, so pin them to the content width (a measured width can
    // be unreliable while the page is still laying out). Plain images keep their
    // own width but are never allowed to exceed the content box.
    const currentWidth = this.getWrapperWidthPx(wrapper);
    const targetWidth = isChartWrapper
      ? Math.floor(contentWidth)
      : Math.max(120, Math.floor(Math.min(currentWidth, contentWidth)));
    wrapper.style.width = `${targetWidth}px`;
    wrapper.dataset.widthPx = String(targetWidth);
    wrapper.style.maxWidth = "100%";
  }

  private normalizeTemporalChartSnapshotsInEditor(editor: HTMLElement): void {
    const wrappers = Array.from(
      editor.querySelectorAll(
        ".resizable-image.chart-image-resizable[data-chart-payload], .resizable-image[data-chart-payload]"
      )
    ) as HTMLElement[];

    for (let i = 0; i < wrappers.length; i += 1) {
      const wrapper = wrappers[i];
      const payload = this.readChartPayloadFromWrapper(wrapper);
      if (!payload) {
        continue;
      }

      const title = String(payload.title || "");
      const isDailyChart = /\bdaily\b/i.test(title);
      const isTimestampChart = /\bhourly\b/i.test(title) || /\braw\b/i.test(title);
      if (!isDailyChart && !isTimestampChart) {
        continue;
      }

      const current = Array.isArray(payload.chartData?.categories)
        ? payload.chartData.categories.map((c) => String(c || ""))
        : [];
      if (!current.length) {
        continue;
      }

      const normalized = isDailyChart
        ? this.normalizeDailyCategorySequence(
            current.map((c) => this.normalizeCategoryLabelForInterval(c, "daily"))
          )
        : current.map((c) => this.normalizeCategoryLabelForInterval(c, "raw"));
      const changed = normalized.some((value, idx) => value !== current[idx]);
      if (!changed) {
        continue;
      }

      const nextPayload = {
        ...payload,
        chartData: {
          ...payload.chartData,
          categories: normalized,
        },
      };
      const dataUrl = this.drawChartSnapshotDataUrl(
        nextPayload.chartData,
        nextPayload.title,
        nextPayload.options,
        nextPayload.chartType
      );
      if (!dataUrl) {
        continue;
      }

      const image = wrapper.querySelector("img.chart-snapshot-image") as HTMLImageElement | null;
      if (image) {
        image.src = dataUrl;
      }
      wrapper.setAttribute("data-chart-payload", this.encodeChartPayload(nextPayload));
    }
  }

  private bindResizableWrapper(wrapper: HTMLElement): void {
    if (wrapper.dataset.resizeBound === "1") {
      return;
    }

    wrapper.dataset.resizeBound = "1";
    this.debugSelectionLog("[SELECT] bind wrapper", this.describeElement(wrapper));
    this.ensureResizeHandle(wrapper);
    wrapper.addEventListener("mousedown", (event) => {
      event.stopPropagation();
      this.debugSelectionLog(
        "[SELECT] wrapper mousedown",
        this.describeElement(wrapper),
        "target:",
        this.describeElement(event.target as Element | null)
      );
      this.selectResizableWrapper(wrapper);
    });
    wrapper.addEventListener("click", (event) => {
      event.stopPropagation();
      this.debugSelectionLog(
        "[SELECT] wrapper click",
        this.describeElement(wrapper),
        "target:",
        this.describeElement(event.target as Element | null)
      );
      this.selectResizableWrapper(wrapper);
    });
  }

  private ensureResizeHandle(wrapper: HTMLElement): void {
    const rightHandle =
      (wrapper.querySelector(".image-resize-handle-right") as HTMLElement | null) ||
      document.createElement("span");
    if (!rightHandle.parentElement) {
      rightHandle.className =
        "image-handle image-resize-handle image-resize-handle-right";
      rightHandle.setAttribute("data-resize-dir", "x");
      rightHandle.contentEditable = "false";
      wrapper.appendChild(rightHandle);
    }

    const bottomHandle =
      (wrapper.querySelector(".image-resize-handle-bottom") as HTMLElement | null) ||
      document.createElement("span");
    if (!bottomHandle.parentElement) {
      bottomHandle.className =
        "image-handle image-resize-handle image-resize-handle-bottom";
      bottomHandle.setAttribute("data-resize-dir", "y");
      bottomHandle.contentEditable = "false";
      wrapper.appendChild(bottomHandle);
    }

    const cornerHandle =
      (wrapper.querySelector(".image-resize-handle-corner") as HTMLElement | null) ||
      document.createElement("span");
    if (!cornerHandle.parentElement) {
      cornerHandle.className =
        "image-handle image-resize-handle image-resize-handle-corner";
      cornerHandle.setAttribute("data-resize-dir", "xy");
      cornerHandle.contentEditable = "false";
      wrapper.appendChild(cornerHandle);
    }

    const rotateHandle =
      (wrapper.querySelector(".image-rotate-handle") as HTMLElement | null) ||
      document.createElement("span");
    if (!rotateHandle.parentElement) {
      rotateHandle.className = "image-handle image-rotate-handle";
      rotateHandle.setAttribute("data-handle-action", "rotate");
      rotateHandle.contentEditable = "false";
      wrapper.appendChild(rotateHandle);
    }

    const handles = [rightHandle, bottomHandle, cornerHandle, rotateHandle];
    for (let i = 0; i < handles.length; i += 1) {
      const handle = handles[i];
      if (handle.dataset.dragBound === "1") {
        continue;
      }
      handle.dataset.dragBound = "1";
      handle.addEventListener("mousedown", (event: MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        const action = String(handle.getAttribute("data-handle-action") || "");
        if (action === "rotate") {
          this.debugSelectionLog("[SELECT] rotate handle mousedown");
          this.selectResizableWrapper(wrapper);
          this.startRotateDrag(wrapper, event);
          return;
        }

        const dir = String(handle.getAttribute("data-resize-dir") || "xy");
        this.debugSelectionLog("[SELECT] resize handle mousedown", "dir:", dir);
        this.selectResizableWrapper(wrapper);
        this.startResizeDrag(wrapper, event, dir);
      });
    }
  }

  private startResizeDrag(wrapper: HTMLElement, event: MouseEvent, direction: string): void {
    event.preventDefault();
    const startX = event.clientX;
    const startY = event.clientY;
    const startWidth = wrapper.offsetWidth;
    const startHeight = wrapper.offsetHeight;

    const onMove = (moveEvent: MouseEvent) => {
      moveEvent.preventDefault();
      if (direction.includes("x")) {
        const deltaX = moveEvent.clientX - startX;
        const nextWidth = Math.max(160, startWidth + deltaX);
        wrapper.style.width = `${Math.round(nextWidth)}px`;
        wrapper.style.maxWidth = "none";
      }
      if (direction.includes("y")) {
        const deltaY = moveEvent.clientY - startY;
        const nextHeight = Math.max(120, startHeight + deltaY);
        wrapper.style.height = `${Math.round(nextHeight)}px`;
      }
    };

    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      this.syncPagesFromDom();
    };

    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  }

  private startRotateDrag(wrapper: HTMLElement, event: MouseEvent): void {
    event.preventDefault();
    const startRotation = this.getWrapperRotation(wrapper);
    const rect = wrapper.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const startAngle =
      (Math.atan2(event.clientY - centerY, event.clientX - centerX) * 180) /
      Math.PI;

    const onMove = (moveEvent: MouseEvent) => {
      moveEvent.preventDefault();
      const moveAngle =
        (Math.atan2(moveEvent.clientY - centerY, moveEvent.clientX - centerX) *
          180) /
        Math.PI;
      const nextRotation = startRotation + (moveAngle - startAngle);
      this.applyWrapperRotation(wrapper, nextRotation);
    };

    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      this.syncPagesFromDom();
    };

    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  }

  private selectResizableWrapper(wrapper: HTMLElement | null): void {
    this.debugSelectionLog(
      "[SELECT] select wrapper",
      "current:",
      this.describeElement(this.selectedResizableWrapper),
      "next:",
      this.describeElement(wrapper)
    );
    if (this.selectedResizableWrapper === wrapper) {
      return;
    }

    if (this.selectedResizableWrapper) {
      this.selectedResizableWrapper.classList.remove("is-selected");
    }

    this.selectedResizableWrapper = wrapper;
    if (wrapper) {
      // Resize handles behave reliably when width is fixed in px (not %/auto).
      const inlineWidth = (wrapper.style.width || "").trim();
      if (!inlineWidth || inlineWidth.endsWith("%") || inlineWidth === "auto") {
        const currentWidth = wrapper.offsetWidth;
        const containerWidth = wrapper.parentElement?.clientWidth || 0;
        const clampedWidth =
          containerWidth > 0 ? Math.min(currentWidth, containerWidth) : currentWidth;
        if (currentWidth > 0) {
          this.setWrapperWidthPx(wrapper, clampedWidth);
        }
      }
      const inlineHeight = (wrapper.style.height || "").trim();
      if (!inlineHeight || inlineHeight === "auto") {
        const currentHeight = wrapper.offsetHeight;
        if (currentHeight > 0) {
          wrapper.style.height = `${Math.round(currentHeight)}px`;
        }
      }

      // Keep selected media within the editable page width so handles stay reachable.
      const pageEditor = wrapper.closest(".page-editor") as HTMLElement | null;
      const boundWidth = pageEditor?.clientWidth || wrapper.parentElement?.clientWidth || 0;
      if (boundWidth > 0) {
        const currentWidth = this.getWrapperWidthPx(wrapper);
        if (currentWidth > boundWidth) {
          this.setWrapperWidthPx(wrapper, boundWidth);
        } else {
          this.setWrapperWidthPx(wrapper, currentWidth);
        }
        wrapper.style.maxWidth = "100%";
      }

      wrapper.classList.add("is-selected");
      this.updateFloatingImageToolbar();
      this.debugSelectionLog(
        "[SELECT] selected",
        this.describeElement(wrapper),
        "width:",
        wrapper.getBoundingClientRect().width,
        "pointer-events:",
        window.getComputedStyle(wrapper).pointerEvents,
        "contentEditable:",
        wrapper.contentEditable
      );
      return;
    }

    this.imageToolbarVisible = false;
  }

  private getWrapperRotation(wrapper: HTMLElement): number {
    const fromData = Number(wrapper.dataset.rotationDeg);
    if (!Number.isNaN(fromData)) {
      return fromData;
    }

    const transformTarget = this.getTransformTarget(wrapper);
    const transform = String(transformTarget?.style.transform || wrapper.style.transform || "");
    const match = transform.match(/rotate\((-?\d+(?:\.\d+)?)deg\)/i);
    return match ? Number(match[1]) : 0;
  }

  private getWrapperScale(wrapper: HTMLElement): number {
    const fromData = Number(wrapper.dataset.scaleVal);
    if (!Number.isNaN(fromData) && fromData > 0) {
      return fromData;
    }

    const transformTarget = this.getTransformTarget(wrapper);
    const transform = String(transformTarget?.style.transform || wrapper.style.transform || "");
    const match = transform.match(/scale\((-?\d+(?:\.\d+)?)\)/i);
    const parsed = match ? Number(match[1]) : 1;
    return Number.isNaN(parsed) || parsed <= 0 ? 1 : parsed;
  }

  private applyWrapperRotation(wrapper: HTMLElement, angle: number): void {
    const scale = this.getWrapperScale(wrapper);
    const offsetX = this.getWrapperOffsetX(wrapper);
    const offsetY = this.getWrapperOffsetY(wrapper);
    this.applyWrapperTransform(wrapper, angle, scale, offsetX, offsetY);
  }

  private applyWrapperScale(wrapper: HTMLElement, scale: number): void {
    const angle = this.getWrapperRotation(wrapper);
    const offsetX = this.getWrapperOffsetX(wrapper);
    const offsetY = this.getWrapperOffsetY(wrapper);
    this.applyWrapperTransform(wrapper, angle, scale, offsetX, offsetY);
  }

  private applyWrapperTransform(
    wrapper: HTMLElement,
    angle: number,
    scale: number,
    offsetX?: number,
    offsetY?: number
  ): void {
    const normalized = Math.round(angle * 10) / 10;
    const boundedScale = Math.max(0.3, Math.min(3, scale));
    const normalizedOffsetX = Math.round((offsetX ?? this.getWrapperOffsetX(wrapper)) * 10) / 10;
    const normalizedOffsetY = Math.round((offsetY ?? this.getWrapperOffsetY(wrapper)) * 10) / 10;
    wrapper.dataset.rotationDeg = String(normalized);
    wrapper.dataset.scaleVal = String(Math.round(boundedScale * 1000) / 1000);
    wrapper.dataset.offsetXPx = String(normalizedOffsetX);
    wrapper.dataset.offsetYPx = String(normalizedOffsetY);
    // Apply transform on the concrete content (img/report-chart) for reliable visual rotation.
    const transformTarget = this.getTransformTarget(wrapper) || wrapper;
    wrapper.style.transform = "none";
    wrapper.style.transformOrigin = "";
    transformTarget.style.transformOrigin = "center center";
    transformTarget.style.transform = `translate(${normalizedOffsetX}px, ${normalizedOffsetY}px) rotate(${normalized}deg) scale(${boundedScale})`;
    this.updateFloatingImageToolbar();
  }

  zoomSelectedImageIn(): void {
    const wrapper = this.selectedResizableWrapper;
    this.debugSelectionLog(
      "[TOOLBAR] zoom in click",
      "hasWrapper:",
      !!wrapper
    );
    if (!wrapper) {
      return;
    }
    const nextScale = this.getWrapperScale(wrapper) * 1.08;
    this.debugSelectionLog(
      "[TOOLBAR] zoom in apply",
      "currentScale:",
      this.getWrapperScale(wrapper),
      "nextScale:",
      nextScale
    );
    this.resizeWrapperByFactor(wrapper, 1.08);
    this.syncPagesFromDom();
  }

  zoomSelectedImageOut(): void {
    const wrapper = this.selectedResizableWrapper;
    this.debugSelectionLog(
      "[TOOLBAR] zoom out click",
      "hasWrapper:",
      !!wrapper
    );
    if (!wrapper) {
      return;
    }
    const nextScale = this.getWrapperScale(wrapper) / 1.08;
    this.debugSelectionLog(
      "[TOOLBAR] zoom out apply",
      "currentScale:",
      this.getWrapperScale(wrapper),
      "nextScale:",
      nextScale
    );
    this.resizeWrapperByFactor(wrapper, 1 / 1.08);
    this.syncPagesFromDom();
  }

  rotateSelectedImageLeft(): void {
    const wrapper = this.selectedResizableWrapper;
    this.debugSelectionLog(
      "[TOOLBAR] rotate left click",
      "hasWrapper:",
      !!wrapper
    );
    if (!wrapper) {
      return;
    }
    const nextAngle = this.getWrapperRotation(wrapper) - 15;
    this.debugSelectionLog("[TOOLBAR] rotate left apply", "nextAngle:", nextAngle);
    this.applyWrapperRotation(wrapper, nextAngle);
    this.syncPagesFromDom();
  }

  rotateSelectedImageRight(): void {
    const wrapper = this.selectedResizableWrapper;
    this.debugSelectionLog(
      "[TOOLBAR] rotate right click",
      "hasWrapper:",
      !!wrapper
    );
    if (!wrapper) {
      return;
    }
    const nextAngle = this.getWrapperRotation(wrapper) + 15;
    this.debugSelectionLog("[TOOLBAR] rotate right apply", "nextAngle:", nextAngle);
    this.applyWrapperRotation(wrapper, nextAngle);
    this.syncPagesFromDom();
  }

  resetSelectedImageTransform(): void {
    const wrapper = this.selectedResizableWrapper;
    this.debugSelectionLog(
      "[TOOLBAR] reset click",
      "hasWrapper:",
      !!wrapper
    );
    if (!wrapper) {
      return;
    }
    const pageEditor = wrapper.closest(".page-editor") as HTMLElement | null;
    const boundWidth = pageEditor?.clientWidth || wrapper.parentElement?.clientWidth || 0;
    if (boundWidth > 0) {
      this.setWrapperWidthPx(wrapper, boundWidth);
    }
    this.applyWrapperTransform(wrapper, 0, 1, 0, 0);
    this.syncPagesFromDom();
  }

  slideSelectedImageLeft(): void {
    const wrapper = this.selectedResizableWrapper;
    this.debugSelectionLog("[TOOLBAR] slide left click", "hasWrapper:", !!wrapper);
    if (!wrapper) {
      return;
    }
    const nextOffset = this.getWrapperOffsetX(wrapper) - 20;
    this.applyWrapperTransform(
      wrapper,
      this.getWrapperRotation(wrapper),
      this.getWrapperScale(wrapper),
      nextOffset,
      this.getWrapperOffsetY(wrapper)
    );
    this.syncPagesFromDom();
  }

  slideSelectedImageRight(): void {
    const wrapper = this.selectedResizableWrapper;
    this.debugSelectionLog("[TOOLBAR] slide right click", "hasWrapper:", !!wrapper);
    if (!wrapper) {
      return;
    }
    const nextOffset = this.getWrapperOffsetX(wrapper) + 20;
    this.applyWrapperTransform(
      wrapper,
      this.getWrapperRotation(wrapper),
      this.getWrapperScale(wrapper),
      nextOffset,
      this.getWrapperOffsetY(wrapper)
    );
    this.syncPagesFromDom();
  }

  moveSelectedImageUp(): void {
    const wrapper = this.selectedResizableWrapper;
    this.debugSelectionLog("[TOOLBAR] move up click", "hasWrapper:", !!wrapper);
    if (!wrapper) {
      return;
    }
    const nextOffsetY = this.getWrapperOffsetY(wrapper) - 1;
    this.applyWrapperTransform(
      wrapper,
      this.getWrapperRotation(wrapper),
      this.getWrapperScale(wrapper),
      this.getWrapperOffsetX(wrapper),
      nextOffsetY
    );
    this.syncPagesFromDom();
  }

  moveSelectedImageDown(): void {
    const wrapper = this.selectedResizableWrapper;
    this.debugSelectionLog("[TOOLBAR] move down click", "hasWrapper:", !!wrapper);
    if (!wrapper) {
      return;
    }
    const nextOffsetY = this.getWrapperOffsetY(wrapper) + 1;
    this.applyWrapperTransform(
      wrapper,
      this.getWrapperRotation(wrapper),
      this.getWrapperScale(wrapper),
      this.getWrapperOffsetX(wrapper),
      nextOffsetY
    );
    this.syncPagesFromDom();
  }

  deleteSelectedVisual(): void {
    const wrapper = this.selectedResizableWrapper;
    this.debugSelectionLog("[TOOLBAR] delete click", "hasWrapper:", !!wrapper);
    if (!wrapper) {
      return;
    }
    wrapper.remove();
    this.selectResizableWrapper(null);
    this.syncPagesFromDom();
    this.reflowFrom(this.activePageIndex);
    this.updateDocumentStats();
  }

  openSelectedVisualEditor(): void {
    const wrapper = this.selectedResizableWrapper;
    if (!wrapper) {
      return;
    }
    // Route to the table editor whenever the selected block holds a report table
    // (wrapped as .report-table-block, or just a .report-data-table inside a
    // chart-image/section container). Otherwise edit it as a chart.
    if (this.findSelectedTableContainer(wrapper)) {
      this.openSelectedTableEditor();
      return;
    }
    this.openSelectedChartEditor();
  }

  /**
   * Resolve the element that holds the selected report table — works whether the
   * table is wrapped as `.report-table-block` or is a bare `.report-data-table`
   * nested in a chart-image / report-block container. Returns null when the
   * selection has no table.
   */
  private findSelectedTableContainer(wrapper: HTMLElement): HTMLElement | null {
    // The wrapper may BE the table block (querySelector only matches descendants).
    if (wrapper.matches(".report-table-block")) {
      return wrapper;
    }
    const direct = wrapper.querySelector(
      ".report-table-block"
    ) as HTMLElement | null;
    if (direct) {
      return direct;
    }
    const table = wrapper.querySelector(
      "table.report-data-table, table"
    ) as HTMLElement | null;
    if (table) {
      return (
        (table.closest(
          ".report-table-block, .report-chart-image, .report-block"
        ) as HTMLElement | null) ||
        (table.parentElement as HTMLElement | null) ||
        table
      );
    }
    return null;
  }

  openSelectedChartEditor(): void {
    const wrapper = this.selectedResizableWrapper;
    this.chartEditorMessage = "";
    if (!wrapper) {
      this.chartEditorMessage = "Select a chart first.";
      return;
    }

    const payload = this.readChartPayloadFromWrapper(wrapper);
    if (!payload) {
      this.chartEditorMessage =
        "Selected element is not an editable chart snapshot.";
      return;
    }

    this.chartEditorTargetWrapper = wrapper;
    this.chartEditorBasePayload = payload;
    // The painted title, which is blank for report charts (the name shows in the
    // heading above instead) — typing one here paints it. Charts saved before
    // titleText existed have their baked-in report name here, as before.
    this.chartEditorTitle = payload.options?.titleText ?? payload.title ?? "";
    this.chartEditorChartType =
      payload.chartType || payload.chartData.series?.[0]?.type || "bar";
    this.chartEditorTitleSize = this.normalizeChartTitleSize(
      payload.options?.titleFontSize
    );
    this.chartEditorTitleColor = payload.options?.titleColor || "#f8fafc";
    this.chartEditorBgStart = payload.options?.bgStart || "#1f2937";
    this.chartEditorBgCenter = payload.options?.bgCenter || "#2b3444";
    this.chartEditorBgEnd = payload.options?.bgEnd || "#3f3f46";
    this.chartEditorBgOuter = payload.options?.bgOuter || "#111827";
    this.chartEditorDateLabelMode =
      payload.options?.dateLabelMode === "all" ? "all" : "auto";
    this.chartEditorDateLabelRotation = this.normalizeDateLabelRotation(
      payload.options?.dateLabelRotation
    );
    this.chartEditorValueLabelSize = this.normalizeValueLabelSize(
      payload.options?.valueLabelSize
    );
    this.chartEditorValueLabelRotation = this.normalizeValueLabelRotation(
      payload.options?.valueLabelRotation
    );
    this.chartEditorLegendSize = this.normalizeLegendSize(
      payload.options?.legendFontSize
    );
    this.chartEditorXAxisLabelSize = this.normalizeAxisLabelSize(
      payload.options?.xAxisLabelSize
    );
    this.chartEditorYAxisLabelSize = this.normalizeAxisLabelSize(
      payload.options?.yAxisLabelSize
    );
    this.chartEditorTitlePadTop = this.normalizeTitlePad(payload.options?.titlePadTop);
    this.chartEditorTitlePadRight = this.normalizeTitlePad(payload.options?.titlePadRight);
    this.chartEditorTitlePadBottom = this.normalizeTitlePad(payload.options?.titlePadBottom);
    this.chartEditorTitlePadLeft = this.normalizeTitlePad(payload.options?.titlePadLeft);
    this.chartEditorMultiAxis = !!payload.options?.multiAxis;
    const savedAxisIndices = Array.isArray(payload.options?.multiAxisIndices)
      ? payload.options!.multiAxisIndices!
      : [];
    this.chartEditorSeriesAxis = payload.chartData.series.map((_, i) =>
      savedAxisIndices.length > 0 ? savedAxisIndices.includes(i) : true
    );
    this.chartEditorSeriesNames = payload.chartData.series.map(
      (s, i) => s.name || `Series ${i + 1}`
    );
    const editorPalette = this.ensureDistinctColors(
      this.defaultChartPalette,
      payload.chartData.series.length
    );
    this.chartEditorSeriesColors = payload.chartData.series.map(
      (_, i) => payload.options?.seriesColors?.[i] || editorPalette[i]
    );
    this.refreshChartEditorPreview();
    this.chartEditorOpen = true;
  }

  openSelectedTableEditor(): void {
    const wrapper = this.selectedResizableWrapper;
    this.tableEditorMessage = "";
    if (!wrapper) {
      this.tableEditorMessage = "Select a table first.";
      return;
    }

    const tableBlock = this.findSelectedTableContainer(wrapper);
    if (!tableBlock) {
      this.tableEditorMessage = "Selected element is not a table.";
      return;
    }

    const title = tableBlock.querySelector(".report-chart-title") as HTMLElement | null;
    const headerCell = tableBlock.querySelector("thead th") as HTMLElement | null;
    const oddCell = tableBlock.querySelector("tbody tr:nth-child(odd) td") as HTMLElement | null;
    const evenCell = tableBlock.querySelector("tbody tr:nth-child(even) td") as HTMLElement | null;
    const anyCell = tableBlock.querySelector("tbody td") as HTMLElement | null;

    const titleStyle = title ? window.getComputedStyle(title) : null;
    const headerStyle = headerCell ? window.getComputedStyle(headerCell) : null;
    const oddStyle = oddCell ? window.getComputedStyle(oddCell) : null;
    const evenStyle = evenCell ? window.getComputedStyle(evenCell) : null;
    const anyStyle = anyCell ? window.getComputedStyle(anyCell) : null;
    const blockStyle = window.getComputedStyle(tableBlock);

    this.tableEditorTitleBg = this.toHexColor(titleStyle?.backgroundColor, "#0b2345");
    this.tableEditorHeaderBg = this.toHexColor(headerStyle?.backgroundColor, "#021b39");
    this.tableEditorOddRowBg = this.toHexColor(oddStyle?.backgroundColor, "#15335a");
    this.tableEditorEvenRowBg = this.toHexColor(evenStyle?.backgroundColor, "#001a3a");
    this.tableEditorTextColor = this.toHexColor(anyStyle?.color, "#f8fafc");
    this.tableEditorBorderColor = this.toHexColor(blockStyle.borderColor, "#2b3f59");

    const align = String(titleStyle?.textAlign || "left").toLowerCase();
    this.tableEditorTitleAlign =
      align === "center" || align === "right" ? align : "left";
    this.tableEditorTitleSize = this.normalizeTableTitleSize(
      titleStyle ? parseFloat(titleStyle.fontSize) : 13
    );

    this.tableEditorTargetWrapper = wrapper;
    this.refreshTableEditorPreview();
    this.tableEditorOpen = true;
  }

  closeChartEditor(): void {
    this.chartEditorOpen = false;
    this.chartEditorMessage = "";
    this.chartEditorPreviewDataUrl = "";
    this.chartEditorBasePayload = null;
    this.chartEditorTargetWrapper = null;
    this.chartEditorDateLabelMode = "auto";
    this.chartEditorDateLabelRotation = -40;
    this.chartEditorValueLabelSize = 11;
    this.chartEditorValueLabelRotation = 0;
    this.chartEditorLegendSize = 15;
    this.chartEditorXAxisLabelSize = 12;
    this.chartEditorYAxisLabelSize = 12;
    this.chartEditorTitlePadTop = 0;
    this.chartEditorTitlePadRight = 0;
    this.chartEditorTitlePadBottom = 0;
    this.chartEditorTitlePadLeft = 0;
    this.chartEditorMultiAxis = false;
    this.chartEditorSeriesAxis = [];
    this.chartEditorTitleSize = 34;
    this.chartEditorTitleColor = "#f8fafc";
    this.chartEditorChartType = "bar";
  }

  closeTableEditor(): void {
    this.tableEditorOpen = false;
    this.tableEditorMessage = "";
    this.tableEditorTargetWrapper = null;
    this.tableEditorPreviewHtml = "";
    this.tableEditorTitleAlign = "left";
    this.tableEditorTitleSize = 13;
  }

  onChartEditorFieldChange(): void {
    this.refreshChartEditorPreview();
  }

  onMultiAxisToggle(): void {
    // When enabling multi-axis with nothing selected yet, default to all series.
    if (this.chartEditorMultiAxis && !this.chartEditorSeriesAxis.some(Boolean)) {
      this.chartEditorSeriesAxis = this.chartEditorSeriesAxis.map(() => true);
    }
    this.refreshChartEditorPreview();
  }

  applyChartEditor(): void {
    const wrapper = this.chartEditorTargetWrapper;
    if (!wrapper) {
      this.chartEditorMessage = "No selected chart target.";
      return;
    }
    const nextPayload = this.buildChartEditorPayload();
    if (!nextPayload) {
      this.chartEditorMessage = "Unable to read chart data from selected item.";
      return;
    }

    const nextDataUrl = this.drawChartSnapshotDataUrl(
      nextPayload.chartData,
      nextPayload.title,
      nextPayload.options,
      nextPayload.chartType
    );
    if (!nextDataUrl) {
      this.chartEditorMessage = "Failed to regenerate chart image.";
      return;
    }

    const targetImage = wrapper.querySelector("img.chart-snapshot-image") as HTMLImageElement | null;
    if (!targetImage) {
      this.chartEditorMessage = "Target chart image not found.";
      return;
    }

    targetImage.src = nextDataUrl;
    targetImage.alt = nextPayload.title;
    wrapper.setAttribute("data-chart-payload", this.encodeChartPayload(nextPayload));

    // Keep the chart-type-* class on the outer block in sync with the new type.
    const chartImageBlock = wrapper.closest(".report-chart-image") as HTMLElement | null;
    if (chartImageBlock) {
      const staleTypeClasses = Array.from(chartImageBlock.classList).filter((cls) =>
        cls.startsWith("chart-type-")
      );
      staleTypeClasses.forEach((cls) => chartImageBlock.classList.remove(cls));
      chartImageBlock.classList.add(`chart-type-${nextPayload.chartType}`);
    }
    this.chartEditorBasePayload = nextPayload;
    this.chartEditorPreviewDataUrl = nextDataUrl;

    this.syncPagesFromDom();
    this.closeChartEditor();
  }

  applyTableEditor(): void {
    const wrapper = this.tableEditorTargetWrapper;
    if (!wrapper) {
      this.tableEditorMessage = "No selected table target.";
      return;
    }

    const tableBlock = this.findSelectedTableContainer(wrapper);
    if (!tableBlock) {
      this.tableEditorMessage = "Target table not found.";
      return;
    }

    const tableId = String(tableBlock.getAttribute("data-table-id") || "").trim();
    const allEditors = this.pageEditors?.toArray().map((ref) => ref.nativeElement) || [];
    const targets: HTMLElement[] = [];
    for (let i = 0; i < allEditors.length; i += 1) {
      const editor = allEditors[i];
      if (!tableId) {
        const direct = editor.querySelector(".report-table-block") as HTMLElement | null;
        if (direct) {
          targets.push(direct);
        }
        continue;
      }
      const blocks = Array.from(
        editor.querySelectorAll(`.report-table-block[data-table-id="${tableId}"]`)
      ) as HTMLElement[];
      targets.push(...blocks);
    }

    const uniqueTargets = Array.from(new Set(targets));
    for (let t = 0; t < uniqueTargets.length; t += 1) {
      this.applyTableStyleTo(uniqueTargets[t]);
    }

    this.syncPagesFromDom();
    this.closeTableEditor();
  }

  /** Apply the current Edit-Table-Style colors to one report-table block. */
  private applyTableStyleTo(block: HTMLElement): void {
    block.style.borderColor = this.tableEditorBorderColor;

    const title = block.querySelector(".report-chart-title") as HTMLElement | null;
    if (title) {
      title.style.background = this.tableEditorTitleBg;
      title.style.color = this.tableEditorTextColor;
      title.style.borderBottomColor = this.tableEditorBorderColor;
      title.style.textAlign = this.tableEditorTitleAlign;
      title.style.fontSize = `${this.normalizeTableTitleSize(
        this.tableEditorTitleSize
      )}px`;
    }

    const headCells = block.querySelectorAll("thead th");
    for (let i = 0; i < headCells.length; i += 1) {
      const cell = headCells[i] as HTMLElement;
      cell.style.background = this.tableEditorHeaderBg;
      cell.style.color = this.tableEditorTextColor;
      cell.style.borderColor = this.tableEditorBorderColor;
    }

    const rows = block.querySelectorAll("tbody tr");
    for (let r = 0; r < rows.length; r += 1) {
      const row = rows[r] as HTMLElement;
      const bg = r % 2 === 0 ? this.tableEditorOddRowBg : this.tableEditorEvenRowBg;
      const cells = row.querySelectorAll("th,td");
      for (let c = 0; c < cells.length; c += 1) {
        const cell = cells[c] as HTMLElement;
        cell.style.background = bg;
        cell.style.color = this.tableEditorTextColor;
        cell.style.borderColor = this.tableEditorBorderColor;
      }
    }
  }

  /** Field-change handler for the Edit Table Style dialog → refresh the preview. */
  onTableEditorFieldChange(): void {
    this.refreshTableEditorPreview();
  }

  private normalizeTableTitleSize(value: unknown): number {
    const parsed = Number(value);
    if (Number.isNaN(parsed)) {
      return 13;
    }
    return Math.max(10, Math.min(40, Math.round(parsed)));
  }

  /** Rebuild the live preview shown inside the Edit Table Style dialog. */
  private refreshTableEditorPreview(): void {
    const wrapper = this.tableEditorTargetWrapper;
    const container = wrapper
      ? this.findSelectedTableContainer(wrapper)
      : null;
    if (!container) {
      this.tableEditorPreviewHtml = "";
      return;
    }
    const clone = container.cloneNode(true) as HTMLElement;
    this.applyTableStyleTo(clone);
    clone.style.margin = "0";
    clone.style.maxWidth = "100%";
    this.tableEditorPreviewHtml = this.sanitizer.bypassSecurityTrustHtml(
      clone.outerHTML
    );
  }

  private buildChartEditorPayload(): {
    title: string;
    chartType?: "bar" | "line" | "dot" | "bar_label" | "line_label" | "pie" | "donut" | "gauge" | "numeric" | "table" | "text" | "box";
    chartData: {
      categories: string[];
      series: Array<{ name: string; data: Array<number | null>; type: "bar" | "line" | "dot" | "bar_label" | "line_label" | "pie" | "donut" | "gauge" | "numeric" | "table" | "text" | "box" }>;
    };
    options?: {
      bgStart?: string;
      bgCenter?: string;
      bgEnd?: string;
      bgOuter?: string;
      seriesColors?: string[];
      titleText?: string;
      titleColor?: string;
      titleFontSize?: number;
      legendTextColor?: string;
      gridColor?: string;
      axisTextColor?: string;
      dateLabelMode?: "all" | "auto";
      dateLabelRotation?: number;
      valueLabelSize?: number;
      valueLabelRotation?: number;
      legendFontSize?: number;
      xAxisLabelSize?: number;
      yAxisLabelSize?: number;
      titlePadTop?: number;
      titlePadRight?: number;
      titlePadBottom?: number;
      titlePadLeft?: number;
      multiAxis?: boolean;
      multiAxisIndices?: number[];
    };
  } | null {
    const base = this.chartEditorBasePayload;
    if (!base) {
      return null;
    }

    const updatedSeries = base.chartData.series.map((series, i) => ({
      ...series,
      name: String(this.chartEditorSeriesNames[i] || series.name || `Series ${i + 1}`).trim(),
      type: this.chartEditorChartType,
    }));

    const options: NonNullable<typeof base.options> = {
      ...(base.options || {}),
      bgStart: this.chartEditorBgStart || "#1f2937",
      bgCenter: this.chartEditorBgCenter || "#2b3444",
      bgEnd: this.chartEditorBgEnd || "#3f3f46",
      bgOuter: this.chartEditorBgOuter || "#111827",
      dateLabelMode: this.chartEditorDateLabelMode,
      dateLabelRotation: this.normalizeDateLabelRotation(
        this.chartEditorDateLabelRotation
      ),
      valueLabelSize: this.normalizeValueLabelSize(this.chartEditorValueLabelSize),
      valueLabelRotation: this.normalizeValueLabelRotation(
        this.chartEditorValueLabelRotation
      ),
      xAxisLabelSize: this.normalizeAxisLabelSize(this.chartEditorXAxisLabelSize),
      yAxisLabelSize: this.normalizeAxisLabelSize(this.chartEditorYAxisLabelSize),
      legendFontSize: this.normalizeLegendSize(this.chartEditorLegendSize),
      titlePadTop: this.normalizeTitlePad(this.chartEditorTitlePadTop),
      titlePadRight: this.normalizeTitlePad(this.chartEditorTitlePadRight),
      titlePadBottom: this.normalizeTitlePad(this.chartEditorTitlePadBottom),
      titlePadLeft: this.normalizeTitlePad(this.chartEditorTitlePadLeft),
      multiAxis: this.chartEditorMultiAxis,
      multiAxisIndices: base.chartData.series
        .map((_, i) => i)
        .filter((i) => this.chartEditorSeriesAxis[i]),
      titleFontSize: this.normalizeChartTitleSize(this.chartEditorTitleSize),
      titleColor: this.chartEditorTitleColor || "#f8fafc",
      titleText: this.chartEditorTitle || "",
      seriesColors: (() => {
        const palette = this.ensureDistinctColors(
          this.defaultChartPalette,
          updatedSeries.length
        );
        return updatedSeries.map(
          (_, i) => this.chartEditorSeriesColors[i] || palette[i]
        );
      })(),
    };

    return {
      ...base,
      chartType: this.chartEditorChartType,
      // `title` stays the report name — the editor's title box feeds
      // options.titleText. Overwriting it here used to silently change the
      // daily/hourly date formatting, which keys off this string.
      title: base.title || "",
      chartData: {
        ...base.chartData,
        series: updatedSeries,
      },
      options,
    };
  }

  private refreshChartEditorPreview(): void {
    const nextPayload = this.buildChartEditorPayload();
    if (!nextPayload) {
      this.chartEditorPreviewDataUrl = "";
      return;
    }
    this.chartEditorPreviewDataUrl = this.drawChartSnapshotDataUrl(
      nextPayload.chartData,
      nextPayload.title,
      nextPayload.options,
      nextPayload.chartType
    );
  }

  private resizeWrapperByFactor(wrapper: HTMLElement, factor: number): void {
    const pageEditor = wrapper.closest(".page-editor") as HTMLElement | null;
    const maxWidth = pageEditor?.clientWidth || wrapper.parentElement?.clientWidth || 0;
    const image = wrapper.querySelector("img") as HTMLImageElement | null;
    const chartBlock = wrapper.querySelector(".report-chart") as HTMLElement | null;
    const renderedImageWidth = image?.getBoundingClientRect().width || 0;
    const renderedChartWidth = chartBlock?.getBoundingClientRect().width || 0;
    const currentWidth =
      renderedImageWidth > 0
        ? renderedImageWidth
        : renderedChartWidth > 0
          ? renderedChartWidth
          : this.getWrapperWidthPx(wrapper);
    const minWidth = 120;
    let nextWidth = Math.round(currentWidth * factor);
    if (maxWidth > 0) {
      nextWidth = Math.min(nextWidth, Math.floor(maxWidth));
    }
    nextWidth = Math.max(minWidth, nextWidth);
    this.setWrapperWidthPx(wrapper, nextWidth);
    // Keep scale normalized so width controls are deterministic.
    const angle = this.getWrapperRotation(wrapper);
    this.applyWrapperTransform(wrapper, angle, 1);
    this.updateFloatingImageToolbar();
    this.debugSelectionLog(
      "[TOOLBAR] width zoom applied",
      "targetWrapper:",
      this.describeElement(wrapper),
      "targetImage:",
      this.describeElement(image),
      "targetChart:",
      this.describeElement(chartBlock),
      "currentWidth:",
      currentWidth,
      "nextWidth:",
      nextWidth,
      "maxWidth:",
      maxWidth,
      "inlineWrapperWidth:",
      wrapper.style.width || "(empty)",
      "inlineImageWidth:",
      image?.style.width || "(empty)",
      "inlineChartWidth:",
      chartBlock?.style.width || "(empty)",
      "renderedWrapperWidth:",
      Math.round(wrapper.getBoundingClientRect().width),
      "renderedImageWidth:",
      image ? Math.round(image.getBoundingClientRect().width) : -1,
      "renderedChartWidth:",
      chartBlock ? Math.round(chartBlock.getBoundingClientRect().width) : -1
    );
  }

  /**
   * Approximate rendered widths of the floating toolbar in each state. They only
   * drive the right-edge anchoring below, so being a few pixels out is harmless.
   */
  private readonly IMAGE_TOOLBAR_COLLAPSED_WIDTH = 36;
  private readonly IMAGE_TOOLBAR_EXPANDED_WIDTH = 470;

  /** Expand/collapse the floating chart toolbar and re-anchor it. */
  toggleImageToolbar(): void {
    this.imageToolbarExpanded = !this.imageToolbarExpanded;
    this.updateFloatingImageToolbar();
  }

  private updateFloatingImageToolbar(): void {
    const wrapper = this.selectedResizableWrapper;
    if (!wrapper) {
      this.imageToolbarVisible = false;
      return;
    }

    const rect = wrapper.getBoundingClientRect();
    const width = this.imageToolbarExpanded
      ? this.IMAGE_TOOLBAR_EXPANDED_WIDTH
      : this.IMAGE_TOOLBAR_COLLAPSED_WIDTH;
    // Anchor the toolbar's RIGHT edge to the visual's right edge instead of its
    // left. Collapsed, that parks the handle in the empty page margin above the
    // chart's top-right corner; expanding grows the row leftwards from the same
    // spot, so the handle never jumps under the pointer.
    this.imageToolbarTop = Math.max(8, rect.top - 40);
    this.imageToolbarLeft = Math.max(
      8,
      Math.min(rect.right - width, window.innerWidth - width - 8)
    );
    this.imageToolbarVisible = true;
  }

  // ----------------------------------------------------------------------------
  // Structural table editing (insert/delete rows & columns) for user tables.
  // Report data tables (.report-data-table / .report-table-block) are excluded:
  // they are regenerated/split by the pagination engine and have their own
  // style editor, so structural edits there are unsafe.
  // ----------------------------------------------------------------------------

  /** Resolve the editable table + cell that contains the given node, if any. */
  private getEditableTableFromNode(
    node: Node | null
  ): { table: HTMLTableElement; cell: HTMLTableCellElement } | null {
    if (!node) {
      return null;
    }
    const el =
      node.nodeType === Node.ELEMENT_NODE
        ? (node as Element)
        : node.parentElement;
    const cell = el?.closest("td,th") as HTMLTableCellElement | null;
    if (!cell) {
      return null;
    }
    const table = cell.closest("table") as HTMLTableElement | null;
    if (!table) {
      return null;
    }
    // Skip report data tables and anything outside an editor.
    if (
      table.classList.contains("report-data-table") ||
      cell.closest(".report-table-block") ||
      !cell.closest(".page-editor")
    ) {
      return null;
    }
    return { table, cell };
  }

  /** Show/position the table toolbar based on the current caret/selection. */
  private updateTableToolbarFromSelection(): void {
    const selection = window.getSelection();
    const node = selection && selection.rangeCount ? selection.anchorNode : null;
    this.updateTableToolbarForNode(node);
  }

  /** Show/position the table toolbar for a specific node (e.g. a click target). */
  private updateTableToolbarForNode(node: Node | null): void {
    const found = this.getEditableTableFromNode(node);
    if (!found) {
      this.tableToolbarVisible = false;
      this.selectedTableCell = null;
      return;
    }

    this.selectedTableCell = found.cell;
    const rect = found.table.getBoundingClientRect();
    this.tableToolbarTop = Math.max(8, rect.top - 44);
    this.tableToolbarLeft = Math.max(
      8,
      Math.min(rect.left, window.innerWidth - 380)
    );
    this.tableToolbarVisible = true;
  }

  private getCellColumnIndex(cell: HTMLTableCellElement): number {
    const row = cell.parentElement as HTMLTableRowElement | null;
    if (!row) {
      return -1;
    }
    return Array.prototype.indexOf.call(row.cells, cell);
  }

  /** Re-paginate and refresh the toolbar after a structural table change. */
  private afterTableEdit(): void {
    this.syncPagesFromDom();
    this.reflowFrom(this.activePageIndex);
    this.updateDocumentStats();
    setTimeout(() => this.updateTableToolbarFromSelection(), 0);
  }

  insertTableRow(above: boolean): void {
    const cell = this.selectedTableCell;
    if (!cell) {
      return;
    }
    const row = cell.parentElement as HTMLTableRowElement | null;
    if (!row || !row.parentElement) {
      return;
    }
    const columnCount = row.cells.length;
    const newRow = document.createElement("tr");
    for (let i = 0; i < columnCount; i += 1) {
      const td = document.createElement("td");
      td.innerHTML = "&nbsp;";
      newRow.appendChild(td);
    }
    if (above) {
      row.parentElement.insertBefore(newRow, row);
    } else {
      row.parentElement.insertBefore(newRow, row.nextSibling);
    }
    this.afterTableEdit();
  }

  insertTableColumn(after: boolean): void {
    const cell = this.selectedTableCell;
    if (!cell) {
      return;
    }
    const table = cell.closest("table") as HTMLTableElement | null;
    if (!table) {
      return;
    }
    const columnIndex = this.getCellColumnIndex(cell);
    if (columnIndex < 0) {
      return;
    }
    const rows = Array.from(table.rows);
    for (let i = 0; i < rows.length; i += 1) {
      const row = rows[i];
      const reference = row.cells[columnIndex] || null;
      const isHeader = reference
        ? reference.tagName === "TH"
        : !!row.querySelector("th");
      const newCell = document.createElement(isHeader ? "th" : "td");
      newCell.innerHTML = "&nbsp;";
      if (after) {
        row.insertBefore(newCell, reference ? reference.nextSibling : null);
      } else {
        row.insertBefore(newCell, reference);
      }
    }
    this.afterTableEdit();
  }

  deleteTableRow(): void {
    const cell = this.selectedTableCell;
    if (!cell) {
      return;
    }
    const row = cell.parentElement as HTMLTableRowElement | null;
    const table = cell.closest("table") as HTMLTableElement | null;
    if (!row || !table) {
      return;
    }
    if (table.rows.length <= 1) {
      this.deleteTable();
      return;
    }
    row.remove();
    this.selectedTableCell = null;
    this.tableToolbarVisible = false;
    this.afterTableEdit();
  }

  deleteTableColumn(): void {
    const cell = this.selectedTableCell;
    if (!cell) {
      return;
    }
    const table = cell.closest("table") as HTMLTableElement | null;
    if (!table) {
      return;
    }
    const columnIndex = this.getCellColumnIndex(cell);
    if (columnIndex < 0) {
      return;
    }
    const rows = Array.from(table.rows);
    const columnCount = rows[0]?.cells.length || 0;
    if (columnCount <= 1) {
      this.deleteTable();
      return;
    }
    for (let i = 0; i < rows.length; i += 1) {
      const target = rows[i].cells[columnIndex];
      if (target) {
        target.remove();
      }
    }
    this.selectedTableCell = null;
    this.tableToolbarVisible = false;
    this.afterTableEdit();
  }

  deleteTable(): void {
    const cell = this.selectedTableCell;
    if (!cell) {
      return;
    }
    const table = cell.closest("table") as HTMLTableElement | null;
    if (!table) {
      return;
    }
    table.remove();
    this.selectedTableCell = null;
    this.tableToolbarVisible = false;
    this.afterTableEdit();
  }

  /**
   * Transpose the selected table — swap its rows and columns. Lets the user flip
   * a wide "parameters-as-columns" report table into a tall "parameters-as-rows"
   * one (and back again). The original first column becomes the new header row
   * and the original header row becomes the new first column; the table is
   * rebuilt with the standard white/black styling and re-paginated.
   */
  transposeSelectedTable(): void {
    const cell = this.selectedTableCell;
    const table = cell?.closest("table") as HTMLTableElement | null;
    if (!table || !this.transposeTableElement(table)) {
      return;
    }
    this.selectedTableCell = null;
    this.tableToolbarVisible = false;
    this.afterTableEdit();
  }

  /** Transpose the table inside the currently open Edit Table Style dialog. */
  transposeTableFromEditor(): void {
    const wrapper = this.tableEditorTargetWrapper;
    if (!wrapper) {
      this.tableEditorMessage = "No selected table target.";
      return;
    }
    const container = this.findSelectedTableContainer(wrapper);
    const table = container?.querySelector(
      "table"
    ) as HTMLTableElement | null;
    if (!table || !this.transposeTableElement(table)) {
      this.tableEditorMessage = "Could not switch rows and columns.";
      return;
    }
    this.tableEditorMessage = "Switched rows and columns.";
    this.refreshTableEditorPreview();
    this.syncPagesFromDom();
    this.reflowFrom(this.activePageIndex);
    this.updateDocumentStats();
  }

  /**
   * Swap a table's rows and columns in place (rebuilds its innerHTML). The
   * original first column becomes the new header row and the original header row
   * becomes the new first column, re-applying the standard white/black styling.
   * Returns false when the table is too small to transpose.
   */
  private transposeTableElement(table: HTMLTableElement): boolean {
    const rows = Array.from(table.rows);
    if (!rows.length) {
      return false;
    }
    const colCount = rows.reduce((m, r) => Math.max(m, r.cells.length), 0);
    if (colCount < 2 && rows.length < 2) {
      return false;
    }

    // Snapshot the current grid (cell HTML only — styling is re-applied below).
    const grid: string[][] = [];
    for (let r = 0; r < rows.length; r += 1) {
      grid[r] = [];
      for (let c = 0; c < colCount; c += 1) {
        const el = rows[r].cells[c] as HTMLTableCellElement | undefined;
        grid[r][c] = el ? el.innerHTML : "";
      }
    }

    const headStyle =
      "padding:9px 12px;border:1px solid #cccccc;text-align:left;color:#000000;background:#f2f2f2;font-size:12px;font-weight:700;";
    const oddStyle =
      "padding:9px 12px;border:1px solid #cccccc;text-align:left;color:#000000;background:#ffffff;font-size:12px;";
    const evenStyle =
      "padding:9px 12px;border:1px solid #cccccc;text-align:left;color:#000000;background:#f7f7f7;font-size:12px;";

    // New header row = original first column (one cell per original row).
    let thead = "<thead><tr>";
    for (let r = 0; r < rows.length; r += 1) {
      thead += `<th style="${headStyle}">${grid[r][0] ?? ""}</th>`;
    }
    thead += "</tr></thead>";

    // New body: one row per original column (skipping column 0, now the header).
    let tbody = "<tbody>";
    for (let c = 1; c < colCount; c += 1) {
      tbody += "<tr>";
      for (let r = 0; r < rows.length; r += 1) {
        // Original header row (r === 0) becomes the new first (label) column.
        if (r === 0) {
          tbody += `<th style="${headStyle}">${grid[r][c] ?? ""}</th>`;
        } else {
          const style = c % 2 === 1 ? oddStyle : evenStyle;
          tbody += `<td style="${style}">${grid[r][c] ?? ""}</td>`;
        }
      }
      tbody += "</tr>";
    }
    tbody += "</tbody>";

    table.innerHTML = thead + tbody;
    return true;
  }

  // ----------------------------------------------------------------------------
  // Cell / row / column background color.
  // ----------------------------------------------------------------------------

  /** Capture the cells to color before the color picker steals focus. */
  captureCellColorTargets(): void {
    this.cellColorTargets = this.getCellsInSelection();
  }

  /** Apply the picked color to the captured cells (current cell or selection). */
  applyCellFill(event: Event): void {
    const color = String((event.target as HTMLInputElement)?.value || "");
    if (!color) {
      return;
    }
    this.cellFillColor = color;
    const cells = this.cellColorTargets.length
      ? this.cellColorTargets
      : this.getCellsInSelection();
    for (let i = 0; i < cells.length; i += 1) {
      cells[i].style.background = color;
    }
    this.cellColorTargets = [];
    this.syncPagesFromDom();
  }

  /** Apply the current fill color to the whole row or column of the active cell. */
  applyCellFillScope(scope: "row" | "col"): void {
    const cell = this.selectedTableCell;
    if (!cell) {
      return;
    }
    const color = this.cellFillColor;
    if (scope === "row") {
      const row = cell.parentElement as HTMLTableRowElement | null;
      if (row) {
        const cells = Array.from(row.cells) as HTMLElement[];
        for (let i = 0; i < cells.length; i += 1) {
          cells[i].style.background = color;
        }
      }
    } else {
      const table = cell.closest("table") as HTMLTableElement | null;
      const columnIndex = this.getCellColumnIndex(cell);
      if (table && columnIndex >= 0) {
        const rows = Array.from(table.rows);
        for (let r = 0; r < rows.length; r += 1) {
          const target = rows[r].cells[columnIndex] as HTMLElement | undefined;
          if (target) {
            target.style.background = color;
          }
        }
      }
    }
    this.syncPagesFromDom();
  }

  /** Cells intersecting the current selection, or the active cell if collapsed. */
  private getCellsInSelection(): HTMLTableCellElement[] {
    const fallback = this.selectedTableCell ? [this.selectedTableCell] : [];
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      return fallback;
    }
    const range = selection.getRangeAt(0);
    const table = this.selectedTableCell?.closest("table") as HTMLElement | null;
    if (range.collapsed || !table) {
      return fallback;
    }
    const cells = Array.from(
      table.querySelectorAll("td,th")
    ) as HTMLTableCellElement[];
    const selected = cells.filter((c) => range.intersectsNode(c));
    return selected.length ? selected : fallback;
  }

  // ----------------------------------------------------------------------------
  // Hover "+" controls: a button on the right of any table adds a column, a
  // button below it adds a row. Works for both inserted tables and report
  // tables (report tables apply the column to every page fragment so the column
  // count stays consistent).
  // ----------------------------------------------------------------------------

  onWorkspaceMouseMove(event: MouseEvent): void {
    if (this.tableHoverRafPending) {
      return;
    }
    const target = event.target as Element | null;
    const clientX = event.clientX;
    const clientY = event.clientY;
    this.tableHoverRafPending = true;
    requestAnimationFrame(() => {
      this.tableHoverRafPending = false;
      const table = target?.closest?.(
        ".page-editor table"
      ) as HTMLElement | null;
      const hoverCell = target?.closest?.(
        "td,th"
      ) as HTMLTableCellElement | null;
      if (table) {
        this.cancelHideTableControls();
        this.showTableControls(table);
        if (hoverCell) {
          this.showTableDeleteControls(hoverCell);
        }
      } else if (
        !target?.closest?.(".table-add-control") &&
        !target?.closest?.(".table-del-control")
      ) {
        this.scheduleHideTableControls();
      }
      // Show a resize cursor when hovering a cell's right/bottom border.
      const cell = target?.closest?.("td,th") as HTMLElement | null;
      if (cell) {
        const rect = cell.getBoundingClientRect();
        const edge = DownloadpdfComponent.TABLE_RESIZE_EDGE;
        const fromRight = rect.right - clientX;
        const fromBottom = rect.bottom - clientY;
        if (fromRight >= -2 && fromRight <= edge) {
          cell.style.cursor = "col-resize";
        } else if (fromBottom >= -2 && fromBottom <= edge) {
          cell.style.cursor = "row-resize";
        } else {
          cell.style.cursor = "";
        }
      }
    });
  }

  private showTableControls(table: HTMLElement): void {
    if (
      this.chartEditorOpen ||
      this.tableEditorOpen ||
      this.dialogType !== "none" ||
      this.exportInProgress
    ) {
      this.tableControlsVisible = false;
      return;
    }
    this.hoveredTable = table;
    const rect = table.getBoundingClientRect();
    // "+" to add a column: just outside the right edge, vertically centered.
    this.tableColAddTop = rect.top + rect.height / 2 - 13;
    this.tableColAddLeft = Math.min(rect.right + 6, window.innerWidth - 30);
    // "+" to add a row: just below the bottom edge, horizontally centered.
    this.tableRowAddTop = rect.bottom + 6;
    this.tableRowAddLeft = rect.left + rect.width / 2 - 13;
    this.tableControlsVisible = true;
  }

  cancelHideTableControls(): void {
    if (this.tableControlsHideTimer) {
      clearTimeout(this.tableControlsHideTimer);
      this.tableControlsHideTimer = null;
    }
  }

  scheduleHideTableControls(): void {
    this.cancelHideTableControls();
    this.tableControlsHideTimer = setTimeout(() => {
      this.tableControlsVisible = false;
      this.hoveredTable = null;
      this.tableDelControlsVisible = false;
      this.hoveredCell = null;
    }, 250);
  }

  /**
   * Position the per-cell "−" delete handles for the cell under the cursor: a
   * button above the table (aligned to the cell's column) deletes that column, a
   * button to the left of the table (aligned to the cell's row) deletes that row.
   * Mirrors showTableControls so add and delete affordances stay consistent.
   */
  private showTableDeleteControls(cell: HTMLTableCellElement): void {
    if (
      this.chartEditorOpen ||
      this.tableEditorOpen ||
      this.dialogType !== "none" ||
      this.exportInProgress
    ) {
      this.tableDelControlsVisible = false;
      return;
    }
    const table = cell.closest("table") as HTMLElement | null;
    if (!table) {
      this.tableDelControlsVisible = false;
      return;
    }
    this.hoveredCell = cell;
    const tableRect = table.getBoundingClientRect();
    const cellRect = cell.getBoundingClientRect();
    // "−" to delete the column: just above the top edge, centered on the cell.
    this.tableDelColTop = Math.max(8, tableRect.top - 24);
    this.tableDelColLeft = cellRect.left + cellRect.width / 2 - 11;
    // "−" to delete the row: just left of the table, centered on the cell's row.
    this.tableDelRowTop = cellRect.top + cellRect.height / 2 - 11;
    this.tableDelRowLeft = Math.max(8, tableRect.left - 26);
    this.tableDelControlsVisible = true;
  }

  /** Delete the column under the cursor across every page fragment. */
  deleteHoveredColumn(): void {
    const cell = this.hoveredCell;
    if (!cell) {
      return;
    }
    const table = cell.closest("table") as HTMLTableElement | null;
    if (!table) {
      return;
    }
    const columnIndex = this.getCellColumnIndex(cell);
    if (columnIndex < 0) {
      return;
    }
    // Never delete the only remaining column.
    if ((table.rows[0]?.cells.length || 0) <= 1) {
      return;
    }
    const targets = this.relatedTables(table);
    for (let t = 0; t < targets.length; t += 1) {
      const rows = Array.from((targets[t] as HTMLTableElement).rows);
      for (let r = 0; r < rows.length; r += 1) {
        const target = rows[r].cells[columnIndex];
        if (target) {
          target.remove();
        }
      }
    }
    this.tableDelControlsVisible = false;
    this.hoveredCell = null;
    this.afterHoverTableEdit(table);
  }

  /** Delete the row under the cursor (within its own page fragment). */
  deleteHoveredRow(): void {
    const cell = this.hoveredCell;
    if (!cell) {
      return;
    }
    const row = cell.closest("tr") as HTMLTableRowElement | null;
    const table = cell.closest("table") as HTMLElement | null;
    if (!row || !table) {
      return;
    }
    // Keep the header row of report tables intact — delete columns instead.
    if (row.parentElement && row.parentElement.tagName === "THEAD") {
      return;
    }
    // Never delete the last body row across all fragments of the table.
    const totalBodyRows = this.relatedTables(table).reduce(
      (sum, t) => sum + t.querySelectorAll("tbody tr").length,
      0
    );
    if (totalBodyRows <= 1) {
      return;
    }
    row.remove();
    this.tableDelControlsVisible = false;
    this.hoveredCell = null;
    this.afterHoverTableEdit(table);
  }

  addColumnToHoveredTable(): void {
    const table = this.hoveredTable;
    if (!table) {
      return;
    }
    const targets = this.relatedTables(table);
    for (let t = 0; t < targets.length; t += 1) {
      const rows = Array.from((targets[t] as HTMLTableElement).rows);
      for (let r = 0; r < rows.length; r += 1) {
        const row = rows[r];
        const isHeaderRow =
          !!row.querySelector("th") ||
          (row.parentElement && row.parentElement.tagName === "THEAD");
        const cell = document.createElement(isHeaderRow ? "th" : "td");
        const lastCell = row.cells[row.cells.length - 1] as
          | HTMLElement
          | undefined;
        if (lastCell) {
          cell.setAttribute("style", lastCell.getAttribute("style") || "");
        }
        cell.innerHTML = "&nbsp;";
        row.appendChild(cell);
      }
    }
    this.afterHoverTableEdit(table);
  }

  addRowToHoveredTable(): void {
    const table = this.hoveredTable;
    if (!table) {
      return;
    }
    const tbody = (table.querySelector("tbody") || table) as HTMLElement;
    const refRow = tbody.querySelector("tr:last-child") as
      | HTMLTableRowElement
      | null;
    const colCount = refRow
      ? refRow.cells.length
      : (table as HTMLTableElement).rows[0]?.cells.length || 1;
    const newRow = document.createElement("tr");
    for (let i = 0; i < colCount; i += 1) {
      const td = document.createElement("td");
      const refCell = refRow?.cells[i] as HTMLElement | undefined;
      if (refCell && refCell.tagName === "TD") {
        td.setAttribute("style", refCell.getAttribute("style") || "");
      }
      td.innerHTML = "&nbsp;";
      newRow.appendChild(td);
    }
    tbody.appendChild(newRow);
    this.afterHoverTableEdit(table);
  }

  /**
   * All table elements that belong to the same logical table. Report tables can
   * be split across pages into several .report-table-block fragments sharing a
   * data-table-id — a new column must be added to all of them. Plain inserted
   * tables are standalone.
   */
  private relatedTables(table: HTMLElement): HTMLElement[] {
    const block = table.closest(".report-table-block") as HTMLElement | null;
    const tableId = block?.getAttribute("data-table-id");
    if (!tableId) {
      return [table];
    }
    const result: HTMLElement[] = [];
    const editors = this.pageEditors?.toArray() || [];
    for (let e = 0; e < editors.length; e += 1) {
      const blocks = Array.from(
        editors[e].nativeElement.querySelectorAll(
          `.report-table-block[data-table-id="${tableId}"]`
        )
      );
      for (let b = 0; b < blocks.length; b += 1) {
        const t = blocks[b].querySelector("table") as HTMLElement | null;
        if (t) {
          result.push(t);
        }
      }
    }
    return result.length ? result : [table];
  }

  private afterHoverTableEdit(table: HTMLElement): void {
    const editor = table.closest(".page-editor") as HTMLElement | null;
    const editors = this.pageEditors?.toArray() || [];
    const idx = editors.findIndex((ref) => ref.nativeElement === editor);
    if (idx >= 0) {
      this.activePageIndex = idx;
    }
    this.syncPagesFromDom();
    this.reflowFrom(0);
    this.updateDocumentStats();
    // Reposition the controls against the (possibly moved) table after reflow.
    setTimeout(() => {
      if (this.hoveredTable && this.hoveredTable.isConnected) {
        this.showTableControls(this.hoveredTable);
      } else {
        this.tableControlsVisible = false;
      }
    }, 0);
  }

  // ----------------------------------------------------------------------------
  // Column / row resize by dragging a cell's right / bottom border.
  // ----------------------------------------------------------------------------

  private static readonly TABLE_RESIZE_EDGE = 6;

  /** If the pointer is on a cell's right/bottom border, begin a resize drag. */
  private maybeStartTableResize(event: MouseEvent, target: Element): boolean {
    const cell = target.closest("td,th") as HTMLTableCellElement | null;
    if (!cell || !cell.closest(".page-editor")) {
      return false;
    }
    const table = cell.closest("table") as HTMLTableElement | null;
    if (!table) {
      return false;
    }
    const rect = cell.getBoundingClientRect();
    const edge = DownloadpdfComponent.TABLE_RESIZE_EDGE;
    const fromRight = rect.right - event.clientX;
    const fromBottom = rect.bottom - event.clientY;

    if (fromRight >= -2 && fromRight <= edge) {
      event.preventDefault();
      event.stopPropagation();
      this.startColumnResize(table, cell, event);
      return true;
    }
    if (fromBottom >= -2 && fromBottom <= edge) {
      event.preventDefault();
      event.stopPropagation();
      this.startRowResize(cell.parentElement as HTMLTableRowElement, event);
      return true;
    }
    return false;
  }

  private startColumnResize(
    table: HTMLTableElement,
    cell: HTMLTableCellElement,
    event: MouseEvent
  ): void {
    // Fixed layout makes per-column widths deterministic.
    table.style.tableLayout = "fixed";
    table.style.width = table.style.width || "100%";
    const columnIndex = this.getCellColumnIndex(cell);
    const startX = event.clientX;
    const startWidth = cell.getBoundingClientRect().width;

    const onMove = (moveEvent: MouseEvent) => {
      moveEvent.preventDefault();
      const nextWidth = Math.max(24, Math.round(startWidth + (moveEvent.clientX - startX)));
      const rows = Array.from(table.rows);
      for (let r = 0; r < rows.length; r += 1) {
        const target = rows[r].cells[columnIndex] as HTMLElement | undefined;
        if (target) {
          target.style.width = `${nextWidth}px`;
        }
      }
    };
    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      this.syncPagesFromDom();
      this.reflowFrom(this.activePageIndex);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  }

  private startRowResize(row: HTMLTableRowElement | null, event: MouseEvent): void {
    if (!row) {
      return;
    }
    const startY = event.clientY;
    const startHeight = row.getBoundingClientRect().height;

    const onMove = (moveEvent: MouseEvent) => {
      moveEvent.preventDefault();
      const nextHeight = Math.max(16, Math.round(startHeight + (moveEvent.clientY - startY)));
      row.style.height = `${nextHeight}px`;
      const cells = Array.from(row.cells) as HTMLElement[];
      for (let c = 0; c < cells.length; c += 1) {
        cells[c].style.height = `${nextHeight}px`;
      }
    };
    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      this.syncPagesFromDom();
      this.reflowFrom(this.activePageIndex);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  }

  private getWrapperWidthPx(wrapper: HTMLElement): number {
    const fromData = Number(wrapper.dataset.widthPx);
    if (!Number.isNaN(fromData) && fromData > 0) {
      return fromData;
    }

    const inlineWidth = String(wrapper.style.width || "").trim();
    const parsedInline = inlineWidth.endsWith("px")
      ? Number(inlineWidth.replace("px", ""))
      : Number.NaN;
    if (!Number.isNaN(parsedInline) && parsedInline > 0) {
      return parsedInline;
    }

    return wrapper.offsetWidth || Math.round(wrapper.getBoundingClientRect().width) || 900;
  }

  private getWrapperOffsetX(wrapper: HTMLElement): number {
    const fromData = Number(wrapper.dataset.offsetXPx);
    if (!Number.isNaN(fromData)) {
      return fromData;
    }

    const transformTarget = this.getTransformTarget(wrapper);
    const transform = String(transformTarget?.style.transform || wrapper.style.transform || "");
    const translate2dMatch = transform.match(
      /translate\(\s*(-?\d+(?:\.\d+)?)px\s*,\s*-?\d+(?:\.\d+)?px\s*\)/i
    );
    if (translate2dMatch) {
      return Number(translate2dMatch[1]);
    }
    const match = transform.match(/translateX\((-?\d+(?:\.\d+)?)px\)/i);
    return match ? Number(match[1]) : 0;
  }

  private getWrapperOffsetY(wrapper: HTMLElement): number {
    const fromData = Number(wrapper.dataset.offsetYPx);
    if (!Number.isNaN(fromData)) {
      return fromData;
    }

    const transformTarget = this.getTransformTarget(wrapper);
    const transform = String(transformTarget?.style.transform || wrapper.style.transform || "");
    const translate2dMatch = transform.match(
      /translate\(\s*-?\d+(?:\.\d+)?px\s*,\s*(-?\d+(?:\.\d+)?)px\s*\)/i
    );
    if (translate2dMatch) {
      return Number(translate2dMatch[1]);
    }
    const translateYMatch = transform.match(/translateY\((-?\d+(?:\.\d+)?)px\)/i);
    return translateYMatch ? Number(translateYMatch[1]) : 0;
  }

  private setWrapperWidthPx(wrapper: HTMLElement, width: number): void {
    const normalized = Math.max(120, Math.round(width));
    wrapper.dataset.widthPx = String(normalized);
    wrapper.style.width = `${normalized}px`;
    wrapper.style.maxWidth = "none";
    wrapper.style.height = "auto";

    const image = wrapper.querySelector("img") as HTMLImageElement | null;
    if (image) {
      image.style.setProperty("width", `${normalized}px`, "important");
      image.style.setProperty("max-width", "none", "important");
      image.style.setProperty("height", "auto", "important");
      image.style.display = "block";
    }

    const chartBlock = wrapper.querySelector(".report-chart") as HTMLElement | null;
    if (chartBlock) {
      chartBlock.style.width = `${normalized}px`;
      chartBlock.style.maxWidth = "none";
    }
  }

  private getTransformTarget(wrapper: HTMLElement): HTMLElement | null {
    const chartBlock = wrapper.querySelector(".report-chart") as HTMLElement | null;
    if (chartBlock) {
      return chartBlock;
    }

    const tableBlock = wrapper.querySelector(".report-table-block") as HTMLElement | null;
    if (tableBlock) {
      return tableBlock;
    }

    const image = wrapper.querySelector("img") as HTMLElement | null;
    if (image) {
      return image;
    }

    return null;
  }

  private placeCaretAtStart(container: HTMLElement): void {
    const selection = window.getSelection();
    if (!selection) {
      return;
    }

    const range = document.createRange();
    range.selectNodeContents(container);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
    container.focus();
  }

  private resolveImageFromTarget(target: Element): HTMLImageElement | null {
    if (target.tagName.toLowerCase() === "img") {
      return target as HTMLImageElement;
    }

    const ancestorImage = target.closest("img") as HTMLImageElement | null;
    if (ancestorImage) {
      return ancestorImage;
    }

    // For chart blocks, users may click on chart padding/background.
    // In that case, resolve the chart's own image only.
    const chartContainer = target.closest(
      ".report-chart, .report-chart-image"
    ) as HTMLElement | null;
    if (chartContainer) {
      const chartImage = chartContainer.querySelector("img");
      if (chartImage) {
        return chartImage as HTMLImageElement;
      }
    }

    return null;
  }

  private resolveImageFromEvent(event: Event): HTMLImageElement | null {
    const path = this.getEventPathElements(event);
    for (let i = 0; i < path.length; i += 1) {
      const el = path[i];
      if (el.tagName.toLowerCase() === "img") {
        return el as HTMLImageElement;
      }
    }

    for (let i = 0; i < path.length; i += 1) {
      const el = path[i];
      const img = el.querySelector?.("img");
      if (img) {
        return img as HTMLImageElement;
      }
    }

    this.debugSelectionLog(
      "[SELECT][RESOLVE] no image from event path",
      "path:",
      this.getEventPathSummary(event)
    );
    return null;
  }

  private resolveWrapperFromTarget(target: Element): HTMLElement | null {
    const directWrapper = target.closest(".resizable-image") as HTMLElement | null;
    if (directWrapper) {
      return directWrapper;
    }

    const image = this.resolveImageFromTarget(target);
    if (!image) {
      return null;
    }

    return image.closest(".resizable-image") as HTMLElement | null;
  }

  private resolveWrapperFromEvent(event: Event): HTMLElement | null {
    const path = this.getEventPathElements(event);
    for (let i = 0; i < path.length; i += 1) {
      const el = path[i];
      if (el.classList?.contains("resizable-image")) {
        return el as HTMLElement;
      }
    }

    const image = this.resolveImageFromEvent(event);
    if (!image) {
      this.debugSelectionLog("[SELECT][RESOLVE] no wrapper and no image from event");
      return null;
    }

    return image.closest(".resizable-image") as HTMLElement | null;
  }

  private placeCaretAtEnd(container: HTMLElement): void {
    const selection = window.getSelection();
    if (!selection) {
      return;
    }

    const range = document.createRange();
    range.selectNodeContents(container);
    range.collapse(false);
    selection.removeAllRanges();
    selection.addRange(range);
    container.focus();
  }

  clearEditor(): void {
    this.pages = [{ id: 1, html: "<p><br></p>" }];
    this.nextPageId = 2;
    this.activePageIndex = 0;
    // Discard the stored draft so a cleared document is not restored later.
    void this.removeDraft();
    this.selectedTableCell = null;
    this.tableToolbarVisible = false;
    setTimeout(() => {
      this.hydratePagesToDom(true);
      this.focusActivePage();
    }, 0);
    this.updateDocumentStats();
  }

  async closeEditor(): Promise<void> {
    // Persist the latest edits locally before leaving so nothing is lost.
    if (this.documentReady) {
      this.syncPagesFromDom();
      await this.saveDraft(false);
    }

    if (window.history.length > 1) {
      this.location.back();
      return;
    }

    this.router.navigate(["/report-system/dashboard-group-list"], {
      queryParamsHandling: "merge",
    });
  }

  // ----------------------------------------------------------------------------
  // Local draft persistence
  // ----------------------------------------------------------------------------

  /** Storage key for the current document, scoped per dashboard group. */
  private getDraftStorageKey(): string {
    const id = this.lastLoadedDashboardGroupId;
    const suffix = id && !Number.isNaN(id) ? `group-${id}` : "standalone";
    return `downloadpdf:draft:v${this.draftVersion}:${suffix}`;
  }

  /** Toolbar "Save" handler: sync the DOM then persist immediately. */
  manualSaveDraft(): void {
    this.syncPagesFromDom();
    void this.saveDraft(true);
  }

  /**
   * Debounced autosave. Only runs once the document has finished its initial
   * load/pagination so we never persist transient mid-render state. saveDraft()
   * deliberately does NOT sync the DOM, so this cannot re-trigger itself.
   */
  private scheduleDraftAutosave(): void {
    if (!this.documentReady) {
      return;
    }
    // Never capture the model mid-pagination — it is intentionally stale during
    // reflow. Defer; reflowFrom re-arms this once it settles.
    if (this.reflowInProgress) {
      this.draftAutosavePending = true;
      return;
    }
    // An edit just landed → mark unsaved until the debounced write completes.
    if (this.draftState !== "saving") {
      this.draftState = "unsaved";
    }
    if (this.draftAutosaveTimer) {
      clearTimeout(this.draftAutosaveTimer);
    }
    this.draftAutosaveTimer = setTimeout(() => {
      this.draftAutosaveTimer = null;
      // A reflow may have started after we were scheduled — defer again rather
      // than persist a half-paginated document.
      if (this.reflowInProgress) {
        this.draftAutosavePending = true;
        return;
      }
      void this.saveDraft(false);
    }, 1500);
  }

  /**
   * Persist the current page model. Best-effort. Uses IndexedDB (large quota)
   * and only falls back to localStorage if IndexedDB is unavailable or fails.
   */
  private async saveDraft(showFeedback: boolean): Promise<void> {
    const key = this.getDraftStorageKey();
    const payload = {
      version: this.draftVersion,
      savedAt: Date.now(),
      activePageIndex: this.activePageIndex,
      pages: (this.pages || []).map((page) => ({
        id: Number(page?.id) || 0,
        html: String(page?.html || ""),
      })),
    };

    // Only surface the transient "Saving…" state if the write is slow enough to
    // be worth showing. Most IndexedDB writes finish in a few ms, so flashing the
    // indicator every save just makes it flicker — skip straight to "Saved".
    const savingIndicatorTimer = setTimeout(() => {
      this.draftState = "saving";
    }, 450);

    const markSaved = () => {
      clearTimeout(savingIndicatorTimer);
      this.draftSavedAt = payload.savedAt;
      this.draftSaveError = false;
      this.draftState = "saved";
      if (showFeedback) {
        this.draftSaving = true;
        setTimeout(() => {
          this.draftSaving = false;
        }, 1400);
      }
    };

    try {
      const db = await this.openDraftDb();
      if (db) {
        await this.idbPut(db, key, payload);
        // Drop any stale localStorage copy from the previous implementation.
        try {
          localStorage.removeItem(key);
        } catch {
          /* ignore */
        }
        markSaved();
        return;
      }
      // No IndexedDB → small-document fallback.
      localStorage.setItem(key, JSON.stringify(payload));
      markSaved();
    } catch (error) {
      // IndexedDB write failed → try localStorage as a last resort.
      try {
        localStorage.setItem(key, JSON.stringify(payload));
        markSaved();
      } catch (fallbackError) {
        clearTimeout(savingIndicatorTimer);
        this.draftSaveError = true;
        this.draftState = "error";
        this.debugLog("[DRAFT] save failed", error, fallbackError);
      }
    }
  }

  /** Read any saved draft for this document and offer to restore it. */
  private async checkForDraft(): Promise<void> {
    const key = this.getDraftStorageKey();
    try {
      let parsed: any = null;

      const db = await this.openDraftDb();
      if (db) {
        parsed = await this.idbGet(db, key);
      }
      // Fall back to (and migrate from) any older localStorage draft.
      if (!parsed) {
        const raw = localStorage.getItem(key);
        if (raw) {
          parsed = JSON.parse(raw);
        }
      }

      if (!parsed || !Array.isArray(parsed.pages) || !parsed.pages.length) {
        return;
      }
      const pages = parsed.pages
        .map((page: any, index: number) => ({
          id: Number(page?.id) || index + 1,
          html: String(page?.html || ""),
        }))
        .filter((page: { html: string }) => typeof page.html === "string");
      if (!pages.length) {
        return;
      }
      this.pendingDraft = {
        pages,
        savedAt: Number(parsed.savedAt) || 0,
        activePageIndex: Number(parsed.activePageIndex) || 0,
      };
      this.draftSavedAt = this.pendingDraft.savedAt;
      this.draftRestoreAvailable = true;
    } catch (error) {
      this.debugLog("[DRAFT] read failed", error);
    }
  }

  // --- IndexedDB plumbing (no external dependency) ---

  /** Open (and cache) the drafts database. Resolves null if IDB is unavailable. */
  private openDraftDb(): Promise<IDBDatabase | null> {
    if (this.draftDb) {
      return Promise.resolve(this.draftDb);
    }
    return new Promise((resolve) => {
      try {
        if (typeof indexedDB === "undefined" || !indexedDB) {
          resolve(null);
          return;
        }
        const request = indexedDB.open(this.draftDbName, 1);
        request.onupgradeneeded = () => {
          const db = request.result;
          if (!db.objectStoreNames.contains(this.draftStoreName)) {
            db.createObjectStore(this.draftStoreName);
          }
        };
        request.onsuccess = () => {
          this.draftDb = request.result;
          resolve(this.draftDb);
        };
        request.onerror = () => {
          this.debugLog("[DRAFT] idb open error", request.error);
          resolve(null);
        };
      } catch (error) {
        this.debugLog("[DRAFT] idb open threw", error);
        resolve(null);
      }
    });
  }

  private idbPut(db: IDBDatabase, key: string, value: unknown): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        const tx = db.transaction(this.draftStoreName, "readwrite");
        tx.objectStore(this.draftStoreName).put(value, key);
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
        tx.onabort = () => reject(tx.error);
      } catch (error) {
        reject(error);
      }
    });
  }

  private idbGet(db: IDBDatabase, key: string): Promise<any> {
    return new Promise((resolve, reject) => {
      try {
        const tx = db.transaction(this.draftStoreName, "readonly");
        const request = tx.objectStore(this.draftStoreName).get(key);
        request.onsuccess = () => resolve(request.result ?? null);
        request.onerror = () => reject(request.error);
      } catch (error) {
        reject(error);
      }
    });
  }

  private idbDelete(db: IDBDatabase, key: string): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        const tx = db.transaction(this.draftStoreName, "readwrite");
        tx.objectStore(this.draftStoreName).delete(key);
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
        tx.onabort = () => reject(tx.error);
      } catch (error) {
        reject(error);
      }
    });
  }

  /** Replace the live document with the saved draft. */
  restoreDraft(): void {
    const draft = this.pendingDraft;
    if (!draft) {
      this.draftRestoreAvailable = false;
      return;
    }

    this.draftRestoreAvailable = false;
    this.documentReady = false;
    // If the fresh API load is still in flight, its response must not overwrite
    // the draft the user just chose to restore.
    this.userRestoredDraft = true;
    this.editorLoading = false;
    this.pages = draft.pages.map((page, index) => ({
      id: Number(page.id) || index + 1,
      html: String(page.html || ""),
    }));
    this.nextPageId =
      this.pages.reduce((max, page) => Math.max(max, Number(page.id) || 0), 0) + 1;
    this.activePageIndex = Math.min(
      Math.max(0, draft.activePageIndex || 0),
      this.pages.length - 1
    );
    this.pendingDraft = null;

    setTimeout(() => {
      this.hydratePagesToDom(true);
      this.reflowFrom(0);
      this.focusActivePage();
      this.updateDocumentStats();
      this.documentReady = true;
      this.draftState = "saved";
    }, 0);
  }

  /** Dismiss the restore offer and keep the freshly generated document. */
  dismissDraft(): void {
    this.draftRestoreAvailable = false;
    this.pendingDraft = null;
  }

  /** Remove the stored draft for this document (used when clearing). */
  private async removeDraft(): Promise<void> {
    const key = this.getDraftStorageKey();
    try {
      const db = await this.openDraftDb();
      if (db) {
        await this.idbDelete(db, key);
      }
    } catch (error) {
      this.debugLog("[DRAFT] remove failed", error);
    }
    try {
      localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
    this.draftSavedAt = null;
    this.draftRestoreAvailable = false;
    this.pendingDraft = null;
  }

  /** Human-readable "last saved" label for the UI. */
  get draftSavedLabel(): string {
    if (!this.draftSavedAt) {
      return "";
    }
    try {
      return new Date(this.draftSavedAt).toLocaleString();
    } catch {
      return "";
    }
  }

  /** Short clock label (HH:MM) for the inline auto-save status. */
  private get draftSavedTimeLabel(): string {
    if (!this.draftSavedAt) {
      return "";
    }
    try {
      return new Date(this.draftSavedAt).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "";
    }
  }

  /** Text shown in the toolbar auto-save indicator. */
  get draftStatusLabel(): string {
    switch (this.draftState) {
      case "saving":
        return "Saving…";
      case "unsaved":
        return "Unsaved";
      case "error":
        return "Save failed";
      case "saved": {
        const time = this.draftSavedTimeLabel;
        return time ? `Saved ${time}` : "Saved";
      }
      default:
        return "Auto-save";
    }
  }

  async exportAsWord(): Promise<void> {
    this.syncPagesFromDom();
    const exportPages = (this.pages || []).filter(
      (page) => !this.isPageHtmlEffectivelyEmpty(page?.html || "")
    );
    if (!exportPages.length) {
      return;
    }
    const title = "Document";
    const headerLogoSrc = await this.getExportHeaderLogoDataUrl(170, 56);
    const html = this.buildWordDocumentHtml(title, headerLogoSrc, exportPages);
    const blob = new Blob([html], {
      type: "application/msword;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${title}.doc`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  /**
   * Export the document to PDF. Renders server-side (headless Chromium worker →
   * S3) so the browser never blocks: we upload the document HTML to a presigned
   * S3 URL, start a render job, poll for completion, then download the finished
   * PDF. On any failure the export reports an error (no local fallback).
   */
  async exportAsPdf(): Promise<void> {
    if (this.exportInProgress) {
      return;
    }
    this.exportInProgress = true;
    this.exportCancelled = false;
    try {
      // Never snapshot a half-paginated document.
      await this.waitForReflowSettled();
      // Final guarantee: re-verify and fix Table of Contents page numbers against
      // the settled layout before the document is saved and exported as PDF.
      this.refreshTableOfContents();
      this.syncPagesFromDom();
      // Phase 1: persist the editable editor document to S3 (reopen + edit later).
      await this.saveDocumentToS3();
      if (this.exportCancelled) {
        this.updateExportProgress(100, "Cancelled.");
        return;
      }
      // Phase 2: export the whole document as a PDF (also stored in S3).
      await this.exportViaServer();
    } catch (error) {
      console.error("[PDF] save & export failed", error);
      this.updateExportProgress(100, "Export failed.");
    } finally {
      setTimeout(() => {
        this.exportInProgress = false;
      }, 600);
    }
  }

  /**
   * "Save & Export" phase 1: persist the full editable editor document (every
   * page's HTML + metadata) to S3 so the user can reopen and edit it later in
   * this editor. Stored under the chosen file name. Best-effort: a failed save
   * is logged but does not block the PDF export. Occupies the first ~30% of the
   * progress bar; the PDF export phase drives the remainder.
   */
  private async saveDocumentToS3(): Promise<void> {
    const pages = this.pages || [];
    // Nothing rendered (e.g. empty editor) → nothing to persist.
    if (!pages.length) {
      return;
    }
    this.updateExportProgress(10, "Saving document…");

    const baseName =
      (this.exportFileName || "").trim() ||
      this.currentDocumentName ||
      (this.isMorReport ? this.getMorExportName() : "") ||
      this.getDefaultExportName();
    // Always tag the document with a site so the (upcoming) listing can filter by
    // site and never mismatch. Prefer the group's own site; fall back to the
    // logged-in user's current site for the standalone editor (no group).
    const siteId =
      this.cachedGroupForRender?.data?.siteId ||
      this.dataShareService.getSiteId() ||
      null;
    const normalizedPages = pages.map((page) => ({
      id: Number(page?.id) || 0,
      html: String(page?.html || ""),
    }));
    // Small metadata sent to init/commit (no page HTML — that goes to S3, so a
    // multi-MB document never hits API Gateway's request-size limit).
    const meta = {
      // Set when editing a previously saved document → update in place.
      id: this.currentDocumentId,
      documentName: baseName,
      fileName: `${this.sanitizeUserFileName(baseName)}.pdf`,
      dashboardGroupId: this.lastLoadedDashboardGroupId ?? null,
      siteId,
      version: this.draftVersion,
      savedAt: Date.now(),
      activePageIndex: this.activePageIndex,
      dataFrom: this.exportWindowFrom || null,
      dataTo: this.exportWindowTo || null,
      // Per-frequency applied ranges (for the saved-documents card).
      intervalWindows: this.buildSavedIntervalWindows(),
      pageCount: normalizedPages.length,
    };

    try {
      // Step 1 — init: send metadata only, get back a presigned PUT URL.
      this.updateExportProgress(15, "Preparing save…");
      const init = await firstValueFrom(this.reportService.initDocument(meta));
      const target = init?.data;
      if (!target?.id || !target?.uploadUrl) {
        throw new Error("Document save init returned no upload URL");
      }

      // Step 2 — PUT the full document JSON (incl. pages) straight to S3
      // (bypasses the API, so multi-MB documents are fine).
      this.updateExportProgress(20, "Uploading document…");
      const documentJson = JSON.stringify({
        id: target.id,
        documentName: meta.documentName,
        fileName: meta.fileName,
        dashboardGroupId: meta.dashboardGroupId,
        siteId: meta.siteId,
        version: meta.version,
        savedAt: meta.savedAt,
        activePageIndex: meta.activePageIndex,
        dataFrom: meta.dataFrom,
        dataTo: meta.dataTo,
        pages: normalizedPages,
      });
      // Raw fetch (not HttpClient) so no Authorization header is sent to S3; the
      // presigned signature pins Content-Type, so echo the returned headers.
      const putRes = await fetch(target.uploadUrl, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...(target.uploadHeaders || {}),
        },
        body: documentJson,
      });
      if (!putRes.ok) {
        throw new Error(`document upload failed (${putRes.status})`);
      }

      // Step 3 — commit: finalize the metadata row now the JSON is in S3.
      const committed = await firstValueFrom(
        this.reportService.commitDocument(target.id, meta)
      );
      // Remember the server id so further saves this session update in place
      // rather than creating duplicates.
      const savedId = committed?.data?.id ?? target.id;
      if (savedId !== undefined && savedId !== null) {
        this.currentDocumentId = savedId;
      }
      this.currentDocumentName = baseName;
      this.updateExportProgress(30, "Document saved.");
    } catch (error) {
      console.error("[PDF] save document failed", error);
      // Non-fatal — the user still gets their PDF.
      this.updateExportProgress(30, "Document save failed; exporting…");
    }
  }

  /** Open the export dialog, prefilling a default file name. */
  openExportDialog(): void {
    if (this.exportInProgress) {
      return;
    }
    // Precedence: a reopened document keeps its saved name; otherwise a MOR report
    // defaults to its MOR-name (lines joined into one string); otherwise the
    // generic monthly-report name.
    this.exportFileName =
      this.currentDocumentName ||
      (this.isMorReport ? this.getMorExportName() : "") ||
      this.getDefaultExportName();
    this.exportDialogOpen = true;
  }

  /**
   * The MOR name as a single file-name-friendly string: the rendered title lines
   * joined and their spaces collapsed to underscores (e.g. lines "September-2025",
   * "Monthly Operational Report", "IOCL Panipat" →
   * "September-2025_Monthly_Operational_Report_IOCL_Panipat"). Empty when no MOR
   * name was entered.
   */
  private getMorExportName(): string {
    const lines = this.morNameLinesForRender();
    if (!lines.length) {
      return "";
    }
    return lines.join(" ").replace(/\s+/g, "_");
  }

  /**
   * Default export file name: `Monthly_Operational_Report_<previousMonth><year>`
   * (e.g. opened in June 2026 → "Monthly_Operational_Report_May2026"). The year
   * is the previous month's own year, so January rolls back to the prior year.
   */
  private getDefaultExportName(): string {
    const { month, year } = this.getPreviousMonth();
    return `Monthly_Operational_Report_${month}${year}`;
  }

  closeExportDialog(): void {
    this.exportDialogOpen = false;
  }

  /** Confirm the dialog → start the export with the chosen file name. */
  confirmExportDialog(): void {
    this.exportDialogOpen = false;
    void this.exportAsPdf();
  }

  /** Cancel an in-flight export (stops polling; the worker may still finish). */
  cancelExport(): void {
    this.exportCancelled = true;
    this.updateExportProgress(100, "Export cancelled.");
  }

  /** Server-side render: upload HTML → start job → poll → download. */
  private async exportViaServer(): Promise<void> {
    this.updateExportProgress(5, "Preparing document…");
    // Use the name the user entered in the dialog; fall back to the MOR name (for
    // MOR reports) then the default monthly-report name if they cleared the field.
    const baseName =
      (this.exportFileName || "").trim() ||
      (this.isMorReport ? this.getMorExportName() : "") ||
      this.getDefaultExportName();
    const title = baseName;
    const fileName = `${this.sanitizeUserFileName(baseName)}.pdf`;
    // Inline the MOR cover image as a data URL so it renders in the off-app PDF.
    const coverImageDataUrl = this.isMorReport
      ? await this.getMorCoverImageDataUrl()
      : "";
    const html = this.buildPdfExportHtml(title, coverImageDataUrl);
    const pageCount = (this.pages || []).length;

    this.updateExportProgress(10, "Starting export…");
    const init = await firstValueFrom(
      this.reportService.initPdfExport({
        title,
        fileName,
        pageCount,
        options: {
          format: "A4",
          orientation: "portrait",
          printBackground: true,
          marginMm: 0,
          scale: 1,
        },
      })
    );
    const job = init?.data;
    if (!job?.jobId || !job?.uploadUrl) {
      throw new Error("Export init returned no job/upload URL");
    }

    // Upload the (multi-MB) HTML straight to S3 via the presigned URL. Use fetch
    // so the auth interceptor doesn't add headers S3's presigned PUT rejects.
    this.updateExportProgress(25, "Uploading document…");
    if (this.exportCancelled) {
      return;
    }
    const putRes = await fetch(job.uploadUrl, {
      method: "PUT",
      headers: { "Content-Type": "text/html", ...(job.uploadHeaders || {}) },
      body: html,
    });
    if (!putRes.ok) {
      throw new Error(`HTML upload failed (${putRes.status})`);
    }

    this.updateExportProgress(35, "Rendering…");
    await firstValueFrom(this.reportService.startPdfExport(job.jobId));

    const downloadUrl = await this.pollPdfExport(job.jobId);
    if (!downloadUrl) {
      return; // cancelled
    }

    this.updateExportProgress(98, "Downloading…");
    await this.triggerDownload(downloadUrl, fileName);
    this.updateExportProgress(100, "Export complete.");
  }

  /** Poll the render job until completed/failed/timeout. Returns the PDF URL. */
  private async pollPdfExport(jobId: string): Promise<string | null> {
    const deadline = Date.now() + 3 * 60 * 1000; // 3-minute ceiling
    let intervalMs = 1500;
    while (Date.now() < deadline) {
      if (this.exportCancelled) {
        return null;
      }
      await this.delay(intervalMs);
      if (this.exportCancelled) {
        return null;
      }
      let status;
      try {
        const res = await firstValueFrom(
          this.reportService.getPdfExportStatus(jobId)
        );
        status = res?.data;
      } catch {
        continue; // transient network blip → keep polling
      }
      if (status?.status === "completed" && status.downloadUrl) {
        return status.downloadUrl;
      }
      if (status?.status === "failed") {
        throw new Error(status.error || "Render failed");
      }
      this.updateExportProgress(
        status?.status === "processing" ? 75 : 50,
        status?.status === "processing" ? "Rendering…" : "Queued…"
      );
      intervalMs = Math.min(intervalMs + 500, 4000); // gentle backoff
    }
    throw new Error("Export timed out");
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private async triggerDownload(url: string, fileName: string): Promise<void> {
    // Preferred: fetch the PDF and download it as a same-origin blob so the
    // `download` filename is honoured. Requires S3 CORS to allow GET.
    try {
      const res = await fetch(url);
      if (!res.ok) {
        throw new Error(`download ${res.status}`);
      }
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(objectUrl), 4000);
      return;
    } catch {
      // Fallback: navigate to the presigned URL directly. Its
      // content-disposition=attachment makes the browser download it. Navigation
      // is NOT subject to CORS, and same-tab (no target=_blank) avoids popup
      // blocking when this runs outside the original click gesture.
      const a = document.createElement("a");
      a.href = url;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  }

  private sanitizeFileName(name: string): string {
    return (
      String(name || "Document")
        .replace(/[^\w.-]+/g, "_")
        .replace(/^[._-]+|[._-]+$/g, "")
        .slice(0, 80) || "Document"
    );
  }

  /** Lighter sanitiser for a user-typed file name: keep spaces, drop only the
   *  characters that are illegal in file names. */
  private sanitizeUserFileName(name: string): string {
    const cleaned = String(name || "")
      // eslint-disable-next-line no-control-regex
      .replace(/[\\/:*?"<>|]/g, "")
      .replace(/\.pdf$/i, "")
      .replace(/\.+$/, "")
      .trim()
      .slice(0, 120);
    return cleaned || "Document";
  }

  /** Best-effort document title: first heading/line of page 1, else "Document". */
  /** Wait (briefly) for any in-flight reflow to settle before snapshotting. */
  private async waitForReflowSettled(): Promise<void> {
    const deadline = Date.now() + 5000;
    while (this.reflowInProgress && Date.now() < deadline) {
      await this.delay(120);
    }
  }

  private updateExportProgress(percent: number, text: string): void {
    this.exportProgress = Math.max(0, Math.min(100, Math.round(percent)));
    this.exportProgressText = text;
  }

  /**
   * Build the self-contained HTML uploaded to the server PDF renderer. Each
   * non-empty page becomes one exact A4 sheet (matching the editor's fixed pages)
   * with the header logo and a footer. All chart/image content is already inline
   * data-URLs and table/text styling is inline, so the document renders
   * identically in headless Chromium without the app's CSS. Editor-only chrome
   * (resize handles, selection outlines, toolbars) is hidden.
   */
  private buildPdfExportHtml(title: string, coverImageDataUrl = ""): string {
    const headerLogoSrc = this.getExportHeaderLogoSrc();
    const pages = (this.pages || []).filter(
      (p) => !this.isPageHtmlEffectivelyEmpty(p?.html || "")
    );
    const pageBlocks = pages
      .map((page, index) => {
        const bodyHtml = String(page?.html || "<p><br></p>");
        // The MOR cover page is a full-bleed image: no header logo, footer, or
        // body padding — the image is attached edge-to-edge to the sheet.
        if (bodyHtml.includes("mor-cover-image")) {
          // Inline the cover image as a data URL so the off-app PDF renderer
          // embeds the bytes (a plain asset URL isn't reliably fetchable there).
          const coverHtml = coverImageDataUrl
            ? bodyHtml.replace(
                /(<img\b[^>]*\bclass="[^"]*mor-cover-image[^"]*"[^>]*>)/,
                (tag) =>
                  tag.replace(/\bsrc="[^"]*"/, () => `src="${coverImageDataUrl}"`)
              )
            : bodyHtml;
          return `
          <section class="pdf-page pdf-cover-page">
            <div class="pdf-page-body pdf-cover-body">${coverHtml}</div>
          </section>`;
        }
        return `
          <section class="pdf-page">
            <div class="pdf-page-header">
              <img src="${headerLogoSrc}" alt="" class="pdf-logo" />
            </div>
            <div class="pdf-page-body">${bodyHtml}</div>
            <div class="pdf-page-footer">Page ${index + 1}</div>
          </section>`;
      })
      .join("");

    return `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>${this.escapeHtml(title)}</title>
<style>
  @page { size: A4; margin: 0; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; background: #ffffff; }
  body {
    font-family: Arial, sans-serif;
    color: #1d2939;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .pdf-page {
    width: 210mm;
    height: 297mm;
    overflow: hidden;
    background: #ffffff;
    display: flex;
    flex-direction: column;
    page-break-after: always;
  }
  .pdf-page:last-child { page-break-after: auto; }
  .pdf-page-header {
    height: 84px;
    padding: 14px 24px 8px;
    display: flex;
    justify-content: flex-end;
    align-items: center;
  }
  .pdf-logo { max-height: 56px; width: auto; object-fit: contain; }
  .pdf-page-body {
    flex: 1;
    padding: 0 56px;
    font-size: 16px;
    line-height: 1.5;
    overflow: hidden;
  }
  .pdf-page-footer {
    height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #64748b;
    font-size: 12px;
  }
  .pdf-page-body p,
  .pdf-page-body li,
  .pdf-page-body div { margin: 0; }
  .pdf-page-body img,
  .pdf-page-body .chart-snapshot-image,
  .pdf-page-body .resizable-image img { max-width: 100%; height: auto; }
  .pdf-page-body table { width: 100%; border-collapse: collapse; }
  .pdf-page-body a { color: #1d4ed8; text-decoration: underline; }
  /* Strip editor-only chrome that may be present in the saved markup. */
  .image-resize-handle,
  .image-handle,
  .image-rotate-handle,
  .image-quick-toolbar,
  .table-add-control { display: none !important; }
  .resizable-image.is-selected { outline: none !important; }
  /* Full-bleed MOR cover page: image fills the whole sheet, no chrome, with the
     MOR name overlaid on the navy band. */
  .pdf-cover-body { padding: 0 !important; position: relative; }
  .pdf-cover-body .mor-cover { position: relative; width: 100%; height: 100%; margin: 0; }
  .pdf-cover-body .mor-cover-image {
    width: 100% !important;
    height: 100% !important;
    max-width: none !important;
    object-fit: fill;
    display: block;
  }
  .pdf-cover-body .mor-cover-title {
    position: absolute;
    top: 14%;
    left: 16%;
    right: 8%;
    height: 42%;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    color: #ffffff;
    line-height: 1.2;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }
  .pdf-cover-body .mor-cover-line { margin: 0 0 8px; }
  .pdf-cover-body .mor-cover-line-1 { font-size: 40px; font-weight: 800; }
  .pdf-cover-body .mor-cover-line-2 { font-size: 30px; font-weight: 500; }
  .pdf-cover-body .mor-cover-line-3 { font-size: 26px; font-weight: 700; }
</style>
</head>
<body>${pageBlocks}</body>
</html>`;
  }

  private buildExportDocumentHtml(title: string, forPrint: boolean): string {
    const headerLogoSrc = this.getExportHeaderLogoSrc();
    const headerBorderCss = forPrint ? "none" : "1px solid #e2e8f0";
    const pageBlocks = (this.pages || [])
      .map((page, index) => {
        const bodyHtml = String(page?.html || "<p><br></p>");
        return `
          <section class="export-page">
            <div class="export-page-header">
              <img src="${headerLogoSrc}" alt="Header Logo" class="export-page-logo" />
            </div>
            <div class="export-page-body">${bodyHtml}</div>
            <div class="export-page-footer">Page ${index + 1}</div>
          </section>
        `;
      })
      .join("");

    const printStyle = forPrint
      ? `
        @page { size: A4; margin: 12mm; }
        body { background: #fff; }
      `
      : "";

    return `
      <!doctype html>
      <html>
      <head>
        <meta charset="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <title>${this.escapeHtml(title)}</title>
        <style>
          ${printStyle}
          * { box-sizing: border-box; }
          body {
            margin: 0;
            padding: 16px;
            font-family: Arial, sans-serif;
            color: #0f172a;
          }
          .export-page {
            width: 100%;
            max-width: 794px;
            min-height: 1123px;
            margin: 0 auto 16px;
            border: 1px solid #d5deea;
            border-radius: 8px;
            background: #fff;
            display: flex;
            flex-direction: column;
            page-break-after: always;
          }
          .export-page-header {
            height: 76px;
            padding: 10px 18px 8px;
            display: flex;
            justify-content: flex-end;
            align-items: center;
            border-bottom: ${headerBorderCss};
          }
          .export-page-logo {
            max-height: 56px;
            width: auto;
            object-fit: contain;
          }
          .export-page:last-child { page-break-after: auto; margin-bottom: 0; }
          .export-page-body {
            flex: 1;
            padding: 24px 28px;
            font-size: 16px;
            line-height: 1.5;
            overflow: hidden;
          }
          .export-page-footer {
            text-align: center;
            padding: 8px 0 12px;
            color: #64748b;
            font-size: 13px;
          }
          .export-page-body img {
            max-width: 100%;
            height: auto;
          }
          .export-page-body table {
            width: 100%;
            border-collapse: collapse;
          }
          .export-page-body table td,
          .export-page-body table th {
            border: 1px solid #cbd5e1;
            padding: 4px 6px;
          }
        </style>
      </head>
      <body>
        ${pageBlocks}
      </body>
      </html>
    `;
  }

  private buildWordDocumentHtml(
    title: string,
    headerLogoSrc: string,
    pagesForExport: Array<{ id: number; html: string }>
  ): string {
    const pageBlocks = (pagesForExport || [])
      .map((page, index) => {
        const bodyHtml = String(page?.html || "<p><br></p>");
        const pageBreak = index < (pagesForExport.length - 1) ? '<br clear="all" style="page-break-before:always;mso-break-type:section-break;" />' : "";
        return `
          <div style="width:794px;min-height:1123px;margin:0 auto 12px;border:1px solid #d5deea;background:#ffffff;">
            <table role="presentation" cellspacing="0" cellpadding="0" border="0" style="width:100%;border-collapse:collapse;border-bottom:1px solid #e2e8f0;">
              <tr>
                <td style="height:76px;padding:10px 18px 8px 18px;text-align:right;vertical-align:middle;overflow:hidden;">
                  <img
                    src="${headerLogoSrc}"
                    alt="Header Logo"
                    width="170"
                    height="56"
                    style="width:170px;height:56px;display:inline-block;vertical-align:middle;border:0;outline:none;text-decoration:none;"
                  />
                </td>
              </tr>
            </table>
            <div style="padding:24px 28px;font-size:16px;line-height:1.5;">
              ${bodyHtml}
            </div>
            <div style="text-align:center;padding:8px 0 12px;color:#64748b;font-size:13px;">Page ${index + 1}</div>
          </div>
          ${pageBreak}
        `;
      })
      .join("");

    return `
      <html xmlns:o="urn:schemas-microsoft-com:office:office"
            xmlns:w="urn:schemas-microsoft-com:office:word"
            xmlns="http://www.w3.org/TR/REC-html40">
      <head>
        <meta charset="utf-8" />
        <title>${this.escapeHtml(title)}</title>
        <!--[if gte mso 9]>
        <xml>
          <w:WordDocument>
            <w:View>Print</w:View>
            <w:Zoom>100</w:Zoom>
          </w:WordDocument>
        </xml>
        <![endif]-->
        <style>
          @page Section1 { size:595.3pt 841.9pt; margin:24pt 24pt 24pt 24pt; }
          div.Section1 { page:Section1; }
          body { font-family: Arial, sans-serif; color:#0f172a; }
          img { max-width: 100%; height: auto; }
          table { border-collapse: collapse; width: 100%; }
          td, th { border: 1px solid #cbd5e1; padding: 4px 6px; }
        </style>
      </head>
      <body>
        <div class="Section1">
          ${pageBlocks}
        </div>
      </body>
      </html>
    `;
  }

  private isDocPageElementEffectivelyEmpty(pageEl: HTMLElement): boolean {
    const editor = pageEl.querySelector(".page-editor") as HTMLElement | null;
    if (!editor) {
      return true;
    }

    const hasVisualContent =
      !!editor.querySelector("img[src]") ||
      !!editor.querySelector(".report-chart img.chart-snapshot-image") ||
      !!editor.querySelector(".report-table-block tbody tr") ||
      !!editor.querySelector("table tbody tr") ||
      !!editor.querySelector("hr") ||
      !!editor.querySelector("iframe[src]") ||
      !!editor.querySelector("video[src]") ||
      !!editor.querySelector("canvas") ||
      !!editor.querySelector("svg path, svg rect, svg circle, svg line, svg polyline, svg polygon");
    if (hasVisualContent) {
      return false;
    }

    const text = String(editor.textContent || "")
      .replace(/\u00a0/g, " ")
      .replace(/[\u200B-\u200D\uFEFF]/g, "")
      .replace(/\s+/g, " ")
      .trim();
    return text.length === 0;
  }

  private isPageHtmlEffectivelyEmpty(html: string): boolean {
    const container = document.createElement("div");
    container.innerHTML = String(html || "");

    const hasVisualContent =
      !!container.querySelector("img[src]") ||
      !!container.querySelector(".report-chart img.chart-snapshot-image") ||
      !!container.querySelector(".report-table-block tbody tr") ||
      !!container.querySelector("table tbody tr") ||
      !!container.querySelector("hr") ||
      !!container.querySelector("iframe[src]") ||
      !!container.querySelector("video[src]") ||
      !!container.querySelector("canvas") ||
      !!container.querySelector("svg path, svg rect, svg circle, svg line, svg polyline, svg polygon");
    if (hasVisualContent) {
      return false;
    }

    const text = String(container.textContent || "")
      .replace(/\u00a0/g, " ")
      .replace(/[\u200B-\u200D\uFEFF]/g, "")
      .replace(/\s+/g, " ")
      .trim();
    return text.length === 0;
  }

  private async getExportHeaderLogoDataUrl(
    width: number,
    height: number
  ): Promise<string> {
    const source = this.getExportHeaderLogoSrc();
    if (!source) {
      return "";
    }

    try {
      const dataUrl = await this.resizeImageToDataUrl(source, width, height);
      return dataUrl || source;
    } catch {
      return source;
    }
  }

  private resizeImageToDataUrl(
    src: string,
    width: number,
    height: number
  ): Promise<string> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          resolve(src);
          return;
        }

        ctx.clearRect(0, 0, width, height);
        const scale = Math.min(width / img.width, height / img.height);
        const drawW = img.width * scale;
        const drawH = img.height * scale;
        const dx = (width - drawW) / 2;
        const dy = (height - drawH) / 2;
        ctx.drawImage(img, dx, dy, drawW, drawH);
        resolve(canvas.toDataURL("image/png"));
      };
      img.onerror = () => reject(new Error("Failed to load image"));
      img.src = src;
    });
  }

  /**
   * Load the MOR cover image and return it as a JPEG data URL at its native size,
   * so the server-rendered PDF embeds the bytes directly (a plain asset URL isn't
   * reliably fetchable by the off-app renderer). Returns "" on any failure so the
   * export falls back to the URL src.
   */
  private getMorCoverImageDataUrl(): Promise<string> {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        try {
          const canvas = document.createElement("canvas");
          canvas.width = img.naturalWidth || img.width;
          canvas.height = img.naturalHeight || img.height;
          const ctx = canvas.getContext("2d");
          if (!ctx) {
            resolve("");
            return;
          }
          ctx.drawImage(img, 0, 0);
          resolve(canvas.toDataURL("image/jpeg", 0.92));
        } catch {
          resolve("");
        }
      };
      img.onerror = () => resolve("");
      img.src = this.getMorCoverImageSrc();
    });
  }

  private getExportHeaderLogoSrc(): string {
    const renderedLogo = document.querySelector(".page-header-logo") as HTMLImageElement | null;
    if (renderedLogo?.src) {
      return renderedLogo.src;
    }
    try {
      return new URL("assets/images/logo-wwtp.png", document.baseURI).toString();
    } catch {
      return "assets/images/logo-wwtp.png";
    }
  }

  private loadDashboardGroupFromQuery(): void {
    this.route.queryParamMap.subscribe((params) => {
      const dashboardGroupId = Number(params.get("dashboardGroupId"));
      this.debugLog("[PDF] queryParam dashboardGroupId", dashboardGroupId);
      if (!dashboardGroupId || Number.isNaN(dashboardGroupId)) {
        this.editorLoading = false;
        // Blank/standalone editor: offer any prior local draft and allow saving.
        void this.checkForDraft();
        this.documentReady = true;
        return;
      }
      if (this.lastLoadedDashboardGroupId === dashboardGroupId) {
        return;
      }
      this.lastLoadedDashboardGroupId = dashboardGroupId;
      this.userRestoredDraft = false;
      this.editorLoading = true;
      // Offer a saved draft (if any) for this group while fresh data loads.
      void this.checkForDraft();

      // Reuse the payload already fetched for the entry dialog's data config
      // instead of refetching the (potentially multi-MB) with-reports response.
      const cached = this.cachedGroupForRender;
      if (cached && cached.id === dashboardGroupId) {
        if (this.userRestoredDraft) {
          this.editorLoading = false;
          return;
        }
        void this.renderDashboardGroupIntoDocument(cached.data);
        return;
      }

      this.reportService.getDashboardGroupWithReports(dashboardGroupId).subscribe({
        next: (response) => {
          // The user restored a draft while this was loading — keep their document.
          if (this.userRestoredDraft) {
            this.editorLoading = false;
            return;
          }
          this.debugLog("[PDF] API response success", response?.success);
          if (!response?.success || !response?.data) {
            this.editorLoading = false;
            this.documentReady = true;
            return;
          }
          this.debugLog("[PDF] API dashboards count", response.data.dashboards?.length || 0);
          void this.renderDashboardGroupIntoDocument(response.data);
        },
        error: () => {
          // Keep editor usable even if loading fails.
          this.editorLoading = false;
          this.documentReady = true;
        },
      });
    });
  }

  /** Absolute URL of the MOR cover image (resolves in the editor and re-hydrated
   *  saved documents). For the server-rendered PDF it's inlined as a data URL. */
  private getMorCoverImageSrc(): string {
    const relative = "assets/images/6628c30b-0b7a-4017-a730-78f9982e41f8-0.jpg";
    try {
      return new URL(relative, document.baseURI).toString();
    } catch {
      return relative;
    }
  }

  /**
   * HTML for the reserved MOR cover page (page 1): the cover image with the MOR
   * name overlaid. Kept as its own block so it survives reflow and exports into
   * the PDF (where the image src is swapped for an inline data URL).
   */
  private buildMorCoverPageHtml(): string {
    const src = this.getMorCoverImageSrc();
    // The MOR name lines entered in the entry dialog are stacked on the cover's
    // navy band (positioned/styled via CSS). Each line gets a hierarchy class
    // (line 1 largest, line 2 medium, line 3+ smaller-bold) to match the design.
    // Baked into the page HTML so it persists in saved documents and the PDF.
    const linesHtml = this.morNameLinesForRender()
      .map((line, i) => {
        const tierClass =
          i === 0
            ? "mor-cover-line-1"
            : i === 1
            ? "mor-cover-line-2"
            : "mor-cover-line-3";
        return `<div class="mor-cover-line ${tierClass}">${this.escapeHtml(
          line
        )}</div>`;
      })
      .join("");
    return (
      `<div class="mor-cover">` +
      `<img src="${src}" alt="MOR cover" class="mor-cover-image" />` +
      `<div class="mor-cover-title">${linesHtml}</div>` +
      `</div>`
    );
  }

  /**
   * Build the reserved page-2 Table of Contents for a MOR report: each dashboard
   * is a numbered heading (1., 2., …) and its data reports the numbered children
   * (1.1, 1.2, …). Page-number cells are placeholders here — they're resolved
   * from the paginated DOM by refreshTableOfContents() (via the shared
   * data-toc-page-for / data-report-id markers) after each reflow and before
   * save/export, so they stay correct if content later shifts pages.
   */
  private buildMorTableOfContentsHtml(): string {
    const rows: string[] = [];
    this.morTocGroups.forEach((group, gi) => {
      const dashNo = gi + 1;
      // The dashboard's page is where its first listed report lands.
      const firstReportId = group.reports[0]?.id ?? null;
      rows.push(this.morTocLevel1Row(`${dashNo}. ${group.name}`, firstReportId));
      group.reports.forEach((report, ri) => {
        rows.push(
          this.morTocLevel2Row(`${dashNo}.${ri + 1}`, report.name, report.id)
        );
      });
    });

    // data-toc-block + data-page-exclusive mark this as the (page-owning) TOC so
    // refreshTableOfContents() fills the page numbers. contenteditable=false keeps
    // the auto-managed index from being edited by hand.
    // data-report-id is a synthetic sentinel (not a real report) so the reflow's
    // block-split continuation matching keys off it cleanly when the TOC spans
    // multiple pages. It's never referenced by a page-number cell.
    return `
      <section class="report-block toc-report-block" data-report-id="mor-toc" data-toc-block="true" data-page-exclusive="1">
        <div class="imported-report-content toc-container" contenteditable="false" style="margin:6px 0 20px;padding:4px 2px 12px;background:#ffffff;">
          <h2 class="toc-title" style="margin:0 0 14px;font-size:19px;font-weight:700;color:#1f3864;text-align:left;">Table of Contents</h2>
          ${rows.join("") || '<div style="color:#94a3b8;font-size:13px;">No reports to list.</div>'}
        </div>
      </section>
    `;
  }

  /** MOR TOC dashboard heading row ("N. Dashboard …… page"). */
  private morTocLevel1Row(label: string, pageRefId: number | null): string {
    const pageAttr = pageRefId != null ? `data-toc-page-for="${pageRefId}"` : "";
    return `
      <div class="toc-row toc-l1" style="display:flex;align-items:flex-end;gap:6px;margin:12px 0 4px;font-size:14px;font-weight:700;color:#1f4e79;line-height:1.5;">
        <span style="flex:0 1 auto;overflow:hidden;white-space:nowrap;text-overflow:ellipsis;">${this.escapeHtml(
          label
        )}</span>
        <span style="flex:1 1 auto;border-bottom:1px dotted #6b7a90;transform:translateY(-4px);"></span>
        <span ${pageAttr} style="flex:0 0 auto;min-width:26px;text-align:right;font-weight:700;font-variant-numeric:tabular-nums;">…</span>
      </div>`;
  }

  /** MOR TOC report row ("N.M Report …… page"), indented under its dashboard. */
  private morTocLevel2Row(
    numLabel: string,
    name: string,
    pageRefId: number | null
  ): string {
    const pageAttr = pageRefId != null ? `data-toc-page-for="${pageRefId}"` : "";
    return `
      <div class="toc-row toc-l2" style="display:flex;align-items:flex-end;gap:6px;margin:3px 0 3px 26px;font-size:13px;font-weight:400;color:#1f3864;line-height:1.5;">
        <span style="flex:0 1 auto;overflow:hidden;white-space:nowrap;text-overflow:ellipsis;"><span style="display:inline-block;min-width:38px;">${this.escapeHtml(
          numLabel
        )}</span>${this.escapeHtml(name)}</span>
        <span style="flex:1 1 auto;border-bottom:1px dotted #9aa0a6;transform:translateY(-4px);"></span>
        <span ${pageAttr} style="flex:0 0 auto;min-width:24px;text-align:right;font-variant-numeric:tabular-nums;">…</span>
      </div>`;
  }

  private async renderDashboardGroupIntoDocument(
    dashboardGroup: DashboardGroupWithReportsResponse
  ): Promise<void> {
    // Restrict the document to the data window chosen in the entry dialog.
    dashboardGroup = this.clipReportsToWindow(dashboardGroup);
    this.reportContentCache.clear();
    this.debugLog("[PDF] render group id", dashboardGroup.dashboardGroupId);

    // Capture the render order so each Table of Contents block can index only what
    // follows it. collectRenderReports uses the same layout order + group-wide
    // dedup as the render loop below, so it lines up one-to-one with how reports
    // land in the document. Page numbers are filled in after pagination settles
    // (refreshTableOfContents).
    this.tocOrder = this.collectRenderReports(dashboardGroup).map((report) => ({
      reportId: report.reportId,
      name: (report.reportName || "Report").trim() || "Report",
      isToc: this.isTocReportName(report.reportName),
      isMorHead: this.isMorHeadingReport(report),
    }));

    const allSections: string[] = [];
    const dashboards = dashboardGroup.dashboards || [];
    this.debugLog("[PDF] dashboards to render", dashboards.length);

    // Dedupe reports across the WHOLE group, not just within a dashboard. The
    // same report can be placed on several dashboards in a group; rendering the
    // continuous stream would otherwise repeat it (e.g. a long table appearing
    // many times). getUniqueReports() only dedupes within one dashboard.
    const seenReportKeys = new Set<string>();
    // Rebuilt below for the MOR page-2 Table of Contents.
    const morTocGroups: Array<{
      name: string;
      reports: Array<{ id: number; name: string }>;
    }> = [];
    for (let i = 0; i < dashboards.length; i += 1) {
      const dashboard = dashboards[i];
      const dashboardName = this.escapeHtml(
        dashboard.dashboardName || `Dashboard ${i + 1}`
      );
      // Collect this dashboard's listed reports for the Table of Contents (data
      // reports only — text notes and section headings are excluded).
      const tocGroup = {
        name: dashboard.dashboardName || `Dashboard ${i + 1}`,
        reports: [] as Array<{ id: number; name: string }>,
      };

      const reports = this.getUniqueReports(
        this.orderReportsByLayout(dashboard)
      );
      this.debugLog(
        "[PDF] dashboard report counts",
        dashboardName,
        "raw:",
        dashboard.reports?.length || 0,
        "unique:",
        reports.length
      );

      // Lay every report out in one continuous stream (no per-text-report or
      // per-dashboard page splits). reflowFrom() only pushes overflow forward and
      // never backfills, so pre-splitting here left each report/title on its own
      // near-empty sheet. A single stream lets reflow pack each page to capacity.
      for (let r = 0; r < reports.length; r += 1) {
        const report = reports[r];
        const key = this.getReportDedupKey(report);
        if (seenReportKeys.has(key)) {
          this.debugLog("[PDF] duplicate report skipped (group-wide)", key);
          continue;
        }
        seenReportKeys.add(key);
        const section = await this.buildReportSection(report);
        this.debugLog(
          "[PDF][PLACE] +report",
          allSections.length + 1,
          "dashboard:",
          dashboardName,
          "name:",
          report.reportName,
          "type:",
          String(report.parameters?.[0]?.graphType || "?"),
          "htmlLen:",
          section.length
        );
        allSections.push(section);

        // Add to the Table of Contents grouping. Text/note reports ARE included;
        // only section-heading (mor_head) reports and any TOC report are skipped.
        if (
          !this.isMorHeadingReport(report) &&
          !this.isTocReportName(report.reportName)
        ) {
          // Same name the report renders its chart/table title with; fall back to
          // the first parameter's name, then a generic label.
          const reportName =
            (
              report.reportName ||
              report.parameters?.[0]?.paramName ||
              "Report"
            ).trim() || "Report";
          tocGroup.reports.push({ id: report.reportId, name: reportName });
        }
      }

      if (tocGroup.reports.length) {
        morTocGroups.push(tocGroup);
      }
    }
    this.morTocGroups = morTocGroups;

    if (!allSections.length) {
      allSections.push("<p>No reports found for this dashboard group.</p>");
    }

    this.debugLog(
      "[PDF][PLACE] built",
      allSections.length,
      "report sections, combined htmlLen:",
      allSections.join("").length,
      "→ laying out as one stream, then paginating"
    );

    // MOR reports reserve only page 1 (the cover). The Table of Contents then
    // LEADS the flowing content as a page-exclusive block, so pagination gives it
    // its own page (page 2) and, when it's too long to fit, continues it onto the
    // next page(s) — reports begin only on the page after the TOC finishes.
    this.reservedLeadingPages = this.isMorReport ? 1 : 0;

    const bodySections = this.isMorReport
      ? [this.buildMorTableOfContentsHtml(), ...allSections]
      : allSections;
    const contentHtml = this.justifyDocumentText(bodySections.join(""));
    this.pages = [];
    for (let p = 0; p < this.reservedLeadingPages; p += 1) {
      // Page 1 = MOR cover image.
      this.pages.push({ id: p + 1, html: this.buildMorCoverPageHtml() });
    }
    this.pages.push({ id: this.reservedLeadingPages + 1, html: contentHtml });
    this.nextPageId = this.reservedLeadingPages + 2;
    // Open on the cover page (page 1) so the document starts at the cover rather
    // than jumping down to the reports.
    this.activePageIndex = 0;

    setTimeout(() => {
      this.hydratePagesToDom(true);
      this.reflowFrom(this.reservedLeadingPages);
      this.focusActivePage();
      this.updateDocumentStats();
      this.debugDumpPages("after-render");
      // Heights settle a tick after reflow; dump the live DOM so we can confirm
      // how reports landed across pages and whether any page came out blank.
      setTimeout(() => this.debugDumpPageDom("after-render+settle"), 0);
      this.editorLoading = false;
      // Document is now stable — enable autosave of further user edits.
      this.documentReady = true;
    }, 0);
  }

  private isTextReport(report: DashboardReportData): boolean {
    return String(report.parameters?.[0]?.graphType || "").toLowerCase() === "text";
  }

  private async buildDashboardSection(
    dashboard: DashboardWithReportsItem,
    dashboardNumber: number
  ): Promise<string> {
    const dashboardName = this.escapeHtml(
      dashboard.dashboardName || `Dashboard ${dashboardNumber}`
    );
    const reports = this.getUniqueReports(dashboard.reports || []);
    this.debugLog(
      "[PDF] dashboard report counts",
      dashboardName,
      "raw:",
      dashboard.reports?.length || 0,
      "unique:",
      reports.length
    );

    const reportSections: string[] = [];
    for (let i = 0; i < reports.length; i += 1) {
      reportSections.push(await this.buildReportSection(reports[i]));
    }

    return `
      <h2>${dashboardNumber}. ${dashboardName}</h2>
      <p>Type: ${this.escapeHtml(dashboard.dashboardType || "-")}</p>
      ${reportSections.join("") || "<p>No reports configured.</p>"}
    `;
  }

  /**
   * Order a dashboard's reports to match the on-screen grid reading order:
   * row by row top-to-bottom, left-to-right within a row.
   *
   * This mirrors the live dashboard's cell→report binding
   * (dashboard-report-view.loadDashboardLayout) so the PDF renders reports in the
   * SAME order the user sees on screen. Each grid cell (layoutConfig.items, sorted
   * by y then x) is bound to its report by, in priority:
   *   1. an explicit reportId stamped on the cell,
   *   2. the report whose section id equals the cell id
   *      (report.metadata.sectionId or report.layoutConfig.id),
   *   3. a reading-order fallback for reports that carry no section.
   * The previous implementation matched only by stamped reportId and otherwise
   * fell back to positional array index, which did NOT match the grid and made
   * reports appear shuffled.
   */
  private orderReportsByLayout(
    dashboard: DashboardWithReportsItem
  ): DashboardReportData[] {
    const reports = dashboard?.reports || [];
    const items = dashboard?.layoutConfig?.items;
    if (!Array.isArray(items) || items.length === 0 || reports.length <= 1) {
      // No usable dashboard grid → fall back to each report's own grid position.
      return this.orderReportsByOwnPosition(reports);
    }

    const reportByReportId = new Map<number, DashboardReportData>();
    reports.forEach((report) => {
      const rid = Number(report?.reportId);
      if (!Number.isNaN(rid) && !reportByReportId.has(rid)) {
        reportByReportId.set(rid, report);
      }
    });

    const reportBySectionId = new Map<number, DashboardReportData>();
    reports.forEach((report) => {
      const sid = this.getReportSectionId(report);
      if (sid !== null && !reportBySectionId.has(sid)) {
        reportBySectionId.set(sid, report);
      }
    });

    const resolved: (DashboardReportData | undefined)[] = new Array(
      items.length
    ).fill(undefined);
    const assigned = new Set<number>();

    // Pass 1: bind each cell by explicit reportId, then by section id.
    items.forEach((item, index) => {
      const stamped = Number((item as any)?.reportId);
      let match: DashboardReportData | undefined;
      if (!Number.isNaN(stamped) && reportByReportId.has(stamped)) {
        match = reportByReportId.get(stamped);
      } else {
        const cellId = Number((item as any)?.id);
        if (!Number.isNaN(cellId) && reportBySectionId.has(cellId)) {
          match = reportBySectionId.get(cellId);
        }
      }
      const rid = Number(match?.reportId);
      if (match && !Number.isNaN(rid) && !assigned.has(rid)) {
        resolved[index] = match;
        assigned.add(rid);
      }
    });

    const cellsInReadingOrder = items
      .map((item, originalIndex) => ({ item, originalIndex }))
      .sort(
        (a, b) =>
          (Number((a.item as any)?.y) || 0) - (Number((b.item as any)?.y) || 0) ||
          (Number((a.item as any)?.x) || 0) - (Number((b.item as any)?.x) || 0) ||
          a.originalIndex - b.originalIndex
      );

    // Pass 2: fill still-empty cells (reading order) with reports not yet placed.
    const leftovers = reports.filter(
      (report) => !assigned.has(Number(report?.reportId))
    );
    let leftoverIdx = 0;
    cellsInReadingOrder.forEach(({ originalIndex }) => {
      if (resolved[originalIndex] === undefined && leftovers[leftoverIdx]) {
        const report = leftovers[leftoverIdx++];
        resolved[originalIndex] = report;
        assigned.add(Number(report?.reportId));
      }
    });

    // Emit reports in reading order (cells sorted by y then x).
    const result: DashboardReportData[] = [];
    const used = new Set<number>();
    cellsInReadingOrder.forEach(({ originalIndex }) => {
      const report = resolved[originalIndex];
      if (report) {
        const rid = Number(report.reportId);
        if (!used.has(rid)) {
          result.push(report);
          used.add(rid);
        }
      }
    });

    // A report not bound to any layout cell is NOT shown on the on-screen
    // dashboard (the grid / export-absolute view renders only celled reports), so
    // it must not appear in the PDF either. Appending un-celled reports here
    // surfaced reports the dashboard hides — out of sequence at the end — and made
    // re-created copies (same content, but a different reportId that never took a
    // cell) look like duplicated reports. Only when the grid bound NOTHING at all
    // (a malformed layout) do we fall back to every report in its original order,
    // so the document is never left empty.
    if (!result.length) {
      return this.orderReportsByOwnPosition(reports);
    }

    return result;
  }

  /**
   * The grid section id a report was assigned to — from the sectionId we stamp at
   * save time, or the per-report layout config id for older dashboards. Mirrors
   * dashboard-report-view.getReportSectionId. Returns null when unavailable.
   */
  private getReportSectionId(report: DashboardReportData): number | null {
    const metaSection = Number((report as any)?.metadata?.sectionId);
    if (!Number.isNaN(metaSection)) {
      return metaSection;
    }
    const layout =
      (report as any)?.layout_config ?? (report as any)?.layoutConfig;
    if (layout && layout.id !== undefined && layout.id !== null) {
      const layoutId = Number(layout.id);
      if (!Number.isNaN(layoutId)) {
        return layoutId;
      }
    }
    return null;
  }

  /**
   * Fallback ordering by each report's own grid position (layoutConfig.{y,x}),
   * used when the dashboard's layout grid is unavailable. Sorts into reading
   * order (top-to-bottom, then left-to-right). Reports without a position keep
   * their original relative order at the end.
   */
  private orderReportsByOwnPosition(
    reports: DashboardReportData[]
  ): DashboardReportData[] {
    if (!Array.isArray(reports) || reports.length <= 1) {
      return reports;
    }

    const positionOf = (report: DashboardReportData) => {
      const layout =
        (report as any)?.layout_config ?? (report as any)?.layoutConfig;
      const hasX = layout && layout.x !== undefined && layout.x !== null;
      const hasY = layout && layout.y !== undefined && layout.y !== null;
      if (!hasX && !hasY) {
        return null;
      }
      return { x: Number(layout.x) || 0, y: Number(layout.y) || 0 };
    };

    // Only reorder when every report exposes a position; otherwise the data
    // doesn't support reliable grid ordering, so leave the order untouched.
    const positioned = reports.map((report, index) => ({
      report,
      index,
      pos: positionOf(report),
    }));
    if (positioned.some((p) => p.pos === null)) {
      return reports;
    }

    return positioned
      .sort(
        (a, b) =>
          a.pos!.y - b.pos!.y || a.pos!.x - b.pos!.x || a.index - b.index
      )
      .map((p) => p.report);
  }

  private getUniqueReports(reports: DashboardReportData[]): DashboardReportData[] {
    const seen = new Set<string>();
    const unique: DashboardReportData[] = [];

    for (let i = 0; i < reports.length; i += 1) {
      const report = reports[i];
      const key = this.getReportDedupKey(report);
      this.debugLog(
        "[PDF] report key",
        key,
        "id:",
        report.reportId,
        "name:",
        report.reportName
      );
      if (seen.has(key)) {
        this.debugLog("[PDF] duplicate skipped", key);
        continue;
      }
      seen.add(key);
      unique.push(report);
    }

    return unique;
  }

  private getReportDedupKey(report: DashboardReportData): string {
    const reportId = Number(report.reportId);
    if (!Number.isNaN(reportId) && reportId > 0) {
      return `id:${reportId}`;
    }

    return [
      report.reportName || "",
      report.moduleType || "",
      report.interval || "",
      report.period?.startDate || "",
      report.period?.endDate || "",
    ].join("|");
  }

  /**
   * Render the report's description beneath its chart/table, mirroring the live
   * report view's description band (chart-view.component). Returns empty when the
   * report has no description.
   */
  private renderReportDescriptionHtml(report: DashboardReportData): string {
    const description = String((report as any)?.description || "").trim();
    if (!description) {
      return "";
    }
    // Rendered in the same size and colour as the body text (not faded), sitting
    // just under the chart/table.
    return `
      <p class="report-description" style="margin:6px 2px 0;font-size:16px;line-height:1.5;color:#1d2939;text-align:left;">
        ${this.escapeHtml(description)}
      </p>
    `;
  }

  /** True when a report's NAME contains the phrase "Table of Content(s)". */
  private isTocReportName(name: string | null | undefined): boolean {
    return /table\s+of\s+contents?/i.test(String(name || ""));
  }

  /**
   * True when a report's authored CONTENT opens with a "Table of Contents"
   * heading. This is the fallback detector for reports whose configured name
   * isn't literally "Table of Contents" but whose body the user started with that
   * title — so the block is still recognised and auto-generated.
   */
  private isTocContent(html: string | null | undefined): boolean {
    const text = this.toPlainText(String(html || "")).trim();
    return /^table\s+of\s+contents?\b/i.test(text);
  }

  /** A bold, top-level (section heading) Table of Contents row. Rendered as a
   *  block-level div so the reflow engine can flow rows across page boundaries. */
  private tocLevel1Row(label: string, pageRefId: number): string {
    return `
      <div class="toc-row toc-l1" style="display:flex;align-items:flex-end;gap:6px;margin:12px 0 4px;font-size:14px;font-weight:700;color:#1f4e79;line-height:1.5;">
        <span style="flex:0 1 auto;overflow:hidden;white-space:nowrap;text-overflow:ellipsis;">${this.escapeHtml(
          label
        )}</span>
        <span style="flex:1 1 auto;border-bottom:1px dotted #6b7a90;transform:translateY(-4px);"></span>
        <span data-toc-page-for="${pageRefId}" style="flex:0 0 auto;min-width:26px;text-align:right;font-weight:700;font-variant-numeric:tabular-nums;">…</span>
      </div>`;
  }

  /** A numbered, nested (report) Table of Contents row. */
  private tocLevel2Row(num: number, label: string, pageRefId: number): string {
    return `
      <div class="toc-row toc-l2" style="display:flex;align-items:flex-end;gap:6px;margin:3px 0 3px 26px;font-size:13px;font-weight:400;color:#1f3864;line-height:1.5;">
        <span style="flex:0 1 auto;overflow:hidden;white-space:nowrap;text-overflow:ellipsis;"><span style="display:inline-block;min-width:24px;">${num})</span>${this.escapeHtml(
          label
        )}</span>
        <span style="flex:1 1 auto;border-bottom:1px dotted #9aa0a6;transform:translateY(-4px);"></span>
        <span data-toc-page-for="${pageRefId}" style="flex:0 0 auto;min-width:24px;text-align:right;font-variant-numeric:tabular-nums;">…</span>
      </div>`;
  }

  /** True when a report is a MOR Heading (heading-only section) report. */
  private isMorHeadingReport(report: DashboardReportData): boolean {
    return (
      String(report.parameters?.[0]?.graphType || "").toLowerCase() === "mor_head"
    );
  }

  /**
   * Render a MOR Heading report as a bold, navy section heading carrying just the
   * report name. It stays in the normal editable flow (so the reports that follow
   * it sit beneath it) and carries a data-report-id so the Table of Contents can
   * resolve its page.
   */
  private buildMorHeadingSection(report: DashboardReportData): string {
    const title = this.escapeHtml(
      (
        report.reportName ||
        report.parameters?.[0]?.paramName ||
        "Section"
      )
        .toString()
        .trim() || "Section"
    );
    return `
      <section class="report-block mor-heading-block" data-report-id="${report.reportId}">
        <h2 class="mor-heading" style="margin:20px 0 10px;font-size:18px;font-weight:700;color:#1f4e79;line-height:1.4;text-align:left;">${title}</h2>
        <p><br /></p>
      </section>
    `;
  }

  /**
   * Render the Table of Contents block, driven by MOR Heading reports: each MOR
   * Heading that follows this block becomes a bold top-level row, and the normal
   * reports after it become numbered children with a single continuous counter
   * across the whole document. Only what comes AFTER this block is listed — and
   * any content the user authored under the report is ignored: the block is always
   * fully system-generated.
   *
   * Names are laid out now so the block's height is fixed up front; the page-number
   * cells are filled in later by refreshTableOfContents() once pagination settles.
   * The inner container is non-editable so the auto-managed index isn't broken.
   */
  private buildTableOfContentsSection(report: DashboardReportData): string {
    // List everything after this TOC report. If the report can't be located in the
    // captured order, fall back to listing the whole document.
    const tocId = report.reportId;
    const startIndex = this.tocOrder.findIndex(
      (entry) => entry.reportId === tocId
    );
    const entries = (
      startIndex >= 0 ? this.tocOrder.slice(startIndex + 1) : this.tocOrder
    ).filter((entry) => !entry.isToc);

    let counter = 0;
    const rows: string[] = [];
    for (const entry of entries) {
      if (entry.isMorHead) {
        // Section heading → bold top-level row (no number).
        rows.push(this.tocLevel1Row(entry.name, entry.reportId));
      } else {
        counter += 1;
        rows.push(this.tocLevel2Row(counter, entry.name, entry.reportId));
      }
    }

    // data-page-exclusive="1" tells the reflow engine this block must own its
    // page(s): no report shares a page with the TOC, and if it overflows, the
    // continuation pages stay report-free too. The content body is an
    // .imported-report-content div so the engine's text-split machinery can flow
    // the rows across page boundaries (each row is a direct child it can move).
    return `
      <section class="report-block toc-report-block" data-report-id="${report.reportId}" data-toc-block="true" data-page-exclusive="1">
        <div class="imported-report-content toc-container" contenteditable="false" style="margin:6px 0 20px;padding:4px 2px 12px;background:#ffffff;">
          <h2 class="toc-title" style="margin:0 0 14px;font-size:19px;font-weight:700;color:#1f3864;text-align:left;">Table of Contents</h2>
          ${rows.join("") || '<div style="color:#94a3b8;font-size:13px;">No reports to list.</div>'}
        </div>
        <p><br /></p>
      </section>
    `;
  }

  /** True when a block is marked page-exclusive (must own its page). */
  private isExclusiveBlock(el: Element | null | undefined): boolean {
    return (
      el instanceof HTMLElement &&
      el.getAttribute("data-page-exclusive") === "1"
    );
  }

  /**
   * True when a page holds a page-exclusive block alongside other blocks — i.e.
   * the exclusive block isn't yet alone on its page, so reflow must keep pushing
   * the other blocks forward.
   */
  private pageHasUnisolatedExclusive(editor: HTMLElement): boolean {
    if (editor.children.length <= 1) {
      return false;
    }
    for (let i = 0; i < editor.children.length; i += 1) {
      if (this.isExclusiveBlock(editor.children[i])) {
        return true;
      }
    }
    return false;
  }

  /**
   * Fill (or correct) the page numbers in every Table of Contents block from the
   * live paginated DOM: each indexed report's number is the 1-based page on which
   * it first appears. Driven purely by the data-* markers in the DOM, so it works
   * for a freshly rendered group AND a reopened saved document. Safe to call
   * repeatedly — it only rewrites the tiny page-number cells (never the row
   * layout), so it can't itself provoke another reflow. Called after every reflow
   * settles and again right before save/export.
   */
  private refreshTableOfContents(): void {
    const editors = this.pageEditors?.toArray() || [];
    if (!editors.length) {
      return;
    }
    const hasToc = editors.some((ed) =>
      ed.nativeElement.querySelector('[data-toc-block="true"]')
    );
    if (!hasToc) {
      return;
    }

    // reportId → the first (lowest) page number on which it appears.
    const pageOf = new Map<string, number>();
    for (let i = 0; i < editors.length; i += 1) {
      const blocks = editors[i].nativeElement.querySelectorAll("[data-report-id]");
      for (let b = 0; b < blocks.length; b += 1) {
        const id = blocks[b].getAttribute("data-report-id");
        if (id && !pageOf.has(id)) {
          pageOf.set(id, i + 1);
        }
      }
    }

    let changed = false;
    for (let i = 0; i < editors.length; i += 1) {
      const cells = editors[i].nativeElement.querySelectorAll("[data-toc-page-for]");
      for (let c = 0; c < cells.length; c += 1) {
        const cell = cells[c] as HTMLElement;
        const id = cell.getAttribute("data-toc-page-for");
        const page = id ? pageOf.get(id) : undefined;
        const text = page ? String(page) : "–";
        if (cell.textContent !== text) {
          cell.textContent = text;
          changed = true;
        }
      }
    }

    if (changed) {
      this.syncPagesFromDom();
    }
  }

  /**
   * A bold, left-aligned heading carrying the report's name, rendered at the top
   * of every data report section — the single place the name appears. Charts and
   * tables used to repeat it internally (baked into the snapshot image, and as a
   * table title bar) which showed the name twice; both now leave it to this
   * heading, which also covers rich-content reports that render with no name.
   */
  private buildReportNameHeading(report: DashboardReportData): string {
    const name = (report.reportName || "").toString().trim();
    if (!name) {
      return "";
    }
    return `
      <h3 class="report-name-heading" style="margin:18px 0 8px;font-size:22px;font-weight:700;color:#1f4e79;line-height:1.4;text-align:left;">${this.escapeHtml(
        name
      )}</h3>
    `;
  }

  private async buildReportSection(
    report: DashboardReportData
  ): Promise<string> {
    // A MOR Heading is a heading-only section (no data) — render its title as a
    // bold section heading and stop. The reports that follow it render normally.
    if (this.isMorHeadingReport(report)) {
      return this.buildMorHeadingSection(report);
    }

    const { content: richContent, source } = await this.getReportContentReportWise(
      report
    );

    // A report named "Table of Content(s)" — or whose authored body opens with a
    // "Table of Contents" heading — renders as the auto-generated, page-aware
    // index instead of its own data. The user's content is intentionally ignored.
    if (
      this.isTocReportName(report.reportName) ||
      this.isTocContent(richContent)
    ) {
      return this.buildTableOfContentsSection(report);
    }

    // Bold report-name heading shown at the top of every data report section so
    // the name always renders in the editor.
    const nameHeading = this.buildReportNameHeading(report);

    const normalizedRichContent = this.normalizeReportHtmlContent(
      richContent,
      report.reportId
    );
    const controlledRichContent = this.wrapImagesForControl(normalizedRichContent);
    const plainPreview = this.toPlainText(controlledRichContent);
    const graphType = report.parameters?.[0]?.graphType || "-";
    this.debugLog(
      "[PDF] report content",
      "id:",
      report.reportId,
      "name:",
      report.reportName,
      "graphType:",
      graphType,
      "source:",
      source,
      "richLengthRaw:",
      richContent.length,
      "richLengthNormalized:",
      normalizedRichContent.length,
      "richLengthControlled:",
      controlledRichContent.length,
      "preview:",
      plainPreview.slice(0, 120)
    );

    // Table reports are data-driven and must be rendered fresh as a real table.
    // Their saved rich content can be a stale chart snapshot from an earlier
    // render, so never substitute it in for a table report.
    const isTableReport = this.resolvesToTableReport(report);

    // A saved chart snapshot is a pre-rendered image of the full dataset; when the
    // export window clipped this report's data, the snapshot is stale, so redraw
    // it fresh from the windowed values instead of substituting the snapshot.
    const windowClipped = !!(
      report as unknown as { __windowClipped?: boolean }
    ).__windowClipped;

    if (controlledRichContent && !isTableReport && !windowClipped) {
      return `
        <section class="report-block" data-report-id="${report.reportId}">
          ${nameHeading}
          <div class="imported-report-content">
            ${controlledRichContent}
          </div>
          <p><br /></p>
        </section>
      `;
    }

    const chartDataForType = this.extractChartDataForReport(report);
    const resolvedType = this.resolveReportChartType(report, chartDataForType);

    if (resolvedType === "table") {
      this.tableLog(
        "[TABLE][SOURCE]",
        "reportId:",
        report.reportId,
        "name:",
        report.reportName,
        "variant:",
        this.isConsumptionCostTableReport(report)
          ? "consumption-cost"
          : "generic",
        "categories(rows):",
        chartDataForType.categories.length,
        "series(cols):",
        chartDataForType.series.length,
        "columns:",
        chartDataForType.series.map((s) => s.name)
      );

      // The six explicit table variants (tbl_row/col/rfn/cfn/rfm/cfm) render
      // through their dedicated layouts. Legacy "table" reports keep the prior
      // behaviour: OPEX consumption+cost pairs use the grouped layout, others
      // use the generic date/parameter grid.
      const variant = this.tableVariantFor(report);
      const tableHtml = variant.startsWith("tbl_")
        ? this.renderTableVariantHtml(
            report,
            chartDataForType,
            report.reportName || "Report",
            variant
          )
        : this.isConsumptionCostTableReport(report)
          ? this.renderConsumptionCostTableHtml(report)
          : this.renderReportTableHtml(
              chartDataForType,
              report.reportName || "Report",
              report.reportId
            );
      if (tableHtml) {
        return `
          <section class="report-block table-report-block" data-report-id="${report.reportId}">
            ${nameHeading}
            ${tableHtml}
            ${this.renderReportDescriptionHtml(report)}
            <p><br /></p>
          </section>
        `;
      }
    }

    const chartSnapshotHtml = this.renderReportChartSnapshotHtml(report);
    if (chartSnapshotHtml) {
      const reportBlockClass =
        resolvedType === "table" ? "report-block table-report-block" : "report-block";
      return `
        <section class="${reportBlockClass}" data-report-id="${report.reportId}">
          ${nameHeading}
          ${chartSnapshotHtml}
          ${this.renderReportDescriptionHtml(report)}
          <p><br /></p>
        </section>
      `;
    }

    const parameterRows = (report.parameters || [])
      .map(
        (param, index) => `
          <tr>
            <td>${index + 1}</td>
            <td>${this.escapeHtml(param.paramName || "-")}</td>
            <td>${this.escapeHtml(param.graphType || "-")}</td>
            <td>${this.escapeHtml(String(param.benchmark ?? "-"))}</td>
          </tr>
        `
      )
      .join("");

    return `
      <section class="report-block" data-report-id="${report.reportId}">
        ${nameHeading}
        <table class="doc-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Parameter</th>
              <th>Graph Type</th>
              <th>Benchmark</th>
            </tr>
          </thead>
          <tbody>
            ${parameterRows || '<tr><td colspan="4">No parameters</td></tr>'}
          </tbody>
        </table>
        <p><br /></p>
      </section>
    `;
  }

  private renderReportChartHtml(report: DashboardReportData): string {
    const chartData = this.extractChartDataForReport(report);
    if (!chartData.series.length || !chartData.categories.length) {
      return "";
    }
    const chartType = this.resolveReportChartType(report, chartData);

    if (chartType === "bar") {
      return this.renderBarChartHtml(chartData, report.reportName || "Chart");
    }

    if (chartType === "line") {
      return this.renderLineChartHtml(chartData, report.reportName || "Chart");
    }

    // Pie and donut are primarily rendered through snapshot mode.
    return this.renderLineChartHtml(chartData, report.reportName || "Chart");
  }

  private renderReportChartSnapshotHtml(report: DashboardReportData): string {
    const chartData = this.extractChartDataForReport(report);
    if (!chartData.series.length || !chartData.categories.length) {
      return "";
    }

    const title = report.reportName || "Chart";
    const chartType = this.resolveReportChartType(report, chartData);
    if (chartType === "table") {
      return this.renderReportTableHtml(chartData, title, report.reportId);
    }

    // Honour the report's multi-axis setting. The report view enables multi-axis
    // from metadata.multiAxis and, when on, gives every series its own Y-axis
    // (chart-view.component: multiAxisIndices = stats.map((_, i) => i)). Only
    // meaningful with more than one series.
    const multiAxisEnabled =
      (report.metadata as any)?.multiAxis === true &&
      chartData.series.length > 1;

    // The user-entered benchmark per parameter (aligned to the chart series), so
    // the snapshot draws each as a reference line — matching the live chart view.
    const seriesBenchmarks = this.seriesBenchmarksForReport(
      report,
      chartData.series
    ).map((b) => this.toNumberOrNull(b));

    const payload = {
      title,
      chartType,
      chartData,
      options: {
        // The report name renders as the heading above the chart, so it isn't
        // painted inside the snapshot as well. `title` above still carries it for
        // the interval detection, the alt text and the chart editor.
        titleText: "",
        bgStart: "#1f2937",
        bgCenter: "#2b3444",
        bgEnd: "#3f3f46",
        bgOuter: "#111827",
        seriesColors: this.ensureDistinctColors(
          this.defaultChartPalette,
          chartData.series.length
        ),
        multiAxis: multiAxisEnabled,
        multiAxisIndices: multiAxisEnabled
          ? chartData.series.map((_, i) => i)
          : [],
        seriesBenchmarks,
      },
    };
    const dataUrl = this.drawChartSnapshotDataUrl(
      chartData,
      title,
      payload.options,
      chartType
    );
    if (!dataUrl) {
      return "";
    }
    const encodedPayload = this.encodeChartPayload(payload);
    const chartTypeClass = `chart-type-${chartType}`;

    return `
      <div class="report-chart report-chart-image ${chartTypeClass}">
        <span class="resizable-image imported-image-control chart-image-resizable" contenteditable="false" data-chart-payload="${encodedPayload}">
          <img src="${dataUrl}" alt="${this.escapeHtml(title)}" class="chart-snapshot-image" />
        </span>
      </div>
    `;
  }

  private renderReportTableHtml(
    chartData: {
      categories: string[];
      series: Array<{ name: string; data: Array<number | null>; type: "bar" | "line" | "dot" | "bar_label" | "line_label" | "pie" | "donut" | "gauge" | "numeric" | "table" | "text" | "box" }>;
    },
    title: string,
    reportId: number,
    // Optional per-parameter units (aligned to series). When supplied, a
    // dedicated "Unit" row is shown under the header (parameters are columns).
    units: string[] = [],
    // Optional per-parameter benchmarks, shown as a "Benchmark" row under the
    // unit row.
    benchmarks: string[] = []
  ): string {
    // White background with black text for all report tables (readable in the
    // document and when exported to PDF).
    const blockStyle =
      "margin:8px 0 20px;border:1px solid #cccccc;border-radius:8px;overflow:hidden;background:#ffffff;";
    const tableStyle =
      "width:100%;border-collapse:collapse;table-layout:fixed;background:#ffffff;";
    const headCellStyle =
      "padding:9px 12px;border:1px solid #cccccc;text-align:left;color:#000000;background:#f2f2f2;font-size:12px;font-weight:700;";
    const metaCellStyle =
      "padding:9px 12px;border:1px solid #cccccc;text-align:left;color:#000000;background:#fafafa;font-size:12px;font-weight:600;";
    const oddCellStyle =
      "padding:9px 12px;border:1px solid #cccccc;text-align:left;color:#000000;background:#ffffff;font-size:12px;";
    const evenCellStyle =
      "padding:9px 12px;border:1px solid #cccccc;text-align:left;color:#000000;background:#f7f7f7;font-size:12px;";

    const headers = chartData.series
      .map((s) => `<th style="${headCellStyle}">${this.escapeHtml(s.name || "Value")}</th>`)
      .join("");
    const metaRowHtml = (label: string, cells: string[]) =>
      cells.length
        ? `<tr><td style="${metaCellStyle}">${label}</td>${chartData.series
            .map(
              (_, i) =>
                `<td style="${metaCellStyle}">${this.escapeHtml(cells[i] || "")}</td>`
            )
            .join("")}</tr>`
        : "";
    const unitRowHtml = metaRowHtml("Unit", units);
    const benchmarkRowHtml = metaRowHtml("Benchmark", benchmarks);

    const rows = chartData.categories
      .map((category, rowIndex) => {
        const cellStyle = rowIndex % 2 === 0 ? oddCellStyle : evenCellStyle;
        const valueCells = chartData.series
          .map((series) => {
            const value = series.data[rowIndex];
            // Round to 2 decimals (drop trailing zeros) like the OPEX table and
            // the live report, instead of dumping raw floats (e.g. 760.4372).
            const text =
              value === null || Number.isNaN(value)
                ? "N/A"
                : this.formatTableNumber(value);
            return `<td style="${cellStyle}">${this.escapeHtml(text)}</td>`;
          })
          .join("");

        return `
          <tr>
            <td style="${cellStyle}">${this.escapeHtml(this.formatDateLikeLabel(category))}</td>
            ${valueCells}
          </tr>
        `;
      })
      .join("");

    const headerLabels = ["Date", ...chartData.series.map((s) => s.name || "Value")];
    const firstRow = chartData.categories.length
      ? [
          this.formatDateLikeLabel(chartData.categories[0]),
          ...chartData.series.map((s) => {
            const v = s.data[0];
            return v === null || Number.isNaN(v) ? "N/A" : String(v);
          }),
        ]
      : [];
    this.tableLog(
      "[TABLE][RENDER] generic",
      "reportId:",
      reportId,
      "title:",
      title,
      "rows(data):",
      chartData.categories.length,
      "rows(html):",
      (rows.match(/<tr>/g) || []).length,
      "cols:",
      headerLabels.length,
      "headers:",
      headerLabels,
      "firstRow:",
      firstRow
    );

    return `
      <div class="report-table-block" data-table-id="report-${reportId}" style="${blockStyle}">
        <table class="report-data-table" style="${tableStyle}">
          <thead>
            <tr>
              <th style="${headCellStyle}">Date</th>
              ${headers}
            </tr>
            ${unitRowHtml}
            ${benchmarkRowHtml}
          </thead>
          <tbody>
            ${rows}
          </tbody>
        </table>
      </div>
    `;
  }

  /**
   * Resolve the specific table variant id for a report (tbl_row/col/rfn/cfn/
   * rfm/cfm), or "table" for the legacy generic table.
   */
  private tableVariantFor(report: DashboardReportData): string {
    const params = Array.isArray(report.parameters) ? report.parameters : [];
    const tableParam = params.find(
      (p) => this.mapToSupportedChartType(p?.graphType, "line") === "table"
    );
    return String(tableParam?.graphType || "table");
  }

  /** Format a raw data value for a table cell (null/NaN → "N/A"). */
  private formatValueCell(value: number | null | undefined): string {
    return value === null || value === undefined || Number.isNaN(value)
      ? "N/A"
      : this.formatTableNumber(value);
  }

  /** Format a computed statistic (non-finite → "N/A"). */
  private formatStatNumber(value: number): string {
    return Number.isFinite(value) ? this.formatTableNumber(value) : "N/A";
  }

  /** avg/max/min/sum/stdDev over the non-null values of a series. */
  private computeSeriesStats(data: Array<number | null>): {
    avg: number;
    max: number;
    min: number;
    sum: number;
    stdDev: number;
  } {
    const valid = (data || []).filter(
      (v): v is number => v !== null && !Number.isNaN(v)
    );
    if (!valid.length) {
      return { avg: 0, max: 0, min: 0, sum: 0, stdDev: 0 };
    }
    const sum = valid.reduce((a, b) => a + b, 0);
    const avg = sum / valid.length;
    const max = Math.max(...valid);
    const min = Math.min(...valid);
    const variance =
      valid.reduce((a, b) => a + (b - avg) ** 2, 0) / valid.length;
    return { avg, max, min, sum, stdDev: Math.sqrt(variance) };
  }

  /** Human-readable label for an OPEX formula key. */
  private formulaLabelFor(key: string): string {
    switch (key) {
      case "totalCost":
        return "Total Cost";
      case "costM3":
        return "Cost m3";
      case "totalConsumption":
        return "Total Consumption";
      case "consumptionM3":
        return "Consumption m3";
      default:
        return key;
    }
  }

  /**
   * Render a styled report table from header labels and string row cells.
   * Mirrors renderReportTableHtml's white-background styling so every table
   * variant looks identical in the document and exported PDF.
   */
  private buildStyledTableHtml(
    title: string,
    reportId: number,
    headers: string[],
    rows: string[][],
    // Optional per-column units. When provided, a dedicated "Unit" row is
    // rendered directly under the header (used by the column-format tables
    // where parameters are columns, so a unit can't be its own column).
    unitRow?: string[],
    // Optional per-column benchmarks, rendered as a "Benchmark" row directly
    // under the unit row for the same column-format tables.
    benchmarkRow?: string[]
  ): string {
    const blockStyle =
      "margin:8px 0 20px;border:1px solid #cccccc;border-radius:8px;overflow:hidden;background:#ffffff;";
    const tableStyle =
      "width:100%;border-collapse:collapse;table-layout:fixed;background:#ffffff;";
    const headCellStyle =
      "padding:9px 12px;border:1px solid #cccccc;text-align:left;color:#000000;background:#f2f2f2;font-size:12px;font-weight:700;";
    const metaCellStyle =
      "padding:9px 12px;border:1px solid #cccccc;text-align:left;color:#000000;background:#fafafa;font-size:12px;font-weight:600;";
    const oddCellStyle =
      "padding:9px 12px;border:1px solid #cccccc;text-align:left;color:#000000;background:#ffffff;font-size:12px;";
    const evenCellStyle =
      "padding:9px 12px;border:1px solid #cccccc;text-align:left;color:#000000;background:#f7f7f7;font-size:12px;";

    const headerHtml = headers
      .map((h) => `<th style="${headCellStyle}">${this.escapeHtml(h)}</th>`)
      .join("");
    const metaRowHtml = (cells?: string[]) =>
      cells && cells.length
        ? `<tr>${cells
            .map((c) => `<td style="${metaCellStyle}">${this.escapeHtml(c)}</td>`)
            .join("")}</tr>`
        : "";
    const unitRowHtml = metaRowHtml(unitRow);
    const benchmarkRowHtml = metaRowHtml(benchmarkRow);
    const rowsHtml = rows
      .map((cells, rowIndex) => {
        const cellStyle = rowIndex % 2 === 0 ? oddCellStyle : evenCellStyle;
        const tds = cells
          .map((c) => `<td style="${cellStyle}">${this.escapeHtml(c)}</td>`)
          .join("");
        return `<tr>${tds}</tr>`;
      })
      .join("");

    return `
      <div class="report-table-block" data-table-id="report-${reportId}" style="${blockStyle}">
        <table class="report-data-table" style="${tableStyle}">
          <thead><tr>${headerHtml}</tr>${unitRowHtml}${benchmarkRowHtml}</thead>
          <tbody>${rowsHtml}</tbody>
        </table>
      </div>
    `;
  }

  /**
   * Per-parameter units for a report's chart series, aligned to the series
   * order. Series in extractChartDataForReport are built from report.parameters
   * in order, so units match by index; a name lookup is used as a safeguard.
   */
  private seriesUnitsForReport(
    report: DashboardReportData,
    series: Array<{ name: string }>
  ): string[] {
    const params = Array.isArray(report.parameters) ? report.parameters : [];
    const unitByName = new Map<string, string>();
    for (const p of params) {
      const unit = String((p as any)?.metadata?.unit || "").trim();
      if (!unit) {
        continue;
      }
      if (p?.paramName) {
        unitByName.set(String(p.paramName), unit);
      }
      if (p?.paramCode) {
        unitByName.set(String(p.paramCode), unit);
      }
    }
    return series.map((s, i) => {
      const byName = unitByName.get(String(s?.name ?? ""));
      if (byName) {
        return byName;
      }
      return String((params[i] as any)?.metadata?.unit || "").trim();
    });
  }

  /**
   * Per-parameter benchmarks (the user-entered benchmark) aligned to the chart
   * series order — mirrors seriesUnitsForReport. Drives the Benchmark column
   * (row-format tables) and the Benchmark row (column-format tables).
   */
  private seriesBenchmarksForReport(
    report: DashboardReportData,
    series: Array<{ name: string }>
  ): string[] {
    const params = Array.isArray(report.parameters) ? report.parameters : [];
    const benchByName = new Map<string, string>();
    for (const p of params) {
      const bench = this.formatBenchmarkCell((p as any)?.benchmark);
      if (!bench) {
        continue;
      }
      if (p?.paramName) {
        benchByName.set(String(p.paramName), bench);
      }
      if (p?.paramCode) {
        benchByName.set(String(p.paramCode), bench);
      }
    }
    return series.map((s, i) => {
      const byName = benchByName.get(String(s?.name ?? ""));
      if (byName) {
        return byName;
      }
      return this.formatBenchmarkCell((params[i] as any)?.benchmark);
    });
  }

  /** Format a user-provided benchmark for a table cell (empty when absent). */
  private formatBenchmarkCell(
    benchmark: string | number | null | undefined
  ): string {
    if (benchmark === null || benchmark === undefined || benchmark === "") {
      return "";
    }
    return String(benchmark);
  }

  /** Collect the parameter × formula grid shared by Table 5 and Table 6. */
  private buildFormulaGridForReport(report: DashboardReportData): {
    formulaKeys: string[];
    paramCols: { code: string; name: string; unit: string; benchmark: string }[];
    cells: Map<string, string>;
  } {
    const params = Array.isArray(report.parameters) ? report.parameters : [];
    const formulaKeys: string[] = [];
    const paramCols: {
      code: string;
      name: string;
      unit: string;
      benchmark: string;
    }[] = [];
    const cells = new Map<string, string>();

    for (const p of params) {
      const formula = String((p as any)?.formula || "");
      if (!formula) {
        continue;
      }
      if (!formulaKeys.includes(formula)) {
        formulaKeys.push(formula);
      }
      const code = String(p?.paramCode || p?.paramName || "");
      if (!paramCols.some((c) => c.code === code)) {
        // Keep the name clean; unit/benchmark are surfaced in their own row/col.
        const unit = String((p as any)?.metadata?.unit || "").trim();
        const baseName = String(p?.paramName || code);
        paramCols.push({
          code,
          name: baseName,
          unit,
          benchmark: this.formatBenchmarkCell((p as any)?.benchmark),
        });
      }
      const nums = (Array.isArray(p?.values) ? p.values : []).map((v) =>
        this.toNumberOrNull(v?.value)
      );
      const stats = this.computeSeriesStats(nums);
      const agg = String((p as any)?.aggregation || (p as any)?.dataType || "avg");
      const cell = agg === "sum" || agg === "raw" ? stats.sum : stats.avg;
      cells.set(`${code}__${formula}`, this.formatStatNumber(cell));
    }

    return { formulaKeys, paramCols, cells };
  }

  /** Dispatch a table report to the matching variant layout. */
  private renderTableVariantHtml(
    report: DashboardReportData,
    chartData: {
      categories: string[];
      series: Array<{ name: string; data: Array<number | null>; type: "bar" | "line" | "dot" | "bar_label" | "line_label" | "pie" | "donut" | "gauge" | "numeric" | "table" | "text" | "box" }>;
    },
    title: string,
    variant: string
  ): string {
    // Per-parameter units and benchmarks (aligned to the chart series) drive the
    // Unit/Benchmark columns (row-format tables) and rows (column-format tables).
    // The benchmark column/row is dropped entirely when no parameter has one.
    const units = this.seriesUnitsForReport(report, chartData.series);
    const rawBenchmarks = this.seriesBenchmarksForReport(report, chartData.series);
    const benchmarks = rawBenchmarks.some((b) => b) ? rawBenchmarks : [];
    switch (variant) {
      case "tbl_row":
        return this.renderTableRowFormatHtml(
          chartData,
          title,
          report.reportId,
          units,
          benchmarks
        );
      case "tbl_rfn":
        return this.renderTableRowFunctionsHtml(
          chartData,
          title,
          report.reportId,
          units,
          benchmarks
        );
      case "tbl_cfn":
        return this.renderTableColumnFunctionsHtml(
          chartData,
          title,
          report.reportId,
          units,
          benchmarks
        );
      case "tbl_rfm":
        return this.renderTableFormulaHtml(report, title, false);
      case "tbl_cfm":
        return this.renderTableFormulaHtml(report, title, true);
      case "tbl_col":
      default:
        return this.renderReportTableHtml(
          chartData,
          title,
          report.reportId,
          units,
          benchmarks
        );
    }
  }

  /** Table 1 (Row Format): parameters as rows, dates as columns. */
  private renderTableRowFormatHtml(
    chartData: { categories: string[]; series: Array<{ name: string; data: Array<number | null> }> },
    title: string,
    reportId: number,
    units: string[] = [],
    benchmarks: string[] = []
  ): string {
    const showBench = benchmarks.length > 0;
    const headers = [
      "Parameter",
      "Unit",
      ...(showBench ? ["Benchmark"] : []),
      ...chartData.categories,
    ];
    const rows = chartData.series.map((s, i) => [
      s.name || "Value",
      units[i] || "",
      ...(showBench ? [benchmarks[i] || ""] : []),
      ...chartData.categories.map((_, ci) => this.formatValueCell(s.data[ci])),
    ]);
    return this.buildStyledTableHtml(title, reportId, headers, rows);
  }

  /**
   * Table 3 (Row Format - Functions): parameters as rows, function columns.
   * Average & SUM aggregate the per-interval values (the series data); Max, Min
   * & STDV use every raw reading so they show the true peak/trough/spread.
   */
  private renderTableRowFunctionsHtml(
    chartData: { categories: string[]; series: Array<{ name: string; data: Array<number | null>; rawValues?: number[] }> },
    title: string,
    reportId: number,
    units: string[] = [],
    benchmarks: string[] = []
  ): string {
    const showBench = benchmarks.length > 0;
    const headers = [
      "Parameter",
      "Unit",
      ...(showBench ? ["Benchmark"] : []),
      "Average",
      "Max",
      "Min",
      "SUM",
      "STDV",
    ];
    const rows = chartData.series.map((s, i) => {
      const st = this.computeSeriesStats(s.data);
      const raw = this.computeSeriesStats(s.rawValues ?? s.data);
      return [
        s.name || "Value",
        units[i] || "",
        ...(showBench ? [benchmarks[i] || ""] : []),
        this.formatStatNumber(st.avg),
        this.formatStatNumber(raw.max),
        this.formatStatNumber(raw.min),
        this.formatStatNumber(st.sum),
        this.formatStatNumber(raw.stdDev),
      ];
    });
    return this.buildStyledTableHtml(title, reportId, headers, rows);
  }

  /**
   * Table 4 (Column Format - Functions): function rows, parameter columns.
   * Average & SUM aggregate the per-interval values; Max, Min & STDV use every
   * raw reading.
   */
  private renderTableColumnFunctionsHtml(
    chartData: { categories: string[]; series: Array<{ name: string; data: Array<number | null>; rawValues?: number[] }> },
    title: string,
    reportId: number,
    units: string[] = [],
    benchmarks: string[] = []
  ): string {
    const stats = chartData.series.map((s) => this.computeSeriesStats(s.data));
    const rawStats = chartData.series.map((s) =>
      this.computeSeriesStats(s.rawValues ?? s.data)
    );
    const headers = ["Function", ...chartData.series.map((s) => s.name || "Value")];
    const unitRow = ["Unit", ...chartData.series.map((_, i) => units[i] || "")];
    const benchmarkRow = benchmarks.length
      ? ["Benchmark", ...chartData.series.map((_, i) => benchmarks[i] || "")]
      : undefined;
    const rows = [
      ["Average", ...stats.map((st) => this.formatStatNumber(st.avg))],
      ["Max", ...rawStats.map((st) => this.formatStatNumber(st.max))],
      ["Min", ...rawStats.map((st) => this.formatStatNumber(st.min))],
      ["SUM", ...stats.map((st) => this.formatStatNumber(st.sum))],
      ["STDV", ...rawStats.map((st) => this.formatStatNumber(st.stdDev))],
    ];
    return this.buildStyledTableHtml(title, reportId, headers, rows, unitRow, benchmarkRow);
  }

  /**
   * Table 5 / Table 6 (Formula). column=false → parameters as rows, formulas
   * as columns; column=true → formulas as rows, parameters as columns.
   */
  private renderTableFormulaHtml(
    report: DashboardReportData,
    title: string,
    column: boolean
  ): string {
    const { formulaKeys, paramCols, cells } =
      this.buildFormulaGridForReport(report);
    const showBench = paramCols.some((c) => c.benchmark !== "");

    if (column) {
      const headers = ["Formula", ...paramCols.map((c) => c.name)];
      const unitRow = ["Unit", ...paramCols.map((c) => c.unit)];
      const benchmarkRow = showBench
        ? ["Benchmark", ...paramCols.map((c) => c.benchmark)]
        : undefined;
      const rows = formulaKeys.map((fk) => [
        this.formulaLabelFor(fk),
        ...paramCols.map((c) => cells.get(`${c.code}__${fk}`) ?? "N/A"),
      ]);
      return this.buildStyledTableHtml(
        title,
        report.reportId,
        headers,
        rows,
        unitRow,
        benchmarkRow
      );
    }

    const headers = [
      "Parameter",
      "Unit",
      ...(showBench ? ["Benchmark"] : []),
      ...formulaKeys.map((k) => this.formulaLabelFor(k)),
    ];
    const rows = paramCols.map((c) => [
      c.name,
      c.unit,
      ...(showBench ? [c.benchmark] : []),
      ...formulaKeys.map((fk) => cells.get(`${c.code}__${fk}`) ?? "N/A"),
    ]);
    return this.buildStyledTableHtml(title, report.reportId, headers, rows);
  }

  /** A report renders as a table when any parameter's graphType is "table". */
  private resolvesToTableReport(report: DashboardReportData): boolean {
    const params = Array.isArray(report.parameters) ? report.parameters : [];
    return params.some(
      (p) => this.mapToSupportedChartType(p?.graphType, "line") === "table"
    );
  }

  /**
   * OPEX chemical reports carry, for each chemical, a "totalConsumption" param
   * and a "totalCost" param. Those get grouped into one row per chemical with
   * a consumption column and a cost column (image 2).
   */
  private isConsumptionCostTableReport(report: DashboardReportData): boolean {
    const params = Array.isArray(report.parameters) ? report.parameters : [];
    const hasCost = params.some((p) => /cost/i.test(String(p?.formula || "")));
    const hasConsumption = params.some((p) =>
      /consumption/i.test(String(p?.formula || ""))
    );
    return hasCost && hasConsumption;
  }

  private sumParameterValues(param: DashboardReportParameter): number {
    const values = Array.isArray(param?.values) ? param.values : [];
    let sum = 0;
    let hasValue = false;
    for (let i = 0; i < values.length; i += 1) {
      const n = this.toNumberOrNull(values[i]?.value);
      if (n !== null) {
        sum += n;
        hasValue = true;
      }
    }
    return hasValue ? sum : 0;
  }

  private formatTableNumber(value: number | null): string {
    if (value === null || value === undefined || Number.isNaN(value)) {
      return "0";
    }
    // Trim to 2 decimals, drop trailing zeros, no thousands separators (e.g.
    // 3.96, 1445.4, 11151) — matches the live report table.
    return String(Math.round(value * 100) / 100);
  }

  /**
   * Format a chart axis tick / value label. Keeps integers and large values as
   * whole numbers, but preserves decimals for fractional axes (e.g. a multi-axis
   * scale of 0.3, 0.6, 0.9, 1.2, 1.5) instead of rounding them to 0, 0, 0, 1, 1.
   */
  private formatAxisTick(value: number): string {
    if (!Number.isFinite(value)) {
      return "0";
    }
    const rounded = Math.round(value * 100) / 100;
    if (Number.isInteger(rounded)) {
      return String(rounded);
    }
    // Up to 2 decimals, trimming trailing zeros (0.30 → 0.3, 1.50 → 1.5).
    return rounded.toFixed(2).replace(/0+$/, "").replace(/\.$/, "");
  }

  /**
   * Render an OPEX chemical "consumption + total cost" table: one row per
   * chemical (grouped by paramCode), pairing the parameter that carries the
   * "totalConsumption" formula with the one carrying "totalCost".
   */
  private renderConsumptionCostTableHtml(report: DashboardReportData): string {
    const params = Array.isArray(report.parameters) ? report.parameters : [];
    if (!params.length) {
      return "";
    }

    const order: string[] = [];
    const groups = new Map<
      string,
      { name: string; consumption: number | null; cost: number | null }
    >();
    for (let i = 0; i < params.length; i += 1) {
      const param = params[i];
      const code = String(param?.paramCode || param?.paramName || "").trim();
      if (!code) {
        continue;
      }
      if (!groups.has(code)) {
        groups.set(code, {
          name: String(param?.paramName || code),
          consumption: null,
          cost: null,
        });
        order.push(code);
      }
      const group = groups.get(code)!;
      const value = this.sumParameterValues(param);
      const formula = String(param?.formula || "").toLowerCase();
      if (formula.includes("cost")) {
        if (group.cost === null) {
          group.cost = value;
        }
      } else {
        // totalConsumption (or anything that is not a cost) → consumption column.
        if (group.consumption === null) {
          group.consumption = value;
        }
      }
    }

    const firstParam = params[0];
    const moduleName =
      String(firstParam?.moduleName || "Chemicals").trim() || "Chemicals";
    const unit = String(firstParam?.metadata?.unit || "").trim();
    const nameHeader = `${moduleName} Names`;
    const consumptionHeader = unit
      ? `Consumption of ${moduleName} in ${unit.toUpperCase()}`
      : `Consumption of ${moduleName}`;
    const costHeader = "Total Cost";
    const title = report.reportName || `${moduleName} Consumption and Cost`;

    // White background with black text (consistent with all report tables).
    const blockStyle =
      "margin:8px 0 20px;background:#ffffff;border:0;overflow:hidden;";
    const tableStyle =
      "width:100%;border-collapse:collapse;table-layout:fixed;background:#ffffff;";
    const headCellStyle =
      "padding:8px 12px;background:#f2f2f2;color:#000000;font-weight:700;font-size:13px;text-align:center;border:1px solid #cccccc;";
    const nameCellStyle =
      "padding:7px 12px;color:#000000;font-size:12px;font-weight:700;text-align:center;border:1px solid #cccccc;";
    const numCellStyle =
      "padding:7px 12px;color:#000000;font-size:12px;text-align:center;border:1px solid #cccccc;";

    const rows = order
      .map((code, index) => {
        const group = groups.get(code)!;
        const rowBg = index % 2 === 0 ? "#ffffff" : "#f7f7f7";
        return `
          <tr>
            <td style="${nameCellStyle};background:${rowBg}">${this.escapeHtml(group.name)}</td>
            <td style="${numCellStyle};background:${rowBg}">${this.escapeHtml(this.formatTableNumber(group.consumption))}</td>
            <td style="${numCellStyle};background:${rowBg}">${this.escapeHtml(this.formatTableNumber(group.cost))}</td>
          </tr>
        `;
      })
      .join("");

    this.tableLog(
      "[TABLE][RENDER] consumption-cost",
      "reportId:",
      report.reportId,
      "title:",
      title,
      "module:",
      moduleName,
      "unit:",
      unit || "(none)",
      "rows:",
      order.length,
      "headers:",
      [nameHeader, consumptionHeader, costHeader],
      "firstRow:",
      order.length
        ? (() => {
            const g = groups.get(order[0])!;
            return [
              g.name,
              this.formatTableNumber(g.consumption),
              this.formatTableNumber(g.cost),
            ];
          })()
        : []
    );

    return `
      <div class="report-table-block" data-table-id="report-${report.reportId}" style="${blockStyle}">
        <table class="report-data-table" style="${tableStyle}">
          <thead>
            <tr>
              <th style="${headCellStyle};width:40%">${this.escapeHtml(nameHeader)}</th>
              <th style="${headCellStyle};width:32%">${this.escapeHtml(consumptionHeader)}</th>
              <th style="${headCellStyle};width:28%">${this.escapeHtml(costHeader)}</th>
            </tr>
          </thead>
          <tbody>
            ${rows}
          </tbody>
        </table>
      </div>
    `;
  }

  private drawChartSnapshotDataUrl(
    chartData: {
      categories: string[];
      series: Array<{ name: string; data: Array<number | null>; type: "bar" | "line" | "dot" | "bar_label" | "line_label" | "pie" | "donut" | "gauge" | "numeric" | "table" | "text" | "box" }>;
    },
    title: string,
    styleOptions?: {
      bgStart?: string;
      bgCenter?: string;
      bgEnd?: string;
      bgOuter?: string;
      seriesColors?: string[];
      // The text to paint. "" paints no title; undefined falls back to `title`.
      titleText?: string;
      titleColor?: string;
      titleFontSize?: number;
      legendTextColor?: string;
      gridColor?: string;
      axisTextColor?: string;
      dateLabelMode?: "all" | "auto";
      dateLabelRotation?: number;
      valueLabelSize?: number;
      valueLabelRotation?: number;
      legendFontSize?: number;
      xAxisLabelSize?: number;
      yAxisLabelSize?: number;
      titlePadTop?: number;
      titlePadRight?: number;
      titlePadBottom?: number;
      titlePadLeft?: number;
      multiAxis?: boolean;
      multiAxisIndices?: number[];
      // Per-series benchmark values (aligned to chartData.series). Each non-null
      // value is drawn as a horizontal reference line in the series colour.
      seriesBenchmarks?: Array<number | null>;
    },
    chartType?: "bar" | "line" | "dot" | "bar_label" | "line_label" | "pie" | "donut" | "gauge" | "numeric" | "table" | "text" | "box"
  ): string {
    const width = 900;
    const inferredType =
      chartType ||
      (chartData.series.some((s) => s.type === "text")
        ? "text"
        : chartData.series.some((s) => s.type === "gauge")
          ? "gauge"
          : chartData.series.some((s) => s.type === "dot")
            ? "dot"
            : chartData.series.some((s) => s.type === "bar_label")
              ? "bar_label"
              : chartData.series.some((s) => s.type === "line_label")
                ? "line_label"
                : chartData.series.some((s) => s.type === "numeric")
                  ? "numeric"
                  : chartData.series.some((s) => s.type === "table")
                    ? "table"
                    : chartData.series.some((s) => s.type === "box")
                      ? "box"
                      : chartData.series.some((s) => s.type === "donut")
                        ? "donut"
                        : chartData.series.some((s) => s.type === "pie")
                          ? "pie"
                          : chartData.series.some((s) => s.type === "bar")
                            ? "bar"
                            : "line");
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = 1;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return "";
    }

    const bgStart = styleOptions?.bgStart || "#1f2937";
    const bgCenter = styleOptions?.bgCenter || "#2b3444";
    const bgEnd = styleOptions?.bgEnd || "#3f3f46";
    const bgOuter = styleOptions?.bgOuter || "#111827";
    const titleColor = styleOptions?.titleColor || "#f8fafc";
    const titleFontSize = this.normalizeChartTitleSize(
      styleOptions?.titleFontSize
    );
    const legendTextColor = styleOptions?.legendTextColor || "#e5e7eb";
    const axisTextColor = styleOptions?.axisTextColor || "#e2e8f0";
    const gridColor = styleOptions?.gridColor || "rgba(209, 213, 219, 0.35)";
    const dateLabelMode = styleOptions?.dateLabelMode === "all" ? "all" : "auto";
    const dateLabelRotation = this.normalizeDateLabelRotation(
      styleOptions?.dateLabelRotation
    );
    const valueLabelSize = this.normalizeValueLabelSize(
      styleOptions?.valueLabelSize
    );
    const valueLabelRotation = this.normalizeValueLabelRotation(
      styleOptions?.valueLabelRotation
    );
    const legendFontSize = this.normalizeLegendSize(
      styleOptions?.legendFontSize
    );
    const xAxisLabelSize = this.normalizeAxisLabelSize(
      styleOptions?.xAxisLabelSize
    );
    const yAxisLabelSize = this.normalizeAxisLabelSize(
      styleOptions?.yAxisLabelSize
    );
    const titlePadTop = this.normalizeTitlePad(styleOptions?.titlePadTop);
    const titlePadRight = this.normalizeTitlePad(styleOptions?.titlePadRight);
    const titlePadBottom = this.normalizeTitlePad(styleOptions?.titlePadBottom);
    const titlePadLeft = this.normalizeTitlePad(styleOptions?.titlePadLeft);

    // What gets painted, as opposed to `title` — which always carries the report
    // name, since the interval detection just below keys off it. The report name
    // already renders as the heading above the chart, so report renders pass
    // titleText:"" to stop it showing twice; a title typed into the chart editor
    // paints as normal. Undefined (charts saved before titleText existed) falls
    // back to the report name so their baked-in title is preserved.
    const displayTitle =
      styleOptions?.titleText === undefined
        ? String(title || "")
        : String(styleOptions.titleText || "");
    const hasTitle = !!displayTitle.trim();

    // Wrap long titles across multiple lines so they are never clipped at the
    // canvas edges. The extra lines are folded into titleExtraSpace so they
    // enlarge the top area (and the canvas height) instead of overlapping the plot.
    const titleLineHeight = Math.round(titleFontSize * 1.25);
    const titleWrapWidth = Math.max(120, width - 96);
    ctx.font = `700 ${titleFontSize}px Arial`;
    const titleLines = hasTitle
      ? this.wrapTextToWidth(ctx, displayTitle, titleWrapWidth)
      : [];
    const titleWrapExtra = Math.max(0, titleLines.length - 1) * titleLineHeight;
    // With no title there is nothing for the title padding to space out, so it
    // collapses too — otherwise a stored pad would leave a gap above an absent title.
    const titleExtraSpace = hasTitle
      ? titlePadTop + titlePadBottom + titleWrapExtra
      : 0;
    const multiAxis = !!styleOptions?.multiAxis;
    const isDailyByTitle = /\bdaily\b/i.test(String(title || ""));
    const isHourlyByTitle = /\bhourly\b/i.test(String(title || ""));
    const isRawByTitle = /\braw\b/i.test(String(title || ""));
    const isTimestampByTitle = isHourlyByTitle || isRawByTitle;
    const displayCategories = isDailyByTitle
      ? chartData.categories.map((c) => this.normalizeCategoryLabelForInterval(String(c), "daily"))
      : isTimestampByTitle
        ? chartData.categories.map((c) => this.normalizeCategoryLabelForInterval(String(c), "raw"))
        : chartData.categories;
    const categoriesCount = Math.max(1, chartData.categories.length);

    const isCartesianChart =
      inferredType !== "text" &&
      inferredType !== "numeric" &&
      inferredType !== "table" &&
      inferredType !== "gauge" &&
      inferredType !== "pie" &&
      inferredType !== "donut" &&
      inferredType !== "box";

    // Widen the left gutter when the Y-axis labels are enlarged so the tick
    // numbers never clip against the canvas edge (no effect at the 12px default).
    const yAxisGutter = 56 + Math.max(0, yAxisLabelSize - 12) * 2.5;
    const margin = {
      // The 56px top band exists to hold the title; with no title it collapses to
      // a plain gutter (kept wide enough for the top Y-axis tick label) and the
      // plot grows into the freed space. The canvas height is unchanged either
      // way, so page layout doesn't shift.
      top: (hasTitle ? 56 : 24) + titleExtraSpace,
      right: 22,
      bottom: 86,
      left: yAxisGutter,
    };
    if (isCartesianChart && displayCategories.length) {
      // Measure with the chosen X-axis label size so the reserved label depth
      // and sampling step match what is actually drawn below.
      ctx.font = `600 ${xAxisLabelSize}px Arial`;
      const basePlotWidth = width - margin.left - margin.right;
      const xStepForLayout = dateLabelMode === "all"
        ? 1
        : this.computeAutoDateLabelStep(
            ctx,
            displayCategories,
            basePlotWidth,
            isTimestampByTitle,
            dateLabelRotation
          );
      const labelDepth = this.computeRotatedDateLabelDepth(
        ctx,
        displayCategories,
        xStepForLayout,
        isTimestampByTitle,
        dateLabelRotation
      );
      const legendReserve = Math.max(38, legendFontSize + 22);
      margin.bottom = Math.max(86, Math.ceil(20 + labelDepth + legendReserve));
    }

    // Multi-axis: one independent Y-axis per SELECTED series (mirrors report behaviour).
    // Only series listed in multiAxisIndices get their own axis; the rest share the
    // first selected axis. First 3 axes go on the right, any remaining on the left.
    // Per-series benchmark values (aligned to series order). A finite value is
    // drawn as a horizontal reference line and folded into that series' Y span so
    // the line always stays within the plot.
    const seriesBenchmarks = chartData.series.map((_, i) => {
      const raw = styleOptions?.seriesBenchmarks?.[i];
      const num = this.toNumberOrNull(raw as any);
      return num !== null && Number.isFinite(num) ? num : null;
    });
    const seriesSpans = chartData.series.map((s, i) => {
      const maxData = (s.data || []).reduce<number>(
        (m, v) => (v !== null && !Number.isNaN(v) && Number(v) > m ? Number(v) : m),
        0
      );
      const bench = seriesBenchmarks[i];
      return Math.max(1, maxData, bench !== null ? bench : 0);
    });
    // Field present (even if empty) → honour exactly what the user selected, so
    // unchecking every parameter turns multi-axis off. Field absent (legacy payload
    // that only stored multiAxis:true) → fall back to all series.
    const selectedAxisIndices = Array.isArray(styleOptions?.multiAxisIndices)
      ? styleOptions!.multiAxisIndices!.filter(
          (idx) => Number.isInteger(idx) && idx >= 0 && idx < chartData.series.length
        )
      : chartData.series.map((_, i) => i);
    const applyMultiAxis =
      multiAxis && isCartesianChart && selectedAxisIndices.length >= 1;
    const axisGap = 50;
    const axisPosForSeries = (origIndex: number): number => {
      const pos = selectedAxisIndices.indexOf(origIndex);
      return pos >= 0 ? pos : 0;
    };
    const spanForSeries = (origIndex: number): number => {
      if (!applyMultiAxis) {
        return span;
      }
      const axisSeries = selectedAxisIndices[axisPosForSeries(origIndex)];
      return seriesSpans[axisSeries] ?? span;
    };
    if (applyMultiAxis) {
      const rightAxesCount = Math.min(selectedAxisIndices.length, 3);
      const leftAxesCount = Math.max(0, selectedAxisIndices.length - 3);
      margin.right += rightAxesCount * axisGap;
      margin.left += leftAxesCount * axisGap;
    }

    // Reserve vertical space for EVERY legend row. The legend wraps when the
    // labels are long (multi-series charts), and the base reservation above only
    // accounts for a single row — so extra rows previously overlapped the date
    // labels. Compute the wrapped row count with the same logic the legend
    // drawing uses (final margins), then grow the bottom margin (and thus the
    // canvas height) to fit all rows plus a gap.
    const reserveLegendRows =
      (isCartesianChart ||
        inferredType === "pie" ||
        inferredType === "donut") &&
      chartData.series.length > 1;
    if (reserveLegendRows) {
      ctx.font = `600 ${legendFontSize}px Arial`;
      const maxLegendWidth = width - margin.left - margin.right;
      const legendRowHeight = Math.max(22, legendFontSize + 10);
      let legendRowCount = 1;
      let rowWidth = 0;
      for (let i = 0; i < chartData.series.length; i += 1) {
        const itemWidth = Math.max(
          110,
          ctx.measureText(String(chartData.series[i].name || "")).width + 36
        );
        if (i > 0 && rowWidth + itemWidth > maxLegendWidth) {
          legendRowCount += 1;
          rowWidth = itemWidth;
        } else {
          rowWidth += itemWidth;
        }
      }
      if (legendRowCount > 1) {
        margin.bottom += (legendRowCount - 1) * legendRowHeight + 10;
      }
    }

    const isPieType = inferredType === "pie" || inferredType === "donut";
    const dynamicTableHeight =
      inferredType === "table"
        ? Math.max(390, 118 + (Math.max(1, chartData.categories.length) + 1) * 32 + 20)
        : isPieType
          ? 640 + Math.max(0, margin.bottom - 86)
          : 390 + Math.max(0, margin.bottom - 86);
    const height = dynamicTableHeight + titleExtraSpace;
    canvas.height = height;
    const plotWidth = width - margin.left - margin.right;
    const plotHeight = height - margin.top - margin.bottom;

    const gradient = ctx.createLinearGradient(0, 0, width, height);
    gradient.addColorStop(0, bgOuter);
    gradient.addColorStop(0.22, bgStart);
    gradient.addColorStop(0.62, bgCenter);
    gradient.addColorStop(1, bgEnd);
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);

    const baseColors =
      styleOptions?.seriesColors && styleOptions.seriesColors.length
        ? styleOptions.seriesColors
        : this.defaultChartPalette;
    // Guarantee one visually-distinct color per series. The base palette only
    // holds 6 entries, so charts with more series (e.g. a 12-slice pie) would
    // otherwise wrap via `colors[i % len]` and repeat the first colors. Extend
    // it to the series count with well-separated generated hues.
    const colors = this.ensureDistinctColors(
      baseColors,
      chartData.series.length
    );
    const resolvedChartType = inferredType;

    if (resolvedChartType === "box") {
      return "";
    }

    const values = chartData.series
      .flatMap((s) => s.data)
      .filter((v): v is number => v !== null && !Number.isNaN(v));
    const edgePadding = Math.min(22, Math.max(8, plotWidth * 0.02));
    const plotStartX = margin.left + edgePadding;
    const plotUsableWidth = Math.max(1, plotWidth - edgePadding * 2);
    const groupWidth = plotUsableWidth / categoriesCount;
    // Include benchmark values so the shared axis scales to keep their reference
    // lines within the plot (multi-axis charts use per-series spans instead).
    const benchmarkValues = seriesBenchmarks.filter(
      (v): v is number => v !== null
    );
    const maxValue =
      values.length || benchmarkValues.length
        ? Math.max(...values, ...benchmarkValues, 0)
        : 0;
    const minValue = 0;
    const span = Math.max(1, maxValue - minValue);

    // Title (with configurable spacing around it). Drawn line-by-line so a wrapped
    // long title stacks vertically instead of being clipped at the canvas edges.
    ctx.fillStyle = titleColor;
    ctx.font = `700 ${titleFontSize}px Arial`;
    ctx.textAlign = "center";
    const titleX = width / 2 + (titlePadLeft - titlePadRight);
    for (let tl = 0; tl < titleLines.length; tl += 1) {
      ctx.fillText(
        titleLines[tl],
        titleX,
        36 + titlePadTop + tl * titleLineHeight
      );
    }

    if (resolvedChartType === "text") {
      const textLines = chartData.series
        .map((s) => s.name)
        .filter((s) => !!String(s || "").trim())
        .slice(0, 4);
      if (!textLines.length) {
        textLines.push(title);
      }
      ctx.fillStyle = legendTextColor;
      ctx.font = "600 22px Arial";
      ctx.textAlign = "center";
      const startY = margin.top + 70;
      for (let i = 0; i < textLines.length; i += 1) {
        ctx.fillText(textLines[i], width / 2, startY + i * 34);
      }
    } else if (resolvedChartType === "numeric") {
      if (!values.length) {
        return "";
      }
      // Render one value card per series (matches the dashboard view, which shows
      // every numeric parameter side by side instead of a single number).
      const cardCount = Math.max(1, chartData.series.length);
      const cols = Math.min(cardCount, 4);
      const rows = Math.ceil(cardCount / cols);
      const gap = 16;
      const cardW = (plotWidth - gap * (cols - 1)) / cols;
      const cardH = (plotHeight - gap * (rows - 1)) / rows;

      for (let s = 0; s < cardCount; s += 1) {
        const series = chartData.series[s];
        const col = s % cols;
        const row = Math.floor(s / cols);
        const cardX = margin.left + col * (cardW + gap);
        const cardY = margin.top + row * (cardH + gap);
        const color = colors[s % colors.length];

        // Card panel.
        this.drawRoundedRect(ctx, cardX, cardY, cardW, cardH, 10);
        ctx.fillStyle = "rgba(255, 255, 255, 0.06)";
        ctx.fill();
        ctx.strokeStyle = "rgba(148, 163, 184, 0.4)";
        ctx.lineWidth = 1;
        ctx.stroke();

        // Parameter name (top of card).
        const nameFont = Math.max(12, Math.min(16, Math.round(cardW * 0.085)));
        ctx.fillStyle = "#cbd5e1";
        ctx.font = `600 ${nameFont}px Arial`;
        ctx.textAlign = "center";
        ctx.textBaseline = "alphabetic";
        ctx.fillText(
          this.shortenLabel(series?.name || `Value ${s + 1}`, 22),
          cardX + cardW / 2,
          cardY + Math.max(22, cardH * 0.22)
        );

        // Value (centered, colored, auto-fit to the card width).
        const value = this.getSeriesRepresentativeValue(series?.data || []);
        const valueText = this.formatNumericValue(value);
        let valueFont = Math.max(20, Math.min(64, Math.round(cardH * 0.34)));
        ctx.font = `700 ${valueFont}px Arial`;
        const maxTextWidth = cardW * 0.86;
        while (valueFont > 14 && ctx.measureText(valueText).width > maxTextWidth) {
          valueFont -= 2;
          ctx.font = `700 ${valueFont}px Arial`;
        }
        ctx.fillStyle = color;
        ctx.fillText(valueText, cardX + cardW / 2, cardY + cardH * 0.62 + valueFont / 3);
      }
    } else if (resolvedChartType === "table") {
      const visibleRows = categoriesCount;
      const visibleSeries = Math.max(1, Math.min(chartData.series.length, 3));
      const tableLeft = margin.left - 6;
      const tableTop = margin.top + 6;
      const tableWidth = width - margin.left - margin.right + 12;
      const rowHeight = 32;
      const tableHeight = rowHeight * (visibleRows + 1);
      const colWidth = tableWidth / (visibleSeries + 1);
      const border = "rgba(148, 163, 184, 0.45)";

      // Table outer shell like report card.
      ctx.fillStyle = "rgba(71, 85, 105, 0.55)";
      ctx.fillRect(tableLeft, tableTop, tableWidth, tableHeight);

      // Header background.
      ctx.fillStyle = "#021b39";
      ctx.fillRect(tableLeft, tableTop, tableWidth, rowHeight);

      // Row zebra backgrounds.
      for (let r = 0; r < visibleRows; r += 1) {
        const y = tableTop + rowHeight * (r + 1);
        ctx.fillStyle = r % 2 === 0 ? "#15335a" : "#001a3a";
        ctx.fillRect(tableLeft, y, tableWidth, rowHeight);
      }

      // Grid lines.
      ctx.strokeStyle = border;
      ctx.lineWidth = 1;
      for (let c = 0; c <= visibleSeries + 1; c += 1) {
        const x = tableLeft + c * colWidth;
        ctx.beginPath();
        ctx.moveTo(x, tableTop);
        ctx.lineTo(x, tableTop + tableHeight);
        ctx.stroke();
      }
      for (let r = 0; r <= visibleRows + 1; r += 1) {
        const y = tableTop + r * rowHeight;
        ctx.beginPath();
        ctx.moveTo(tableLeft, y);
        ctx.lineTo(tableLeft + tableWidth, y);
        ctx.stroke();
      }

      // Header text.
      ctx.fillStyle = "#f8fafc";
      ctx.textAlign = "left";
      ctx.font = "700 12px Arial";
      ctx.fillText("Date", tableLeft + 12, tableTop + 21);
      for (let c = 0; c < visibleSeries; c += 1) {
        ctx.fillText(
          this.shortenLabel(chartData.series[c].name, 22),
          tableLeft + (c + 1) * colWidth + 8,
          tableTop + 21
        );
      }

      // Cell text.
      ctx.font = "600 11px Arial";
      for (let r = 0; r < visibleRows; r += 1) {
        const y = tableTop + (r + 1) * rowHeight + 21;
        ctx.fillStyle = "#ffffff";
        ctx.fillText(
          this.shortenLabel(this.formatDateLikeLabel(chartData.categories[r]), 16),
          tableLeft + 12,
          y
        );
        for (let c = 0; c < visibleSeries; c += 1) {
          const v = chartData.series[c].data[r];
          ctx.fillText(
            v === null || Number.isNaN(v) ? "N/A" : `${v}`,
            tableLeft + (c + 1) * colWidth + 8,
            y
          );
        }
      }

      // full table rendered; no row truncation note needed.
    } else if (resolvedChartType === "gauge") {
      if (!values.length) {
        return "";
      }
      const radius = Math.min(plotWidth, plotHeight) * 0.5;
      const centerX = width / 2;
      const centerY = margin.top + plotHeight / 2 + 64;
      const gaugeValue = this.getSeriesRepresentativeValue(chartData.series[0]?.data || []);
      const gaugeMaxBase = Math.max(maxValue, gaugeValue, 1);
      const gaugeMax = Math.max(1, Math.ceil((gaugeMaxBase * 1.5) / 10) * 10);
      const ratio = Math.max(0, Math.min(1, gaugeValue / gaugeMax));
      const startAngle = Math.PI;
      const endAngle = 2 * Math.PI;
      const sweep = endAngle - startAngle;
      const innerRadius = radius - 22;

      // Background track
      ctx.lineWidth = 22;
      ctx.lineCap = "round";
      ctx.strokeStyle = "rgba(148, 163, 184, 0.28)";
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius, startAngle, endAngle, false);
      ctx.stroke();

      // Colored zones (red -> yellow -> cyan -> green)
      const zoneStops = [0, 0.28, 0.52, 0.76, 1];
      const zoneColors = ["#ff6b78", "#f4d35e", "#4fd1ff", "#7ef7ae"];
      ctx.lineWidth = 16;
      ctx.lineCap = "butt";
      for (let zi = 0; zi < zoneColors.length; zi += 1) {
        const a1 = startAngle + zoneStops[zi] * sweep;
        const a2 = startAngle + zoneStops[zi + 1] * sweep;
        ctx.strokeStyle = zoneColors[zi];
        ctx.beginPath();
        ctx.arc(centerX, centerY, radius, a1, a2, false);
        ctx.stroke();
      }

      // Minor/major ticks
      const majorCount = 4;
      const minorPerMajor = 3;
      for (let i = 0; i <= majorCount * minorPerMajor; i += 1) {
        const t = i / (majorCount * minorPerMajor);
        const angle = startAngle + t * sweep;
        const isMajor = i % minorPerMajor === 0;
        const outer = radius - 2;
        const tickLen = isMajor ? 18 : 10;
        const inner = outer - tickLen;
        const x1 = centerX + Math.cos(angle) * outer;
        const y1 = centerY + Math.sin(angle) * outer;
        const x2 = centerX + Math.cos(angle) * inner;
        const y2 = centerY + Math.sin(angle) * inner;

        ctx.strokeStyle = isMajor ? "#f8fafc" : "rgba(226, 232, 240, 0.8)";
        ctx.lineWidth = isMajor ? 3 : 2;
        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.stroke();
      }

      // Numeric labels on major ticks
      ctx.fillStyle = "#e2e8f0";
      ctx.font = "600 14px Arial";
      ctx.textAlign = "center";
      for (let i = 0; i <= majorCount; i += 1) {
        const t = i / majorCount;
        const angle = startAngle + t * sweep;
        const labelValue = (gaugeMax * t);
        const lx = centerX + Math.cos(angle) * (radius + 20);
        const ly = centerY + Math.sin(angle) * (radius + 20);
        const text = `${Math.round(labelValue).toLocaleString("en-US")}`;
        ctx.fillText(text, lx, ly + 4);
      }

      // Needle pointer
      const needleAngle = startAngle + ratio * sweep;
      const tipR = radius - 26;
      const baseR = innerRadius - 26;
      const halfWidth = 8;
      const tipX = centerX + Math.cos(needleAngle) * tipR;
      const tipY = centerY + Math.sin(needleAngle) * tipR;
      const baseX = centerX + Math.cos(needleAngle) * baseR;
      const baseY = centerY + Math.sin(needleAngle) * baseR;
      const leftX = baseX + Math.cos(needleAngle - Math.PI / 2) * halfWidth;
      const leftY = baseY + Math.sin(needleAngle - Math.PI / 2) * halfWidth;
      const rightX = baseX + Math.cos(needleAngle + Math.PI / 2) * halfWidth;
      const rightY = baseY + Math.sin(needleAngle + Math.PI / 2) * halfWidth;

      ctx.fillStyle = "#4fd1ff";
      ctx.beginPath();
      ctx.moveTo(tipX, tipY);
      ctx.lineTo(leftX, leftY);
      ctx.lineTo(rightX, rightY);
      ctx.closePath();
      ctx.fill();

      ctx.fillStyle = "#0f172a";
      ctx.beginPath();
      ctx.arc(centerX, centerY, 7, 0, Math.PI * 2);
      ctx.fill();

      // Center value + parameter name
      ctx.fillStyle = legendTextColor;
      const valueFontSize = Math.max(26, Math.min(44, Math.round(radius * 0.42)));
      ctx.font = `700 ${valueFontSize}px Arial`;
      ctx.textAlign = "center";
      ctx.fillText(`${gaugeValue.toFixed(2)}`, centerX, centerY - 6);
      const nameFontSize = Math.max(14, Math.min(28, Math.round(radius * 0.22)));
      ctx.font = `600 ${nameFontSize}px Arial`;
      ctx.fillStyle = "#dbeafe";
      ctx.fillText(
        this.shortenLabel(chartData.series[0]?.name || "Value", 26),
        centerX,
        centerY + Math.max(40, Math.round(radius * 0.36))
      );
    } else if (resolvedChartType === "pie" || resolvedChartType === "donut") {
      if (!values.length) {
        return "";
      }
      const slices = chartData.series.map((series) =>
        this.getSeriesRepresentativeValue(series.data)
      );
      const total = slices.reduce((sum, v) => sum + v, 0);
      if (total > 0) {
        // Fill the plot area: use ~half the available height (minus a little room
        // for the leader-line % labels top/bottom) and clamp so the side labels
        // still fit within the canvas width.
        const labelRoom = 30;
        const radiusByHeight = plotHeight / 2 - labelRoom;
        const radiusByWidth = (plotWidth - 150) / 2;
        const radius = Math.max(150, Math.min(radiusByHeight, radiusByWidth));
        const centerX = width / 2;
        const centerY = margin.top + plotHeight / 2 + 4;
        const innerRadius = radius * 0.56;

        // Pre-compute each slice's angles so we can draw the slice and then its
        // leader-line label from the same geometry.
        const sliceInfo: Array<{ index: number; start: number; sweep: number; mid: number; value: number }> = [];
        let angleCursor = -Math.PI / 2;
        for (let i = 0; i < slices.length; i += 1) {
          const value = slices[i];
          if (value <= 0) {
            continue;
          }
          const sweep = (value / total) * Math.PI * 2;
          sliceInfo.push({ index: i, start: angleCursor, sweep, mid: angleCursor + sweep / 2, value });
          angleCursor += sweep;
        }

        // Draw the slices.
        for (let s = 0; s < sliceInfo.length; s += 1) {
          const slice = sliceInfo[s];
          ctx.fillStyle = colors[slice.index % colors.length];
          if (resolvedChartType === "donut") {
            const endAngle = slice.start + slice.sweep;
            // Draw as ring segment so center is never sliced.
            ctx.beginPath();
            ctx.arc(centerX, centerY, radius, slice.start, endAngle, false);
            ctx.arc(centerX, centerY, innerRadius, endAngle, slice.start, true);
            ctx.closePath();
            ctx.fill();
          } else {
            ctx.beginPath();
            ctx.moveTo(centerX, centerY);
            ctx.arc(centerX, centerY, radius, slice.start, slice.start + slice.sweep);
            ctx.closePath();
            ctx.fill();
          }
        }

        // Leader-line percentage labels around the pie (matches the live report
        // view's labelled slices). Names stay in the legend below; the slice label
        // shows the share. Negligible slices (<2%) are skipped to avoid clutter.
        const pctLabelSize = 16;
        ctx.font = `700 ${pctLabelSize}px Arial`;
        ctx.textBaseline = "middle";
        for (let s = 0; s < sliceInfo.length; s += 1) {
          const slice = sliceInfo[s];
          const pct = (slice.value / total) * 100;
          if (pct < 2) {
            continue;
          }
          const cos = Math.cos(slice.mid);
          const sin = Math.sin(slice.mid);
          const onRight = cos >= 0;
          const x1 = centerX + cos * radius;
          const y1 = centerY + sin * radius;
          const x2 = centerX + cos * (radius + 16);
          const y2 = centerY + sin * (radius + 16);
          const x3 = x2 + (onRight ? 24 : -24);
          ctx.strokeStyle = colors[slice.index % colors.length];
          ctx.lineWidth = 1.4;
          ctx.beginPath();
          ctx.moveTo(x1, y1);
          ctx.lineTo(x2, y2);
          ctx.lineTo(x3, y2);
          ctx.stroke();
          ctx.fillStyle = legendTextColor;
          ctx.textAlign = onRight ? "left" : "right";
          ctx.fillText(`${this.formatAxisTick(pct)}%`, x3 + (onRight ? 6 : -6), y2);
        }
      }
    } else {
      // Axes and grid
      ctx.strokeStyle = gridColor;
      ctx.lineWidth = 1;
      const yTicks = 6;
      for (let t = 0; t <= yTicks; t += 1) {
        const ratio = t / yTicks;
        const y = margin.top + plotHeight - ratio * plotHeight;
        ctx.beginPath();
        ctx.moveTo(margin.left, y);
        ctx.lineTo(margin.left + plotWidth, y);
        ctx.stroke();

        if (!applyMultiAxis) {
          const tickValue = minValue + ratio * span;
          ctx.fillStyle = axisTextColor;
          ctx.font = `700 ${yAxisLabelSize}px Arial`;
          ctx.textAlign = "right";
          ctx.fillText(`${this.formatAxisTick(tickValue)}`, margin.left - 8, y + 4);
        }
      }

      // Draw bars and lines/dots
      const barSeries = chartData.series.filter((s) =>
        s.type === "bar" || s.type === "bar_label"
      );
      const lineSeries = chartData.series.filter((s) =>
        s.type === "line" || s.type === "line_label" || s.type === "dot"
      );
      const barCount = Math.max(1, barSeries.length);
      // Cap the bar width so charts with few categories don't get absurdly thick
      // bars. Charts with many categories have a small groupWidth, so the cap
      // never kicks in and they render exactly as before.
      const maxBarWidth = 80;
      // Per-bar slot inside the ~75%-of-group cluster. The drawn bar fills most of
      // its slot and the remainder becomes the gap between adjacent parameter bars,
      // so grouped bars (multiple parameters per category) are visually separated
      // instead of touching. Single-series charts keep no inner gap.
      const barSlot = (groupWidth * 0.75) / barCount;
      const barGap = barCount > 1 ? Math.min(barSlot * 0.25, 14) : 0;
      const barWidth = Math.min(maxBarWidth, Math.max(3, barSlot - barGap));
      // Total width occupied by a category's bar slots, used to centre them in the slot.
      const groupBarsWidth = barSlot * barCount;
      const showBarLabels = resolvedChartType === "bar_label";
      const showLineLabels = resolvedChartType === "line_label";
      const dotOnly = resolvedChartType === "dot";
      const showPointMarkers = dotOnly;

      for (let ci = 0; ci < categoriesCount; ci += 1) {
        for (let si = 0; si < barSeries.length; si += 1) {
          const value = barSeries[si].data[ci];
          if (value === null || Number.isNaN(value)) {
            continue;
          }
          const barOrigIndex = chartData.series.indexOf(barSeries[si]);
          const barSpan = spanForSeries(barOrigIndex >= 0 ? barOrigIndex : si);
          const colorIndex = barOrigIndex >= 0 ? barOrigIndex : si;
          const h = ((value - minValue) / barSpan) * plotHeight;
          // Centre the (possibly capped) bar group within its category slot so
          // bars sit in the middle instead of hugging the left edge.
          const groupStartX =
            plotStartX + ci * groupWidth + (groupWidth - groupBarsWidth) / 2;
          // Centre the (narrower) bar inside its slot so the leftover slot width
          // shows as an even gap on each side, separating adjacent parameter bars.
          const x = groupStartX + si * barSlot + (barSlot - barWidth) / 2;
          const y = margin.top + plotHeight - h;
          ctx.fillStyle = colors[colorIndex % colors.length];
          ctx.fillRect(x, y, barWidth, h);
          if (showBarLabels) {
            this.drawValueLabel(
              ctx,
              `${this.formatAxisTick(value)}`,
              x + barWidth / 2,
              Math.max(margin.top + 10, y - 4),
              valueLabelSize,
              valueLabelRotation,
              axisTextColor
            );
          }
        }
      }

      for (let li = 0; li < lineSeries.length; li += 1) {
        const series = lineSeries[li];
        const lineOrigIndex = chartData.series.indexOf(series);
        const lineSpan = spanForSeries(lineOrigIndex >= 0 ? lineOrigIndex : li);
        const color =
          applyMultiAxis && lineOrigIndex >= 0
            ? colors[lineOrigIndex % colors.length]
            : colors[(barSeries.length + li) % colors.length];
        if (!dotOnly) {
          ctx.strokeStyle = color;
          ctx.lineWidth = 2.2;
          ctx.beginPath();
        }
        let started = false;
        for (let i = 0; i < categoriesCount; i += 1) {
          const value = series.data[i];
          if (value === null || Number.isNaN(value)) {
            continue;
          }
          const x = plotStartX + i * groupWidth + groupWidth / 2;
          const y = margin.top + plotHeight - (((value - minValue) / lineSpan) * plotHeight);
          if (!dotOnly) {
            if (!started) {
              ctx.moveTo(x, y);
              started = true;
            } else {
              ctx.lineTo(x, y);
            }
          }
          if (showPointMarkers) {
            ctx.beginPath();
            ctx.fillStyle = color;
            ctx.arc(x, y, 3.8, 0, Math.PI * 2);
            ctx.fill();
          }
          if (showLineLabels || dotOnly) {
            this.drawValueLabel(
              ctx,
              `${this.formatAxisTick(value)}`,
              x,
              y - 8,
              valueLabelSize,
              valueLabelRotation,
              axisTextColor
            );
          }
        }
        if (started && !dotOnly) {
          ctx.stroke();
        }
      }

      // Benchmark reference lines: a horizontal line at each series' benchmark
      // value, in the series colour, with a labelled value chip near the right
      // edge (mirrors the live chart's benchmark markLine).
      for (let bi = 0; bi < chartData.series.length; bi += 1) {
        const bench = seriesBenchmarks[bi];
        if (bench === null) {
          continue;
        }
        const bSpan = spanForSeries(bi);
        const by =
          margin.top + plotHeight - (((bench - minValue) / bSpan) * plotHeight);
        if (by < margin.top - 0.5 || by > margin.top + plotHeight + 0.5) {
          continue;
        }
        const color = colors[bi % colors.length];
        ctx.save();
        ctx.strokeStyle = color;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(margin.left, by);
        ctx.lineTo(margin.left + plotWidth, by);
        ctx.stroke();

        const label = `${
          chartData.series[bi].name || `Series ${bi + 1}`
        } Benchmark: ${this.formatAxisTick(bench)}`;
        ctx.font = "700 12px Arial";
        const padX = 6;
        const padY = 4;
        const chipW = ctx.measureText(label).width + padX * 2;
        const chipH = 12 + padY * 2;
        let chipX = margin.left + plotWidth - chipW - 2;
        if (chipX < margin.left) {
          chipX = margin.left;
        }
        // Prefer just above the line (insideEndTop); flip below if it'd clip the top.
        let chipY = by - chipH - 2;
        if (chipY < margin.top) {
          chipY = by + 2;
        }
        this.drawRoundedRect(ctx, chipX, chipY, chipW, chipH, 4);
        ctx.fillStyle = "rgba(0, 0, 0, 0.85)";
        ctx.fill();
        ctx.fillStyle = "#ffffff";
        ctx.textAlign = "left";
        ctx.textBaseline = "middle";
        ctx.fillText(label, chipX + padX, chipY + chipH / 2);
        ctx.restore();
      }

      // Multi-axis: draw one colored Y-axis per series.
      if (applyMultiAxis) {
        const yTicks = 6;
        ctx.textBaseline = "alphabetic";
        ctx.font = `700 ${yAxisLabelSize}px Arial`;
        const plotRight = margin.left + plotWidth;
        for (let p = 0; p < selectedAxisIndices.length; p += 1) {
          const seriesIdx = selectedAxisIndices[p];
          const color = colors[seriesIdx % colors.length];
          const sSpan = seriesSpans[seriesIdx];
          const onRight = p <= 2;
          const axisX = onRight
            ? plotRight + p * axisGap
            : margin.left - (p - 2) * axisGap;
          const align: CanvasTextAlign = onRight ? "left" : "right";

          // Axis line.
          ctx.strokeStyle = color;
          ctx.lineWidth = 1.5;
          ctx.beginPath();
          ctx.moveTo(axisX, margin.top);
          ctx.lineTo(axisX, margin.top + plotHeight);
          ctx.stroke();

          // Tick labels (colored to match the series).
          ctx.fillStyle = color;
          ctx.textAlign = align;
          for (let t = 0; t <= yTicks; t += 1) {
            const ratio = t / yTicks;
            const y = margin.top + plotHeight - ratio * plotHeight;
            const labelValue = ratio * sSpan;
            const tx = onRight ? axisX + 6 : axisX - 6;
            ctx.fillText(`${this.formatAxisTick(labelValue)}`, tx, y + 4);
          }
        }
      }

      // X labels (sampled). Force one-date-per-point for daily charts.
      if (isDailyByTitle) {
        this.rawDebugLog(
          "[DAILY][LABELS] title:",
          title,
          "sample:",
          displayCategories.slice(0, 12)
        );
      }
      if (isRawByTitle) {
        this.rawDebugLog(
          "[RAW][DRAW] title:",
          title,
          "count:",
          displayCategories.length,
          "sample:",
          displayCategories.slice(0, 12)
        );
      }
      const xStep = dateLabelMode === "all"
        ? 1
        : this.computeAutoDateLabelStep(
            ctx,
            displayCategories,
            plotWidth,
            isTimestampByTitle,
            dateLabelRotation
          );
      ctx.fillStyle = legendTextColor;
      ctx.font = `600 ${xAxisLabelSize}px Arial`;
      for (let i = 0; i < categoriesCount; i += 1) {
        if (i % xStep !== 0 && i !== categoriesCount - 1) {
          continue;
        }
        const label = isTimestampByTitle
          ? this.formatHourlyTimestampLabel(displayCategories[i])
          : this.formatDateLikeLabel(displayCategories[i]);
        // Bars (and line points) are centred in each category slot, so the tick
        // and date label go at the slot centre to line up under them.
        const x = plotStartX + i * groupWidth + groupWidth / 2;
        const axisY = margin.top + plotHeight;
        ctx.strokeStyle = "rgba(203, 213, 225, 0.55)";
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(x, axisY);
        ctx.lineTo(x, axisY + 7);
        ctx.stroke();
        const absRot = Math.abs(dateLabelRotation);
        const baseLabelY = margin.top + plotHeight + 16;
        const y = baseLabelY + (absRot >= 85 ? 10 : absRot >= 75 ? 6 : 0);
        ctx.save();
        ctx.translate(x, y);
        ctx.rotate((dateLabelRotation * Math.PI) / 180);
        if (absRot >= 80) {
          // Keep vertical labels flowing downward from x-axis.
          ctx.textAlign = dateLabelRotation >= 0 ? "left" : "right";
        } else if (absRot >= 75) {
          ctx.textAlign = "center";
        } else if (dateLabelRotation <= -12) {
          ctx.textAlign = "right";
        } else if (dateLabelRotation >= 12) {
          ctx.textAlign = "left";
        } else {
          ctx.textAlign = "center";
        }
        ctx.textBaseline = "top";
        ctx.fillText(label, 0, 2);
        ctx.restore();
      }
    }

    if (resolvedChartType === "table" || resolvedChartType === "gauge") {
      return canvas.toDataURL("image/png");
    }

    // Legend at bottom, centered.
    ctx.textAlign = "left";
    ctx.font = `600 ${legendFontSize}px Arial`;
    // Keep in sync with the legend-row reservation in drawChartSnapshotDataUrl so
    // multi-row legends get the same vertical gap they were given space for.
    const legendRowHeight = Math.max(22, legendFontSize + 10);
    const legendBottomY = height - 12;
    const maxLegendWidth = width - margin.left - margin.right;
    const legendItems = chartData.series.map((series, i) => {
      // Render the full parameter name (no truncation/ellipsis).
      const label = String(series.name || "");
      const itemWidth = Math.max(110, ctx.measureText(label).width + 36);
      return { i, label, itemWidth };
    });

    const legendRows: Array<Array<{ i: number; label: string; itemWidth: number }>> = [];
    let currentRow: Array<{ i: number; label: string; itemWidth: number }> = [];
    let currentRowWidth = 0;

    for (let i = 0; i < legendItems.length; i += 1) {
      const item = legendItems[i];
      if (currentRow.length > 0 && currentRowWidth + item.itemWidth > maxLegendWidth) {
        legendRows.push(currentRow);
        currentRow = [item];
        currentRowWidth = item.itemWidth;
      } else {
        currentRow.push(item);
        currentRowWidth += item.itemWidth;
      }
    }
    if (currentRow.length) {
      legendRows.push(currentRow);
    }

    for (let rowIndex = 0; rowIndex < legendRows.length; rowIndex += 1) {
      const row = legendRows[rowIndex];
      const rowWidth = row.reduce((sum, item) => sum + item.itemWidth, 0);
      let legendX = (width - rowWidth) / 2;
      const legendY = legendBottomY - (legendRows.length - 1 - rowIndex) * legendRowHeight;

      for (let ri = 0; ri < row.length; ri += 1) {
        const item = row[ri];
        const color = colors[item.i % colors.length];
        ctx.beginPath();
        ctx.fillStyle = color;
        ctx.arc(legendX + 7, legendY - 5, 6, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = legendTextColor;
        ctx.fillText(item.label, legendX + 20, legendY);
        legendX += item.itemWidth;
      }
    }

    return canvas.toDataURL("image/png");
  }

  /**
   * Return at least `count` visually-distinct colors. The base palette (default
   * brand colors or user-chosen series colors) is kept as-is for the leading
   * entries so existing charts look unchanged; any shortfall is filled by
   * placing each new color at the hue that is FARTHEST from every hue already in
   * use (greedy farthest-point on the color wheel). This is what keeps generated
   * colors away from the base brand hues too — a naive fixed-step generator
   * would land a new green next to the brand green, etc. Lightness/saturation
   * also shift as the wheel fills so that, once hues must crowd together, near
   * neighbours still differ in brightness. Exact-duplicate hexes are dropped.
   */
  private ensureDistinctColors(base: string[], count: number): string[] {
    const out: string[] = [];
    const usedHexes = new Set<string>();
    const usedHues: number[] = [];
    const add = (hex: string): void => {
      const key = hex.toLowerCase();
      if (usedHexes.has(key)) {
        return;
      }
      usedHexes.add(key);
      usedHues.push(this.hexToHue(hex));
      out.push(hex);
    };

    for (const color of base) {
      if (out.length >= count) {
        break;
      }
      if (color) {
        add(color);
      }
    }

    const circularDistance = (a: number, b: number): number => {
      const d = Math.abs(a - b) % 360;
      return Math.min(d, 360 - d);
    };

    let guard = 0;
    while (out.length < count && guard < count * 4) {
      guard += 1;
      // Scan the wheel for the hue with the largest minimum distance to every
      // hue already used (empty palette → fall back to 0°).
      let bestHue = 0;
      let bestScore = -1;
      for (let h = 0; h < 360; h += 2) {
        let minDistance = 360;
        for (let u = 0; u < usedHues.length; u += 1) {
          minDistance = Math.min(minDistance, circularDistance(h, usedHues[u]));
        }
        if (minDistance > bestScore) {
          bestScore = minDistance;
          bestHue = h;
        }
      }
      // Each full pass around the wheel drops lightness so re-used hue regions
      // read as a different shade.
      const cycle = Math.floor(out.length / 6);
      const saturation = 70 - (cycle % 2) * 14;
      const lightness = 60 - (cycle % 3) * 10;
      let hex = this.hslToHex(bestHue, saturation, lightness);
      if (usedHexes.has(hex.toLowerCase())) {
        hex = this.hslToHex(bestHue, saturation, Math.max(20, lightness - 12));
      }
      const before = out.length;
      add(hex);
      if (out.length === before) {
        // Guard against a stuck loop if a duplicate still slipped through.
        usedHues.push(bestHue);
      }
    }

    return out;
  }

  /** Extract the HSL hue (0–360°) from a #rrggbb / #rgb hex color. */
  private hexToHue(hex: string): number {
    let value = (hex || "").replace("#", "").trim();
    if (value.length === 3) {
      value = value
        .split("")
        .map((c) => c + c)
        .join("");
    }
    if (value.length < 6) {
      return 0;
    }
    const r = parseInt(value.substring(0, 2), 16) / 255;
    const g = parseInt(value.substring(2, 4), 16) / 255;
    const b = parseInt(value.substring(4, 6), 16) / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const delta = max - min;
    if (delta === 0) {
      return 0;
    }
    let hue: number;
    if (max === r) {
      hue = ((g - b) / delta) % 6;
    } else if (max === g) {
      hue = (b - r) / delta + 2;
    } else {
      hue = (r - g) / delta + 4;
    }
    hue *= 60;
    return hue < 0 ? hue + 360 : hue;
  }

  /** Convert an HSL color (h in degrees, s/l in percent) to a #rrggbb hex string. */
  private hslToHex(h: number, s: number, l: number): string {
    const sat = s / 100;
    const light = l / 100;
    const c = (1 - Math.abs(2 * light - 1)) * sat;
    const hp = (((h % 360) + 360) % 360) / 60;
    const x = c * (1 - Math.abs((hp % 2) - 1));
    let r = 0;
    let g = 0;
    let b = 0;
    if (hp >= 0 && hp < 1) {
      r = c;
      g = x;
    } else if (hp < 2) {
      r = x;
      g = c;
    } else if (hp < 3) {
      g = c;
      b = x;
    } else if (hp < 4) {
      g = x;
      b = c;
    } else if (hp < 5) {
      r = x;
      b = c;
    } else {
      r = c;
      b = x;
    }
    const m = light - c / 2;
    const toHex = (v: number): string =>
      Math.round((v + m) * 255)
        .toString(16)
        .padStart(2, "0");
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  }

  private encodeChartPayload(payload: {
    title: string;
    chartType?: "bar" | "line" | "dot" | "bar_label" | "line_label" | "pie" | "donut" | "gauge" | "numeric" | "table" | "text" | "box";
    chartData: {
      categories: string[];
      series: Array<{ name: string; data: Array<number | null>; type: "bar" | "line" | "dot" | "bar_label" | "line_label" | "pie" | "donut" | "gauge" | "numeric" | "table" | "text" | "box" }>;
    };
    options?: {
      bgStart?: string;
      bgCenter?: string;
      bgEnd?: string;
      bgOuter?: string;
      seriesColors?: string[];
      titleColor?: string;
      titleFontSize?: number;
      legendTextColor?: string;
      gridColor?: string;
      axisTextColor?: string;
      dateLabelMode?: "all" | "auto";
      dateLabelRotation?: number;
      valueLabelSize?: number;
      valueLabelRotation?: number;
      legendFontSize?: number;
      xAxisLabelSize?: number;
      yAxisLabelSize?: number;
      titlePadTop?: number;
      titlePadRight?: number;
      titlePadBottom?: number;
      titlePadLeft?: number;
      multiAxis?: boolean;
      multiAxisIndices?: number[];
    };
  }): string {
    try {
      return encodeURIComponent(JSON.stringify(payload));
    } catch {
      return "";
    }
  }

  private decodeChartPayload(encoded: string): {
    title: string;
    chartType?: "bar" | "line" | "dot" | "bar_label" | "line_label" | "pie" | "donut" | "gauge" | "numeric" | "table" | "text" | "box";
    chartData: {
      categories: string[];
      series: Array<{ name: string; data: Array<number | null>; type: "bar" | "line" | "dot" | "bar_label" | "line_label" | "pie" | "donut" | "gauge" | "numeric" | "table" | "text" | "box" }>;
    };
    options?: {
      bgStart?: string;
      bgCenter?: string;
      bgEnd?: string;
      bgOuter?: string;
      seriesColors?: string[];
      titleColor?: string;
      titleFontSize?: number;
      legendTextColor?: string;
      gridColor?: string;
      axisTextColor?: string;
      dateLabelMode?: "all" | "auto";
      dateLabelRotation?: number;
      valueLabelSize?: number;
      valueLabelRotation?: number;
      legendFontSize?: number;
      xAxisLabelSize?: number;
      yAxisLabelSize?: number;
      titlePadTop?: number;
      titlePadRight?: number;
      titlePadBottom?: number;
      titlePadLeft?: number;
      multiAxis?: boolean;
      multiAxisIndices?: number[];
    };
  } | null {
    if (!encoded) {
      return null;
    }
    try {
      const decoded = decodeURIComponent(encoded);
      const parsed = JSON.parse(decoded);
      if (
        !parsed ||
        typeof parsed !== "object" ||
        !parsed.chartData ||
        !Array.isArray(parsed.chartData.series) ||
        !Array.isArray(parsed.chartData.categories)
      ) {
        return null;
      }
      return parsed;
    } catch {
      return null;
    }
  }

  private readChartPayloadFromWrapper(wrapper: HTMLElement): {
    title: string;
    chartType?: "bar" | "line" | "dot" | "bar_label" | "line_label" | "pie" | "donut" | "gauge" | "numeric" | "table" | "text" | "box";
    chartData: {
      categories: string[];
      series: Array<{ name: string; data: Array<number | null>; type: "bar" | "line" | "dot" | "bar_label" | "line_label" | "pie" | "donut" | "gauge" | "numeric" | "table" | "text" | "box" }>;
    };
    options?: {
      bgStart?: string;
      bgCenter?: string;
      bgEnd?: string;
      bgOuter?: string;
      seriesColors?: string[];
      // Absent on charts saved before this existed → falls back to `title`.
      titleText?: string;
      titleColor?: string;
      titleFontSize?: number;
      legendTextColor?: string;
      gridColor?: string;
      axisTextColor?: string;
      dateLabelMode?: "all" | "auto";
      dateLabelRotation?: number;
      valueLabelSize?: number;
      valueLabelRotation?: number;
      legendFontSize?: number;
      xAxisLabelSize?: number;
      yAxisLabelSize?: number;
      titlePadTop?: number;
      titlePadRight?: number;
      titlePadBottom?: number;
      titlePadLeft?: number;
      multiAxis?: boolean;
      multiAxisIndices?: number[];
    };
  } | null {
    const encoded = String(wrapper.getAttribute("data-chart-payload") || "");
    return this.decodeChartPayload(encoded);
  }

  /**
   * Group a reading's timestamp into its interval bucket key so raw
   * per-timestamp readings collapse into one value per interval (mirrors the
   * dashboard's getTimelineBucketKey). Raw (unaggregated) reports keep their
   * exact timestamp key so the existing raw-timestamp handling is untouched.
   */
  private intervalBucketKey(
    startStr: string,
    interval: string,
    aggregation?: string
  ): string {
    if (String(aggregation) === "raw") {
      return startStr;
    }
    const date = new Date(startStr);
    if (Number.isNaN(date.getTime())) {
      return startStr;
    }
    const yyyy = date.getUTCFullYear();
    const mm = String(date.getUTCMonth() + 1).padStart(2, "0");
    const dd = String(date.getUTCDate()).padStart(2, "0");
    if (interval === "monthly") {
      return `${yyyy}-${mm}`;
    }
    if (
      interval === "15minutes" ||
      interval === "15_min" ||
      interval === "30minutes" ||
      interval === "30_min" ||
      interval === "1hour" ||
      interval === "hourly"
    ) {
      const hh = String(date.getUTCHours()).padStart(2, "0");
      const mi = String(date.getUTCMinutes()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd} ${hh}:${mi}`;
    }
    // Daily and weekly both key by day: a weekly report's readings carry the
    // week-start day, so day-keying preserves the existing weekly grouping.
    return `${yyyy}-${mm}-${dd}`;
  }

  private extractChartDataForReport(report: DashboardReportData): {
    categories: string[];
    series: Array<{ name: string; data: Array<number | null>; type: "bar" | "line" | "dot" | "bar_label" | "line_label" | "pie" | "donut" | "gauge" | "numeric" | "table" | "text" | "box"; rawValues?: number[] }>;
  } {
    const params = Array.isArray(report.parameters) ? report.parameters : [];
    const hasParamValues = params.some(
      (param) =>
        Array.isArray(param?.values) &&
        param.values.some((v) => v && v.value !== undefined)
    );

    const rawTimestampCategories = this.isRawReport(report)
      ? this.extractRawTimestampCategories(report)
      : [];

    if (hasParamValues) {
      const baseParam = params.find(
        (param) => Array.isArray(param?.values) && param.values.length > 0
      );
      const baseValues = Array.isArray(baseParam?.values) ? baseParam.values : [];
      const interval = this.inferIntervalHint(report, baseValues);

      // Build a unified timeline across ALL parameters (mirrors the dashboard's
      // buildUnifiedTimeline). Using only the first parameter's values dropped
      // periods (e.g. a month) that other parameters had but it didn't.
      const timelineByKey = new Map<string, any>();
      for (const param of params) {
        const pInterval = String(param?.interval || interval || "daily");
        for (const v of Array.isArray(param?.values) ? param.values : []) {
          const key = this.intervalBucketKey(
            String(v?.start ?? ""),
            pInterval,
            (param as any)?.aggregation
          );
          if (key && !timelineByKey.has(key)) {
            timelineByKey.set(key, v);
          }
        }
      }
      const timeline = Array.from(timelineByKey.entries())
        .map(([key, value]) => ({ key, value }))
        .sort((a, b) => {
          const da = this.parseDateSafe(a.key)?.getTime() ?? 0;
          const db = this.parseDateSafe(b.key)?.getTime() ?? 0;
          return da - db;
        });

      let categories = timeline.map((t) =>
        this.buildCategoryLabel(t.value, interval)
      );
      if (this.isRawReport(report)) {
        if (rawTimestampCategories.length > 0) {
          const targetLen =
            timeline.length || baseValues.length || rawTimestampCategories.length;
          categories = this.alignCategoriesToLength(
            rawTimestampCategories,
            targetLen,
            categories
          );
          this.rawDebugLog(
            "[RAW][PARAM] using extracted timestamps",
            "report:",
            report.reportName,
            "targetLen:",
            targetLen,
            "rawCount:",
            rawTimestampCategories.length,
            "sample:",
            rawTimestampCategories.slice(0, 8)
          );
        } else {
          categories = categories.map((c) =>
            this.normalizeCategoryLabelForInterval(String(c), "raw")
          );
          this.rawDebugLog(
            "[RAW][PARAM] fallback normalized categories",
            "report:",
            report.reportName,
            "sample:",
            categories.slice(0, 8)
          );
        }
      } else if (this.isHourlyReport(report)) {
        categories = categories.map((c) =>
          this.normalizeCategoryLabelForInterval(String(c), "hourly")
        );
      } else if (this.isDailyReport(report)) {
        categories = this.normalizeDailyCategorySequence(
          categories.map((c) => this.normalizeCategoryLabelForInterval(String(c), "daily"))
        );
      }

      const reportType = this.mapToSupportedChartType(report.parameters?.[0]?.graphType, "line");
      const series: Array<{ name: string; data: Array<number | null>; type: "bar" | "line" | "dot" | "bar_label" | "line_label" | "pie" | "donut" | "gauge" | "numeric" | "table" | "text" | "box"; rawValues?: number[] }> = params.map((param) => {
        // The backend may send raw readings per timestamp, so an interval bucket
        // can hold many readings. Collect them per bucket and aggregate by the
        // parameter's data type (sum adds, avg/raw averages) instead of letting
        // the last reading win. `rawValues` keeps every reading so the function
        // tables (Table 3/4) can compute Max/Min/STDV over the true readings.
        const pInterval = String(param?.interval || interval || "daily");
        const agg = String((param as any)?.aggregation || (param as any)?.dataType || "avg");
        const bucketValues = new Map<string, number[]>();
        const rawValues: number[] = [];
        for (const v of Array.isArray(param?.values) ? param.values : []) {
          const num = this.toNumberOrNull(v?.value);
          if (num === null) {
            continue;
          }
          const key = this.intervalBucketKey(
            String(v?.start ?? ""),
            pInterval,
            (param as any)?.aggregation
          );
          const bucket = bucketValues.get(key);
          if (bucket) {
            bucket.push(num);
          } else {
            bucketValues.set(key, [num]);
          }
          rawValues.push(num);
        }
        return {
          name: param?.paramName || param?.paramCode || "Series",
          data: timeline.map((t) => {
            const arr = bucketValues.get(t.key);
            if (!arr || arr.length === 0) {
              return null;
            }
            const sum = arr.reduce((a, b) => a + b, 0);
            return agg === "sum" ? sum : sum / arr.length;
          }),
          type: this.mapToSupportedChartType(param?.graphType, reportType),
          rawValues,
        };
      });

      return { categories, series };
    }

    const chartData = (report.metadata as any)?.chart_data || {};
    const rawSeries = Array.isArray(chartData.series) ? chartData.series : [];
    const rawCategories = Array.isArray(chartData.categories)
      ? chartData.categories
      : [];

    const series: Array<{ name: string; data: Array<number | null>; type: "bar" | "line" | "dot" | "bar_label" | "line_label" | "pie" | "donut" | "gauge" | "numeric" | "table" | "text" | "box" }> = rawSeries.map((s: any, idx: number) => ({
      name: s?.name || `Series ${idx + 1}`,
      data: (Array.isArray(s?.data) ? s.data : []).map((point: any) => {
        if (typeof point === "number") {
          return point;
        }
        if (typeof point === "string") {
          const value = Number(point);
          return Number.isNaN(value) ? null : value;
        }
        if (point && typeof point === "object" && "value" in point) {
          return this.toNumberOrNull((point as any).value);
        }
        return null;
      }),
      type:
        this.mapToSupportedChartType(s?.type || s?.graphType || chartData?.graphType || chartData?.chartType, "line"),
    }));

    const maxLen = Math.max(0, ...series.map((s) => s.data.length));
    const interval = this.inferIntervalHint(report, []);
    const pointCategories = this.extractCategoriesFromSeriesData(rawSeries);
    let sourceCategories =
      pointCategories.length === maxLen && pointCategories.some((c) => String(c || "").trim())
        ? pointCategories
        : rawCategories;

    if (this.isRawReport(report) && rawTimestampCategories.length > 0) {
      sourceCategories = rawTimestampCategories;
      this.rawDebugLog(
        "[RAW][META] source overridden by extracted timestamps",
        "report:",
        report.reportName,
        "maxLen:",
        maxLen,
        "rawCount:",
        rawTimestampCategories.length,
        "sample:",
        rawTimestampCategories.slice(0, 8)
      );
    }

    let categories =
      sourceCategories.length > 0
        ? sourceCategories.map((c: any) =>
            this.normalizeCategoryLabelForInterval(String(c), interval)
          )
        : Array.from({ length: maxLen }, (_, i) => String(i + 1));
    if (this.isRawReport(report)) {
      categories = rawTimestampCategories.length > 0
        ? this.alignCategoriesToLength(rawTimestampCategories, maxLen || categories.length, categories)
        : categories.map((c) =>
            this.normalizeCategoryLabelForInterval(String(c), "raw")
          );
      this.rawDebugLog(
        "[RAW][FINAL] categories for render",
        "report:",
        report.reportName,
        "count:",
        categories.length,
        "sample:",
        categories.slice(0, 12)
      );
    } else if (this.isHourlyReport(report)) {
      categories = categories.map((c) =>
        this.normalizeCategoryLabelForInterval(String(c), "hourly")
      );
    } else if (this.isDailyReport(report)) {
      categories = this.normalizeDailyCategorySequence(
        categories.map((c) => this.normalizeCategoryLabelForInterval(String(c), "daily"))
      );
    }

    return { categories, series };
  }

  private isDailyReport(report: DashboardReportData): boolean {
    const candidates: unknown[] = [
      report.interval,
      report.reportName,
      (report as any)?.title,
      (report as any)?.name,
      (report as any)?.reportTitle,
      (report.metadata as any)?.title,
      (report.metadata as any)?.reportName,
      (report.metadata as any)?.chart_data?.interval,
      (report.metadata as any)?.chart_data?.frequency,
      (report.metadata as any)?.chart_data?.period,
      (report.metadata as any)?.chart_data?.rangeType,
      (report.metadata as any)?.chart_data?.groupBy,
    ];

    for (let i = 0; i < candidates.length; i += 1) {
      const raw = String(candidates[i] || "").toLowerCase();
      if (raw.includes("daily") || raw.includes("day")) {
        return true;
      }
    }
    return false;
  }

  private isHourlyReport(report: DashboardReportData): boolean {
    const candidates: unknown[] = [
      report.interval,
      report.reportName,
      (report as any)?.title,
      (report as any)?.name,
      (report as any)?.reportTitle,
      (report.metadata as any)?.title,
      (report.metadata as any)?.reportName,
      (report.metadata as any)?.chart_data?.interval,
      (report.metadata as any)?.chart_data?.frequency,
      (report.metadata as any)?.chart_data?.period,
      (report.metadata as any)?.chart_data?.rangeType,
      (report.metadata as any)?.chart_data?.groupBy,
    ];

    for (let i = 0; i < candidates.length; i += 1) {
      const raw = String(candidates[i] || "").toLowerCase();
      if (raw.includes("hourly") || raw.includes("hour")) {
        return true;
      }
    }
    return false;
  }

  private isRawReport(report: DashboardReportData): boolean {
    const candidates: unknown[] = [
      report.interval,
      report.reportName,
      (report as any)?.title,
      (report as any)?.name,
      (report as any)?.reportTitle,
      (report.metadata as any)?.title,
      (report.metadata as any)?.reportName,
      (report.metadata as any)?.chart_data?.interval,
      (report.metadata as any)?.chart_data?.frequency,
      (report.metadata as any)?.chart_data?.period,
      (report.metadata as any)?.chart_data?.rangeType,
      (report.metadata as any)?.chart_data?.groupBy,
    ];

    for (let i = 0; i < candidates.length; i += 1) {
      const raw = String(candidates[i] || "").toLowerCase();
      if (raw.includes("raw")) {
        return true;
      }
    }
    return false;
  }

  private getMetadataChartCategories(report: DashboardReportData): string[] {
    const categories = (report.metadata as any)?.chart_data?.categories;
    if (!Array.isArray(categories)) {
      return [];
    }
    return categories.map((c: any) => String(c ?? ""));
  }

  private alignCategoriesToLength(
    normalizedCategories: string[],
    targetLen: number,
    fallbackCategories: string[]
  ): string[] {
    const target = Math.max(0, targetLen);
    if (!target) {
      return normalizedCategories;
    }
    if (normalizedCategories.length >= target) {
      return normalizedCategories.slice(0, target);
    }
    const fallback = Array.isArray(fallbackCategories) ? fallbackCategories : [];
    const merged = [...normalizedCategories];
    for (let i = normalizedCategories.length; i < target; i += 1) {
      merged.push(String(fallback[i] || fallback[fallback.length - 1] || ""));
    }
    return merged;
  }

  private extractRawTimestampCategories(report: DashboardReportData): string[] {
    const candidateSets: string[][] = [];

    const addCandidateSet = (values: unknown[]): void => {
      if (!Array.isArray(values) || !values.length) {
        return;
      }
      const normalized = values
        .map((value) => this.normalizeRawTimestampValue(value))
        .filter((value) => !!value);
      if (normalized.length) {
        candidateSets.push(normalized);
      }
    };

    addCandidateSet(this.getMetadataChartCategories(report));

    const chartData = (report.metadata as any)?.chart_data || {};
    addCandidateSet(Array.isArray(chartData?.xAxis?.categories) ? chartData.xAxis.categories : []);

    const rawSeries = Array.isArray(chartData.series) ? chartData.series : [];
    for (let i = 0; i < rawSeries.length; i += 1) {
      const data = Array.isArray(rawSeries[i]?.data) ? rawSeries[i].data : [];
      const fromPoints = data.map((p: any) =>
        p && typeof p === "object"
          ? (p.x ?? p.time ?? p.timestamp ?? p.dateTime ?? p.datetime ?? p.date ?? p.start ?? p.label ?? p.category ?? "")
          : p
      );
      addCandidateSet(fromPoints);
    }

    const params = Array.isArray(report.parameters) ? report.parameters : [];
    for (let p = 0; p < params.length; p += 1) {
      const values = Array.isArray(params[p]?.values) ? params[p].values : [];
      const fromValues = values.map((v: any) =>
        v && typeof v === "object"
          ? (v.timestamp ?? v.time ?? v.dateTime ?? v.datetime ?? v.date ?? v.start ?? v.label ?? v.category ?? "")
          : v
      );
      addCandidateSet(fromValues);
    }

    if (!candidateSets.length) {
      this.rawDebugLog("[RAW][SOURCE] no candidate sets found", "report:", report.reportName);
      return [];
    }

    let best: string[] = [];
    let bestScore = -1;
    for (let i = 0; i < candidateSets.length; i += 1) {
      const set = candidateSets[i];
      const score = set.reduce((sum, value) => sum + (this.isLikelyTimeText(value) ? 1 : 0), 0);
      if (score > bestScore) {
        best = set;
        bestScore = score;
      }
    }

    this.rawDebugLog(
      "[RAW][SOURCE] candidate sets",
      "report:",
      report.reportName,
      "count:",
      candidateSets.length,
      "bestScore:",
      bestScore,
      "bestLen:",
      best.length,
      "bestSample:",
      best.slice(0, 8)
    );
    return best;
  }

  private normalizeRawTimestampValue(value: unknown): string {
    const parsed = this.parseRawTimestamp(value);
    if (parsed) {
      return this.formatHourlyTimestamp(parsed);
    }
    return this.shortenLabel(String(value ?? "").trim(), 48);
  }

  private parseRawTimestamp(value: unknown): Date | null {
    if (value === null || value === undefined || value === "") {
      return null;
    }
    if (typeof value === "number") {
      const ms = value > 1e12 ? value : value > 1e9 ? value * 1000 : NaN;
      if (!Number.isNaN(ms)) {
        const d = new Date(ms);
        if (!Number.isNaN(d.getTime())) {
          return d;
        }
      }
      return null;
    }

    const raw = String(value).trim();
    if (!raw) {
      return null;
    }
    if (/^\d+$/.test(raw)) {
      const n = Number(raw);
      if (!Number.isNaN(n)) {
        return this.parseRawTimestamp(n);
      }
    }
    return this.parseDateTimeSafe(raw);
  }

  private extractCategoriesFromSeriesData(rawSeries: any[]): string[] {
    if (!Array.isArray(rawSeries)) {
      return [];
    }
    for (let i = 0; i < rawSeries.length; i += 1) {
      const data = Array.isArray(rawSeries[i]?.data) ? rawSeries[i].data : [];
      if (!data.length) {
        continue;
      }
      const categories = data.map((point: any) => {
        if (point === null || point === undefined) {
          return "";
        }
        if (typeof point === "string") {
          return point;
        }
        if (typeof point === "object") {
          return String(
            point.start ??
              point.date ??
              point.x ??
              point.category ??
              point.label ??
              point.name ??
              ""
          );
        }
        return "";
      });
      if (categories.some((c) => String(c || "").trim())) {
        return categories;
      }
    }
    return [];
  }

  private normalizeDailyCategorySequence(categories: string[]): string[] {
    if (!Array.isArray(categories) || !categories.length) {
      return [];
    }
    // Do not synthesize missing days here. Keep only API-provided labels,
    // normalized when parseable, otherwise preserved as-is.
    return categories.map((raw) => {
      const text = String(raw || "").trim();
      if (!text) {
        return "";
      }
      const parsed = this.parseDateSafe(text) || this.parseDateFromRangeLikeLabel(text);
      return parsed ? this.toIsoDate(parsed) : text;
    });
  }

  private inferIntervalHint(report: DashboardReportData, values: any[]): string {
    const normalizeHint = (raw: unknown): "raw" | "hourly" | "daily" | "weekly" | "monthly" | "" => {
      const v = String(raw || "").toLowerCase();
      if (!v) {
        return "";
      }
      if (v.includes("raw")) {
        return "raw";
      }
      if (v.includes("hourly") || v.includes("hour")) {
        return "hourly";
      }
      if (v.includes("daily") || v.includes("day")) {
        return "daily";
      }
      if (v.includes("weekly") || v.includes("week")) {
        return "weekly";
      }
      if (v.includes("monthly") || v.includes("month")) {
        return "monthly";
      }
      return "";
    };

    const fromInterval = normalizeHint(report.interval);
    if (fromInterval) {
      return fromInterval;
    }

    const chartMeta = ((report.metadata as any)?.chart_data || {}) as Record<string, any>;
    const metaCandidates = [
      chartMeta.interval,
      chartMeta.frequency,
      chartMeta.period,
      chartMeta.rangeType,
      chartMeta.groupBy,
      chartMeta.xAxisLabelFormat,
      chartMeta.title,
      chartMeta.subTitle,
      (report.metadata as any)?.title,
      (report.metadata as any)?.reportName,
    ];
    for (let i = 0; i < metaCandidates.length; i += 1) {
      const hint = normalizeHint(metaCandidates[i]);
      if (hint) {
        return hint;
      }
    }

    const nameCandidates = [
      report.reportName,
      (report as any)?.title,
      (report as any)?.name,
      (report as any)?.reportTitle,
      (report as any)?.heading,
    ];
    for (let i = 0; i < nameCandidates.length; i += 1) {
      const hint = normalizeHint(nameCandidates[i]);
      if (hint) {
        return hint;
      }
    }

    const spans: number[] = [];
    for (let i = 0; i < values.length; i += 1) {
      const start = this.parseDateSafe(values[i]?.start);
      const end = this.parseDateSafe(values[i]?.end);
      if (!start || !end) {
        continue;
      }
      const msPerDay = 24 * 60 * 60 * 1000;
      const diffDays = Math.max(0, Math.round((end.getTime() - start.getTime()) / msPerDay)) + 1;
      spans.push(diffDays);
    }
    if (spans.length) {
      const avg = spans.reduce((a, b) => a + b, 0) / spans.length;
      if (avg <= 1 / 24) {
        return "hourly";
      }
      if (avg <= 2) {
        return "daily";
      }
      if (avg >= 27) {
        return "monthly";
      }
      return "weekly";
    }

    return "";
  }

  private normalizeCategoryLabelForInterval(rawValue: string, interval: string): string {
    const raw = String(rawValue || "").trim();
    if (!raw) {
      return "";
    }

    if (interval.includes("raw")) {
      const parsed = this.parseDateTimeSafe(raw);
      if (parsed) {
        return this.formatHourlyTimestamp(parsed);
      }
      return this.shortenLabel(raw, 48);
    }

    if (interval.includes("hour")) {
      const parsed = this.parseDateTimeSafe(raw) || this.parseDateSafe(raw) || this.parseDateFromRangeLikeLabel(raw);
      if (parsed) {
        return this.formatHourlyTimestamp(parsed);
      }
      return this.shortenLabel(raw, 36);
    }

    if (interval.includes("day")) {
      const parsed = this.parseDateSafe(raw) || this.parseDateFromRangeLikeLabel(raw);
      if (parsed) {
        return this.toIsoDate(parsed);
      }
    }

    return this.formatDateLikeLabel(raw);
  }

  private mapToSupportedChartType(
    rawType: unknown,
    fallback: "bar" | "line" | "dot" | "bar_label" | "line_label" | "pie" | "donut" | "gauge" | "numeric" | "table" | "text" | "box" = "line"
  ): "bar" | "line" | "dot" | "bar_label" | "line_label" | "pie" | "donut" | "gauge" | "numeric" | "table" | "text" | "box" {
    const normalized = String(rawType || "").toLowerCase();
    if (!normalized) {
      return fallback;
    }
    // The six table variants (tbl_row/col/rfn/cfn/rfm/cfm) all render as tables.
    if (normalized.startsWith("tbl_")) {
      return "table";
    }
    if (normalized.includes("bar_label") || normalized.includes("bar label")) {
      return "bar_label";
    }
    if (normalized.includes("line_label") || normalized.includes("line label")) {
      return "line_label";
    }
    if (normalized.includes("dot")) {
      return "dot";
    }
    if (normalized.includes("donut") || normalized.includes("doughnut")) {
      return "donut";
    }
    if (normalized.includes("pie")) {
      return "pie";
    }
    if (normalized.includes("gauge")) {
      return "gauge";
    }
    if (normalized.includes("text")) {
      return "text";
    }
    if (normalized.includes("bar") || normalized.includes("column")) {
      return "bar";
    }
    if (
      normalized.includes("line") ||
      normalized.includes("spline") ||
      normalized.includes("area")
    ) {
      return "line";
    }
    if (normalized.includes("numeric")) {
      return "numeric";
    }
    if (normalized.includes("table")) {
      return "table";
    }
    if (normalized.includes("box")) {
      return "box";
    }
    return fallback;
  }

  private resolveReportChartType(
    report: DashboardReportData,
    chartData: {
      categories: string[];
      series: Array<{ name: string; data: Array<number | null>; type: "bar" | "line" | "dot" | "bar_label" | "line_label" | "pie" | "donut" | "gauge" | "numeric" | "table" | "text" | "box" }>;
    }
  ): "bar" | "line" | "dot" | "bar_label" | "line_label" | "pie" | "donut" | "gauge" | "numeric" | "table" | "text" | "box" {
    const candidates = new Set<
      "bar" | "line" | "dot" | "bar_label" | "line_label" | "pie" | "donut" | "gauge" | "numeric" | "table" | "text" | "box"
    >();

    const reportLevel = this.mapToSupportedChartType(
      (report.metadata as any)?.chart_data?.graphType || (report.metadata as any)?.chart_data?.chartType,
      "line"
    );
    candidates.add(reportLevel);

    const params = Array.isArray(report.parameters) ? report.parameters : [];
    for (let i = 0; i < params.length; i += 1) {
      candidates.add(this.mapToSupportedChartType(params[i]?.graphType, reportLevel));
    }

    for (let i = 0; i < chartData.series.length; i += 1) {
      candidates.add(chartData.series[i].type);
    }

    const priority: Array<
      "text" | "gauge" | "pie" | "donut" | "table" | "numeric" | "bar_label" | "line_label" | "bar" | "line" | "dot" | "box"
    > = [
      "text",
      "gauge",
      "pie",
      "donut",
      "table",
      "numeric",
      "bar_label",
      "line_label",
      "bar",
      "line",
      "dot",
      "box",
    ];

    for (let i = 0; i < priority.length; i += 1) {
      const type = priority[i];
      if (candidates.has(type)) {
        return type;
      }
    }

    return "line";
  }

  private getSeriesRepresentativeValue(values: Array<number | null>): number {
    for (let i = values.length - 1; i >= 0; i -= 1) {
      const v = values[i];
      if (v !== null && !Number.isNaN(v)) {
        return Math.max(0, Number(v));
      }
    }
    return 0;
  }

  private renderBarChartHtml(
    chartData: { categories: string[]; series: Array<{ name: string; data: Array<number | null>; type: "bar" | "line" | "dot" | "bar_label" | "line_label" | "pie" | "donut" | "gauge" | "numeric" | "table" | "text" | "box" }> },
    title: string
  ): string {
    const width = 640;
    const height = 280;
    const margin = { top: 28, right: 16, bottom: 60, left: 42 };
    const plotWidth = width - margin.left - margin.right;
    const plotHeight = height - margin.top - margin.bottom;
    const colors = ["#2563eb", "#14b8a6", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4"];

    const values = chartData.series
      .flatMap((s) => s.data)
      .filter((v): v is number => v !== null && !Number.isNaN(v));
    if (!values.length) {
      return "";
    }
    const max = Math.max(...values, 0);
    const categoriesCount = Math.max(chartData.categories.length, 1);
    const barSeries = chartData.series.filter((s) => s.type === "bar");
    const lineSeries = chartData.series.filter((s) => s.type !== "bar");
    const seriesCount = Math.max(barSeries.length, 1);
    const groupWidth = plotWidth / categoriesCount;
    const barWidth = Math.max(3, (groupWidth * 0.8) / seriesCount);

    const bars: string[] = [];
    const lines: string[] = [];
    for (let ci = 0; ci < categoriesCount; ci += 1) {
      for (let si = 0; si < barSeries.length; si += 1) {
        const value = barSeries[si].data[ci];
        if (value === null || Number.isNaN(value)) {
          continue;
        }
        const h = max > 0 ? (value / max) * plotHeight : 0;
        const x = margin.left + ci * groupWidth + (groupWidth * 0.1) + si * barWidth;
        const y = margin.top + (plotHeight - h);
        bars.push(
          `<rect x="${x.toFixed(2)}" y="${y.toFixed(2)}" width="${barWidth.toFixed(
            2
          )}" height="${h.toFixed(2)}" fill="${colors[si % colors.length]}" fill-opacity="0.95" />`
        );
      }
    }

    for (let li = 0; li < lineSeries.length; li += 1) {
      const series = lineSeries[li];
      const points = series.data
        .map((v, i) => {
          if (v === null || Number.isNaN(v)) {
            return "";
          }
          const x = margin.left + i * groupWidth + groupWidth / 2;
          const y = margin.top + (plotHeight - ((v / max) * plotHeight));
          return `${x.toFixed(2)},${y.toFixed(2)}`;
        })
        .filter(Boolean)
        .join(" ");
      if (!points) {
        continue;
      }
      lines.push(
        `<polyline points="${points}" fill="none" stroke="${
          colors[(barSeries.length + li) % colors.length]
        }" stroke-width="2.5" />`
      );
    }

    const labelStep = Math.max(1, Math.ceil(chartData.categories.length / 10));
    const labels = chartData.categories
      .map((c, i) => {
        if (i % labelStep !== 0 && i !== chartData.categories.length - 1) {
          return "";
        }
        const x = margin.left + i * groupWidth + groupWidth / 2;
        return `<text x="${x.toFixed(2)}" y="${height - 16}" text-anchor="end" transform="rotate(-40 ${x.toFixed(
          2
        )} ${height - 16})">${this.escapeHtml(this.shortenLabel(c, 10))}</text>`;
      })
      .join("");

    const legend = this.renderChartLegend(chartData.series, colors);
    const yTicks = Array.from({ length: 7 }, (_, i) => (max / 6) * i)
      .map((tick) => {
        const y = margin.top + plotHeight - ((tick / max) * plotHeight || 0);
        return `
          <line x1="${margin.left}" y1="${y.toFixed(2)}" x2="${margin.left + plotWidth}" y2="${y.toFixed(2)}" class="grid-line"></line>
          <text x="${margin.left - 6}" y="${(y + 4).toFixed(2)}" text-anchor="end" class="y-tick">${Math.round(tick)}</text>
        `;
      })
      .join("");

    return `
      <div class="report-chart">
        <div class="report-chart-title">${this.escapeHtml(title)}</div>
        ${legend}
        <svg viewBox="0 0 ${width} ${height}" class="report-chart-svg" role="img" aria-label="${this.escapeHtml(
          title
        )}">
          <defs>
            <linearGradient id="chartBgGradient" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stop-color="#1f2937" />
              <stop offset="100%" stop-color="#3f3f46" />
            </linearGradient>
          </defs>
          <rect x="0" y="0" width="${width}" height="${height}" fill="url(#chartBgGradient)"></rect>
          ${yTicks}
          <line x1="${margin.left}" y1="${margin.top}" x2="${margin.left}" y2="${
      margin.top + plotHeight
    }" class="axis-line"></line>
          <line x1="${margin.left}" y1="${margin.top + plotHeight}" x2="${
      margin.left + plotWidth
    }" y2="${margin.top + plotHeight}" class="axis-line"></line>
          ${bars.join("")}
          ${lines.join("")}
          <g class="axis-labels">${labels}</g>
        </svg>
      </div>
    `;
  }

  private renderLineChartHtml(
    chartData: { categories: string[]; series: Array<{ name: string; data: Array<number | null>; type: "bar" | "line" | "dot" | "bar_label" | "line_label" | "pie" | "donut" | "gauge" | "numeric" | "table" | "text" | "box" }> },
    title: string
  ): string {
    const width = 640;
    const height = 260;
    const margin = { top: 20, right: 20, bottom: 50, left: 48 };
    const plotWidth = width - margin.left - margin.right;
    const plotHeight = height - margin.top - margin.bottom;
    const colors = ["#2563eb", "#14b8a6", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4"];

    const values = chartData.series
      .flatMap((s) => s.data)
      .filter((v): v is number => v !== null && !Number.isNaN(v));
    if (!values.length) {
      return "";
    }

    const min = Math.min(...values);
    const max = Math.max(...values);
    const span = max - min || 1;
    const pointsCount = Math.max(chartData.categories.length - 1, 1);

    const lines = chartData.series
      .map((series, idx) => {
        const pts = series.data
          .map((v, i) => {
            if (v === null || Number.isNaN(v)) {
              return "";
            }
            const x = margin.left + (i / pointsCount) * plotWidth;
            const y = margin.top + ((max - v) / span) * plotHeight;
            return `${x.toFixed(2)},${y.toFixed(2)}`;
          })
          .filter(Boolean)
          .join(" ");

        if (!pts) {
          return "";
        }

        return `<polyline points="${pts}" fill="none" stroke="${
          colors[idx % colors.length]
        }" stroke-width="2" />`;
      })
      .join("");

    const labels = chartData.categories
      .map((c, i) => {
        if (i % Math.ceil(Math.max(chartData.categories.length, 1) / 8) !== 0) {
          return "";
        }
        const x = margin.left + (i / Math.max(chartData.categories.length - 1, 1)) * plotWidth;
        return `<text x="${x.toFixed(2)}" y="${height - 16}" text-anchor="middle">${this.escapeHtml(
          this.shortenLabel(c, 12)
        )}</text>`;
      })
      .join("");

    const legend = this.renderChartLegend(chartData.series, colors);

    return `
      <div class="report-chart">
        <div class="report-chart-title">${this.escapeHtml(title)}</div>
        <svg viewBox="0 0 ${width} ${height}" class="report-chart-svg" role="img" aria-label="${this.escapeHtml(
          title
        )}">
          <line x1="${margin.left}" y1="${margin.top}" x2="${margin.left}" y2="${
      margin.top + plotHeight
    }" class="axis-line"></line>
          <line x1="${margin.left}" y1="${margin.top + plotHeight}" x2="${
      margin.left + plotWidth
    }" y2="${margin.top + plotHeight}" class="axis-line"></line>
          ${lines}
          <g class="axis-labels">${labels}</g>
        </svg>
        ${legend}
      </div>
    `;
  }

  private renderChartLegend(
    series: Array<{ name: string; data: Array<number | null>; type: "bar" | "line" | "dot" | "bar_label" | "line_label" | "pie" | "donut" | "gauge" | "numeric" | "table" | "text" | "box" }>,
    colors: string[]
  ): string {
    const items = series
      .map(
        (s, i) => `
          <span class="legend-item">
            <span class="legend-swatch" style="background:${colors[i % colors.length]}"></span>
            <span class="legend-label">${this.escapeHtml(s.name || `Series ${i + 1}`)}</span>
          </span>
        `
      )
      .join("");

    return `<div class="report-chart-legend">${items}</div>`;
  }

  private toNumberOrNull(value: unknown): number | null {
    if (value === null || value === undefined || value === "") {
      return null;
    }
    const num = Number(value);
    return Number.isNaN(num) ? null : num;
  }

  private shortenLabel(value: string, max = 18): string {
    const text = String(value || "").trim();
    if (text.length <= max) {
      return text;
    }
    return `${text.slice(0, max - 1)}...`;
  }

  private formatDateLikeLabel(value: string): string {
    const raw = String(value || "").trim();
    if (!raw) {
      return "";
    }

    if (this.looksLikeWeeklyLabel(raw)) {
      return raw;
    }

    const dateOnly = raw.match(/\d{4}-\d{2}-\d{2}/);
    if (dateOnly) {
      return dateOnly[0];
    }
    return this.shortenLabel(raw, 28);
  }

  private formatHourlyTimestampLabel(value: string): string {
    const raw = String(value || "").trim();
    if (!raw) {
      return "";
    }
    const parsed = this.parseDateTimeSafe(raw);
    if (parsed) {
      return this.formatHourlyTimestamp(parsed);
    }
    return this.shortenLabel(raw, 36);
  }

  private buildCategoryLabel(value: any, interval: string): string {
    const rawStart = String(value?.start || "").trim();
    const rawEnd = String(value?.end || "").trim();
    const isRaw = interval.includes("raw");
    const isHourly = interval.includes("hour") || isRaw;
    const isDaily = interval.includes("day");

    if (this.looksLikeWeeklyLabel(rawStart) && !isDaily) {
      return rawStart;
    }

    const startDate = this.parseDateSafe(rawStart);
    const endDate = this.parseDateSafe(rawEnd);

    if (isHourly) {
      const hourlyDate =
        this.parseDateTimeSafe(rawStart) ||
        (!isRaw ? startDate : null) ||
        this.parseDateTimeSafe(rawEnd) ||
        (!isRaw ? endDate : null);
      if (hourlyDate) {
        return this.formatHourlyTimestamp(hourlyDate);
      }
      return this.shortenLabel(rawStart || rawEnd, isRaw ? 48 : 36);
    }

    if (isDaily) {
      const dailyDate =
        startDate ||
        this.parseDateFromRangeLikeLabel(rawStart) ||
        this.parseDateSafe(rawEnd) ||
        this.parseDateFromRangeLikeLabel(rawEnd);
      if (dailyDate) {
        return this.toIsoDate(dailyDate);
      }
    }

    if (interval.includes("week") && startDate && endDate) {
      return this.formatWeeklyRangeLabel(startDate, endDate);
    }

    if (interval.includes("month") && startDate) {
      return this.formatMonthlyLabel(startDate);
    }

    if (interval.includes("day") && startDate) {
      return this.toIsoDate(startDate);
    }

    if (interval.includes("day")) {
      const parsedRangeDate = this.parseDateFromRangeLikeLabel(rawStart);
      if (parsedRangeDate) {
        return this.toIsoDate(parsedRangeDate);
      }
      return this.formatDateLikeLabel(rawStart);
    }

    if (!!rawEnd && startDate && endDate) {
      const inferred = this.inferRangeGranularity(startDate, endDate);
      if (inferred === "daily") {
        return this.toIsoDate(startDate);
      }
      if (inferred === "monthly") {
        return this.formatMonthlyLabel(startDate);
      }
      return this.formatWeeklyRangeLabel(startDate, endDate);
    }

    if (startDate) {
      return this.toIsoDate(startDate);
    }

    if (rawStart) {
      return this.formatDateLikeLabel(rawStart);
    }

    return this.shortenLabel(String(value?.label || value?.category || ""), 28);
  }

  private parseDateFromRangeLikeLabel(raw: string): Date | null {
    const text = this.normalizeDateLabelText(raw);
    if (!text) {
      return null;
    }

    const months: Record<string, number> = {
      jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
      jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11,
    };

    let m = text.match(/^\(?\s*(\d{1,2})\s*-\s*(\d{1,2})\s*\)?\s*([A-Za-z]{3,9})\s*'?\s*(\d{2,4})$/i);
    if (m) {
      const day = Number(m[1]);
      const mon = months[String(m[3]).slice(0, 3).toLowerCase()];
      const y = Number(m[4]);
      const year = y < 100 ? 2000 + y : y;
      if (!Number.isNaN(day) && mon !== undefined) {
        return new Date(year, mon, day);
      }
    }

    m = text.match(/^\(?\s*(\d{1,2})\s+([A-Za-z]{3,9})\s*-\s*(\d{1,2})\s+([A-Za-z]{3,9})\s*'?\s*(\d{2,4})\s*\)?$/i);
    if (m) {
      const day = Number(m[1]);
      const mon = months[String(m[2]).slice(0, 3).toLowerCase()];
      const y = Number(m[5]);
      const year = y < 100 ? 2000 + y : y;
      if (!Number.isNaN(day) && mon !== undefined) {
        return new Date(year, mon, day);
      }
    }

    return null;
  }

  private inferRangeGranularity(
    start: Date,
    end: Date
  ): "daily" | "weekly" | "monthly" {
    const msPerDay = 24 * 60 * 60 * 1000;
    const diffDays = Math.max(0, Math.round((end.getTime() - start.getTime()) / msPerDay)) + 1;
    if (diffDays <= 1) {
      return "daily";
    }
    if (diffDays >= 27) {
      return "monthly";
    }
    return "weekly";
  }

  private areDailyCategories(categories: string[]): boolean {
    if (!categories.length) {
      return false;
    }
    return categories.every((c) => /^\d{4}-\d{2}-\d{2}$/.test(String(c || "").trim()));
  }

  private computeAutoDateLabelStep(
    ctx: CanvasRenderingContext2D,
    labels: string[],
    plotWidth: number,
    isTimestamp: boolean,
    rotationDeg = -40
  ): number {
    const count = Math.max(1, labels.length);
    if (count <= 1 || plotWidth <= 0) {
      return 1;
    }
    // Strict UX rule: from 28 points onward, always alternate labels.
    if (count >= 28) {
      return 2;
    }

    const spacing = plotWidth / count;
    let maxWidth = 0;
    for (let i = 0; i < labels.length; i += 1) {
      const label = isTimestamp
        ? this.formatHourlyTimestampLabel(labels[i])
        : this.formatDateLikeLabel(labels[i]);
      const width = ctx.measureText(label).width;
      if (width > maxWidth) {
        maxWidth = width;
      }
    }

    const absRad = (Math.abs(rotationDeg) * Math.PI) / 180;
    const textHeight = 12;
    const projectedWidth =
      maxWidth * Math.abs(Math.cos(absRad)) + textHeight * Math.abs(Math.sin(absRad));
    const padded = projectedWidth + 14;
    const byWidth = Math.max(1, Math.ceil(padded / Math.max(spacing, 1)));
    const byCount = isTimestamp
      ? Math.max(1, Math.ceil(count / 18))
      : Math.max(1, Math.ceil(count / 12));
    return Math.max(1, byWidth, byCount);
  }

  private computeRotatedDateLabelDepth(
    ctx: CanvasRenderingContext2D,
    labels: string[],
    step: number,
    isTimestamp: boolean,
    rotationDeg: number
  ): number {
    if (!labels.length) {
      return 0;
    }
    const rad = (Math.abs(rotationDeg) * Math.PI) / 180;
    const sin = Math.sin(rad);
    const cos = Math.abs(Math.cos(rad));
    const approxTextHeight = 12;
    let maxDepth = 0;

    for (let i = 0; i < labels.length; i += 1) {
      if (i % Math.max(1, step) !== 0 && i !== labels.length - 1) {
        continue;
      }
      const label = isTimestamp
        ? this.formatHourlyTimestampLabel(labels[i])
        : this.formatDateLikeLabel(labels[i]);
      const width = ctx.measureText(label).width;
      const depth = width * sin + approxTextHeight * cos;
      if (depth > maxDepth) {
        maxDepth = depth;
      }
    }

    return Math.ceil(maxDepth);
  }

  private normalizeDateLabelRotation(value: unknown): number {
    const parsed = Number(value);
    if (Number.isNaN(parsed)) {
      return -40;
    }
    return Math.max(-180, Math.min(180, Math.round(parsed)));
  }

  private normalizeChartTitleSize(value: unknown): number {
    const parsed = Number(value);
    if (Number.isNaN(parsed)) {
      return 24;
    }
    return Math.max(16, Math.min(64, Math.round(parsed)));
  }

  /**
   * Greedily wrap `text` into lines that each fit within `maxWidth` for the
   * canvas context's current font. Used to keep long chart titles fully visible
   * instead of clipping them at the canvas edges. A single word wider than
   * maxWidth is kept on its own line (it cannot be broken without hyphenation).
   */
  private wrapTextToWidth(
    ctx: CanvasRenderingContext2D,
    text: string,
    maxWidth: number
  ): string[] {
    const trimmed = String(text || "").trim();
    if (!trimmed) {
      return [""];
    }
    if (ctx.measureText(trimmed).width <= maxWidth) {
      return [trimmed];
    }

    const words = trimmed.split(/\s+/);
    const lines: string[] = [];
    let current = "";
    for (let i = 0; i < words.length; i += 1) {
      const word = words[i];
      const candidate = current ? `${current} ${word}` : word;
      if (current && ctx.measureText(candidate).width > maxWidth) {
        lines.push(current);
        current = word;
      } else {
        current = candidate;
      }
    }
    if (current) {
      lines.push(current);
    }
    return lines.length ? lines : [trimmed];
  }

  private drawRoundedRect(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number
  ): void {
    const r = Math.max(0, Math.min(radius, width / 2, height / 2));
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + width - r, y);
    ctx.arcTo(x + width, y, x + width, y + r, r);
    ctx.lineTo(x + width, y + height - r);
    ctx.arcTo(x + width, y + height, x + width - r, y + height, r);
    ctx.lineTo(x + r, y + height);
    ctx.arcTo(x, y + height, x, y + height - r, r);
    ctx.lineTo(x, y + r);
    ctx.arcTo(x, y, x + r, y, r);
    ctx.closePath();
  }

  private formatNumericValue(value: number): string {
    if (Number.isNaN(value)) {
      return "0";
    }
    const rounded = Math.round(value * 100) / 100;
    return rounded.toLocaleString("en-US", { maximumFractionDigits: 2 });
  }

  private drawValueLabel(
    ctx: CanvasRenderingContext2D,
    text: string,
    x: number,
    y: number,
    fontSize: number,
    rotationDeg: number,
    color: string
  ): void {
    ctx.save();
    ctx.fillStyle = color;
    ctx.font = `700 ${fontSize}px Arial`;
    ctx.textAlign = "center";
    ctx.textBaseline = "alphabetic";
    if (rotationDeg) {
      ctx.translate(x, y);
      ctx.rotate((rotationDeg * Math.PI) / 180);
      ctx.fillText(text, 0, 0);
    } else {
      ctx.fillText(text, x, y);
    }
    ctx.restore();
  }

  private normalizeValueLabelSize(value: unknown): number {
    const parsed = Number(value);
    if (Number.isNaN(parsed)) {
      return 11;
    }
    return Math.max(8, Math.min(36, Math.round(parsed)));
  }

  private normalizeValueLabelRotation(value: unknown): number {
    const parsed = Number(value);
    if (Number.isNaN(parsed)) {
      return 0;
    }
    return Math.max(-90, Math.min(90, Math.round(parsed)));
  }

  private normalizeLegendSize(value: unknown): number {
    const parsed = Number(value);
    if (Number.isNaN(parsed)) {
      return 15;
    }
    return Math.max(10, Math.min(40, Math.round(parsed)));
  }

  private normalizeAxisLabelSize(value: unknown): number {
    const parsed = Number(value);
    if (Number.isNaN(parsed)) {
      return 12;
    }
    return Math.max(7, Math.min(30, Math.round(parsed)));
  }

  private normalizeTitlePad(value: unknown): number {
    const parsed = Number(value);
    if (Number.isNaN(parsed)) {
      return 0;
    }
    return Math.max(0, Math.min(200, Math.round(parsed)));
  }

  private looksLikeWeeklyLabel(value: string): boolean {
    const raw = this.normalizeDateLabelText(value);
    if (!raw) {
      return false;
    }
    return (
      /^\(?\d{1,2}\s*-\s*\d{1,2}\)?\s*[A-Za-z]{3,9}\s*'?\d{2,4}$/i.test(raw) ||
      /^\(?\d{1,2}\s+[A-Za-z]{3,9}\s*-\s*\d{1,2}\s+[A-Za-z]{3,9}\s*'?\d{2,4}\)?$/i.test(raw)
    );
  }

  private parseDateSafe(raw: string): Date | null {
    const text = this.normalizeDateLabelText(raw);
    if (!text) {
      return null;
    }
    const dateOnly = text.match(/\d{4}-\d{2}-\d{2}/);
    if (!dateOnly && !this.isLikelyDateText(text)) {
      return null;
    }
    const candidate = dateOnly ? dateOnly[0] : text;
    const d = new Date(candidate);
    if (!Number.isNaN(d.getTime())) {
      return d;
    }

    const m = text.match(/^(\d{1,2})\s+([A-Za-z]{3,9})\s*'?\s*(\d{2,4})$/i);
    if (m) {
      const day = Number(m[1]);
      const monthMap: Record<string, number> = {
        jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
        jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11,
      };
      const mon = monthMap[String(m[2]).slice(0, 3).toLowerCase()];
      const y = Number(m[3]);
      const year = y < 100 ? 2000 + y : y;
      if (!Number.isNaN(day) && mon !== undefined) {
        return new Date(year, mon, day);
      }
    }

    return null;
  }

  private parseDateTimeSafe(raw: string): Date | null {
    const text = this.normalizeDateLabelText(raw);
    if (!text) {
      return null;
    }
    if (!this.isLikelyDateText(text) || !this.isLikelyTimeText(text)) {
      return null;
    }
    const manual = text.match(
      /^(\d{1,2})\s+([A-Za-z]{3,9})\s+(\d{2,4})(?:,?\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/i
    );
    if (manual) {
      const day = Number(manual[1]);
      const monthMap: Record<string, number> = {
        jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
        jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11,
      };
      const mon = monthMap[String(manual[2]).slice(0, 3).toLowerCase()];
      const y = Number(manual[3]);
      const year = y < 100 ? 2000 + y : y;
      const hh = Number(manual[4] || 0);
      const mm = Number(manual[5] || 0);
      const ss = Number(manual[6] || 0);
      if (!Number.isNaN(day) && mon !== undefined && !Number.isNaN(year)) {
        return new Date(year, mon, day, hh, mm, ss);
      }
    }
    const d = new Date(text);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  private isLikelyDateText(text: string): boolean {
    const raw = String(text || '').trim();
    if (!raw) {
      return false;
    }
    return (
      /\d{4}-\d{1,2}-\d{1,2}/.test(raw) ||
      /\d{1,2}\/\d{1,2}\/\d{2,4}/.test(raw) ||
      /\d{1,2}\s+[A-Za-z]{3,9}\s+\d{2,4}/.test(raw) ||
      /[A-Za-z]{3,9}\s+\d{1,2},?\s+\d{2,4}/.test(raw)
    );
  }
  private isLikelyTimeText(text: string): boolean {
    const raw = String(text || '').trim();
    if (!raw) {
      return false;
    }
    return (
      /(?:^|\s|T)\d{1,2}:\d{2}(?::\d{2})?(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?(?:\s|$)/.test(raw) ||
      /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(raw)
    );
  }

  private normalizeDateLabelText(raw: string): string {
    return String(raw || "")
      .trim()
      .replace(/[’‘`]/g, "'")
      .replace(/[–—]/g, "-")
      .replace(/\s+/g, " ");
  }

  private toIsoDate(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }

  private formatHourlyTimestamp(date: Date): string {
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const day = String(date.getDate()).padStart(2, "0");
    const mon = months[date.getMonth()];
    const year = date.getFullYear();
    const hh = String(date.getHours()).padStart(2, "0");
    const mm = String(date.getMinutes()).padStart(2, "0");
    return `${day} ${mon} ${year}, ${hh}:${mm}`;
  }

  private formatWeeklyRangeLabel(start: Date, end: Date): string {
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const sDay = start.getDate();
    const eDay = end.getDate();
    const sMon = months[start.getMonth()];
    const eMon = months[end.getMonth()];
    const sYear = start.getFullYear();
    const eYear = end.getFullYear();
    const eYY = String(eYear).slice(-2);
    const sYY = String(sYear).slice(-2);

    if (sYear === eYear && sMon === eMon) {
      return `(${sDay}-${eDay}) ${sMon}'${eYY}`;
    }
    if (sYear === eYear) {
      return `(${sDay} ${sMon}-${eDay} ${eMon} '${eYY})`;
    }
    return `(${sDay} ${sMon}-${eDay} ${eMon} '${eYY})`;
  }

  private formatMonthlyLabel(date: Date): string {
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const mon = months[date.getMonth()];
    const yy = String(date.getFullYear()).slice(-2);
    return `${mon}'${yy}`;
  }

  private toHexColor(value: string | undefined, fallback: string): string {
    const raw = String(value || "").trim();
    if (!raw) {
      return fallback;
    }
    if (raw.startsWith("#")) {
      return raw.length === 7 ? raw : fallback;
    }
    const match = raw.match(
      /rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})/i
    );
    if (!match) {
      return fallback;
    }
    const toHex = (n: number): string =>
      Math.max(0, Math.min(255, n)).toString(16).padStart(2, "0");
    return `#${toHex(Number(match[1]))}${toHex(Number(match[2]))}${toHex(Number(match[3]))}`;
  }


  private getReportRichContent(report: DashboardReportData): string {
    const isAiReport = report.parameters?.[0]?.graphType === "ai";
    if (isAiReport) {
      return report.metadata?.ai_graph_details?.ai_response || "";
    }
    return report.metadata?.text_content || "";
  }

  private getReportRichContentByReportId(
    report: DashboardReportData
  ): { content: string; source: string } {
    const metadata = (report.metadata || {}) as Record<string, unknown>;
    const reportIdKey = String(report.reportId);

    this.debugLog("[PDF] metadata keys", report.reportId, Object.keys(metadata));

    const idMappedContent = this.extractIdMappedContent(metadata, reportIdKey);
    if (idMappedContent) {
      return { content: idMappedContent, source: "id-mapped" };
    }

    const listMappedContent = this.extractListMappedContent(metadata, report.reportId);
    if (listMappedContent) {
      return { content: listMappedContent, source: "list-mapped" };
    }

    const fallback = this.getReportRichContent(report);
    return { content: fallback, source: "direct-fallback" };
  }

  private async getReportContentReportWise(
    report: DashboardReportData
  ): Promise<{ content: string; source: string }> {
    const reportId = Number(report.reportId);
    if (!Number.isNaN(reportId) && reportId > 0) {
      const cached = this.reportContentCache.get(reportId);
      if (typeof cached === "string") {
        return { content: cached, source: "report-id-cache" };
      }

      try {
        const response = await firstValueFrom(
          this.reportService.getReportById(reportId)
        );
        const data = response?.data as any;
        const fromReportById = this.extractContentFromReportByIdPayload(data);
        if (fromReportById) {
          this.reportContentCache.set(reportId, fromReportById);
          return { content: fromReportById, source: "report-id-api" };
        }
      } catch (error) {
        this.debugLog("[PDF] report-id fetch failed", reportId, error);
      }
    }

    return this.getReportRichContentByReportId(report);
  }

  private extractContentFromReportByIdPayload(data: any): string {
    if (!data || typeof data !== "object") {
      return "";
    }

    const metadata = (data.metadata || {}) as Record<string, unknown>;
    const reportId = Number(data.reportId || data.id || 0);
    const reportIdKey = reportId > 0 ? String(reportId) : "";

    if (reportIdKey) {
      const idMapped = this.extractIdMappedContent(metadata, reportIdKey);
      if (idMapped) {
        return idMapped;
      }

      const listMapped = this.extractListMappedContent(metadata, reportId);
      if (listMapped) {
        return listMapped;
      }
    }

    const firstGraphType =
      data?.parameters?.[0]?.graphType || data?.parameters?.[0]?.graph_type;
    const isAi = String(firstGraphType || "").toLowerCase() === "ai";

    if (isAi) {
      return String(metadata["ai_graph_details"]?.["ai_response"] || "");
    }

    return String(metadata["text_content"] || "");
  }

  private extractIdMappedContent(
    metadata: Record<string, unknown>,
    reportIdKey: string
  ): string {
    const maps = [
      metadata["report_content_by_id"],
      metadata["report_contents_by_id"],
      metadata["text_content_by_id"],
      metadata["ai_response_by_id"],
      metadata["reportContentById"],
      metadata["textContentById"],
      metadata["aiResponseById"],
      metadata["text_content"],
      metadata["ai_response"],
    ];

    for (let i = 0; i < maps.length; i += 1) {
      const map = maps[i] as unknown;
      if (!map || typeof map !== "object" || Array.isArray(map)) {
        continue;
      }

      const byId = map as Record<string, unknown>;
      const candidate = byId[reportIdKey];
      if (typeof candidate === "string" && candidate.trim()) {
        return candidate;
      }
    }

    return "";
  }

  private extractListMappedContent(
    metadata: Record<string, unknown>,
    reportId: number
  ): string {
    const lists = [
      metadata["report_contents"],
      metadata["report_content"],
      metadata["reports_content"],
      metadata["sections"],
      metadata["items"],
    ];

    for (let i = 0; i < lists.length; i += 1) {
      const list = lists[i] as unknown;
      if (!Array.isArray(list)) {
        continue;
      }

      for (let j = 0; j < list.length; j += 1) {
        const item = list[j] as Record<string, unknown>;
        if (!item || typeof item !== "object") {
          continue;
        }

        const id = Number(item["reportId"] ?? item["report_id"] ?? item["id"]);
        if (id !== reportId) {
          continue;
        }

        const candidate =
          item["content"] ??
          item["html"] ??
          item["text_content"] ??
          item["ai_response"];

        if (typeof candidate === "string" && candidate.trim()) {
          return candidate;
        }
      }
    }

    return "";
  }

  private sanitizeImportedHtml(html: string): string {
    if (!html) {
      return "";
    }

    return String(html)
      .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, "")
      .replace(/\son\w+="[^"]*"/gi, "")
      .replace(/\son\w+='[^']*'/gi, "")
      .replace(/javascript:/gi, "");
  }

  /**
   * Justify the body text of the freshly generated document so the user doesn't
   * have to align each block by hand. Applies to paragraphs and list items only,
   * skips text inside charts/tables (they manage their own alignment) and any
   * block that already carries an explicit alignment.
   */
  private justifyDocumentText(html: string): string {
    if (!html) {
      return html;
    }
    const container = document.createElement("div");
    container.innerHTML = html;

    const blocks = Array.from(
      container.querySelectorAll("p, li")
    ) as HTMLElement[];
    for (let i = 0; i < blocks.length; i += 1) {
      const block = blocks[i];
      if (
        block.closest(
          ".report-chart, .report-chart-image, .report-table-block, table"
        )
      ) {
        continue;
      }
      const text = String(block.textContent || "").trim();
      if (!text) {
        continue;
      }
      if (!block.style.textAlign) {
        block.style.textAlign = "justify";
      }
    }

    return container.innerHTML;
  }

  private normalizeReportHtmlContent(html: string, reportId: number): string {
    const sanitized = this.sanitizeImportedHtml(html);
    if (!sanitized) {
      return "";
    }

    const container = document.createElement("div");
    container.innerHTML = sanitized;

    // Some legacy/saved report payloads include complete editor pages repeatedly.
    // Keep only unique page blocks by text signature.
    const pageNodes = Array.from(
      container.querySelectorAll(".doc-page, .editor-page, .page")
    );

    if (pageNodes.length > 1) {
      const seen = new Set<string>();
      let removed = 0;

      for (let i = 0; i < pageNodes.length; i += 1) {
        const node = pageNodes[i] as HTMLElement;
        const signature = this.previewText(node.innerText || node.textContent || "", 200)
          .toLowerCase()
          .trim();

        if (!signature) {
          continue;
        }

        if (seen.has(signature)) {
          node.remove();
          removed += 1;
          continue;
        }

        seen.add(signature);
      }

      if (removed > 0) {
        this.debugLog(
          "[PDF] normalized duplicate pages",
          "reportId:",
          reportId,
          "originalPages:",
          pageNodes.length,
          "removed:",
          removed,
          "kept:",
          pageNodes.length - removed
        );
      }
    }

    this.relaxNonBreakingSpaces(container);

    return container.innerHTML;
  }

  /**
   * Saved/pasted report content often joins words with non-breaking spaces
   * (&nbsp;), which the browser will not wrap at — causing whole lines to either
   * overflow the page or get broken mid-word. Convert single non-breaking spaces
   * back to regular spaces so text wraps naturally at word boundaries. Runs of
   * 2+ non-breaking spaces are kept as-is (those are usually intentional padding
   * for alignment, e.g. "Subject     :").
   */
  private relaxNonBreakingSpaces(container: HTMLElement): void {
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
    const textNodes: Text[] = [];
    let current = walker.nextNode();
    while (current) {
      textNodes.push(current as Text);
      current = walker.nextNode();
    }

    for (let i = 0; i < textNodes.length; i += 1) {
      const node = textNodes[i];
      const value = node.nodeValue || "";
      if (value.indexOf(" ") === -1) {
        continue;
      }
      node.nodeValue = value.replace(/ +/g, (run) =>
        run.length === 1 ? " " : run
      );
    }
  }

  private wrapImagesForControl(html: string): string {
    if (!html) {
      return "";
    }

    const container = document.createElement("div");
    container.innerHTML = html;
    const images = Array.from(container.querySelectorAll("img"));

    for (let i = 0; i < images.length; i += 1) {
      const img = images[i];
      const parent = img.parentElement;
      if (!parent || parent.classList.contains("resizable-image")) {
        continue;
      }

      const wrapper = document.createElement("span");
      wrapper.className = "resizable-image imported-image-control";
      wrapper.setAttribute("contenteditable", "false");

      const attrWidth = img.getAttribute("width");
      const cssWidth = img.style.width;
      if (attrWidth && /^\d+$/.test(attrWidth)) {
        wrapper.style.width = `${attrWidth}px`;
      } else if (attrWidth) {
        wrapper.style.width = attrWidth;
      } else if (cssWidth) {
        wrapper.style.width = cssWidth;
      }

      img.removeAttribute("width");
      img.removeAttribute("height");
      img.style.maxWidth = "100%";
      img.style.height = "auto";

      parent.insertBefore(wrapper, img);
      wrapper.appendChild(img);
    }

    return container.innerHTML;
  }

  private toPlainText(html: string): string {
    if (!html) {
      return "";
    }

    const normalized = String(html)
      .replace(/<\/h[1-6]>/gi, "\n\n")
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/p>/gi, "\n")
      .replace(/<\/div>/gi, "\n")
      .replace(/&nbsp;/gi, " ");

    const temp = document.createElement("div");
    temp.innerHTML = normalized;

    const orderedLists = Array.from(temp.querySelectorAll("ol"));
    for (let i = 0; i < orderedLists.length; i += 1) {
      const list = orderedLists[i];
      const items = Array.from(list.children).filter(
        (child) => child.tagName.toLowerCase() === "li"
      );

      for (let itemIndex = 0; itemIndex < items.length; itemIndex += 1) {
        const li = items[itemIndex] as HTMLElement;
        li.insertBefore(
          document.createTextNode(`${itemIndex + 1}. `),
          li.firstChild
        );
        li.appendChild(document.createTextNode("\n"));
      }

      list.insertAdjacentText("beforebegin", "\n");
      list.insertAdjacentText("afterend", "\n");
    }

    const unorderedLists = Array.from(temp.querySelectorAll("ul"));
    for (let i = 0; i < unorderedLists.length; i += 1) {
      const list = unorderedLists[i];
      const items = Array.from(list.children).filter(
        (child) => child.tagName.toLowerCase() === "li"
      );

      for (let itemIndex = 0; itemIndex < items.length; itemIndex += 1) {
        const li = items[itemIndex] as HTMLElement;
        li.insertBefore(document.createTextNode("- "), li.firstChild);
        li.appendChild(document.createTextNode("\n"));
      }

      list.insertAdjacentText("beforebegin", "\n");
      list.insertAdjacentText("afterend", "\n");
    }

    return (temp.textContent || temp.innerText || "")
      .replace(/\u00a0/g, " ")
      .replace(/[ \t]{2,}/g, " ")
      .replace(/[ \t]+\n/g, "\n")
      .replace(/\n{3,}/g, "\n\n")
      .trim();
  }

  private escapeHtml(value: string): string {
    return String(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  private debugLog(...args: unknown[]): void {
    if (!this.debugPdf) {
      return;
    }
    console.log(...args);
  }

  private tableLog(...args: unknown[]): void {
    if (!this.debugTables) {
      return;
    }
    console.log(...args);
  }

  private previewText(html: string, max = 120): string {
    const text = String(html || "")
      .replace(/<[^>]*>/g, " ")
      .replace(/\s+/g, " ")
      .trim();
    return text.slice(0, max);
  }

  private debugDumpPages(tag: string): void {
    const pages = this.pages || [];
    this.debugLog(`[PDF] page dump (${tag}) count`, pages.length);

    const seen = new Map<string, number>();
    for (let i = 0; i < pages.length; i += 1) {
      const preview = this.previewText(pages[i].html, 140);
      const key = preview.toLowerCase();
      seen.set(key, (seen.get(key) || 0) + 1);
      this.debugLog(
        "[PDF] page",
        i + 1,
        "len:",
        (pages[i].html || "").length,
        "preview:",
        preview
      );
    }

    const duplicates = Array.from(seen.entries()).filter(([, count]) => count > 1);
    if (duplicates.length) {
      this.debugLog("[PDF] duplicate page previews", duplicates);
    }
  }

  /**
   * Dump the LIVE page editors (not the model) so we can see, per page, how tall
   * the content is vs the page box, whether it overflows, how many block children
   * it holds, and whether the page is visually blank. This is the key trace for
   * diagnosing phantom/blank pages and how reports get distributed by reflow.
   */
  private debugDumpPageDom(tag: string): void {
    if (!this.debugPdf) {
      return;
    }
    const editors = this.pageEditors?.toArray() || [];
    this.debugLog(`[PDF][DOM] dump (${tag}) pageCount`, editors.length);
    for (let i = 0; i < editors.length; i += 1) {
      const el = editors[i].nativeElement;
      const overflow = el.scrollHeight - el.clientHeight;
      const counts = {
        blocks: el.children.length,
        imgs: el.querySelectorAll("img").length,
        tables: el.querySelectorAll("table").length,
        charts: el.querySelectorAll(".report-chart, .report-chart-image").length,
      };
      this.debugLog(
        `[PDF][DOM] page ${i + 1}/${editors.length}`,
        this.isBlankPageEl(el) ? "⬜ BLANK" : "▦ content",
        overflow > 1 ? `OVERFLOW(+${overflow}px)` : "fits",
        "scrollH:",
        el.scrollHeight,
        "clientH:",
        el.clientHeight,
        "children:",
        JSON.stringify(counts),
        "text:",
        // Use textContent (already plain) sliced — NOT a regex over innerHTML,
        // which on the pre-split page can be multi-MB and is dumped every reflow.
        (el.textContent || "").replace(/\s+/g, " ").trim().slice(0, 90)
      );
    }
  }

  /** A page is "blank" when it has no visible text and no media/table/chart. */
  private isBlankPageEl(editor: HTMLElement): boolean {
    const text = (editor.textContent || "").replace(/ /g, " ").trim();
    if (text.length > 0) {
      return false;
    }
    return !editor.querySelector(
      "img, table, svg, canvas, hr, .report-chart, .report-table-block, .resizable-image"
    );
  }

  /**
   * String (model) counterpart of isBlankPageEl: true only when the page HTML has
   * no media/structural element AND no visible text. Used by trailing-page cleanup
   * so a media-only page (chart/image/table with no text) is never dropped.
   */
  private isBlankPageHtml(html: string): boolean {
    const s = String(html || "");
    if (/<(img|table|svg|canvas|hr|iframe)[\s/>]/i.test(s)) {
      return false;
    }
    if (
      /class\s*=\s*["'][^"']*(report-chart|report-table-block|resizable-image)/i.test(
        s
      )
    ) {
      return false;
    }
    const text = s
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/g, "")
      .replace(/\s+/g, "")
      .trim();
    return text.length === 0;
  }

  private debugSelectionLog(...args: unknown[]): void {
    if (!this.debugSelection) {
      return;
    }
    console.log(...args);
  }

  clearRawDebug(): void {
    this.rawDebugLines = [];
  }

  private rawDebugLog(...args: unknown[]): void {
    const line = args
      .map((a) => {
        if (typeof a === "string") {
          return a;
        }
        try {
          return JSON.stringify(a);
        } catch {
          return String(a);
        }
      })
      .join(" ");
    this.rawDebugLines = [...this.rawDebugLines.slice(-79), line];
    console.log(...args);
  }

  private describeElement(el: Element | null): string {
    if (!el) {
      return "null";
    }
    const cls = (el.className || "").toString().trim();
    const id = (el as HTMLElement).id ? `#${(el as HTMLElement).id}` : "";
    const classPart = cls ? `.${cls.replace(/\s+/g, ".")}` : "";
    return `${el.tagName.toLowerCase()}${id}${classPart}`;
  }

  private getEventPathSummary(event: Event): string {
    const path = this.getEventPathElements(event);
    return path
      .slice(0, 6)
      .map((el) => this.describeElement(el))
      .join(" -> ");
  }

  private getEventPathElements(event: Event): Element[] {
    const path = (event.composedPath?.() || []) as Array<EventTarget>;
    return path.filter((p): p is Element => p instanceof Element);
  }

}
